import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import posthog from 'posthog-js';
import { usePlaidLink } from 'react-plaid-link';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  ArrowDownRight,
  ArrowUpRight,
  ArrowUpDown,
  BarChart3,
  Bell,
  Bot,
  Brain,
  Briefcase,
  Building2,
  Camera,
  Car,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  CreditCard,
  Database,
  DollarSign,
  Download,
  Edit2,
  Eye,
  EyeOff,
  FileText,
  Flame,
  Home,
  Info,
  LayoutDashboard,
  Loader,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageSquare,
  Navigation,
  PieChart,
  PiggyBank,
  Plus,
  PlusCircle,
  Receipt,
  RefreshCw,
  Search,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  Sparkles,
  Target,
  ToggleLeft,
  ToggleRight,
  Trash2,
  TrendingDown,
  TrendingUp,
  Upload,
  User,
  Users,
  Wallet,
  Wifi,
  X,
  XCircle,
  Zap,
  Sun,
  Moon,
  Globe,
  Lightbulb,
  BookOpen,
} from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import {
  addTaxVaultTransaction,
  auth,
  checkDuplicateRevenue,
  checkDuplicateExpense,
  deleteAllExpenses,
  deleteExpense,
  deleteInvoice,
  deleteRevenue,
  deleteMileageTrip,
  deleteSubscription,
  deleteBill,
  deleteBudget,
  deleteScheduledTx,
  loadExpenses,
  loadInvoices,
  loadRevenue,
  loadBills,
  loadBudgets,
  loadScheduledTx,
  loadMileageTrips,
  loadSuggestedKeywords,
  loadSubscriptions,
  loadTaxVaultSettings,
  loadTaxVaultTransactions,
  loadUserProfile,
  findSimilarPatterns,
  logOut,
  onAuthStateChanged,
  predictCategory,
  saveExpense,
  saveQuantraChatHistory,
  saveInvoice,
  saveRevenue,
  saveBill,
  saveBudget,
  saveScheduledTx,
  saveMileageTrip,
  saveSubscription,
  saveSuggestedKeyword,
  saveTaxVaultSettings,
  saveUserProfile,
  signIn,
  signInWithGoogle,
  completeNativeMfa,
  getRestUser,
  signUp,
  syncTaxVaultFromIncome,
  loadQuantraChatHistory,
  clearQuantraChatHistory,
  createBillingPortal,
  createCheckoutSession,
  deductPaygCredits,
  getUsage,
  incrementUsage,
  logEvent,
  requestNotificationPermission,
  onForegroundMessage,
  updateInvoice,
  updateRevenue,
  updateScheduledTx,
  updateSubscription,
  markBillPaid,
  db,
  acceptWorkspaceInvite,
  getWorkspaceInvite,
  createInvoiceLink,
  getInvoiceByToken,
  resetPassword,
} from './firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import {
  EmailAuthProvider,
  getMultiFactorResolver,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  sendEmailVerification,
} from 'firebase/auth';
import { scanReceipt } from './ocrService';
import LearnedKeywordsManager from './LearnedKeywordsManager';
import {
  SHARED_SERVICE_KEYWORDS,
  normalizeForMatch,
  normalizeServiceKey,
  findServiceNameByText,
} from './subscriptionKeywords';
import { SetupChecklist, HelpBadge } from './Onboarding';
import { OnboardingTour } from './OnboardingTour';
import centrisLogo from './assets/logo.png';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

const IAPPlugin = {
  getProducts: (args) => window.Capacitor.nativePromise('IAP', 'getProducts', args),
  purchase: (args) => window.Capacitor.nativePromise('IAP', 'purchase', args),
  restorePurchases: (args) => window.Capacitor.nativePromise('IAP', 'restorePurchases', args || {}),
};
const MarketingHub = React.lazy(() => import('./MarketingHub'));
const WebsiteBuilder = React.lazy(() => import('./WebsiteBuilder'));
const ProductBuilder = React.lazy(() => import('./ProductBuilder'));
const ClientInvoiceView = React.lazy(() => import('./ClientInvoiceView'));

const generateNonce = () => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const random = new Uint8Array(32);
  crypto.getRandomValues(random);
  return Array.from(random).map(b => charset[b % charset.length]).join('');
};
const sha256Hex = async (str) => {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
};

const isNative = Capacitor.isNativePlatform();

/** Light haptic tap — no-op on web */
const haptic = (style = ImpactStyle.Light) => {
  if (isNative) Haptics.impact({ style }).catch(() => {});
};

/** Live Plaid bank link — off until production approval. UI uses statements + receipt scan instead. */
const PLAID_BANK_CONNECT_ENABLED = false;

/** User preference: how bank activity is brought in (persisted). Live option only applies when Plaid is enabled. */
const BANK_IMPORT_PREF_KEY = 'centris_bank_import_mode';
const BANK_IMPORT_STATEMENTS = 'statements';
const BANK_IMPORT_LIVE = 'live_bank';

function readBankImportPref() {
  try {
    const v = localStorage.getItem(BANK_IMPORT_PREF_KEY);
    if (v === BANK_IMPORT_LIVE || v === BANK_IMPORT_STATEMENTS) return v;
  } catch { /* ignore */ }
  return BANK_IMPORT_STATEMENTS;
}

document.documentElement.style.background = 'linear-gradient(135deg, #050913, #0c1428, #071020)';
document.body.style.background = 'linear-gradient(135deg, #050913, #0c1428, #071020)';

//ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬ Unified Feature Card ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬
// ============ INLINED UI COMPONENTS ============

const ModalPortal = ({ children }) => ReactDOM.createPortal(children, document.body);

const GlobalStyles = () => (
  <style>{`
    /* ======================================================
       CENTRIS DESIGN SYSTEM — ANIMATIONS
    ====================================================== */

    /* Page enter: slide up + fade with spring overshoot */
    @keyframes pageEnter {
      0%   { opacity: 0; transform: translateX(16px); }
      100% { opacity: 1; transform: none; }
    }
    @keyframes pageExit {
      0%   { opacity: 1; transform: none; }
      100% { opacity: 0; transform: translateX(-12px); }
    }
    @keyframes cardIn {
      0%   { opacity: 0; transform: translateY(18px) scale(0.97); }
      60%  { opacity: 1; transform: translateY(-2px) scale(1.005); }
      100% { opacity: 1; transform: none; }
    }
    /* Card stagger in */
    @keyframes cardIn {
      0%   { opacity: 0; transform: translateY(16px) scale(0.97); }
      100% { opacity: 1; transform: none; }
    }
    @keyframes cardFadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: none; }
    }
    @keyframes pageSlideIn {
      from { opacity: 0; transform: translateX(-16px); }
      to   { opacity: 1; transform: none; }
    }
    @keyframes quantraSlideIn {
      from { opacity: 0; transform: translateY(24px) scale(0.92); }
      to   { opacity: 1; transform: none; }
    }
    @keyframes toastIn {
      from { opacity: 0; transform: translateX(-20px) scale(0.94); }
      to   { opacity: 1; transform: none; }
    }
    @keyframes floatOrb {
      0%,100% { transform: translate(0,    0)    scale(1);    }
      33%     { transform: translate(28px,-18px)  scale(1.06); }
      66%     { transform: translate(-18px, 14px) scale(0.96); }
    }
    @keyframes floatOrb2 {
      0%,100% { transform: translate(0,    0)    scale(1);    }
      33%     { transform: translate(-22px, 16px) scale(1.04); }
      66%     { transform: translate(20px, -12px) scale(0.97); }
    }
    @keyframes shimmer {
      from { background-position: -200% 0; }
      to   { background-position:  200% 0; }
    }
    @keyframes pulseGlow {
      0%,100% { box-shadow: 0 0 12px rgba(99,102,241,0.4); }
      50%     { box-shadow: 0 0 28px rgba(99,102,241,0.7); }
    }

    /* ======================================================
       PAGE TRANSITIONS
    ====================================================== */
    .centris-page-enter {
      animation: pageEnter 0.28s ease both;
    }
    .centris-page-exit {
      animation: pageExit 0.15s ease both;
      pointer-events: none;
    }
    /* Staggered children inside a page */
    .centris-page-enter > *:nth-child(1) { animation: cardIn 0.5s cubic-bezier(0.16,1,0.3,1) 0.00s both; }
    .centris-page-enter > *:nth-child(2) { animation: cardIn 0.5s cubic-bezier(0.16,1,0.3,1) 0.05s both; }
    .centris-page-enter > *:nth-child(3) { animation: cardIn 0.5s cubic-bezier(0.16,1,0.3,1) 0.09s both; }
    .centris-page-enter > *:nth-child(4) { animation: cardIn 0.5s cubic-bezier(0.16,1,0.3,1) 0.13s both; }
    .centris-page-enter > *:nth-child(5) { animation: cardIn 0.5s cubic-bezier(0.16,1,0.3,1) 0.16s both; }
    .centris-page-enter > *:nth-child(6) { animation: cardIn 0.5s cubic-bezier(0.16,1,0.3,1) 0.19s both; }

    /* ======================================================
       CARD DEPTH — violet-navy glass, matches app palette
    ====================================================== */
    .centris-card {
      background: rgba(14, 16, 54, 0.78);
      border: 1px solid rgba(99, 102, 241, 0.14);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      box-shadow:
        0 1px 0  rgba(139, 92, 246, 0.15) inset,
        0 -1px 0 rgba(0, 0, 0, 0.30) inset,
        0 2px 6px  rgba(0, 0, 0, 0.40),
        0 8px 24px rgba(0, 0, 0, 0.28),
        0 0 0 0.5px rgba(99, 102, 241, 0.07) inset;
      transition: all 0.25s cubic-bezier(0.16,1,0.3,1);
    }
    .centris-card:hover {
      border-color: rgba(99, 102, 241, 0.26);
      box-shadow:
        0 1px 0  rgba(139, 92, 246, 0.20) inset,
        0 -1px 0 rgba(0, 0, 0, 0.30) inset,
        0 4px 10px rgba(0, 0, 0, 0.40),
        0 12px 32px rgba(0, 0, 0, 0.32),
        0 0 24px rgba(99, 102, 241, 0.07);
      transform: translateY(-2px);
    }
    .centris-shell.light-mode .centris-card {
      background: rgba(255, 255, 255, 0.88);
      border: 1px solid rgba(99, 102, 241, 0.11);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow:
        0 1px 0  rgba(255, 255, 255, 1) inset,
        0 -1px 0 rgba(99, 102, 241, 0.05) inset,
        0 2px 8px  rgba(99, 102, 241, 0.07),
        0 8px 24px rgba(0, 0, 0, 0.05);
    }
    .centris-shell.light-mode .centris-card:hover {
      border-color: rgba(99, 102, 241, 0.20);
      box-shadow:
        0 1px 0  rgba(255, 255, 255, 1) inset,
        0 -1px 0 rgba(99, 102, 241, 0.06) inset,
        0 4px 12px rgba(99, 102, 241, 0.10),
        0 10px 28px rgba(0, 0, 0, 0.06);
      transform: translateY(-2px);
    }

    /* ======================================================
       SIDEBAR
    ====================================================== */
    .centris-sidebar {
      background: linear-gradient(90deg, #02012E 0%, #261B93 100%) !important;
      border-right: 1px solid rgba(99, 102, 241, 0.18) !important;
      box-shadow: 4px 0 32px rgba(0,0,0,0.45), 1px 0 0 rgba(99,102,241,0.06);
    }
    .centris-shell.light-mode .centris-sidebar {
      background: linear-gradient(90deg, #C8C9CE 0%, #F5F5F5 100%) !important;
      border-right: 1px solid rgba(15, 23, 42, 0.10) !important;
      box-shadow: 4px 0 24px rgba(15, 23, 42, 0.06);
      color: #334155;
    }
    .centris-shell.light-mode .centris-sidebar [class*="border-white"] {
      border-color: rgba(15, 23, 42, 0.10) !important;
    }
    .centris-shell.light-mode .centris-sidebar .text-white { color: #1e293b !important; }
    .centris-shell.light-mode .centris-sidebar .text-slate-400 { color: #64748b !important; }
    .centris-shell.light-mode .centris-sidebar .text-slate-500 { color: #64748b !important; }
    .centris-shell.light-mode .centris-sidebar button:hover { color: #0f172a !important; }
    .centris-shell.light-mode .centris-sidebar button:hover svg { color: #0f172a !important; }
    .centris-shell.light-mode .centris-sidebar button:hover .group-hover\:text-slate-200 { color: #0f172a !important; }
    .centris-shell.light-mode .centris-sidebar .centris-nav-active.text-white { color: #1e293b !important; }
    .centris-shell.light-mode .centris-sidebar button[class*="hover:bg-white"]:hover { background: rgba(15,23,42,0.07) !important; }

    /* === SIDEBAR ACTIVE NAV ITEM === */
    .centris-nav-active {
      background: linear-gradient(135deg, rgba(99,102,241,0.20), rgba(139,92,246,0.13)) !important;
      box-shadow: 0 0 0 1px rgba(99,102,241,0.25) inset, 0 2px 8px rgba(99,102,241,0.12);
    }
    .centris-shell.light-mode .centris-nav-active {
      background: linear-gradient(135deg, rgba(37,99,235,0.10), rgba(59,130,246,0.06)) !important;
      box-shadow: 0 0 0 1px rgba(37,99,235,0.16) inset;
    }

    /* ======================================================
       GRADIENT BUTTONS
    ====================================================== */
    .centris-shell button.bg-gradient-to-r {
      box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 4px 16px rgba(99,102,241,0.25);
      transition: all 0.22s cubic-bezier(0.16,1,0.3,1);
    }
    .centris-shell button.bg-gradient-to-r:hover {
      box-shadow: 0 4px 12px rgba(0,0,0,0.3), 0 8px 28px rgba(99,102,241,0.4);
      transform: translateY(-2px);
    }
    .centris-shell button.bg-gradient-to-r:active {
      transform: translateY(0px);
      box-shadow: 0 1px 4px rgba(0,0,0,0.3), 0 2px 8px rgba(99,102,241,0.2);
    }

    /* ======================================================
       THEME TRANSITIONS
    ====================================================== */
    .centris-shell, .centris-sidebar, .centris-content, .centris-card {
      transition: background 0.38s cubic-bezier(0.16,1,0.3,1),
                  border-color 0.38s cubic-bezier(0.16,1,0.3,1),
                  color 0.28s cubic-bezier(0.16,1,0.3,1),
                  box-shadow 0.38s cubic-bezier(0.16,1,0.3,1);
    }

    /* ======================================================
       INTERACTIVE ELEMENTS
    ====================================================== */
    .centris-content button:not([disabled]) {
      transition: all 0.18s cubic-bezier(0.16,1,0.3,1);
    }
    .centris-content input, .centris-content select, .centris-content textarea {
      transition: border-color 0.18s ease, box-shadow 0.18s ease;
    }
    .centris-content input:focus, .centris-content select:focus, .centris-content textarea:focus {
      box-shadow: 0 0 0 3px rgba(99,102,241,0.2);
    }

    /* ======================================================
       SCROLLBAR
    ====================================================== */
    ::-webkit-scrollbar { width: 5px; height: 5px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 100px; }
    ::-webkit-scrollbar-thumb:hover { background: rgba(99,102,241,0.45); }

    /* ======================================================
       MODAL / POPUP ENTRIES
    ====================================================== */
    .centris-modal-enter { animation: quantraSlideIn 0.38s cubic-bezier(0.16,1,0.3,1) both; }

    /* ======================================================
       CATCH-ALL: FORCE ALL REMAINING SLATE SURFACES SOLID
       Kills any leftover bg-slate-* transparent/translucent
       backgrounds that haven't been converted to centris-card
    ====================================================== */
    .centris-shell:not(.light-mode) [class*="bg-slate-800"],
    .centris-shell:not(.light-mode) [class*="bg-slate-700"],
    .centris-shell:not(.light-mode) [class*="bg-slate-900\/"],
    .centris-shell:not(.light-mode) [class*="bg-slate-800\/"],
    .centris-shell:not(.light-mode) [class*="bg-slate-700\/"],
    .centris-shell:not(.light-mode) [class*="bg-white\/0"],
    .centris-shell:not(.light-mode) [class*="bg-white\/[0"],
    .centris-shell:not(.light-mode) [class*="backdrop-blur"] {
      background-color: rgba(14, 16, 54, 0.78) !important;
      backdrop-filter: blur(14px) !important;
      -webkit-backdrop-filter: blur(14px) !important;
    }
    /* Re-allow sidebar and shell itself to keep their own bg */
    .centris-shell .centris-sidebar.centris-sidebar,
    .centris-shell .centris-sidebar.centris-sidebar * {
      background-color: unset !important;
    }
    /* Light mode: NO catch-all — the bright-mode sheet in index.css owns
       every surface. A single flat white here erases the designed surface
       hierarchy and paints hover-only classes as permanent white pills. */

    /* Force mobile layout on mobile devices regardless of screen width */
    .centris-shell.is-mobile .centris-sidebar { display: none !important; }
    .centris-shell.is-mobile .centris-content { margin-left: 0 !important; }
    .centris-shell.is-mobile .md\:hidden { display: flex !important; }
    .centris-shell.is-mobile .hidden.md\:flex,
    .centris-shell.is-mobile .hidden.md\:block { display: none !important; }

  `}</style>
);

const AmbientBg = ({ darkMode }) => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
    {/* Top-left corner glow — indigo/blue */}
    <div style={{
      position: 'absolute', top: '-18%', left: '-12%',
      width: '58vw', height: '58vw', maxWidth: 720, maxHeight: 720,
      borderRadius: '50%',
      background: darkMode
        ? 'radial-gradient(circle, rgba(99,102,241,0.20) 0%, rgba(59,130,246,0.10) 38%, transparent 68%)'
        : 'radial-gradient(circle, rgba(59,130,246,0.10) 0%, rgba(37,99,235,0.05) 38%, transparent 68%)',
      animation: 'floatOrb 20s ease-in-out infinite',
      filter: 'blur(1px)',
    }} />
    {/* Bottom-right corner glow — violet/purple */}
    <div style={{
      position: 'absolute', bottom: '-18%', right: '-12%',
      width: '54vw', height: '54vw', maxWidth: 680, maxHeight: 680,
      borderRadius: '50%',
      background: darkMode
        ? 'radial-gradient(circle, rgba(139,92,246,0.22) 0%, rgba(168,85,247,0.11) 38%, transparent 68%)'
        : 'radial-gradient(circle, rgba(96,165,250,0.10) 0%, rgba(59,130,246,0.05) 38%, transparent 68%)',
      animation: 'floatOrb2 26s ease-in-out infinite',
      filter: 'blur(1px)',
    }} />
    {/* Subtle mid accent — cyan */}
    <div style={{
      position: 'absolute', top: '28%', right: '22%',
      width: 320, height: 320, borderRadius: '50%',
      background: darkMode
        ? 'radial-gradient(circle, rgba(6,182,212,0.07) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(6,182,212,0.04) 0%, transparent 70%)',
      animation: 'floatOrb 34s ease-in-out infinite 8s',
    }} />
    {/* Noise grain */}
    <svg className="absolute inset-0 w-full h-full" style={{ opacity: darkMode ? 0.038 : 0.022 }}>
      <filter id="centris-grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="4" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#centris-grain)" />
    </svg>
  </div>
);

const Spinner = ({ size = 'w-4 h-4', className = '' }) => (
  <span className={`inline-block rounded-full border-2 border-white/30 border-t-white animate-spin ${size} ${className}`} />
);

// ---- BankAnalyzer.js ----
const BankAnalyzer = ({ user, onExpensesExtracted, onIncomeExtracted, onCancel }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [postProcessing, setPostProcessing] = useState(false);
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    const validExtensions = ['csv', 'xls', 'xlsx', 'pdf'];

    if (!validExtensions.includes(fileExtension)) {
      setError('Please upload a CSV, Excel (.xlsx, .xls), or PDF file');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const parseCSVRow = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result.map(c => c.trim());
  };

  const parseSignedAmount = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return NaN;
    const hasParens = raw.includes('(') && raw.includes(')');
    const normalized = raw.replace(/[$,\s]/g, '').replace(/[()]/g, '');
    const parsed = parseFloat(normalized);
    if (!Number.isFinite(parsed)) return NaN;
    return hasParens ? -Math.abs(parsed) : parsed;
  };

  const isLikelyBusinessIncome = ({ description = '', type = '', amount = 0 }) => {
    const d = String(description || '').toLowerCase();
    const t = String(type || '').toLowerCase();

    const exclude = /(transfer|internal|zelle to|venmo cashout|credit card payment|refund|reversal|reimburse|cashback|interest payment)/;
    if (exclude.test(d)) return false;

    const include = /(stripe|square|shopify|paypal|intuit|quickbooks|invoice|client payment|wire|ach credit|deposit|payment received|consulting|service payment)/;
    if (include.test(d) || include.test(t)) return true;

    return Number(amount) >= 100;
  };

  const detectIncomeFromBankRow = ({ amount = 0, description = '', type = '', category = '' }) => {
    if (!(Number(amount) > 0)) return false;

    const d = String(description || '').toLowerCase();
    const t = String(type || '').toLowerCase();
    const c = String(category || '').toLowerCase();

    // Explicit payment rows (like Chase "Type: Payment") should always count as incoming payment detection.
    if (t.includes('payment') || c.includes('payment')) return true;

    const EXPENSE_PATTERNS = /menard|home depot|lowes|walmart|target|costco|amazon|amzn|starbucks|mcdonald|uber|lyft|shell|chevron|exxon|bp|arco|gas|fuel|parking|toll|staples|office depot|best buy|kroger|whole foods|trader joe|aldi|publix|safeway|walgreen|cvs|rite aid|netflix|spotify|hulu|adobe|microsoft|google|apple|dropbox|zoom|slack|github|godaddy|cloudflare|mailchimp|squarespace|shopify|wix|insurance|electric|comcast|xfinity|verizon|att|t-mobile|sprint|water|sewer|utility|restaurant|pizza|chipotle|subway|panera|dunkin|doordash|grubhub|instacart|postmates|airbnb|hotel|delta|united|southwest|airline|fedex|ups|usps/i;
    const looksLikeExpenseMerchant = EXPENSE_PATTERNS.test(d);

    const looksLikeTransfer = /transfer|autopay|payment to|bill pay|zelle sent|venmo.*pay/i.test(d);

    const looksLikeIncome = /direct deposit|payroll|ach credit|wire credit|wire in|refund|reimburs|client|invoice|consulting|freelance|deposit from|payment from|stripe|paypal.*credit|square.*deposit|gusto.*deposit/i.test(d);

    if (looksLikeExpenseMerchant || looksLikeTransfer) return false;
    if (looksLikeIncome) return true;

    return true;
  };

  const shouldSkipExpenseRow = ({ description = '', chaseCategory = '' }) => {
    const rawCategory = chaseCategory ? String(chaseCategory).toLowerCase() : '';
    const descLower = String(description || '').toLowerCase();
    return (
      rawCategory.includes('transfer') ||
      rawCategory.includes('credit card payment') ||
      rawCategory.includes('auto payment') ||
      rawCategory.includes('federal tax') ||
      rawCategory.includes('financial & investment') ||
      descLower.includes('tfr to') ||
      descLower.includes('trf to') ||
      descLower.includes('chase credit crd') ||
      descLower.includes('dividend paid') ||
      descLower.includes('edward jones') ||
      descLower.includes('dividend') ||
      descLower.includes('us treasury irs')
    );
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) return [];

    const headers = parseCSVRow(lines[0]).map(h => h.trim().toLowerCase());
    
    // Find column indices
    const dateIndex = headers.findIndex(h => 
      h.includes('date') || h.includes('transaction date') || h.includes('posted date')
    );
    const descIndex = headers.findIndex(h => 
      h.includes('description') || h.includes('merchant') || h.includes('payee') || h.includes('name')
    );
    const amountIndex = headers.findIndex(h => h.includes('amount'));
    const typeIndex = headers.findIndex(h => h.includes('type'));
    const categoryIndex = headers.findIndex(h => h.includes('category'));

    console.log('CSV Headers:', headers);
    console.log('Column indices - Date:', dateIndex, 'Description:', descIndex, 'Amount:', amountIndex, 'Type:', typeIndex, 'Category:', categoryIndex);

    if (dateIndex === -1 || descIndex === -1 || amountIndex === -1) {
      throw new Error('Could not find date, description, or amount columns');
    }

    const transactions = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = parseCSVRow(lines[i]);
      
      if (cols.length <= Math.max(dateIndex, descIndex, amountIndex)) continue;

      const dateStr = String(cols[dateIndex] || '').replace(/["']/g, '');
      const description = String(cols[descIndex] || '').replace(/["']/g, '');
      const amountRaw = String(cols[amountIndex] || '').replace(/["']/g, '');
      const type = typeIndex !== -1 ? String(cols[typeIndex] || '').toLowerCase() : '';
      const chaseCategory = categoryIndex !== -1 ? String(cols[categoryIndex] || '').replace(/["']/g, '') : null;
      
      let amount = parseSignedAmount(amountRaw);
      if (!Number.isFinite(amount) || amount === 0) continue;
      
      if (!description || description.length < 2) continue;

      if (shouldSkipExpenseRow({ amount, description, chaseCategory })) continue;

      // Parse date
      let date = new Date();
      if (dateStr.match(/\d{1,2}\/\d{1,2}\/\d{2,4}/)) {
        const [month, day, year] = dateStr.split('/');
        date = new Date(year.length === 2 ? '20' + year : year, month - 1, day);
      } else if (dateStr.match(/\d{4}-\d{2}-\d{2}/)) {
        date = new Date(dateStr);
      }

      const isIncome = detectIncomeFromBankRow({
        amount,
        description,
        type,
        category: chaseCategory,
      });
      amount = Math.abs(amount);

      transactions.push({
        date: date.toISOString().split('T')[0],
        description,
        amount,
        isIncome,
        type,
        chaseCategory,
      });
    }

    console.log('Parsed transactions:', transactions.length);
    console.log('Sample transactions:', transactions.slice(0, 5));
    console.log('Total amount:', transactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2));
    return transactions;
  };

  const parseExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length === 0) {
            reject(new Error('Excel file is empty'));
            return;
          }

          const headers = jsonData[0].map(h => String(h).toLowerCase());

          const dateIndex = headers.findIndex(h =>
            h.includes('date') || h.includes('transaction') || h.includes('posted')
          );
          const descIndex = headers.findIndex(h =>
            h.includes('description') || h.includes('merchant') || h.includes('payee') ||
            h.includes('memo') || h.includes('narration') || h.includes('particular') ||
            h.includes('details') || h.includes('reference')
          );
          // Many bank exports split the amount into separate Debit/Withdrawal and
          // Credit/Deposit columns instead of one signed Amount column — fall back
          // to combining those when no single amount column exists.
          const amountIndex = headers.findIndex(h => h.includes('amount') || h.includes('value'));
          const debitIndex = headers.findIndex(h => h.includes('debit') || h.includes('withdrawal'));
          const creditIndex = headers.findIndex(h => h.includes('credit') || h.includes('deposit'));
          const typeIndex = headers.findIndex(h => h.includes('type'));
          const categoryIndex = headers.findIndex(h => h.includes('category'));
          const hasAmountColumn = amountIndex !== -1;
          const hasSplitColumns = debitIndex !== -1 && creditIndex !== -1;
          console.log('Excel Headers:', headers);
          console.log('Column indices - Date:', dateIndex, 'Description:', descIndex, 'Amount:', amountIndex, 'Debit:', debitIndex, 'Credit:', creditIndex, 'Type:', typeIndex, 'Category:', categoryIndex);

          if (dateIndex === -1 || descIndex === -1 || (!hasAmountColumn && !hasSplitColumns)) {
            reject(new Error('Could not find required columns (need a date, description, and either an amount column or separate debit/credit columns)'));
            return;
          }

          const transactions = [];
          const requiredIndices = [dateIndex, descIndex, amountIndex, debitIndex, creditIndex].filter(idx => idx !== -1);
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length <= Math.max(...requiredIndices)) continue;

            const dateVal = row[dateIndex];
            const description = String(row[descIndex] || '').trim();
            const type = typeIndex !== -1 ? String(row[typeIndex] || '').toLowerCase() : '';
            const chaseCategory = categoryIndex !== -1 ? String(row[categoryIndex] || '').trim() : null;

            let amount = 0;
            if (hasAmountColumn) {
              const amountVal = row[amountIndex];
              if (typeof amountVal === 'number') {
                amount = amountVal;
              } else if (typeof amountVal === 'string') {
                amount = parseSignedAmount(amountVal);
              }
            } else if (hasSplitColumns) {
              const debitNum = typeof row[debitIndex] === 'number' ? row[debitIndex] : parseSignedAmount(String(row[debitIndex] || ''));
              const creditNum = typeof row[creditIndex] === 'number' ? row[creditIndex] : parseSignedAmount(String(row[creditIndex] || ''));
              if (Number.isFinite(creditNum) && creditNum !== 0) {
                amount = Math.abs(creditNum);
              } else if (Number.isFinite(debitNum) && debitNum !== 0) {
                amount = -Math.abs(debitNum);
              }
            }
            
            if (!Number.isFinite(amount) || amount === 0) continue;
            
            if (!description || description.length < 2) continue;

            if (shouldSkipExpenseRow({ amount, description, chaseCategory })) continue;

            let date = new Date();
            if (typeof dateVal === 'number') {
              date = new Date((dateVal - 25569) * 86400 * 1000);
            } else if (typeof dateVal === 'string') {
              date = new Date(dateVal);
            }

            if (isNaN(date.getTime())) {
              date = new Date();
            }

            const isIncome = detectIncomeFromBankRow({
              amount,
              description,
              type,
              category: chaseCategory,
            });
            amount = Math.abs(amount);

            transactions.push({
              date: date.toISOString().split('T')[0],
              description,
              amount,
              isIncome,
              type,
              chaseCategory,
            });
          }

          console.log('Parsed Excel transactions:', transactions.length);
          resolve(transactions);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read Excel file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const parsePDF = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          
          if (!window.pdfjsLib) {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
              script.onload = resolve;
              script.onerror = reject;
              document.head.appendChild(script);
            });
          }
          
          const pdfjsLib = window.pdfjsLib;
          if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          }
          
          const pdf = await pdfjsLib.getDocument({ data }).promise;
          const allLines = [];

          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();

            // Group items by y-position to reconstruct real lines (avoids smashing all columns together)
            const lineMap = {};
            for (const item of textContent.items) {
              if (!item.str || !item.str.trim()) continue;
              const y = Math.round(item.transform[5]);
              if (!lineMap[y]) lineMap[y] = [];
              lineMap[y].push({ x: item.transform[4], str: item.str });
            }
            const sortedYs = Object.keys(lineMap).map(Number).sort((a, b) => b - a);
            for (const y of sortedYs) {
              const lineItems = lineMap[y].sort((a, b) => a.x - b.x);
              allLines.push(lineItems.map(it => it.str).join(' '));
            }
          }

          const transactions = [];
          // Amount at end of line; description allows digits/symbols common in merchant names
          const AMT = '(-?\\$?\\s*\\d{1,3}(?:,\\d{3})*(?:\\.\\d{2})?|\\d+\\.\\d{2})';
          const DESC = '([\\w\\s&\'*.#@!()/:,-]+?)';
          const datePatterns = [
            new RegExp(`(\\d{1,2}\\/\\d{1,2}\\/\\d{2,4})\\s+${DESC}\\s+${AMT}\\s*$`),
            new RegExp(`(\\d{4}-\\d{2}-\\d{2})\\s+${DESC}\\s+${AMT}\\s*$`),
            new RegExp(`(\\d{1,2}-\\d{1,2}-\\d{2,4})\\s+${DESC}\\s+${AMT}\\s*$`),
          ];

          for (const line of allLines) {
            let match = null;
            for (const re of datePatterns) {
              match = line.match(re);
              if (match) break;
            }

            if (match && match.length >= 4) {
              const dateStr = match[1];
              const description = match[2];
              const amountStr = match[3];

              const signedAmount = parseSignedAmount(amountStr);

              if (Number.isFinite(signedAmount) && signedAmount !== 0 && description.trim().length > 1) {
                let date;
                if (dateStr.includes('/')) {
                  const parts = dateStr.split('/');
                  if (parts.length === 3) {
                    const [month, day, year] = parts;
                    date = new Date(year.length === 2 ? '20' + year : year, parseInt(month) - 1, parseInt(day));
                  }
                } else if (dateStr.includes('-')) {
                  date = new Date(dateStr);
                }

                if (date && !isNaN(date.getTime())) {
                  const flow = signedAmount >= 0 ? 'income' : 'expense';
                  transactions.push({
                    date: date.toISOString().split('T')[0],
                    description: description.trim(),
                    amount: Math.abs(signedAmount),
                    flow,
                    signedAmount,
                    likelyBusinessIncome: flow === 'income' ? isLikelyBusinessIncome({ description: description.trim(), amount: Math.abs(signedAmount) }) : false,
                  });
                }
              }
            }
          }

          if (transactions.length === 0) {
            reject(new Error('Could not parse transactions from PDF. Please try a CSV or Excel file for better results.'));
            return;
          }

          resolve(transactions);
        } catch (error) {
          reject(new Error('Failed to parse PDF: ' + error.message));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read PDF file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const analyzeTransactions = (transactions) => {
    const allCategorized = transactions.map(t => {
      const txIsIncome = typeof t.isIncome === 'boolean'
        ? t.isIncome
        : (t.flow ? t.flow === 'income' : Number(t.signedAmount || 0) > 0);

      // null = skip (payment/transfer), undefined = use description fallback, string = use mapped category
      const mappedCategory = t.chaseCategory != null
        ? mapChaseCategory(t.chaseCategory, t.description)
        : undefined;
      if (mappedCategory === null) return null;

      const category = txIsIncome
        ? 'Income'
        : (mappedCategory !== undefined ? mappedCategory : categorizeTransaction(t.description));
      return {
        ...t,
        isIncome: txIsIncome,
        category,
        merchant: extractMerchant(t.description),
        amount: Math.abs(Number(t.amount || t.signedAmount || 0)),
        taxDeductible: !txIsIncome && isBusinessCategory(category)
      };
    }).filter(Boolean);

    const categorized = allCategorized.filter(t => !t.isIncome);
    const incomeTransactions = allCategorized.filter(t => t.isIncome);
    const duplicates = findDuplicates(categorized);

    const byCategory = categorized.reduce((acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + t.amount;
      return acc;
    }, {});

    const byMerchant = categorized.reduce((acc, t) => {
      acc[t.merchant] = (acc[t.merchant] || 0) + t.amount;
      return acc;
    }, {});

    const topMerchants = Object.entries(byMerchant)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([merchant, amount]) => ({ merchant, amount }));

    const byMonth = categorized.reduce((acc, t) => {
      const month = t.date.substring(0, 7);
      acc[month] = (acc[month] || 0) + t.amount;
      return acc;
    }, {});

    const total = categorized.reduce((sum, t) => sum + t.amount, 0);
    const taxDeductibleTotal = categorized.filter(t => t.taxDeductible).reduce((sum, t) => sum + t.amount, 0);

    return {
      transactions: categorized,
      incomeTransactions,
      total,
      expenseTotal: total,
      count: allCategorized.length,
      byCategory,
      topMerchants,
      byMonth,
      duplicates,
      taxDeductibleTotal
    };
  };

  const categorizeTransaction = (description) => {
    const desc = description.toLowerCase();

    // Clearly business (auto-deductible)
    if (desc.match(/adobe|aws|amazon web|azure|github|gitlab|bitbucket|slack|zoom pro|teams|webex|salesforce|hubspot|mailchimp|constant contact|zapier|notion|trello|asana|monday\.com|clickup|jira|confluence|shopify|wix|squarespace|wordpress|hostgator|godaddy|bluehost|namecheap|cloudflare|netlify|vercel|heroku|digitalocean|linode|vultr|hosting|domain|ssl|figma|canva pro|sketch|invision|miro|lucid/)) {
      return 'Software & Technology';
    }

    if (desc.match(/legal|law|attorney|lawyer|accountant|accounting|cpa|bookkeep|consultant|consulting|tax prep|h&r block|turbotax|quickbooks|freshbooks|xero|payroll|adp|paychex|gusto/)) {
      return 'Professional Services';
    }

    if (desc.match(/advertis|ad spend|facebook ads|meta ads|google ads|adwords|linkedin ad|twitter ad|pinterest ad|tiktok ad|reddit ads|youtube ad|social media market|promo|campaign|vistaprint|moo\.com|printful|printify|fedex office|ups store|kinkos/)) {
      return 'Marketing & Advertising';
    }

    if (desc.match(/service charge|overdraft|wire transfer fee|monthly fee|annual fee|finance charge|late fee/)) {
      return 'Banking & Fees';
    }

    // Ambiguous categories (not auto-deductible)
    if (desc.match(/starbucks|coffee|cafe|dunkin|restaurant|mcdonald|burger|wendys|subway|chipotle|pizza|taco|kfc|chick.fil.a|panera|domino|papa john|arbys|sonic|popeyes|five guys|shake shack|jimmy john|panda express|olive garden|red lobster|applebee|chili|outback|texas roadhouse|eat|dine|dining|food|lunch|dinner|breakfast|brunch|bistro|grill|kitchen|bar|pub|tavern/)) {
      return 'Meals & Entertainment';
    }

    if (desc.match(/staples|office depot|officemax|office max|ink|toner|printer|pen|pencil|folder|binder|shredder/)) {
      return 'Office Supplies';
    }

    if (desc.match(/walmart|wal.mart|target|costco|sams club|sam.s|bjs|bj.s|amazon|amzn|dollar|family dollar|dollar general|five below|marshalls|tj.?maxx|ross|goodwill/)) {
      return 'Shopping';
    }

    if (desc.match(/shell|chevron|exxon|mobil|bp|arco|76|circle k|wawa|sheetz|7.?eleven|gas|fuel|petrol|gasoline|uber|lyft|taxi|parking|toll|kwik trip/)) {
      return 'Transportation';
    }

    if (desc.match(/netflix|spotify|hulu|disney|hbo|apple tv|youtube premium|amazon prime|audible|kindle|xbox|playstation|steam|nintendo|twitch/)) {
      return 'Entertainment & Streaming';
    }

    if (desc.match(/internet|broadband|spectrum|comcast|xfinity|cox|verizon.*fios|att.*fiber|centurylink|frontier.*internet/)) {
      return 'Phone & Internet';
    }

    if (desc.match(/verizon|att|at&t|t.mobile|sprint|wireless|cellular|phone/)) {
      return 'Phone & Internet';
    }

    if (desc.match(/electric|electricity|power|utility|water|sewer|gas company|energy|cable/)) {
      return 'Utilities';
    }

    if (desc.match(/hotel|motel|inn|lodge|resort|hilton|marriott|hyatt|airbnb|vrbo|booking\.com|expedia|airline|flight|delta|united|american|southwest|jetblue|spirit|frontier|alaska air/)) {
      return 'Travel';
    }

    if (desc.match(/fedex|ups|usps|dhl|postage|stamp|shipping|courier/)) {
      return 'Shipping & Postage';
    }

    if (desc.match(/udemy|coursera|linkedin learn|skillshare|masterclass|training|seminar|conference|workshop|tuition/)) {
      return 'Education & Training';
    }

    if (desc.match(/dues|membership|association|subscription|annual fee/)) {
      return 'Dues & Subscriptions';
    }

    if (desc.match(/best buy|micro center|newegg|b&h|adorama|dell|hp|lenovo|apple store|home depot|lowes|menards|ace hardware|ikea|wayfair/)) {
      return 'Equipment';
    }

    // Clearly personal
    if (desc.match(/grocery|groceries|kroger|whole foods|trader joe|aldi|publix|safeway|wegmans|piggly|food lion|giant|stop.shop|market basket|sprouts|fresh market/)) {
      return 'Groceries';
    }

    if (desc.match(/pharmacy|cvs|walgreens|rite aid|doctor|hospital|dental|medical|health|gym|fitness|planet fitness|la fitness|orangetheory|ymca|crossfit/)) {
      return 'Health & Personal';
    }

    if (desc.match(/insurance|state farm|allstate|geico|progressive|farmers|nationwide|liberty mutual|usaa/)) {
      return 'Insurance';
    }

    if (desc.match(/rent|mortgage|hoa|property tax|apartment/)) {
      return 'Housing';
    }

    if (desc.match(/transfer|payment|zelle|venmo|paypal|cash app|direct deposit|atm|withdrawal|check/)) {
      return null;
    }

    return 'Other';
  };
  
  // Map Chase categories to app categories, using description for ambiguous cases.
  // Returns: null = skip row, undefined = fall back to categorizeTransaction, string = use this category
  const mapChaseCategory = (chaseCategory, description = '') => {
    if (!chaseCategory) return null;
    const c = String(chaseCategory).trim();
    const desc = String(description || '').toLowerCase();

    const skipSet = new Set([
      'Auto Payment', 'Credit Card Payment', 'Transfer',
      'Interest Income', 'Misc. Income', 'Financial & Investment', 'Federal Tax',
    ]);
    if (skipSet.has(c)) return null;

    if (c === 'Office & Shipping' || c === 'Merchandise & Inventory') {
      if (desc.match(/adobe|microsoft|msft|google.*suite|gsuite|github|slack|zoom|notion|dropbox|aws|azure|intuit|quickbooks|qbooks|software|agri.*soft|valley.*agr/)) {
        return 'Software & Technology';
      }
      if (desc.match(/verizon|at&t|att|t.mobile|sprint|wireless|cellular/)) {
        return 'Utilities';
      }
    }

    if (c === 'Bills & Utilities') {
      if (desc.match(/insurance|state farm|allstate|geico|progressive|farmers|nationwide|liberty mutual|usaa/)) {
        return 'Insurance';
      }
    }

    const categoryMap = {
      'Gas': 'Transportation',
      'Food & Drink': 'Meals & Entertainment',
      'Travel': 'Travel & Lodging',
      'Office & Shipping': 'Software & Technology',
      'Professional Services': 'Professional Services',
      'Bills & Utilities': 'Utilities',
      'Automotive': 'Transportation',
      'Repair & Maintenance': 'Equipment',
      'Health & Wellness': 'Health & Personal',
      'Entertainment': 'Meals & Entertainment',
      'Merchandise & Inventory': 'Shopping',
      'Gifts & Donations': 'Other',
      'Fees & Adjustments': 'Banking & Fees',
      'Wireless / Cellular': 'Utilities',
      'Service Fee': 'Banking & Fees',
    };

    if (Object.prototype.hasOwnProperty.call(categoryMap, c)) return categoryMap[c];

    return undefined;
  };

  const extractMerchant = (description) => {
    return description
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim()
      .split(' ')
      .slice(0, 3)
      .join(' ')
      .substring(0, 30);
  };

  const isBusinessCategory = (category) => {
    // Categories that are deductible per IRS / QuickBooks standard rules
    return [
      'Software & Technology',
      'Professional Services',
      'Marketing & Advertising',
      'Banking & Fees',
      'Office Supplies',
      'Meals & Entertainment',
      'Transportation',
      'Travel',
      'Phone & Internet',
      'Utilities',
      'Rent & Lease',
      'Insurance',
      'Education & Training',
      'Dues & Subscriptions',
      'Shipping & Postage',
      'Equipment',
      'Repairs & Maintenance',
      'Taxes & Licenses',
      'Contract Labor',
    ].includes(category);
  };

  const findDuplicates = (transactions) => {
    const duplicates = [];
    const seen = {};

    for (const t of transactions) {
      const key = `${t.date}-${t.amount.toFixed(2)}-${t.merchant}`;
      
      if (seen[key]) {
        duplicates.push({
          ...t,
          originalDate: seen[key].date
        });
      } else {
        seen[key] = t;
      }
    }

    return duplicates;
  };

  const handleAnalyze = async () => {
    if (!file) return;

    setAnalyzing(true);
    setError(null);
    setResults(null);

    try {
      let transactions = [];
      const fileType = file.name.split('.').pop().toLowerCase();

      if (fileType === 'csv') {
        const text = await file.text();
        transactions = parseCSV(text);
      } else if (fileType === 'xlsx' || fileType === 'xls') {
        transactions = await parseExcel(file);
      } else if (fileType === 'pdf') {
        transactions = await parsePDF(file);
      }

      if (transactions.length === 0) {
        throw new Error('No transactions found in file. Make sure the file has date, description, and amount columns.');
      }

      const analysis = analyzeTransactions(transactions);
      setResults(analysis);

      setPostProcessing(true);
      try {
        if (onExpensesExtracted) {
          await onExpensesExtracted(analysis.transactions, analysis.incomeTransactions || []);
        }
        if (onIncomeExtracted) {
          await onIncomeExtracted(analysis.incomeTransactions || []);
        }
      } finally {
        setPostProcessing(false);
      }

    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze file');
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResults(null);
    setError(null);
  };

  if (analyzing || postProcessing) {
    return (
      <div className="centris-card rounded-xl border border-blue-500/20 p-12 flex flex-col items-center justify-center gap-6 min-h-[320px]">
        <div className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-blue-500/20 border-t-blue-400 animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <FileText className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-white mb-1">
            {analyzing ? 'Analyzing statement…' : 'Saving transactions…'}
          </p>
          <p className="text-sm text-slate-400">
            {analyzing
              ? 'Reading and categorizing your transactions. Large files can take up to a minute.'
              : 'Categorizing and saving to your account. Almost done.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {onCancel && (
        <div className="flex justify-end">
          <button
            onClick={onCancel}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-white/[0.05]"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
        </div>
      )}
      {!results ? (
        <div className="centris-card rounded-xl border border-blue-500/20 p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-2xl mb-4">
              <Upload className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Upload Bank Statement</h3>
            <p className="text-slate-400">
              Supports CSV, Excel (.xlsx, .xls), and PDF formats
            </p>
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {!file ? (
            <label className="centris-card flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-blue-500/30 rounded-xl cursor-pointer hover:border-blue-500 transition-all">
              <Upload className="w-12 h-12 text-blue-400 mb-3" />
              <p className="text-white font-medium">Click to upload statement</p>
              <p className="text-sm text-slate-400 mt-1">or drag and drop</p>
              <p className="text-xs text-slate-500 mt-2">
                CSV, Excel (XLSX, XLS), or PDF up to 10MB
              </p>
              <input
                type="file"
                className="hidden"
                accept=".csv,.xlsx,.xls,.pdf,text/csv,text/comma-separated-values,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf"
                onChange={handleFileSelect}
              />
            </label>
          ) : (
            <div className="space-y-4">
              <div className="centris-card rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-8 h-8 text-blue-400" />
                    <div>
                      <p className="font-medium text-white">{file.name}</p>
                      <p className="text-sm text-slate-400">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={reset}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <button
                onClick={handleAnalyze}
                disabled={analyzing || postProcessing}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {(analyzing || postProcessing) ? (
                  <>
                    <Spinner size="w-4 h-4" />
                    <span>{analyzing ? 'Analyzing statement...' : 'Saving results...'}</span>
                  </>
                ) : (
                  'Analyze Statement'
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="centris-card rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-400">Total Transactions</p>
                <CreditCard className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-4xl font-bold text-white">{results.count}</p>
            </div>

            <div className="centris-card rounded-xl border border-green-500/20 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-400">Total Spent</p>
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-4xl font-bold text-white">${results.expenseTotal.toFixed(2)}</p>
            </div>

            {results.incomeTransactions?.length > 0 && (
              <div className="centris-card rounded-xl border border-emerald-500/20 p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-400">Income Detected</p>
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-2xl font-bold text-emerald-400">
                  ${results.incomeTransactions.reduce((s, t) => s + t.amount, 0).toFixed(2)}
                </p>
                <p className="text-xs text-slate-500 mt-1">{results.incomeTransactions.length} payments &rarr; added to Revenue</p>
              </div>
            )}

            <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-blue-100">Tax Deductible</p>
                <TrendingDown className="w-5 h-5 text-white" />
              </div>
              <p className="text-4xl font-bold">${results.taxDeductibleTotal.toFixed(2)}</p>
              <p className="text-xs text-blue-100 mt-1">
                {results.expenseTotal > 0 ? ((results.taxDeductibleTotal / results.expenseTotal) * 100).toFixed(0) : 0}% of expenses
              </p>
            </div>
          </div>

          {/* Duplicates Alert */}
          {results.duplicates.length > 0 && (
            <div className="bg-red-900/50 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                <div>
                  <p className="font-medium text-red-300">
                    {results.duplicates.length} Potential Duplicate Charges Found
                  </p>
                  <p className="text-sm text-red-200 mt-1">
                    Review these transactions - you may have been charged twice
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Category Breakdown */}
          <div className="centris-card rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Spending by Category</h3>
            <div className="space-y-3">
              {Object.entries(results.byCategory)
                .sort((a, b) => b[1] - a[1])
                .map(([category, amount]) => (
                  <div key={category}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-slate-300">{category}</span>
                      <span className="text-sm font-bold text-white">${amount.toFixed(2)}</span>
                    </div>
                    <div className="w-full bg-slate-700/50 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full"
                        style={{ width: `${results.expenseTotal > 0 ? (amount / results.expenseTotal) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Top Merchants */}
          <div className="centris-card rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Top 10 Merchants</h3>
            <div className="space-y-2">
              {results.topMerchants.map(({ merchant, amount }, index) => (
                <div
                  key={merchant}
                  className="flex items-center justify-between p-3 centris-card rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg font-bold text-blue-400">#{index + 1}</span>
                    <span className="text-white">{merchant}</span>
                  </div>
                  <span className="font-bold text-white">${amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={reset}
              className="flex-1 bg-slate-800 text-slate-300 px-6 py-3 rounded-xl font-semibold hover:bg-slate-700 transition-colors border border-slate-700"
            >
              Analyze Another Statement
            </button>
            {onCancel && (
              <button
                onClick={onCancel}
                className="flex-1 bg-violet-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-violet-500 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- GmailSubscriptionScanner.js ----
// GmailSubscriptionScanner.js
// Scans Gmail with shared subscription keywords and cancellation detection


const CANCEL_PATTERNS = [
  /\bcancel(?:led|ed|ation)?\b/i,
  /\bsubscription ended\b/i,
  /\bauto(?:\s|-)?renew(?:al)?\s+(?:off|disabled|canceled|cancelled)\b/i,
  /\bwill not renew\b/i,
  /\bhas been terminated\b/i,
  /\bmembership ended\b/i,
  /\brefund(?:ed)?\b/i,
];

const BILLING_PATTERNS = [
  /\breceipt\b/i,
  /\binvoice\b/i,
  /\bcharged\b/i,
  /\bbilled\b/i,
  /\bpayment\b/i,
  /\brenewed\b/i,
  /\brenewal\b/i,
  /\bsubscription\b/i,
  /\bmembership\b/i,
];

const YEARLY_PATTERNS = [/\byearly\b/i, /\bannual(?:ly)?\b/i, /\b12\s*months?\b/i];
const QUARTERLY_PATTERNS = [/\bquarterly\b/i, /\bevery\s*3\s*months\b/i];
const GMAIL_SERVICE_QUERY_CHUNK_SIZE = 20;

const buildGmailServiceQueries = () => {
  const cleanedServiceTerms = Array.from(new Set(
    SHARED_SERVICE_KEYWORDS
      .map(([serviceName]) => normalizeForMatch(String(serviceName || '')))
      .filter((term) => term.length >= 3)
  ));

  const queries = [];
  for (let i = 0; i < cleanedServiceTerms.length; i += GMAIL_SERVICE_QUERY_CHUNK_SIZE) {
    const chunk = cleanedServiceTerms.slice(i, i + GMAIL_SERVICE_QUERY_CHUNK_SIZE);
    if (!chunk.length) continue;
    const expression = chunk.map((term) => `"${term}"`).join(' OR ');
    queries.push(`newer_than:2y (${expression})`);
  }
  return queries;
};

const GMAIL_SERVICE_QUERIES = buildGmailServiceQueries();

const GmailSubscriptionScannerInner = ({ user, onSubscriptionsFound }) => {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [foundSubs, setFoundSubs] = useState([]);
  const [error, setError] = useState(null);

  const scanGmail = useGoogleLogin({
    onSuccess: async (response) => {
      setError(null);
      setScanning(true);
      await searchEmails(response.access_token);
    },
    scope: 'https://www.googleapis.com/auth/gmail.readonly',
    onError: (authError) => {
      console.error('Gmail auth error:', authError);
      setError('Failed to connect to Gmail. Please try again.');
    },
  });

  const searchEmails = async (accessToken) => {
    try {
      const queries = [
        'newer_than:2y (subject:(receipt OR invoice OR charged OR billed OR renewal OR subscription OR membership) OR ("thank you for your payment"))',
        'newer_than:2y (subject:(cancel OR cancelled OR canceled OR termination OR "will not renew") OR ("auto-renew" AND (off OR cancelled OR canceled)))',
        'newer_than:2y ("subscription" OR "membership" OR "recurring payment" OR "renewal notice")',
        ...GMAIL_SERVICE_QUERIES,
      ];

      let allMessages = [];
      for (const query of queries) {
        try {
          const response = await fetch(
            `https://gmail.googleapis.com/gmail/v1/users/me/messages?q=${encodeURIComponent(query)}&maxResults=150`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );

          if (!response.ok) {
            console.error('Gmail API error for query:', query);
            continue;
          }

          const data = await response.json();
          if (data.messages) {
            allMessages = [...allMessages, ...data.messages];
          }
        } catch (queryError) {
          console.error('Query error:', queryError);
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      const uniqueMessages = [...new Map(allMessages.map((m) => [m.id, m])).values()];
      setProgress({ current: 0, total: uniqueMessages.length });

      const events = [];
      for (let i = 0; i < uniqueMessages.length; i++) {
        const message = uniqueMessages[i];
        setProgress({ current: i + 1, total: uniqueMessages.length });

        const email = await fetchEmailContent(message.id, accessToken);
        const event = analyzeSubscriptionEmail(email);
        if (event) events.push(event);

        await new Promise((resolve) => setTimeout(resolve, 120));
      }

      const merged = buildSubscriptionsFromEvents(events);
      setFoundSubs(merged);
      setScanning(false);

      if (merged.length > 0) {
        onSubscriptionsFound(merged);
      } else {
        setError('No subscriptions found in your emails. Try uploading a bank statement instead.');
      }
    } catch (scanError) {
      console.error('Email scanning error:', scanError);
      setScanning(false);
      setError('Error scanning emails. Please try again.');
    }
  };

  const decodeBody = (data) => {
    try {
      return atob(data.replace(/-/g, '+').replace(/_/g, '/'));
    } catch {
      return '';
    }
  };

  const fetchEmailContent = async (messageId, accessToken) => {
    try {
      const response = await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}?format=full`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (!response.ok) throw new Error('Failed to fetch email');

      const data = await response.json();
      const headers = data.payload?.headers || [];
      const subject = headers.find((h) => h.name === 'Subject')?.value || '';
      const from = headers.find((h) => h.name === 'From')?.value || '';
      const date = headers.find((h) => h.name === 'Date')?.value || '';

      let body = '';
      if (data.payload?.body?.data) {
        body = decodeBody(data.payload.body.data);
      } else if (data.payload?.parts) {
        const textPart = data.payload.parts.find(
          (p) => p.mimeType === 'text/plain' || p.mimeType === 'text/html'
        );
        if (textPart?.body?.data) {
          body = decodeBody(textPart.body.data);
        }
      }

      return { subject, from, date, body };
    } catch (fetchError) {
      console.error('Error fetching email:', fetchError);
      return { subject: '', from: '', date: '', body: '' };
    }
  };

  const parseAmount = (text) => {
    const amountPatterns = [
      /\$(\d+\.?\d{0,2})/g,
      /(\d+\.?\d{0,2})\s*usd/gi,
      /total[:\s]+\$?(\d+\.?\d{0,2})/gi,
      /charged[:\s]+\$?(\d+\.?\d{0,2})/gi,
      /amount[:\s]+\$?(\d+\.?\d{0,2})/gi,
    ];

    let amount = 0;
    for (const pattern of amountPatterns) {
      const matches = [...text.matchAll(pattern)];
      for (const match of matches) {
        const parsed = parseFloat(match[1]);
        if (Number.isFinite(parsed) && parsed > amount && parsed < 1000) {
          amount = parsed;
        }
      }
    }

    return amount;
  };

  const inferCycleHint = (text) => {
    if (YEARLY_PATTERNS.some((pattern) => pattern.test(text))) return 'yearly';
    if (QUARTERLY_PATTERNS.some((pattern) => pattern.test(text))) return 'quarterly';
    return 'monthly';
  };

  const analyzeSubscriptionEmail = (email) => {
    const subject = email.subject || '';
    const from = email.from || '';
    const body = email.body || '';
    const combined = `${subject} ${from} ${body}`;
    const normalized = normalizeForMatch(combined);

    const serviceName = findServiceNameByText(combined);
    if (!serviceName) return null;

    const emailDateObj = new Date(email.date || '');
    const emailDate = Number.isNaN(emailDateObj.getTime()) ? new Date() : emailDateObj;

    const explicitCancelled = CANCEL_PATTERNS.some((pattern) => pattern.test(normalized));
    const billingSignal = BILLING_PATTERNS.some((pattern) => pattern.test(normalized));

    return {
      key: normalizeServiceKey(serviceName),
      serviceName,
      category: inferCategory(serviceName),
      amount: parseAmount(normalized),
      emailDate,
      explicitCancelled,
      billingSignal: billingSignal && !explicitCancelled,
      cycleHint: inferCycleHint(normalized),
    };
  };

  const inferCategory = (serviceName) => {
    const key = normalizeServiceKey(serviceName);
    if (['netflix', 'hulu', 'disney', 'spotify', 'youtube', 'audible', 'xbox', 'playstation'].some((k) => key.includes(k))) {
      return 'Entertainment';
    }
    if (['zoom', 'slack', 'verizon', 'att', 'tmobile', 'comcast', 'spectrum'].some((k) => key.includes(normalizeServiceKey(k)))) {
      return 'Communication';
    }
    if (['github', 'adobe', 'microsoft', 'notion', 'figma', 'dropbox', 'googleone', 'icloud', 'quickbooks', 'aws', 'shopify'].some((k) => key.includes(normalizeServiceKey(k)))) {
      return 'Software';
    }
    if (['linkedin', 'coursera', 'udemy', 'masterclass', 'duolingo'].some((k) => key.includes(k))) {
      return 'Professional';
    }
    return 'Other';
  };

  const median = (values) => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  const inferBillingCycleFromEvents = (events) => {
    const yearlyHint = events.some((e) => e.cycleHint === 'yearly');
    const quarterlyHint = events.some((e) => e.cycleHint === 'quarterly');
    if (yearlyHint) return 'yearly';
    if (quarterlyHint) return 'quarterly';

    const billingDates = events
      .filter((e) => e.billingSignal)
      .map((e) => e.emailDate)
      .sort((a, b) => a.getTime() - b.getTime());

    if (billingDates.length < 2) return 'monthly';

    const gaps = [];
    for (let i = 1; i < billingDates.length; i++) {
      const days = (billingDates[i].getTime() - billingDates[i - 1].getTime()) / (1000 * 60 * 60 * 24);
      if (days > 0) gaps.push(days);
    }

    const gap = median(gaps);
    if (gap >= 300) return 'yearly';
    if (gap >= 65) return 'quarterly';
    return 'monthly';
  };

  const getInactivityCutoffDays = (billingCycle) => {
    if (billingCycle === 'yearly') return 500;
    if (billingCycle === 'quarterly') return 190;
    return 95;
  };

  const buildSubscriptionsFromEvents = (events) => {
    const byService = new Map();

    for (const event of events) {
      if (!byService.has(event.key)) byService.set(event.key, []);
      byService.get(event.key).push(event);
    }

    const today = new Date();
    const subscriptions = [];

    for (const serviceEvents of byService.values()) {
      serviceEvents.sort((a, b) => a.emailDate.getTime() - b.emailDate.getTime());

      const latestEvent = serviceEvents[serviceEvents.length - 1];
      const billingCycle = inferBillingCycleFromEvents(serviceEvents);
      const inactivityDays = getInactivityCutoffDays(billingCycle);

      const billingEvents = serviceEvents.filter((e) => e.billingSignal);
      const latestBilling = billingEvents[billingEvents.length - 1] || null;
      const latestCancel = [...serviceEvents].reverse().find((e) => e.explicitCancelled) || null;

      const daysSinceLastBilling = latestBilling
        ? Math.floor((today.getTime() - latestBilling.emailDate.getTime()) / (1000 * 60 * 60 * 24))
        : null;

      const canceledByEmail = Boolean(
        latestCancel && (!latestBilling || latestCancel.emailDate.getTime() >= latestBilling.emailDate.getTime())
      );
      const canceledByInactivity =
        Number.isFinite(daysSinceLastBilling) && daysSinceLastBilling > inactivityDays;

      const amountCandidates = billingEvents.map((e) => e.amount).filter((n) => n > 0);
      const amount = amountCandidates.length
        ? median(amountCandidates)
        : serviceEvents.map((e) => e.amount).find((n) => n > 0) || 0;

      const baseDate = latestBilling?.emailDate || latestEvent.emailDate;
      const renewalDateObj = new Date(baseDate);
      if (billingCycle === 'yearly') renewalDateObj.setFullYear(renewalDateObj.getFullYear() + 1);
      else if (billingCycle === 'quarterly') renewalDateObj.setMonth(renewalDateObj.getMonth() + 3);
      else renewalDateObj.setMonth(renewalDateObj.getMonth() + 1);

      const status = canceledByEmail || canceledByInactivity ? 'cancelled' : 'active';
      let statusReason = '';
      if (canceledByEmail) statusReason = 'Cancellation email detected';
      else if (canceledByInactivity) statusReason = `No billing email for ${daysSinceLastBilling} days`;

      subscriptions.push({
        name: latestEvent.serviceName,
        cost: amount,
        billingCycle,
        category: latestEvent.category,
        status,
        statusReason,
        lastEmailDate: latestEvent.emailDate.toISOString().split('T')[0],
        renewalDate: renewalDateObj.toISOString().split('T')[0],
        lastUsed: latestEvent.emailDate.toISOString().split('T')[0],
        detectedFrom: 'gmail',
        autoDetected: true,
      });
    }

    return subscriptions.sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <div className="space-y-4">
      {!scanning && !foundSubs.length && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-white font-semibold mb-1">Gmail Access Required</p>
              <p className="text-sm text-slate-300 mb-2">
                We&apos;ll scan your email for subscription receipts. We only read emails from known subscription services.
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>Read-only access (we can&apos;t send or delete emails)</li>
                <li>Only searches subscription-related emails</li>
                <li>Your email data is never stored</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        </div>
      )}

      {scanning && (
        <div className="centris-card rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Loader className="w-5 h-5 text-blue-400 animate-spin" />
            <p className="text-white font-medium">Scanning your emails...</p>
          </div>
          {progress.total > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-slate-400">
                <span>Processing email {progress.current} of {progress.total}</span>
                <span>{Math.round((progress.current / progress.total) * 100)}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {!scanning && (
        <button
          onClick={() => scanGmail()}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all flex items-center justify-center space-x-2 font-semibold"
        >
          <Mail className="w-5 h-5" />
          <span>Scan Gmail for Subscriptions</span>
        </button>
      )}

      {foundSubs.length > 0 && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <p className="text-green-300 font-semibold">Found {foundSubs.length} subscriptions!</p>
          </div>
          <div className="space-y-1 text-sm text-slate-300">
            {foundSubs.map((sub, i) => (
              <div key={i}>{sub.name} - ${sub.cost.toFixed(2)}/{sub.billingCycle} ({sub.status})</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Capacitor guard: GIS script crashes WKWebView with _onload error; Gmail scanning not available in native app
const GmailSubscriptionScanner = (props) => {
  if (isNative) return null;
  return <GmailSubscriptionScannerInner {...props} />;
};

// ---- TaxWriteOffsHub.js ----
const TaxWriteOffsHub = ({ user, expenses, setExpenses, saveExpense, setShowUploadModal, invoices = [], setRevenue, withBackgroundTask, setTaxVaultReminder, showToast }) => {
  const [selectedMethod, setSelectedMethod] = useState(null);

  if (selectedMethod === 'bank') {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedMethod(null)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group"
          style={{ animation: 'pageSlideIn 0.3s ease-out both' }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]">
            <ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          </div>
          Back
        </button>
        
        <BankAnalyzer
          user={user}
          onCancel={() => setSelectedMethod(null)}
          onExpensesExtracted={async (transactions, incomeTransactions = []) => {
            const runInBackground = withBackgroundTask || (async (_label, runner) => runner());
            await runInBackground('Categorizing and saving bank transactions...', async () => {
              let savedCount = 0, skippedCount = 0;
              for (const transaction of transactions) {
                if (transaction.taxDeductible) {
                  const expenseData = {
                    merchant: transaction.merchant,
                    amount: transaction.amount,
                    date: transaction.date,
                    category: transaction.category,
                    taxDeductible: true,
                    fromBankStatement: true
                  };
                  const dupCheck = await checkDuplicateExpense(user.uid, expenseData);
                  if (dupCheck.isDuplicate) { skippedCount++; continue; }
                  const result = await saveExpense(user.uid, expenseData);
                  if (result.success) savedCount++;
                }
              }

              let incomeSaved = 0, incomeSkipped = 0;
              for (const tx of incomeTransactions) {
                const revenueData = {
                  source: tx.merchant || tx.description?.split(' ').slice(0, 3).join(' ') || 'Unknown',
                  amount: tx.amount,
                  date: tx.date,
                  description: tx.description,
                  status: 'received',
                  fromBankStatement: true,
                  createdAt: new Date().toISOString()
                };
                const dupCheck = await checkDuplicateRevenue(user.uid, revenueData, invoices);
                if (dupCheck.isDuplicate) { incomeSkipped++; continue; }
                const revenueResult = await saveRevenue(user.uid, revenueData);
                if (revenueResult.success) {
                  incomeSaved++;
                  const vaultSettings = await loadTaxVaultSettings(user.uid);
                  const reservePct = vaultSettings?.settings?.reservePercent || 25;
                  const vaultAmount = parseFloat((revenueData.amount * (reservePct / 100)).toFixed(2));
                  if (vaultAmount > 0 && setTaxVaultReminder) {
                    setTaxVaultReminder({
                      amount: vaultAmount,
                      invoiceTotal: revenueData.amount,
                      reservePct,
                      invoiceNumber: revenueData.description || 'Income',
                      client: revenueData.source || 'income entry',
                      invoiceId: revenueData.id || revenueData.sourceInvoiceId || Date.now(),
                    });
                  }
                }
              }

              const loadResult = await loadExpenses(user.uid);
              if (loadResult.success) setExpenses(loadResult.expenses);

              if (setRevenue) {
                const revResult = await loadRevenue(user.uid);
                if (revResult.success) setRevenue(revResult.revenue);
              }

              let msg = `Saved ${savedCount} deductible expenses`;
              if (skippedCount > 0) msg += `, skipped ${skippedCount} duplicates`;
              if (incomeSaved > 0) msg += ` · Added ${incomeSaved} payments to Revenue`;
              if (incomeSkipped > 0) msg += ` (${incomeSkipped} already existed)`;
              showToast?.(msg, 'success');
            });
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2">
            <h1 className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>Tax Write-Offs</h1>
          </div>
          <p className="text-sm text-slate-400 mt-1">Choose how to find deductions</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <button
          onClick={() => setShowUploadModal(true)}
          className="centris-card rounded-xl p-5 text-left hover:border-indigo-500/30 transition-all group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="bg-indigo-500/10 p-2.5 rounded-lg">
              <Camera className="w-5 h-5 text-indigo-400" />
            </div>
            <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-indigo-400 transition-colors" />
          </div>
          <p className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
            <span>Scan Receipts</span>
            <HelpBadge id="receipt-scanner" />
          </p>
          <p className="text-xs text-slate-400 mb-3">AI instantly categorizes and flags deductions</p>
          <div className="space-y-1">
            {['Instant AI categorization', 'Auto-detect deductible expenses', 'Duplicate detection'].map(f => (
              <p key={f} className="text-xs text-slate-500 flex items-center gap-1.5"><span className="text-emerald-400">OK</span>{f}</p>
            ))}
          </div>
          <p className="text-xs text-indigo-400 font-medium mt-3">{expenses.length} receipts scanned</p>
        </button>

        <button
          onClick={() => setSelectedMethod('bank')}
          className="centris-card rounded-xl p-5 text-left hover:border-cyan-500/30 transition-all group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="bg-cyan-500/10 p-2.5 rounded-lg">
              <CreditCard className="w-5 h-5 text-cyan-400" />
            </div>
            <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors" />
          </div>
          <p className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
            <span>Analyze Bank Statements</span>
            <HelpBadge id="bank-csv" />
          </p>
          <p className="text-xs text-slate-400 mb-3">Upload statements to find missed deductions in bulk</p>
          <div className="space-y-1">
            {['Scan 100s of transactions at once', 'Find hidden deductions', 'Skip duplicate transactions'].map(f => (
              <p key={f} className="text-xs text-slate-500 flex items-center gap-1.5"><span className="text-emerald-400">OK</span>{f}</p>
            ))}
          </div>
          <p className="text-xs text-cyan-400 font-medium mt-3">Supports CSV, PDF, Excel</p>
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Total Deductible</p>
          <p className="text-2xl font-bold text-white">${expenses.filter(e => e.taxDeductible).reduce((s, e) => s + e.amount, 0).toFixed(2)}</p>
        </div>
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Potential Savings</p>
          <p className="text-2xl font-bold text-emerald-400">${(expenses.filter(e => e.taxDeductible).reduce((s, e) => s + e.amount, 0) * 0.25).toFixed(2)}</p>
          <p className="text-xs text-slate-500 mt-0.5">At 25% tax rate</p>
        </div>
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Deductible Items</p>
          <p className="text-2xl font-bold text-indigo-400">{expenses.filter(e => e.taxDeductible).length}</p>
        </div>
      </div>

      {/* Tax Tips */}
      <div className="centris-card rounded-xl p-5">
        <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-400" /> Tax Deduction Tips</p>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            { n: '1', title: 'Keep receipts over $75', desc: 'IRS requires documentation for purchases over this amount' },
            { n: '2', title: 'Business meals are 50% deductible', desc: 'Scan restaurant receipts from client meetings' },
            { n: '3', title: 'Home office expenses save thousands', desc: 'Calculate square footage for significant savings' },
            { n: '4', title: 'Software subscriptions are 100% deductible', desc: 'All business software qualifies for full deduction' },
          ].map(tip => (
            <div key={tip.n} className="centris-card rounded-lg p-3 flex gap-3">
              <span className="text-indigo-400 font-bold text-sm shrink-0">{tip.n}.</span>
              <div>
                <p className="text-xs font-semibold text-white mb-0.5">{tip.title}</p>
                <p className="text-xs text-slate-500">{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      {expenses.length > 0 && (
        <div className="centris-card rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-lg font-bold text-white mb-4">Recent Deductible Expenses</h3>
          <div className="space-y-3">
            {expenses
              .filter(e => e.taxDeductible)
              .slice(0, 5)
              .map((expense, idx) => (
                <div key={idx} className="centris-card flex items-center justify-between p-4 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-500/20 p-2 rounded-lg">
                      <DollarSign className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{expense.merchant}</p>
                      <p className="text-slate-400 text-sm">{expense.category}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold">${expense.amount.toFixed(2)}</p>
                    <p className="text-green-400 text-xs">${(expense.amount * 0.25).toFixed(2)} saved</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-3">
        <button
          onClick={() => setShowUploadModal(true)}
          className="centris-card rounded-xl p-4 text-left hover:border-indigo-500/30 transition-all flex items-center gap-3 group"
        >
          <div className="bg-indigo-500/10 p-2.5 rounded-lg shrink-0"><Camera className="w-5 h-5 text-indigo-400" /></div>
          <div>
            <p className="text-sm font-semibold text-white">Scan Receipt Now</p>
            <p className="text-xs text-slate-500">Quick upload</p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-indigo-400 ml-auto transition-colors" />
        </button>
        <button
          onClick={() => setSelectedMethod('bank')}
          className="centris-card rounded-xl p-4 text-left hover:border-cyan-500/30 transition-all flex items-center gap-3 group"
        >
          <div className="bg-cyan-500/10 p-2.5 rounded-lg shrink-0"><CreditCard className="w-5 h-5 text-cyan-400" /></div>
          <div>
            <p className="text-sm font-semibold text-white">Analyze Statement</p>
            <p className="text-xs text-slate-500">Bulk import</p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 ml-auto transition-colors" />
        </button>
      </div>
    </div>
  );
};

// ---- InvoiceGenerator.js ----
const InvoiceGenerator = ({ user, setTaxVaultReminder, showToast, perms = null, onCanProceed = null, paygPrices = null }) => {
  const INVOICE_TEMPLATES = {
    aurora: { label: 'Aurora', accent: [79, 70, 229], accentSoft: [224, 231, 255], text: [30, 41, 59] },
    executive: { label: 'Executive', accent: [15, 23, 42], accentSoft: [241, 245, 249], text: [17, 24, 39] },
    minimal: { label: 'Minimal', accent: [22, 163, 74], accentSoft: [240, 253, 244], text: [20, 83, 45] },
  };
  const INVOICE_TEMPLATE_OPTIONS = Object.entries(INVOICE_TEMPLATES).map(([value, cfg]) => ({ value, label: cfg.label }));
  const canUseBrandedInvoices = useMemo(() => {
    if (typeof perms?.brandedInvoices === 'boolean') return perms.brandedInvoices;
    if (user?.isPro) return true;
    const planSignals = [
      user?.plan,
      user?.tier,
      user?.subscriptionTier,
      user?.subscriptionPlan,
      user?.planName,
    ]
      .map((v) => String(v || '').toLowerCase())
      .join(' ');
    return ['pro', 'premium', 'business', 'enterprise', 'team'].some((token) => planSignals.includes(token));
  }, [user]);
  const [invoices, setInvoices] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState({
    invoiceNumber: `INV-${Date.now()}`,
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    items: [{ description: '', quantity: 1, rate: 0 }],
    notes: '',
    dueDate: '',
    invoiceTemplate: 'aurora',
    status: 'draft'
  });

  // Load invoices from Firestore on mount
  useEffect(() => {
    if (user) {
      loadInvoicesFromFirestore();
    }
  }, [user]);

  const loadInvoicesFromFirestore = async () => {
    if (user?.uid === 'demo-user') { setInvoices(DEMO_DATA.invoices); return; }
    const result = await loadInvoices(user.uid);
    if (result.success) {
      setInvoices(result.invoices);
    } else {
      console.error('Failed to load invoices:', result.error);
    }
  };

  const addItem = () => {
    setCurrentInvoice({
      ...currentInvoice,
      items: [...currentInvoice.items, { description: '', quantity: 1, rate: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = currentInvoice.items.filter((_, i) => i !== index);
    setCurrentInvoice({ ...currentInvoice, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...currentInvoice.items];
    newItems[index][field] = value;
    setCurrentInvoice({ ...currentInvoice, items: newItems });
  };

  const calculateTotal = () => {
    return currentInvoice.items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0);
    }, 0);
  };

  const saveInvoiceHandler = async () => {
    haptic(ImpactStyle.Medium);
    if (typeof onCanProceed === 'function') {
      const ok = await onCanProceed('invoice', 'invoicesPerMonth');
      if (!ok) return;
    }
    if (!currentInvoice.clientName.trim()) {
      showToast?.('Please enter a client name', 'error');
      return;
    }

    if (currentInvoice.items.some(item => !item.description.trim())) {
      showToast?.('Please fill in all item descriptions', 'error');
      return;
    }

    const invoiceToSave = {
      ...currentInvoice,
      total: calculateTotal(),
      createdAt: new Date().toISOString()
    };

    if (user?.uid === 'demo-user') {
      const demoInvoice = { ...invoiceToSave, id: 'demo-i-' + Date.now() };
      setInvoices(prev => [demoInvoice, ...prev]);
      setShowCreateModal(false);
      resetForm();
      showToast?.('Invoice created', 'success');
      return;
    }
    const result = await saveInvoice(user.uid, invoiceToSave);

    if (result.success) {
      logEvent('invoice_created', { total: invoiceToSave.total, currency: 'USD' });
      await loadInvoicesFromFirestore();
      setShowCreateModal(false);
      resetForm();
      showToast?.('Invoice created', 'success');
    } else {
      showToast?.('Failed to save invoice: ' + result.error, 'error');
    }
  };

  const resetForm = () => {
    setCurrentInvoice({
      invoiceNumber: `INV-${Date.now()}`,
      clientName: '',
      clientEmail: '',
      clientAddress: '',
      items: [{ description: '', quantity: 1, rate: 0 }],
      notes: '',
      dueDate: '',
      invoiceTemplate: 'aurora',
      status: 'draft'
    });
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    const existingInvoice = invoices.find((inv) => inv.id === invoiceId);
    const updates = { status: newStatus };
    const transitionedToPaid = existingInvoice && existingInvoice.status !== 'paid' && newStatus === 'paid';
    if (transitionedToPaid && !existingInvoice.paidDate) {
      updates.paidDate = new Date().toISOString().split('T')[0];
    }

    const result = await updateInvoice(user.uid, invoiceId, updates);
    if (result.success) {
      if (transitionedToPaid) {
        const revenueId = `invoice-${invoiceId}`;
        const revenueData = {
          id: revenueId,
          source: 'invoice',
          sourceInvoiceId: String(invoiceId),
          invoiceNumber: existingInvoice.invoiceNumber || '',
          amount: Number(existingInvoice.total || 0),
          date: updates.paidDate || existingInvoice.paidDate || existingInvoice.date || new Date().toISOString().split('T')[0],
          description: `Invoice ${existingInvoice.invoiceNumber || invoiceId}${existingInvoice.clientName ? ` - ${existingInvoice.clientName}` : ''}`,
          status: 'received'
        };
        const revenueResult = await saveRevenue(user.uid, revenueData);
        if (!revenueResult.success) {
          showToast?.('Invoice marked paid, but failed to route to income: ' + revenueResult.error, 'error');
        }

        // ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬ Tax Vault Reminder ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬
        const vaultSettings = await loadTaxVaultSettings(user.uid);
        const reservePct = vaultSettings?.settings?.reservePercent || 25;
        const invoiceTotal = Number(existingInvoice.total || 0);
        const vaultAmount = parseFloat((invoiceTotal * (reservePct / 100)).toFixed(2));

        if (vaultAmount > 0 && setTaxVaultReminder) {
          setTaxVaultReminder({
            amount: vaultAmount,
            invoiceTotal,
            reservePct,
            invoiceNumber: existingInvoice.invoiceNumber || invoiceId,
            client: existingInvoice.clientName || 'your client',
            invoiceId,
          });
        }
      }
      await loadInvoicesFromFirestore();
    } else {
      showToast?.('Failed to update invoice: ' + result.error, 'error');
    }
  };

  const deleteInvoiceHandler = async (invoiceId) => {
    const result = await deleteInvoice(user.uid, invoiceId);
    if (result.success) {
      await loadInvoicesFromFirestore();
      showToast?.('Invoice deleted', 'success');
    } else {
      showToast?.('Failed to delete invoice: ' + result.error, 'error');
    }
  };

  const deleteAllInvoicesHandler = async () => {
    if (invoices.length === 0) return;

    let failed = 0;
    for (const inv of invoices) {
      const result = await deleteInvoice(user.uid, inv.id);
      if (!result.success) failed++;
    }

    await loadInvoicesFromFirestore();
    if (failed > 0) {
      showToast?.(`Deleted with issues: ${failed} invoice(s) could not be removed.`, 'error');
    } else {
      showToast?.('All invoices deleted', 'info');
    }
  };

  const updateInvoiceTemplate = async (invoiceId, invoiceTemplate) => {
    const result = await updateInvoice(user.uid, invoiceId, { invoiceTemplate });
    if (result.success) {
      await loadInvoicesFromFirestore();
    } else {
      showToast?.('Failed to update invoice template: ' + result.error, 'error');
    }
  };

  const downloadPDF = async (invoice) => {
    haptic(ImpactStyle.Medium);
    setPdfLoading(true);
    try {
      const { generateInvoicePDF } = await import('./invoicePDF');
      const pdfBlob = await generateInvoicePDF(invoice, canUseBrandedInvoices);
      const safeNumber = String(invoice.invoiceNumber || 'invoice').replace(/[^\w-]/g, '_');
      const fileName = `${safeNumber}.pdf`;
      if (Capacitor.isNativePlatform()) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(pdfBlob);
        });
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { Share } = await import('@capacitor/share');
        const safeName = fileName.replace(/[^\w.\-]/g, '_');
        await Filesystem.writeFile({ path: safeName, data: base64, directory: Directory.Cache });
        const { uri } = await Filesystem.getUri({ path: safeName, directory: Directory.Cache });
        await Share.share({ title: safeName, url: uri, dialogTitle: 'Save or share PDF' });
        haptic(ImpactStyle.Light);
      } else {
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      showToast?.(`PDF failed: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const sendEmail = async (invoice) => {
    // Send invoice is a Plus+ feature
    if (perms && !perms.brandedInvoices) {
      showToast?.('Upgrade to Plus or higher to send invoices', 'warning');
      return;
    }
    if (!invoice.clientEmail) {
      showToast?.('No email address for this client', 'error');
      return;
    }

    try {
      const { generateInvoicePDF } = await import('./invoicePDF');
      const pdfBlob = await generateInvoicePDF(invoice, canUseBrandedInvoices);
      const safeNumber = String(invoice.invoiceNumber || 'invoice').replace(/[^\w-]/g, '_');
      const fileName = `${safeNumber}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        await navigator.share({
          title: `Invoice ${invoice.invoiceNumber}`,
          text: `Invoice ${invoice.invoiceNumber} for ${invoice.clientName}`,
          files: [pdfFile],
        });
      } else {
        // Fallback: download the PDF and open email compose so the user can attach it.
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        const subject = encodeURIComponent(`Invoice ${invoice.invoiceNumber}`);
        const body = encodeURIComponent(
          `Hi ${invoice.clientName || ''},\n\nPlease find invoice ${invoice.invoiceNumber} attached.\n\nTotal: $${Number(invoice.total || 0).toFixed(2)}\nDue Date: ${invoice.dueDate || 'Not set'}\n\nThanks.`
        );
        window.location.href = `mailto:${encodeURIComponent(invoice.clientEmail)}?subject=${subject}&body=${body}`;
      }

      if (invoice.id && invoice.status === 'draft') {
        await updateInvoice(user.uid, invoice.id, { status: 'sent', sentAt: new Date().toISOString() });
        await loadInvoicesFromFirestore();
      }
    } catch (error) {
      console.error('Failed to send invoice email:', error);
      showToast?.(`Failed to prepare email: ${error.message}`, 'error');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'sent': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'overdue': return 'bg-red-500/20 text-red-300 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2">
            <h1 className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>Invoice Generator</h1>
          </div>
          <p className="text-sm text-slate-400 mt-1">Create and manage professional invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={deleteAllInvoicesHandler}
            disabled={invoices.length === 0}
            className="bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Delete All
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all"
          >
            <Plus className="w-4 h-4" />
            New Invoice
          </button>
        </div>
      </div>
      {perms?.tier === 'payg' && (
        <PaygCostBadge action="invoice" user={user} paygPrices={paygPrices} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Total Invoices</p>
          <p className="text-2xl font-bold text-white">{invoices.length}</p>
        </div>
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Total Billed</p>
          <p className="text-2xl font-bold text-white">${invoices.reduce((s, i) => s + i.total, 0).toFixed(2)}</p>
        </div>
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Collected</p>
          <p className="text-2xl font-bold text-emerald-400">${invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0).toFixed(2)}</p>
          <p className="text-xs text-slate-500 mt-0.5">{invoices.filter(i => i.status === 'paid').length} invoices</p>
        </div>
      </div>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <div className="centris-card rounded-xl border border-purple-500/20 p-16 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-500/20 rounded-2xl mb-6">
            <FileText className="w-10 h-10 text-purple-400" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">No invoices yet</h3>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">Create your first professional invoice</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-purple-700 transition-colors"
          >
            Create Invoice
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="centris-card rounded-xl border border-purple-500/20 p-6 hover:border-purple-500/40 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-white">{invoice.invoiceNumber}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full border bg-violet-500/10 text-violet-300 border-violet-500/30">
                      {INVOICE_TEMPLATES[invoice.invoiceTemplate]?.label || INVOICE_TEMPLATES.aurora.label}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(invoice.status)}`}>
                      {invoice.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-slate-300">
                      <User className="w-4 h-4 text-slate-500" />
                      <p className="text-sm">{invoice.clientName}</p>
                    </div>
                    {invoice.clientEmail && (
                      <div className="flex items-center space-x-2 text-slate-400">
                        <Mail className="w-4 h-4 text-slate-500" />
                        <p className="text-xs">{invoice.clientEmail}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={invoice.invoiceTemplate || 'aurora'}
                    onChange={(e) => updateInvoiceTemplate(invoice.id, e.target.value)}
                    className="px-3 py-1.5 bg-slate-900/50 border border-slate-700 rounded-lg text-xs font-medium text-white cursor-pointer focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  >
                    {INVOICE_TEMPLATE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <select
                    value={invoice.status}
                    onChange={(e) => updateInvoiceStatus(invoice.id, e.target.value)}
                    className="px-3 py-1.5 bg-slate-900/50 border border-slate-700 rounded-lg text-xs font-medium text-white cursor-pointer focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                  <button
                    onClick={() => deleteInvoiceHandler(invoice.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="centris-card rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Amount</p>
                  <p className="text-2xl font-bold text-white">${invoice.total.toFixed(2)}</p>
                </div>
                <div className="centris-card rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Due Date</p>
                  <p className="text-sm font-medium text-white">{invoice.dueDate || 'Not set'}</p>
                </div>
                <div className="centris-card rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Created</p>
                  <p className="text-sm font-medium text-white">
                    {new Date(invoice.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Items Preview */}
              <div className="mb-4 centris-card rounded-lg p-4">
                <p className="text-xs font-medium text-slate-400 mb-3 uppercase">Items:</p>
                <div className="space-y-2">
                  {invoice.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-300">{item.description}</span>
                      <span className="text-white font-medium">
                        {item.quantity} x ${item.rate.toFixed(2)} = ${(item.quantity * item.rate).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => downloadPDF(invoice)}
                  disabled={pdfLoading}
                  className="flex-1 bg-purple-500/20 text-purple-300 px-4 py-2.5 rounded-lg font-medium hover:bg-purple-500/30 transition-colors flex items-center justify-center space-x-2 border border-purple-500/30 disabled:opacity-60"
                >
                  {pdfLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  <span>{pdfLoading ? 'Generating…' : 'Download PDF'}</span>
                </button>
                <button 
                  onClick={() => sendEmail(invoice)}
                  className="flex-1 bg-indigo-500/20 text-indigo-300 px-4 py-2.5 rounded-lg font-medium hover:bg-indigo-500/30 transition-colors flex items-center justify-center space-x-2 border border-indigo-500/30"
                >
                  <Send className="w-4 h-4" />
                  <span>Send Email</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4 overflow-y-auto">
          <div className="centris-card rounded-2xl border border-purple-500/20 max-w-3xl w-full p-4 sm:p-6 md:p-8 my-4 sm:my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-500/20 p-2 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">Create Invoice</h3>
              </div>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }}>
                <X className="w-6 h-6 text-slate-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Invoice Number</label>
                  <input
                    type="text"
                    value={currentInvoice.invoiceNumber}
                    onChange={(e) => setCurrentInvoice({...currentInvoice, invoiceNumber: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={currentInvoice.dueDate}
                    onChange={(e) => setCurrentInvoice({...currentInvoice, dueDate: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Invoice Style</label>
                <select
                  value={currentInvoice.invoiceTemplate || 'aurora'}
                  onChange={(e) => setCurrentInvoice({ ...currentInvoice, invoiceTemplate: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                >
                  {INVOICE_TEMPLATE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Client Name *</label>
                <input
                  type="text"
                  value={currentInvoice.clientName}
                  onChange={(e) => setCurrentInvoice({...currentInvoice, clientName: e.target.value})}
                  placeholder="John Doe"
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none placeholder-slate-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Client Email</label>
                <input
                  type="email"
                  value={currentInvoice.clientEmail}
                  onChange={(e) => setCurrentInvoice({...currentInvoice, clientEmail: e.target.value})}
                  placeholder="john@example.com"
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none placeholder-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Items *</label>
                <div className="space-y-3">
                  {currentInvoice.items.map((item, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        placeholder="Description"
                        className="flex-1 px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none placeholder-slate-500"
                        required
                      />
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        onBlur={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        placeholder="Qty"
                        min="0"
                        step="0.01"
                        className="w-20 px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none placeholder-slate-500"
                      />
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateItem(index, 'rate', e.target.value)}
                        onBlur={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                        placeholder="Rate"
                        min="0"
                        step="0.01"
                        className="w-28 px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none placeholder-slate-500"
                      />
                      <div className="centris-card w-28 px-4 py-2.5 rounded-lg text-white font-medium text-center">
                        ${((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)).toFixed(2)}
                      </div>
                      {currentInvoice.items.length > 1 && (
                        <button
                          onClick={() => removeItem(index)}
                          type="button"
                          className="p-2.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={addItem}
                  type="button"
                  className="mt-3 text-purple-400 hover:text-purple-300 font-medium text-sm flex items-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Item</span>
                </button>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Notes (Optional)</label>
                <textarea
                  value={currentInvoice.notes}
                  onChange={(e) => setCurrentInvoice({...currentInvoice, notes: e.target.value})}
                  placeholder="Payment terms, thank you message, etc."
                  rows="3"
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:outline-none placeholder-slate-500"
                />
              </div>

              <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 flex items-center justify-between backdrop-blur-sm">
                <span className="text-lg font-medium text-white">Total:</span>
                <span className="text-4xl font-bold text-purple-400">${calculateTotal().toFixed(2)}</span>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  type="button"
                  className="flex-1 bg-slate-800 text-slate-300 px-6 py-3 rounded-xl font-semibold hover:bg-slate-700 transition-colors border border-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={saveInvoiceHandler}
                  type="button"
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  Save Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>}
    </div>
  );
};

// ---- MileageTracker.js ----
const MileageTracker = ({ user, showToast }) => {
  const [trips, setTrips] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [trackingTrip, setTrackingTrip] = useState(false);
  const [currentTrip, setCurrentTrip] = useState({
    date: new Date().toISOString().split('T')[0],
    startLocation: '',
    endLocation: '',
    startCoords: null,
    endCoords: null,
    miles: 0,
    purpose: '',
    category: 'Client Meeting',
    roundTrip: false,
    autoCalculated: false
  });

  const IRS_RATE_2026 = 0.70;

  const categories = [
    'Client Meeting',
    'Business Travel',
    'Delivery',
    'Supply Run',
    'Networking Event',
    'Conference',
    'Site Visit',
    'Other'
  ];

  // Load trips from Firestore on mount
  useEffect(() => {
    if (user) {
      loadTripsFromFirestore();
    }
  }, [user]);

  const loadTripsFromFirestore = async () => {
    if (user?.uid === 'demo-user') { setTrips(DEMO_DATA.mileageTrips); return; }
    const result = await loadMileageTrips(user.uid);
    if (result.success) {
      setTrips(result.trips);
    } else {
      console.error('Failed to load trips:', result.error);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  const getAddressFromCoords = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
        {
          headers: {
            'User-Agent': 'CENTRIS Finance Mileage Tracker'
          }
        }
      );
      const data = await response.json();
      
      const address = data.address;
      const parts = [];
      
      if (address.road) parts.push(address.road);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      
      return parts.length > 0 ? parts.join(', ') : data.display_name;
    } catch (error) {
      console.error('Geocoding error:', error);
      return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    }
  };

  const getCurrentLocation = async (isStart = true) => {
    if (!navigator.geolocation) {
      showToast?.('Geolocation is not supported by your browser', 'error');
      return;
    }

    setGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        
        const address = await getAddressFromCoords(latitude, longitude);

        if (isStart) {
          setCurrentTrip({
            ...currentTrip,
            startLocation: address,
            startCoords: { lat: latitude, lon: longitude, accuracy }
          });
          showToast?.('Start location captured!', 'success');
        } else {
          const newTrip = {
            ...currentTrip,
            endLocation: address,
            endCoords: { lat: latitude, lon: longitude, accuracy }
          };

          if (currentTrip.startCoords) {
            const distance = calculateDistance(
              currentTrip.startCoords.lat,
              currentTrip.startCoords.lon,
              latitude,
              longitude
            );
            newTrip.miles = parseFloat(distance.toFixed(1));
            newTrip.autoCalculated = true;
            showToast?.(`Trip distance calculated: ${distance.toFixed(1)} miles`, 'success');
          }

          setCurrentTrip(newTrip);
        }

        setGettingLocation(false);
      },
      (error) => {
        setGettingLocation(false);
        let errorMessage = 'Unable to retrieve your location';

        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }

        showToast?.(errorMessage, 'error');
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  const saveTrip = async () => {
    if (!currentTrip.startLocation.trim() || !currentTrip.endLocation.trim()) {
      showToast?.('Please enter start and end locations', 'error');
      return;
    }

    if (currentTrip.miles <= 0) {
      showToast?.('Please enter a valid mileage or use GPS to calculate distance', 'error');
      return;
    }

    if (!currentTrip.purpose.trim()) {
      showToast?.('Please enter the business purpose', 'error');
      return;
    }

    const totalMiles = currentTrip.roundTrip ? currentTrip.miles * 2 : currentTrip.miles;
    const deduction = totalMiles * IRS_RATE_2026;

    const tripToSave = {
      ...currentTrip,
      totalMiles,
      deduction,
      createdAt: new Date().toISOString()
    };

    if (editingTrip) {
      tripToSave.id = editingTrip.id;
    }

    if (user?.uid === 'demo-user') {
      const demoTrip = { ...tripToSave, id: 'demo-m-' + Date.now() };
      setTrips(prev => editingTrip ? prev.map(t => t.id === editingTrip.id ? demoTrip : t) : [demoTrip, ...prev]);
      setShowCreateModal(false);
      setEditingTrip(null);
      resetForm();
      showToast?.(editingTrip ? 'Trip updated' : 'Trip logged', 'success');
      return;
    }
    const result = await saveMileageTrip(user.uid, tripToSave);

    if (result.success) {
      await loadTripsFromFirestore();
      setShowCreateModal(false);
      setEditingTrip(null);
      resetForm();
      showToast?.(editingTrip ? 'Trip updated' : 'Trip logged', 'success');
    } else {
      showToast?.('Failed to save trip: ' + result.error, 'error');
    }
  };

  const resetForm = () => {
    setCurrentTrip({
      date: new Date().toISOString().split('T')[0],
      startLocation: '',
      endLocation: '',
      startCoords: null,
      endCoords: null,
      miles: 0,
      purpose: '',
      category: 'Client Meeting',
      roundTrip: false,
      autoCalculated: false
    });
    setTrackingTrip(false);
  };

  const editTrip = (trip) => {
    setEditingTrip(trip);
    setCurrentTrip({
      date: trip.date,
      startLocation: trip.startLocation,
      endLocation: trip.endLocation,
      startCoords: trip.startCoords,
      endCoords: trip.endCoords,
      miles: trip.roundTrip ? trip.totalMiles / 2 : trip.totalMiles,
      purpose: trip.purpose,
      category: trip.category,
      roundTrip: trip.roundTrip,
      autoCalculated: trip.autoCalculated
    });
    setShowCreateModal(true);
  };

  const deleteTrip = async (tripId) => {
    const result = await deleteMileageTrip(user.uid, tripId);
    if (result.success) {
      await loadTripsFromFirestore();
      showToast('Trip deleted', 'success');
    } else {
      showToast('Failed to delete trip: ' + (result.error || 'Unknown error'), 'error');
    }
  };

  const getTotalMiles = () => {
    return trips.reduce((sum, trip) => sum + (trip.totalMiles || 0), 0);
  };

  const getTotalDeduction = () => {
    return trips.reduce((sum, trip) => sum + (trip.deduction || 0), 0);
  };

  const getMonthlyBreakdown = () => {
    const breakdown = {};
    trips.forEach(trip => {
      const month = trip.date.substring(0, 7);
      if (!breakdown[month]) {
        breakdown[month] = { miles: 0, deduction: 0, trips: 0 };
      }
      breakdown[month].miles += trip.totalMiles;
      breakdown[month].deduction += trip.deduction;
      breakdown[month].trips += 1;
    });
    return breakdown;
  };

  const exportToCSV = () => {
    const headers = [
      'Date', 'Start Location', 'End Location', 'One-Way Miles', 'Round Trip', 
      'Total Miles', 'Category', 'Purpose', 'IRS Rate', 'Tax Deduction', 
      'GPS Calculated', 'Start Lat', 'Start Lon', 'End Lat', 'End Lon'
    ];
    
    const rows = trips.map(trip => [
      trip.date,
      trip.startLocation,
      trip.endLocation,
      trip.roundTrip ? (trip.totalMiles / 2).toFixed(1) : trip.totalMiles.toFixed(1),
      trip.roundTrip ? 'Yes' : 'No',
      trip.totalMiles.toFixed(1),
      trip.category,
      trip.purpose,
      `$${IRS_RATE_2026}`,
      `$${trip.deduction.toFixed(2)}`,
      trip.autoCalculated ? 'Yes' : 'Manual',
      trip.startCoords ? trip.startCoords.lat.toFixed(6) : 'N/A',
      trip.startCoords ? trip.startCoords.lon.toFixed(6) : 'N/A',
      trip.endCoords ? trip.endCoords.lat.toFixed(6) : 'N/A',
      trip.endCoords ? trip.endCoords.lon.toFixed(6) : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mileage_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const cancelEdit = () => {
    setShowCreateModal(false);
    setEditingTrip(null);
    resetForm();
  };

  const startTracking = async () => {
    setTrackingTrip(true);
    await getCurrentLocation(true);
  };

  const endTracking = async () => {
    await getCurrentLocation(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2">
            <h1 className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>GPS Mileage Tracker</h1>
          </div>
          <p className="text-sm text-slate-400 mt-1">Track business trips with GPS for IRS-compliant deductions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all"
        >
          <Plus className="w-4 h-4" />
          Log Trip
        </button>
      </div>

      <div className="centris-card rounded-xl p-4 flex items-center gap-3">
        <div className="bg-emerald-500/10 p-2 rounded-lg shrink-0"><DollarSign className="w-4 h-4 text-emerald-400" /></div>
        <div>
          <p className="text-xs font-semibold text-white">2026 IRS Standard Mileage Rate - $0.70/mile</p>
          <p className="text-xs text-slate-500 mt-0.5">GPS-calculated distances are IRS-compliant when documented with business purpose</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Total Trips</p>
          <p className="text-2xl font-bold text-white">{trips.length}</p>
          <p className="text-xs text-slate-500 mt-0.5">{trips.filter(t => t.autoCalculated).length} GPS-tracked</p>
        </div>
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Total Miles</p>
          <p className="text-2xl font-bold text-white">{getTotalMiles().toFixed(1)}</p>
          <p className="text-xs text-slate-500 mt-0.5">Business miles</p>
        </div>
        <div className="centris-card rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">IRS Rate</p>
          <p className="text-2xl font-bold text-emerald-400">${IRS_RATE_2026}</p>
          <p className="text-xs text-slate-500 mt-0.5">Per mile, 2026</p>
        </div>
        <div className="centris-card rounded-xl border border-emerald-500/20 p-4">
          <p className="text-xs text-slate-400 mb-1">Total Deduction</p>
          <p className="text-2xl font-bold text-emerald-400">${getTotalDeduction().toFixed(2)}</p>
          <p className="text-xs text-slate-500 mt-0.5">Tax savings: ${(getTotalDeduction() * 0.25).toFixed(2)}</p>
        </div>
      </div>

      {/* Monthly Breakdown */}
      {trips.length > 0 && (
        <div className="centris-card rounded-xl border border-green-500/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Monthly Breakdown</h3>
            <button
              onClick={exportToCSV}
              className="bg-slate-900/50 text-slate-300 px-4 py-2 rounded-lg font-medium hover:bg-slate-800/50 transition-colors text-sm flex items-center space-x-2 border border-slate-700"
            >
              <Download className="w-4 h-4" />
              <span>Export IRS Report</span>
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(getMonthlyBreakdown())
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map(([month, data]) => (
                <div key={month} className="centris-card rounded-xl p-4">
                  <p className="text-sm font-medium text-white mb-2">
                    {new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                  <div className="space-y-1 text-sm text-slate-400">
                    <div className="flex justify-between">
                      <span>Trips:</span>
                      <span className="font-medium text-white">{data.trips}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Miles:</span>
                      <span className="font-medium text-white">{data.miles.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Deduction:</span>
                      <span className="font-bold text-green-400">${data.deduction.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Trips List */}
      {trips.length === 0 ? (
        <div className="centris-card rounded-xl border border-green-500/20 p-16 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-2xl mb-6">
            <Car className="w-10 h-10 text-green-400" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">No trips logged yet</h3>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">Start tracking your business mileage with GPS to maximize tax deductions</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-green-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-green-700 transition-colors"
          >
            Log Your First Trip
          </button>
        </div>
      ) : (
        <div className="centris-card rounded-xl border border-green-500/20 overflow-hidden">
          <div className="p-6 border-b border-slate-700">
            <h3 className="text-lg font-bold text-white">Trip History</h3>
            <p className="text-sm text-slate-400 mt-1">
              {trips.filter(t => t.autoCalculated).length} of {trips.length} trips calculated with GPS
            </p>
          </div>
          <div className="divide-y divide-slate-700">
            {trips.map((trip) => (
              <div key={trip.id} className="p-6 transition-colors hover:opacity-90">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-bold text-white">{trip.category}</h4>
                      {trip.autoCalculated && (
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-300 text-xs font-medium rounded-full flex items-center space-x-1 border border-green-500/30">
                          <Navigation className="w-3 h-3" />
                          <span>GPS</span>
                        </span>
                      )}
                      {trip.roundTrip && (
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs font-medium rounded-full border border-blue-500/30">
                          Round Trip
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mb-1">{trip.purpose}</p>
                    <div className="flex items-center space-x-2 text-sm text-slate-500">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(trip.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => editTrip(trip)}
                      className="p-2 text-indigo-400 hover:bg-indigo-500/20 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteTrip(trip.id)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-3">
                  <div className="centris-card rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Route</p>
                    <div className="flex items-start space-x-2">
                      <MapPin className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-white">{trip.startLocation}</p>
                        <p className="text-slate-400 mt-1">{trip.endLocation}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-900/20 backdrop-blur-sm rounded-lg p-3 border border-green-500/30">
                    <p className="text-xs text-slate-500 mb-1">Deduction Calculation</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Distance:</span>
                        <span className="font-medium text-white">{trip.totalMiles.toFixed(1)} mi</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">IRS Rate:</span>
                        <span className="font-medium text-white">${IRS_RATE_2026}/mi</span>
                      </div>
                      <div className="flex justify-between border-t border-green-500/20 pt-1">
                        <span className="text-white font-medium">Tax Deduction:</span>
                        <span className="font-bold text-green-400">${trip.deduction.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {trip.startCoords && trip.endCoords && (
                  <div className="text-xs text-slate-500">
                    GPS: {trip.startCoords.lat.toFixed(4)}, {trip.startCoords.lon.toFixed(4)} &rarr; {trip.endCoords.lat.toFixed(4)}, {trip.endCoords.lon.toFixed(4)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4 overflow-y-auto">
          <div className="centris-card rounded-2xl border border-green-500/20 max-w-2xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">
                {editingTrip ? 'Edit Trip' : 'Log New Trip'}
              </h3>
              <button onClick={cancelEdit}>
                <X className="w-6 h-6 text-slate-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-6">
              {/* GPS Tracking Section */}
              {!editingTrip && (
                <div className="bg-gradient-to-br from-green-900/50 to-emerald-900/50 border-2 border-green-500/30 rounded-xl p-6 backdrop-blur-sm">
                  <div className="flex items-center space-x-2 mb-3">
                    <Navigation className="w-5 h-5 text-green-400" />
                    <h4 className="font-bold text-white">GPS Tracking</h4>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Use your device's GPS to automatically track your trip and calculate mileage
                  </p>
                  
                  {!trackingTrip ? (
                    <button
                      onClick={startTracking}
                      disabled={gettingLocation}
                      className="w-full bg-green-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      {gettingLocation ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          <span>Getting your location...</span>
                        </>
                      ) : (
                        <>
                          <Navigation className="w-5 h-5" />
                          <span>Start GPS Tracking</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={endTracking}
                      disabled={gettingLocation}
                      className="w-full bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      {gettingLocation ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          <span>Getting destination...</span>
                        </>
                      ) : (
                        <>
                          <MapPin className="w-5 h-5" />
                          <span>End Trip & Calculate Distance</span>
                        </>
                      )}
                    </button>
                  )}
                  
                  {trackingTrip && currentTrip.startLocation && (
                    <div className="mt-3 centris-card rounded-lg p-3 border border-green-500/30">
                      <p className="text-xs text-slate-400 mb-1">Starting from:</p>
                      <p className="text-sm font-medium text-white">{currentTrip.startLocation}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Date *</label>
                  <input
                    type="date"
                    value={currentTrip.date}
                    onChange={(e) => setCurrentTrip({...currentTrip, date: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Category *</label>
                  <select
                    value={currentTrip.category}
                    onChange={(e) => setCurrentTrip({...currentTrip, category: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:outline-none"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Start Location *
                  {!editingTrip && !currentTrip.startCoords && (
                    <button
                      onClick={() => getCurrentLocation(true)}
                      disabled={gettingLocation}
                      className="ml-2 text-green-400 hover:text-green-300 text-xs font-normal"
                    >
                      {gettingLocation ? 'Getting location...' : '(Use current location)'}
                    </button>
                  )}
                </label>
                <input
                  type="text"
                  value={currentTrip.startLocation}
                  onChange={(e) => setCurrentTrip({...currentTrip, startLocation: e.target.value})}
                  placeholder="123 Main St, City, State"
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:outline-none placeholder-slate-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  End Location *
                  {!editingTrip && !currentTrip.endCoords && currentTrip.startCoords && (
                    <button
                      onClick={() => getCurrentLocation(false)}
                      disabled={gettingLocation}
                      className="ml-2 text-green-400 hover:text-green-300 text-xs font-normal"
                    >
                      {gettingLocation ? 'Getting location...' : '(Use current location)'}
                    </button>
                  )}
                </label>
                <input
                  type="text"
                  value={currentTrip.endLocation}
                  onChange={(e) => setCurrentTrip({...currentTrip, endLocation: e.target.value})}
                  placeholder="456 Oak Ave, City, State"
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:outline-none placeholder-slate-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Distance (miles) *
                  {currentTrip.autoCalculated && (
                    <span className="ml-2 text-green-400 text-xs font-normal">(GPS calculated)</span>
                  )}
                </label>
                <input
                  type="number"
                  value={currentTrip.miles}
                  onChange={(e) => setCurrentTrip({...currentTrip, miles: parseFloat(e.target.value) || 0, autoCalculated: false})}
                  placeholder="0.0"
                  min="0"
                  step="0.1"
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:outline-none placeholder-slate-500"
                  required
                />
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={currentTrip.roundTrip}
                    onChange={(e) => setCurrentTrip({...currentTrip, roundTrip: e.target.checked})}
                    className="w-4 h-4 text-green-600 border-slate-700 rounded focus:ring-green-500 bg-slate-800"
                  />
                  <span className="text-sm font-medium text-slate-300">
                    Round Trip (doubles the mileage)
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Business Purpose *</label>
                <textarea
                  value={currentTrip.purpose}
                  onChange={(e) => setCurrentTrip({...currentTrip, purpose: e.target.value})}
                  placeholder="Meeting with client, delivery to customer, etc."
                  rows="3"
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:outline-none placeholder-slate-500"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">IRS requires documentation of business purpose for all mileage deductions</p>
              </div>

              {/* Deduction Preview */}
              {currentTrip.miles > 0 && (
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 backdrop-blur-sm">
                  <p className="text-sm font-semibold text-green-300 mb-2">Tax Deduction Preview</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-slate-300">
                      <span>Distance:</span>
                      <span className="font-medium text-white">
                        {currentTrip.miles.toFixed(1)} mi {currentTrip.roundTrip && 'x 2'} = {(currentTrip.roundTrip ? currentTrip.miles * 2 : currentTrip.miles).toFixed(1)} mi
                      </span>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>IRS Rate (2026):</span>
                      <span className="font-medium text-white">${IRS_RATE_2026}/mi</span>
                    </div>
                    <div className="flex justify-between border-t border-green-500/30 pt-2 mt-2">
                      <span className="font-medium text-white">Tax Deduction:</span>
                      <span className="text-xl font-bold text-green-400">
                        ${((currentTrip.roundTrip ? currentTrip.miles * 2 : currentTrip.miles) * IRS_RATE_2026).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={cancelEdit}
                  type="button"
                  className="flex-1 bg-slate-800 text-slate-300 px-6 py-3 rounded-xl font-semibold hover:bg-slate-700 transition-colors border border-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTrip}
                  type="button"
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  {editingTrip ? 'Update Trip' : 'Save Trip'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>}
    </div>
  );
};

const FREQ_TO_MONTHLY = { monthly: 1, biweekly: 2.17, weekly: 4.33, quarterly: 1 / 3, annual: 1 / 12 };
const FREQ_LABELS = { monthly: '/mo', biweekly: '/2wk', weekly: '/wk', quarterly: '/qtr', annual: '/yr' };

const BUDGET_CATS = [
  'Housing', 'Groceries', 'Dining Out', 'Transportation', 'Fuel', 'Utilities',
  'Insurance', 'Healthcare', 'Subscriptions', 'Entertainment', 'Clothing',
  'Personal Care', 'Savings', 'Debt Payments', 'Education', 'Kids / Family',
  'Pets', 'Travel', 'Gifts / Donations', 'Other',
];

const BILL_CATS = [
  'Housing', 'Utilities', 'Insurance', 'Loan / Debt', 'Subscription',
  'Healthcare', 'Childcare', 'Education', 'Vehicle', 'Other',
];

const PersonalToolsHub = ({
  user,
  expenses = [],
  budgets = [],
  setBudgets,
  bills = [],
  setBills,
  scheduledTx = [],
  setScheduledTx,
  smartAlerts = [],
  dismissSmartAlert,
  bankAccounts = [],
  onOpenBanking,
  liveBankImportActive = false,
  showToast,
}) => {
  const [selectedTool, setSelectedTool] = useState(null);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(n || 0));
  const urgentAlerts = smartAlerts.filter((a) => a.severity === 'critical' || a.severity === 'warning').length;
  const billsDueSoon = bills.filter((b) => {
    const days = (new Date(b.nextDueDate) - new Date()) / 86400000;
    return days >= 0 && days <= 7;
  }).length;
  const totalBankBalance = bankAccounts.reduce((sum, acc) => sum + Number(acc?.balances?.current || 0), 0);

  if (selectedTool) {
    return (
      <div>
        <button
          onClick={() => setSelectedTool(null)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group"
          style={{ animation: 'pageSlideIn 0.3s ease-out both' }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]">
            <ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          </div>
          Back
        </button>
        {selectedTool === 'budget' && (
          <FlexibleBudget user={user} expenses={expenses} budgets={budgets} setBudgets={setBudgets} fmt={fmt} />
        )}
        {selectedTool === 'bills' && (
          <BillReminders user={user} bills={bills} setBills={setBills} fmt={fmt} showToast={showToast} />
        )}
        {selectedTool === 'scheduled' && (
          <ScheduledTransactions user={user} scheduledTx={scheduledTx} setScheduledTx={setScheduledTx} fmt={fmt} />
        )}
        {selectedTool === 'alerts' && (
          <SmartAlerts alerts={smartAlerts} dismissAlert={dismissSmartAlert} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
      <div className="mb-2">
        <h1 className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>Personal Tools</h1>
        <p className="text-sm text-slate-500 mt-1">Budgets, bills, and financial housekeeping</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { tool: 'alerts', Icon: Bell, color: 'orange', label: 'Smart Alerts', desc: 'Unusual activity, overspend, and balance alerts.', sub: urgentAlerts > 0 ? `${urgentAlerts} need attention` : 'All clear' },
          { tool: 'budget', Icon: PieChart, color: 'indigo', label: 'Flexible Budget', desc: 'Monthly and paycheck-based budgets.', sub: budgets.length === 0 ? 'No budget set' : `${budgets.length} categories` },
          { tool: 'bills', Icon: Calendar, color: 'purple', label: 'Bill Reminders', desc: 'Upcoming bills with due dates and reminders.', sub: billsDueSoon > 0 ? `${billsDueSoon} due this week` : `${bills.length} bills tracked` },
          { tool: 'scheduled', Icon: RefreshCw, color: 'emerald', label: 'Scheduled Tx', desc: 'Recurring income, bills, and transfers.', sub: scheduledTx.length === 0 ? 'None set up' : `${scheduledTx.length} recurring` },
          { tool: 'banking', Icon: Wallet, color: 'cyan', label: 'Banking', desc: liveBankImportActive ? 'Connected accounts in one place.' : 'Import statements or switch to live bank.', sub: liveBankImportActive ? (bankAccounts.length > 0 ? `${bankAccounts.length} account${bankAccounts.length !== 1 ? 's' : ''} · $${totalBankBalance.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : 'Connect an account') : 'Import via statement', onClick: () => onOpenBanking?.() },
        ].map(({ tool, Icon, color, label, desc, sub, onClick }) => {
          const borderMap = { orange: 'hover:border-orange-400/40', indigo: 'hover:border-indigo-400/40', purple: 'hover:border-purple-400/40', emerald: 'hover:border-emerald-400/40', cyan: 'hover:border-cyan-400/40' };
          const textMap = { orange: 'text-orange-400', indigo: 'text-indigo-400', purple: 'text-purple-400', emerald: 'text-emerald-400', cyan: 'text-cyan-400' };
          return (
            <button key={tool} onClick={onClick || (() => setSelectedTool(tool))} className={`centris-card rounded-xl p-4 text-left ${borderMap[color]} transition-all flex flex-col gap-2`}>
              <div className="flex items-center justify-between">
                <Icon className={`w-5 h-5 ${textMap[color]}`} />
                <ArrowRight className={`w-3.5 h-3.5 ${textMap[color]}`} />
              </div>
              <div>
                <p className="text-sm font-bold text-white">{label}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{desc}</p>
              </div>
              <p className={`text-[10px] font-semibold ${textMap[color]} mt-auto`}>{sub}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};
const SmartAlerts = ({ alerts = [], dismissAlert }) => {
  const [filter, setFilter] = useState('all');
  const categories = ['all', 'transactions', 'balance', 'budget', 'bills', 'unusual'];
  const filtered = filter === 'all' ? alerts : alerts.filter((a) => a.category === filter);

  const severityStyle = {
    critical: 'border-red-500/20 bg-red-500/5',
    warning: 'border-amber-500/20 bg-amber-500/5',
    info: 'border-slate-700/40 bg-slate-800/20',
  };
  const textStyle = {
    critical: 'text-red-300',
    warning: 'text-amber-300',
    info: 'text-slate-300',
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Smart Alerts</h2>
        <p className="text-sm text-slate-400 mt-0.5">Unusual activity, overspend, and things that need attention</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-all border ${filter === c ? 'bg-white/10 text-white border-white/20' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
          >
            {c} {c === 'all' ? `(${alerts.length})` : `(${alerts.filter((a) => a.category === c).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500 py-6">
          {alerts.length === 0 ? 'No alerts right now - everything looks normal.' : 'No alerts in this category.'}
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((alert) => (
            <div
              key={alert.id}
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${severityStyle[alert.severity] || severityStyle.info}`}
            >
              <span className="text-base mt-0.5 shrink-0">{alert.icon}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${textStyle[alert.severity] || textStyle.info}`}>{alert.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{alert.message}</p>
              </div>
              <button onClick={() => dismissAlert(alert.id)} className="text-slate-600 hover:text-slate-400 text-xs mt-0.5 shrink-0">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FlexibleBudget = ({ user, expenses = [], budgets = [], setBudgets, fmt }) => {
  const [mode, setMode] = useState('monthly');
  const [paycheckAmt, setPaycheckAmt] = useState('');
  const [paycheckFreq, setPaycheckFreq] = useState('biweekly');
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newLine, setNewLine] = useState({ category: 'Groceries', label: '', amount: '', frequency: 'monthly' });

  const monthlyBudgets = useMemo(
    () => budgets.map((b) => ({ ...b, monthlyAmount: Number(b.amount || 0) * (FREQ_TO_MONTHLY[b.frequency] || 1) })),
    [budgets],
  );

  const totalMonthly = monthlyBudgets.reduce((s, b) => s + b.monthlyAmount, 0);
  const paycheckMonthly = paycheckAmt ? parseFloat(paycheckAmt) * (FREQ_TO_MONTHLY[paycheckFreq] || 1) : null;

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const actuals = useMemo(() => {
    const map = {};
    expenses.filter((e) => e.date >= monthStart).forEach((e) => {
      map[e.category] = (map[e.category] || 0) + e.amount;
    });
    return map;
  }, [expenses, monthStart]);

  const totalSpent = Object.values(actuals).reduce((s, v) => s + v, 0);

  const handleAdd = async () => {
    if (!newLine.amount || Number.isNaN(parseFloat(newLine.amount))) return;
    if (user?.uid === 'demo-user') {
      const demoBudget = { ...newLine, id: 'demo-bu-' + Date.now(), amount: parseFloat(newLine.amount), label: newLine.label || newLine.category, createdAt: new Date().toISOString() };
      setBudgets(prev => [...prev, demoBudget]);
      setNewLine({ category: 'Groceries', label: '', amount: '', frequency: 'monthly' });
      setShowAdd(false);
      return;
    }
    setSaving(true);
    const data = {
      ...newLine,
      amount: parseFloat(newLine.amount),
      label: newLine.label || newLine.category,
      createdAt: new Date().toISOString(),
    };
    await saveBudget(user.uid, data);
    const r = await loadBudgets(user.uid);
    if (r.success) setBudgets(r.budgets);
    setNewLine({ category: 'Groceries', label: '', amount: '', frequency: 'monthly' });
    setShowAdd(false);
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (user?.uid === 'demo-user') { setBudgets(prev => prev.filter(b => b.id !== id)); return; }
    await deleteBudget(user.uid, id);
    const r = await loadBudgets(user.uid);
    if (r.success) setBudgets(r.budgets);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Flexible Budget</h2>
          <p className="text-sm text-slate-400 mt-0.5">Quarterly bills and annual subs automatically smoothed to monthly</p>
        </div>
        <div className="flex gap-1 centris-card rounded-lg p-0.5">
          {[['monthly', 'Monthly'], ['paycheck', 'Paycheck']].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setMode(k)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === k ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300'}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {mode === 'paycheck' && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-400">Paycheck</span>
          <div className="relative">
            <span className="absolute left-3 top-2 text-slate-400 text-sm">$</span>
            <input
              type="number"
              placeholder="0"
              value={paycheckAmt}
              onChange={(e) => setPaycheckAmt(e.target.value)}
              className="pl-7 pr-3 py-1.5 bg-slate-800/60 border border-white/[0.06] rounded-lg text-sm text-white w-24"
            />
          </div>
          <select
            value={paycheckFreq}
            onChange={(e) => setPaycheckFreq(e.target.value)}
            className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-1.5 text-sm text-white"
          >
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every 2 weeks</option>
            <option value="monthly">Monthly</option>
          </select>
          {paycheckMonthly && <span className="text-xs text-slate-500">≈  {fmt(paycheckMonthly)}/mo</span>}
        </div>
      )}

      {totalMonthly > 0 && (
        <div className="flex items-center gap-6 pb-3 border-b border-white/[0.06] flex-wrap">
          {paycheckMonthly && (
            <>
              <div><p className="text-[10px] text-slate-500 uppercase tracking-wider">Income</p><p className="text-xl font-bold text-white">{fmt(paycheckMonthly)}</p></div>
              <span className="text-slate-700">-</span>
            </>
          )}
          <div><p className="text-[10px] text-slate-500 uppercase tracking-wider">Budgeted</p><p className="text-xl font-bold text-white">{fmt(totalMonthly)}</p></div>
          <div><p className="text-[10px] text-slate-500 uppercase tracking-wider">Spent so far</p><p className="text-xl font-bold text-white">{fmt(totalSpent)}</p></div>
          {paycheckMonthly && (
            <>
              <span className="text-slate-700">=</span>
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Remaining</p>
                <p className={`text-xl font-bold ${paycheckMonthly - totalMonthly >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmt(paycheckMonthly - totalMonthly)}</p>
              </div>
            </>
          )}
        </div>
      )}

      {monthlyBudgets.length > 0 && (
        <div>
          <div className="grid text-[10px] text-slate-600 uppercase tracking-wider mb-2 px-1" style={{ gridTemplateColumns: '1fr 72px 72px 72px 24px' }}>
            <span>Category</span>
            <span className="text-right">Budget</span>
            <span className="text-right">Spent</span>
            <span className="text-right">Left</span>
            <span />
          </div>

          {monthlyBudgets.map((b) => {
            const spent = actuals[b.category] || actuals[b.label] || 0;
            const left = b.monthlyAmount - spent;
            const pct = b.monthlyAmount > 0 ? Math.min(100, (spent / b.monthlyAmount) * 100) : 0;
            const over = left < 0;
            return (
              <div key={b.id} className="group border-b border-white/[0.04] last:border-0">
                <div className="grid items-center py-2.5 px-1" style={{ gridTemplateColumns: '1fr 72px 72px 72px 24px' }}>
                  <div>
                    <span className="text-sm text-white">{b.label || b.category}</span>
                    {b.frequency !== 'monthly' && (
                      <span className="ml-2 text-[10px] text-slate-600">{fmt(b.amount)}{FREQ_LABELS[b.frequency]}</span>
                    )}
                  </div>
                  <span className="text-xs text-slate-400 text-right">{fmt(b.monthlyAmount)}</span>
                  <span className="text-xs text-white text-right">{fmt(spent)}</span>
                  <span className={`text-xs font-medium text-right ${over ? 'text-red-400' : 'text-emerald-400'}`}>
                    {over ? '-' : ''}{fmt(Math.abs(left))}
                  </span>
                  <button onClick={() => handleDelete(b.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-red-400 flex justify-end">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <div className="h-0.5 bg-slate-700/30 rounded-full mb-1 mx-1">
                  <div
                    className={`h-0.5 rounded-full ${over ? 'bg-red-500' : pct > 80 ? 'bg-amber-400' : 'bg-emerald-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}

          <div className="grid items-center pt-3 px-1 mt-1" style={{ gridTemplateColumns: '1fr 72px 72px 72px 24px' }}>
            <span className="text-xs text-slate-400 font-semibold">Total</span>
            <span className="text-xs font-bold text-white text-right">{fmt(totalMonthly)}</span>
            <span className="text-xs font-bold text-white text-right">{fmt(totalSpent)}</span>
            <span className={`text-xs font-bold text-right ${totalMonthly - totalSpent < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {fmt(totalMonthly - totalSpent)}
            </span>
            <span />
          </div>
        </div>
      )}

      {monthlyBudgets.length === 0 && !showAdd && (
        <p className="text-sm text-slate-500 py-2">No budget lines yet. Mix any frequency - everything converts to monthly.</p>
      )}

      {showAdd ? (
        <div className="border-t border-white/[0.06] pt-4 space-y-3">
          <p className="text-xs text-slate-500 uppercase tracking-wider">New budget line</p>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Category</label>
              <select
                value={newLine.category}
                onChange={(e) => setNewLine((p) => ({ ...p, category: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              >
                {BUDGET_CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Label (optional)</label>
              <input
                type="text"
                placeholder={newLine.category}
                value={newLine.label}
                onChange={(e) => setNewLine((p) => ({ ...p, label: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white w-36 placeholder-slate-700"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-slate-400 text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={newLine.amount}
                  onChange={(e) => setNewLine((p) => ({ ...p, amount: e.target.value }))}
                  className="pl-7 pr-3 py-2 bg-slate-800/60 border border-white/[0.06] rounded-lg text-sm text-white w-28"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Frequency</label>
              <select
                value={newLine.frequency}
                onChange={(e) => setNewLine((p) => ({ ...p, frequency: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
            {newLine.amount && (
              <span className="text-xs text-slate-500 self-end pb-2">
                ≈  {fmt(parseFloat(newLine.amount || 0) * (FREQ_TO_MONTHLY[newLine.frequency] || 1))}/mo
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
            <button
              onClick={handleAdd}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Add line'}
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1.5 transition-colors">
          <Plus className="w-4 h-4" /> Add budget line
        </button>
      )}
    </div>
  );
};

const BillReminders = ({ user, bills = [], setBills, fmt, showToast }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newBill, setNewBill] = useState({
    name: '',
    amount: '',
    nextDueDate: '',
    frequency: 'monthly',
    category: 'Utilities',
    reminderDays: 3,
    notes: '',
  });

  const today = new Date().toISOString().split('T')[0];

  const refresh = async () => {
    const r = await loadBills(user.uid);
    if (r.success) setBills(r.bills);
  };

  const handleAdd = async () => {
    if (!newBill.name || !newBill.amount || !newBill.nextDueDate) return;
    if (user?.uid === 'demo-user') {
      const demoBill = { ...newBill, id: 'demo-b-' + Date.now(), amount: parseFloat(newBill.amount), createdAt: new Date().toISOString() };
      setBills(prev => [...prev, demoBill]);
      setNewBill({ name: '', amount: '', nextDueDate: '', frequency: 'monthly', category: 'Utilities', reminderDays: 3, notes: '' });
      setShowAdd(false);
      return;
    }
    setSaving(true);
    await saveBill(user.uid, { ...newBill, amount: parseFloat(newBill.amount), createdAt: new Date().toISOString() });
    await refresh();
    setNewBill({ name: '', amount: '', nextDueDate: '', frequency: 'monthly', category: 'Utilities', reminderDays: 3, notes: '' });
    setShowAdd(false);
    setSaving(false);
  };

  const handlePaid = async (bill) => {
    if (user?.uid === 'demo-user') { setBills(prev => prev.map(b => b.id === bill.id ? { ...b, paid: true } : b)); showToast?.('Bill marked paid', 'success'); return; }
    const result = await markBillPaid(user.uid, bill.id, bill);
    await refresh();
    if (result.success) showToast?.('Bill marked paid — next due date updated', 'success');
    else showToast?.('Failed to mark bill paid', 'error');
  };

  const handleDelete = async (id) => {
    if (user?.uid === 'demo-user') { setBills(prev => prev.filter(b => b.id !== id)); showToast?.('Bill deleted', 'success'); return; }
    const result = await deleteBill(user.uid, id);
    await refresh();
    if (result.success) showToast?.('Bill deleted', 'success');
    else showToast?.('Failed to delete bill', 'error');
  };

  const daysUntil = (dateStr) => Math.round((new Date(dateStr) - new Date()) / 86400000);

  const sorted = [...bills].sort((a, b) => a.nextDueDate.localeCompare(b.nextDueDate));
  const overdue = sorted.filter((b) => b.nextDueDate < today);
  const upcoming = sorted.filter((b) => b.nextDueDate >= today);
  const totalMonthly = bills.reduce((s, b) => s + Number(b.amount || 0) * (FREQ_TO_MONTHLY[b.frequency] || 1), 0);

  const BillRow = ({ bill }) => {
    const days = daysUntil(bill.nextDueDate);
    const isOverdue = days < 0;
    const isDueSoon = days >= 0 && days <= 3;
    return (
      <div className="flex items-center gap-3 py-3 border-b border-white/[0.04] last:border-0 group">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm text-white font-medium">{bill.name}</p>
            <span className="text-[10px] text-slate-600 capitalize">{bill.frequency}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className={`text-xs ${isOverdue ? 'text-red-400' : isDueSoon ? 'text-amber-400' : 'text-slate-500'}`}>
              {isOverdue ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `Due in ${days}d`}
              {' · '}{new Date(bill.nextDueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
            <span className="text-[10px] text-slate-600">{bill.category}</span>
          </div>
        </div>
        <span className="text-sm font-semibold text-white shrink-0">{fmt(bill.amount)}</span>
        <button onClick={() => handlePaid(bill)} className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-emerald-400 hover:text-emerald-300 border border-emerald-500/20 px-2 py-1 rounded-lg">
          Paid ✓
        </button>
        <button onClick={() => handleDelete(bill.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-red-400">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Bill Reminders</h2>
          <p className="text-sm text-slate-400 mt-0.5">{bills.length} bills · {fmt(totalMonthly)}/mo total</p>
        </div>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)} className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1.5 transition-colors">
            <Plus className="w-4 h-4" /> Add bill
          </button>
        )}
      </div>

      {overdue.length > 0 && (
        <div>
          <p className="text-xs text-red-400 uppercase tracking-wider mb-2">Overdue</p>
          {overdue.map((b) => <BillRow key={b.id} bill={b} />)}
        </div>
      )}

      {upcoming.length > 0 && (
        <div>
          {overdue.length > 0 && <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Upcoming</p>}
          {upcoming.map((b) => <BillRow key={b.id} bill={b} />)}
        </div>
      )}

      {bills.length === 0 && !showAdd && (
        <p className="text-sm text-slate-500 py-2">No bills yet. Add one to track due dates and get reminders.</p>
      )}

      {showAdd && (
        <div className="border-t border-white/[0.06] pt-4 space-y-3">
          <p className="text-xs text-slate-500 uppercase tracking-wider">New bill</p>
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-40">
              <label className="text-[11px] text-slate-500 block mb-1">Bill name</label>
              <input
                type="text"
                placeholder="e.g. Internet"
                value={newBill.name}
                onChange={(e) => setNewBill((p) => ({ ...p, name: e.target.value }))}
                className="w-full bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-700"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-slate-400 text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={newBill.amount}
                  onChange={(e) => setNewBill((p) => ({ ...p, amount: e.target.value }))}
                  className="pl-7 pr-3 py-2 bg-slate-800/60 border border-white/[0.06] rounded-lg text-sm text-white w-28"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Next due date</label>
              <input
                type="date"
                value={newBill.nextDueDate}
                onChange={(e) => setNewBill((p) => ({ ...p, nextDueDate: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Repeats</label>
              <select
                value={newBill.frequency}
                onChange={(e) => setNewBill((p) => ({ ...p, frequency: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
                <option value="once">One-time</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Category</label>
              <select
                value={newBill.category}
                onChange={(e) => setNewBill((p) => ({ ...p, category: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              >
                {BILL_CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Remind me</label>
              <select
                value={newBill.reminderDays}
                onChange={(e) => setNewBill((p) => ({ ...p, reminderDays: parseInt(e.target.value, 10) }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value={1}>1 day before</option>
                <option value={3}>3 days before</option>
                <option value={7}>1 week before</option>
                <option value={14}>2 weeks before</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
            <button
              onClick={handleAdd}
              disabled={saving}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium disabled:opacity-60 transition-all"
            >
              {saving ? 'Saving...' : 'Add bill'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TX_TYPES = { income: 'Income', bill: 'Bill', transfer: 'Transfer', expense: 'Expense' };
const TX_COLORS = { income: 'text-emerald-400', bill: 'text-red-400', transfer: 'text-blue-400', expense: 'text-orange-400' };
const TX_SIGNS = { income: '+', bill: '-', transfer: '->', expense: '-' };

const ScheduledTransactions = ({ user, scheduledTx = [], setScheduledTx, fmt }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTx, setNewTx] = useState({
    name: '',
    type: 'income',
    amount: '',
    nextDate: '',
    frequency: 'monthly',
    category: '',
    notes: '',
    active: true,
  });

  const refresh = async () => {
    const r = await loadScheduledTx(user.uid);
    if (r.success) setScheduledTx(r.txs);
  };

  const handleAdd = async () => {
    if (!newTx.name || !newTx.amount || !newTx.nextDate) return;
    if (user?.uid === 'demo-user') {
      const demoTx = { ...newTx, id: 'demo-tx-' + Date.now(), amount: parseFloat(newTx.amount), createdAt: new Date().toISOString() };
      setScheduledTx(prev => [...prev, demoTx]);
      setNewTx({ name: '', type: 'income', amount: '', nextDate: '', frequency: 'monthly', category: '', notes: '', active: true });
      setShowAdd(false);
      return;
    }
    setSaving(true);
    await saveScheduledTx(user.uid, { ...newTx, amount: parseFloat(newTx.amount), createdAt: new Date().toISOString() });
    await refresh();
    setNewTx({ name: '', type: 'income', amount: '', nextDate: '', frequency: 'monthly', category: '', notes: '', active: true });
    setShowAdd(false);
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (user?.uid === 'demo-user') { setScheduledTx(prev => prev.filter(t => t.id !== id)); return; }
    await deleteScheduledTx(user.uid, id);
    await refresh();
  };

  const handleToggle = async (tx) => {
    if (user?.uid === 'demo-user') { setScheduledTx(prev => prev.map(t => t.id === tx.id ? { ...t, active: !t.active } : t)); return; }
    await updateScheduledTx(user.uid, tx.id, { active: !tx.active });
    await refresh();
  };

  const totalMonthlyIn = scheduledTx
    .filter((t) => t.active && t.type === 'income')
    .reduce((s, t) => s + Number(t.amount || 0) * (FREQ_TO_MONTHLY[t.frequency] || 1), 0);
  const totalMonthlyOut = scheduledTx
    .filter((t) => t.active && (t.type === 'bill' || t.type === 'expense'))
    .reduce((s, t) => s + Number(t.amount || 0) * (FREQ_TO_MONTHLY[t.frequency] || 1), 0);

  const byType = { income: [], bill: [], expense: [], transfer: [] };
  scheduledTx.forEach((t) => (byType[t.type] || byType.expense).push(t));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Scheduled Transactions</h2>
          <p className="text-sm text-slate-400 mt-0.5">Recurring income and bills that feed into your budget and forecast</p>
        </div>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)} className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1.5 transition-colors">
            <Plus className="w-4 h-4" /> Add
          </button>
        )}
      </div>

      {scheduledTx.length > 0 && (
        <div className="flex items-center gap-6 pb-3 border-b border-white/[0.06]">
          <div><p className="text-[10px] text-slate-500 uppercase tracking-wider">Monthly in</p><p className="text-xl font-bold text-emerald-400">+{fmt(totalMonthlyIn)}</p></div>
          <div><p className="text-[10px] text-slate-500 uppercase tracking-wider">Monthly out</p><p className="text-xl font-bold text-red-400">-{fmt(totalMonthlyOut)}</p></div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Net</p>
            <p className={`text-xl font-bold ${totalMonthlyIn - totalMonthlyOut >= 0 ? 'text-white' : 'text-red-400'}`}>
              {fmt(totalMonthlyIn - totalMonthlyOut)}
            </p>
          </div>
        </div>
      )}

      {Object.entries(byType).filter(([, txs]) => txs.length > 0).map(([type, txs]) => (
        <div key={type}>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{TX_TYPES[type]}</p>
          {txs.map((tx) => {
            const daysUntil = Math.round((new Date(tx.nextDate) - new Date()) / 86400000);
            const monthly = Number(tx.amount || 0) * (FREQ_TO_MONTHLY[tx.frequency] || 1);
            return (
              <div key={tx.id} className={`flex items-center gap-3 py-3 border-b border-white/[0.04] last:border-0 group ${!tx.active ? 'opacity-40' : ''}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">{tx.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-slate-500 capitalize">{tx.frequency}</span>
                    {tx.nextDate && (
                      <span className="text-xs text-slate-500">
                        next: {new Date(tx.nextDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {daysUntil >= 0 && ` (${daysUntil}d)`}
                      </span>
                    )}
                    {tx.frequency !== 'monthly' && (
                      <span className="text-[10px] text-slate-600">≈  {fmt(monthly)}/mo</span>
                    )}
                  </div>
                </div>
                <span className={`text-sm font-semibold ${TX_COLORS[type]} shrink-0`}>
                  {TX_SIGNS[type]}{fmt(tx.amount)}
                </span>
                <button
                  onClick={() => handleToggle(tx)}
                  className={`opacity-0 group-hover:opacity-100 transition-opacity text-[10px] px-2 py-1 rounded border ${tx.active ? 'text-slate-400 border-slate-700 hover:text-white' : 'text-emerald-400 border-emerald-500/20'}`}
                >
                  {tx.active ? 'Pause' : 'Resume'}
                </button>
                <button onClick={() => handleDelete(tx.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      ))}

      {scheduledTx.length === 0 && !showAdd && (
        <p className="text-sm text-slate-500 py-2">No scheduled transactions yet. Add your salary, rent, and recurring bills to get accurate forecasts.</p>
      )}

      {showAdd && (
        <div className="border-t border-white/[0.06] pt-4 space-y-3">
          <p className="text-xs text-slate-500 uppercase tracking-wider">New scheduled transaction</p>
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-40">
              <label className="text-[11px] text-slate-500 block mb-1">Name</label>
              <input
                type="text"
                placeholder="e.g. Salary, Rent"
                value={newTx.name}
                onChange={(e) => setNewTx((p) => ({ ...p, name: e.target.value }))}
                className="w-full bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-700"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Type</label>
              <select
                value={newTx.type}
                onChange={(e) => setNewTx((p) => ({ ...p, type: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              >
                {Object.entries(TX_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-slate-400 text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={newTx.amount}
                  onChange={(e) => setNewTx((p) => ({ ...p, amount: e.target.value }))}
                  className="pl-7 pr-3 py-2 bg-slate-800/60 border border-white/[0.06] rounded-lg text-sm text-white w-28"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Frequency</label>
              <select
                value={newTx.frequency}
                onChange={(e) => setNewTx((p) => ({ ...p, frequency: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Next date</label>
              <input
                type="date"
                value={newTx.nextDate}
                onChange={(e) => setNewTx((p) => ({ ...p, nextDate: e.target.value }))}
                className="bg-slate-800/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              />
            </div>
          </div>
          {newTx.amount && newTx.frequency && (
            <p className="text-xs text-slate-500">≈  {fmt(parseFloat(newTx.amount || 0) * (FREQ_TO_MONTHLY[newTx.frequency] || 1))}/mo</p>
          )}
          <div className="flex gap-2">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
            <button
              onClick={handleAdd}
              disabled={saving}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium disabled:opacity-60 transition-all"
            >
              {saving ? 'Saving...' : 'Add'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- BusinessIdeaEngine ----
const BusinessIdeaEngine = ({ user }) => {
  const CLOUD = 'https://us-central1-receiptwise-prod.cloudfunctions.net';
  const [phase, setPhase] = useState('idle'); // idle | loading-ideas | ideas | loading-plan | plan
  const [ideas, setIdeas] = useState([]);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [plan, setPlan] = useState('');
  const [error, setError] = useState('');
  const [aiConsentGiven, setAiConsentGiven] = useState(() => {
    try { return localStorage.getItem(AI_CONSENT_IDEAS_KEY) === 'true' || localStorage.getItem(AI_CONSENT_KEY) === 'true'; } catch { return false; }
  });
  const [aiConsentDeclined, setAiConsentDeclined] = useState(false);

  if (aiConsentDeclined) {
    return (
      <div className="max-w-lg mx-auto mt-8 text-center space-y-3 p-6">
        <p className="text-sm text-slate-400">AI Business Ideas is unavailable without data sharing consent.</p>
        <button onClick={() => setAiConsentDeclined(false)} className="text-xs text-amber-400 underline">Review consent again</button>
      </div>
    );
  }

  if (!aiConsentGiven) {
    return (
      <div className="max-w-lg mx-auto mt-8">
        <div className="centris-card rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Brain className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">AI Business Ideas — Data Sharing Notice</p>
              <p className="text-xs text-slate-400">Permission required before continuing</p>
            </div>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-white">What data is shared:</p>
            <p className="text-xs text-slate-300 leading-relaxed">Your business profile and goals that you enter into this feature. No financial account data, bank credentials, or personal identifiers are included.</p>
            <p className="text-xs font-semibold text-white mt-2">Who it is shared with:</p>
            <p className="text-xs text-slate-300 leading-relaxed"><span className="text-white font-semibold">Anthropic</span> (claude.ai) — a third-party AI company. Data is sent to Anthropic's API to generate ideas. Anthropic does not use API data to train their models.</p>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            By tapping "I Agree", you consent to sharing the above data with Anthropic. Review our <a href="https://centrisfinance.com/privacy" target="_blank" rel="noreferrer" onClick={(e) => { if (window.Capacitor?.isNativePlatform?.()) { e.preventDefault(); window.open('https://centrisfinance.com/privacy', '_system'); } }} className="text-amber-400 underline">privacy policy</a>.
          </p>
          <div className="flex gap-3 pt-1">
            <button
              onClick={() => {
                try { localStorage.setItem(AI_CONSENT_IDEAS_KEY, 'true'); localStorage.setItem(AI_CONSENT_KEY, 'true'); } catch {}
                setAiConsentGiven(true);
              }}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white text-sm font-semibold"
            >
              I Agree — Continue
            </button>
            <button onClick={() => setAiConsentDeclined(true)} className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-400 text-sm">
              No Thanks
            </button>
          </div>
        </div>
      </div>
    );
  }

  const discoverIdeas = async () => {
    setPhase('loading-ideas');
    setError('');
    logEvent('business_idea_generator_used', { uid: auth.currentUser?.uid });
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 30000);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${CLOUD}/businessIdeaEngine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode: 'trends' }),
        signal: controller.signal,
      });
      clearTimeout(abortTimer);
      if (!res.ok) throw new Error('Research failed. Please try again.');
      const data = await res.json();
      if (!data.ideas?.length) throw new Error('No ideas returned. Please try again.');
      setIdeas(data.ideas);
      setPhase('ideas');
    } catch (e) {
      clearTimeout(abortTimer);
      setError(e.name === 'AbortError' ? 'Request timed out. Please try again.' : e.message);
      setPhase('idle');
    }
  };

  const buildPlan = async (idea) => {
    setSelectedIdea(idea);
    setPhase('loading-plan');
    setError('');
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 30000);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${CLOUD}/businessIdeaEngine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode: 'plan', idea }),
        signal: controller.signal,
      });
      clearTimeout(abortTimer);
      if (!res.ok) throw new Error('Plan generation failed. Please try again.');
      const data = await res.json();
      setPlan(data.plan || '');
      setPhase('plan');
    } catch (e) {
      clearTimeout(abortTimer);
      setError(e.name === 'AbortError' ? 'Request timed out. Please try again.' : e.message);
      setPhase('ideas');
    }
  };

  const reset = () => { setPhase('idle'); setIdeas([]); setSelectedIdea(null); setPlan(''); setError(''); };

  // Renders a block of markdown text (### headers, **bold**, bullet/numbered lists)
  const renderMarkdownBody = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let listType = null; // 'ul' | 'ol'

    const flushList = (key) => {
      if (!listItems.length) return;
      if (listType === 'ol') {
        elements.push(
          <ol key={`list-${key}`} className="list-decimal list-inside space-y-1 mb-3 pl-1">
            {listItems.map((item, j) => <li key={j} className="text-sm text-slate-300 leading-relaxed">{renderInline(item)}</li>)}
          </ol>
        );
      } else {
        elements.push(
          <ul key={`list-${key}`} className="space-y-1 mb-3">
            {listItems.map((item, j) => (
              <li key={j} className="flex items-start gap-2 text-sm text-slate-300 leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0" />
                {renderInline(item)}
              </li>
            ))}
          </ul>
        );
      }
      listItems = [];
      listType = null;
    };

    const renderInline = (str) => {
      const parts = str.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((p, j) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={j} className="text-white font-semibold">{p.slice(2, -2)}</strong>
          : p
      );
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed || /^-{2,}$/.test(trimmed)) {
        flushList(idx);
        return;
      }
      // ### sub-header
      if (trimmed.startsWith('### ')) {
        flushList(idx);
        elements.push(
          <p key={idx} className="text-xs font-bold uppercase tracking-widest text-slate-400 mt-4 mb-1.5">{trimmed.slice(4)}</p>
        );
        return;
      }
      // ## (shouldn't appear inside body but handle gracefully)
      if (trimmed.startsWith('## ')) {
        flushList(idx);
        elements.push(
          <p key={idx} className="text-sm font-bold text-white mt-3 mb-1">{trimmed.slice(3)}</p>
        );
        return;
      }
      // bullet
      const bulletMatch = trimmed.match(/^[-*•]\s+(.+)/);
      if (bulletMatch) {
        if (listType && listType !== 'ul') flushList(idx);
        listType = 'ul';
        listItems.push(bulletMatch[1]);
        return;
      }
      // numbered list
      const numMatch = trimmed.match(/^\d+\.\s+(.+)/);
      if (numMatch) {
        if (listType && listType !== 'ol') flushList(idx);
        listType = 'ol';
        listItems.push(numMatch[1]);
        return;
      }
      // plain paragraph
      flushList(idx);
      elements.push(
        <p key={idx} className="text-sm text-slate-300 leading-relaxed mb-2">{renderInline(trimmed)}</p>
      );
    });
    flushList('end');
    return elements;
  };

  // Plan renderer — splits on ## headers
  const renderPlan = (text) => {
    // Strip leading # title, --- separators, and the business-name ## section (no real body)
    const cleaned = text
      .replace(/^#\s+[^\n]*\n?/m, '')       // remove # Title at top
      .replace(/^---+\s*$/gm, '')            // remove --- separators
      .trim();
    const sections = cleaned.split(/^## /m).filter(Boolean);
    const accentColors = ['indigo', 'violet', 'purple', 'blue', 'cyan', 'emerald', 'amber', 'pink', 'rose'];
    let colorIdx = 0;
    return sections.map((section, i) => {
      const lines = section.split('\n');
      const title = lines[0].trim();
      const body = lines.slice(1).join('\n').trim();
      // Skip sections that are just a business name with no real body content
      if (!body || body.replace(/[-\s]/g, '').length < 10) return null;
      const color = accentColors[colorIdx++ % accentColors.length];
      const borderMap = { indigo:'border-indigo-500/20', violet:'border-violet-500/20', purple:'border-purple-500/20', blue:'border-blue-500/20', cyan:'border-cyan-500/20', emerald:'border-emerald-500/20', amber:'border-amber-500/20', pink:'border-pink-500/20', rose:'border-rose-500/20' };
      const textMap = { indigo:'text-indigo-400', violet:'text-violet-400', purple:'text-purple-400', blue:'text-blue-400', cyan:'text-cyan-400', emerald:'text-emerald-400', amber:'text-amber-400', pink:'text-pink-400', rose:'text-rose-400' };
      return (
        <div key={i} className={`centris-card rounded-2xl p-6 border ${borderMap[color]}`}>
          <p className={`text-[10px] uppercase tracking-widest font-bold ${textMap[color]} mb-3`}>{title}</p>
          <div>{renderMarkdownBody(body)}</div>
        </div>
      );
    });
  };

  if (phase === 'idle') return (
    <div className="space-y-6" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
      <div>
        <h2 className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>Business Idea Engine</h2>
        <p className="text-sm text-slate-500 mt-1">Perplexity scans global trends to surface underexplored opportunities, then Haiku builds a full startup plan — including how CENTRIS accelerates your launch.</p>
      </div>
      <div className="centris-card rounded-2xl p-8 border border-purple-500/20 flex flex-col items-start gap-6 max-w-2xl">
        <div className="w-12 h-12 rounded-2xl bg-purple-500/15 flex items-center justify-center">
          <Lightbulb className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <p className="text-white font-semibold text-base mb-2">How it works</p>
          <div className="space-y-2">
            {[
              ['1', 'Perplexity scans current market trends, regulatory shifts, and cultural changes to find 5 new, viable business gaps.'],
              ['2', 'You select the idea that resonates most.'],
              ['3', 'Claude Haiku writes a full startup and marketing plan — financials, go-to-market strategy, 90-day milestones, and a CENTRIS integration guide.'],
            ].map(([n, desc]) => (
              <div key={n} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center text-[10px] font-bold text-purple-400 shrink-0 mt-0.5">{n}</span>
                <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
        {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 w-full">{error}</p>}
        <button onClick={discoverIdeas} className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white font-semibold px-6 py-3 rounded-xl text-sm flex items-center gap-2 transition-all">
          <Search className="w-4 h-4" /> Discover Trending Ideas
        </button>
      </div>
    </div>
  );

  if (phase === 'loading-ideas') return (
    <div className="centris-card rounded-2xl p-12 flex flex-col items-center gap-5 max-w-md mx-auto mt-12">
      <div className="w-14 h-14 rounded-2xl bg-purple-500/15 flex items-center justify-center">
        <Loader className="w-7 h-7 text-purple-400 animate-spin" />
      </div>
      <div className="text-center">
        <p className="text-white font-semibold text-base">Scanning global trends</p>
        <p className="text-slate-400 text-sm mt-1">Perplexity is analyzing market shifts, regulatory changes, and emerging consumer behavior...</p>
      </div>
    </div>
  );

  if (phase === 'ideas') return (
    <div className="space-y-6" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">5 Emerging Opportunities</h2>
          <p className="text-sm text-slate-500 mt-1">Select one to generate your full startup and marketing plan.</p>
        </div>
        <button onClick={reset} className="text-xs text-slate-500 hover:text-white transition-colors">Start over</button>
      </div>
      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">{error}</p>}
      <div className="grid md:grid-cols-1 gap-4">
        {ideas.map((idea, i) => (
          <button
            key={i}
            onClick={() => buildPlan(idea)}
            className="centris-card rounded-2xl p-6 text-left hover:border-purple-400/40 hover:bg-slate-900/80 transition-all group"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-md">Idea {i + 1}</span>
                  <span className="text-xs text-slate-500">{idea.revenueModel}</span>
                </div>
                <p className="text-base font-bold text-white mb-1">{idea.name}</p>
                <p className="text-sm text-slate-400 mb-3 leading-relaxed">{idea.concept}</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <div><p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Target Customer</p><p className="text-xs text-slate-300 mt-0.5">{idea.targetCustomer}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Why Now</p><p className="text-xs text-slate-300 mt-0.5">{idea.whyNow}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Market Size</p><p className="text-xs text-slate-300 mt-0.5">{idea.marketSize}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Gap</p><p className="text-xs text-slate-300 mt-0.5">{idea.competitiveGap}</p></div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-purple-400 shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  if (phase === 'loading-plan') return (
    <div className="centris-card rounded-2xl p-12 flex flex-col items-center gap-5 max-w-md mx-auto mt-12">
      <div className="w-14 h-14 rounded-2xl bg-violet-500/15 flex items-center justify-center">
        <Loader className="w-7 h-7 text-violet-400 animate-spin" />
      </div>
      <div className="text-center">
        <p className="text-white font-semibold text-base">Building your startup plan</p>
        <p className="text-slate-400 text-sm mt-1">Claude Haiku is writing your full launch strategy for <span className="text-white font-medium">{selectedIdea?.name}</span>...</p>
      </div>
    </div>
  );

  if (phase === 'plan') return (
    <div className="space-y-6" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
      <div className="flex items-start justify-between gap-4">
        <div>

          <h2 className="text-2xl font-bold text-white">{selectedIdea?.name}</h2>
          <p className="text-sm text-slate-500 mt-1">{selectedIdea?.concept}</p>
        </div>
        <button onClick={() => setPhase('ideas')} className="text-xs text-slate-500 hover:text-white transition-colors shrink-0 mt-2">Back to ideas</button>
      </div>
      <div className="space-y-4">{renderPlan(plan)}</div>
      <div className="flex gap-3">
        <button
          onClick={() => { navigator.clipboard.writeText(plan); }}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white centris-card rounded-xl transition-all"
        >
          <BookOpen className="w-4 h-4" /> Copy Plan
        </button>
        <button onClick={reset} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white rounded-xl transition-all">
          <Lightbulb className="w-4 h-4" /> New Ideas
        </button>
      </div>
    </div>
  );

  return null;
};

// ---- SideHustleFinder ----
const SideHustleFinder = ({ user }) => {
  const CLOUD = 'https://us-central1-receiptwise-prod.cloudfunctions.net';
  const [skills, setSkills] = useState('');
  const [hours, setHours] = useState('');
  const [income, setIncome] = useState('');
  const [loading, setLoading] = useState(false);
  const [hustles, setHustles] = useState([]);
  const [error, setError] = useState('');
  const [aiConsentGiven, setAiConsentGiven] = useState(() => {
    try { return localStorage.getItem(AI_CONSENT_HUSTLE_KEY) === 'true' || localStorage.getItem(AI_CONSENT_KEY) === 'true'; } catch { return false; }
  });
  const [aiConsentDeclined, setAiConsentDeclined] = useState(false);

  if (aiConsentDeclined) {
    return (
      <div className="max-w-lg mx-auto mt-8 text-center space-y-3 p-6">
        <p className="text-sm text-slate-400">AI Side Hustle Finder is unavailable without data sharing consent.</p>
        <button onClick={() => setAiConsentDeclined(false)} className="text-xs text-emerald-400 underline">Review consent again</button>
      </div>
    );
  }

  if (!aiConsentGiven) {
    return (
      <div className="max-w-lg mx-auto mt-8">
        <div className="centris-card rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Brain className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">AI Side Hustle Finder — Data Sharing Notice</p>
              <p className="text-xs text-slate-400">Permission required before continuing</p>
            </div>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-white">What data is shared:</p>
            <p className="text-xs text-slate-300 leading-relaxed">The skills, hours per week, and income goal you enter into this form. No financial account data, bank credentials, or personal identifiers are included.</p>
            <p className="text-xs font-semibold text-white mt-2">Who it is shared with:</p>
            <p className="text-xs text-slate-300 leading-relaxed"><span className="text-white font-semibold">Anthropic</span> (claude.ai) — a third-party AI company. Data is sent to Anthropic's API to generate recommendations. Anthropic does not use API data to train their models.</p>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            By tapping "I Agree", you consent to sharing the above data with Anthropic. Review our <a href="https://centrisfinance.com/privacy" target="_blank" rel="noreferrer" onClick={(e) => { if (window.Capacitor?.isNativePlatform?.()) { e.preventDefault(); window.open('https://centrisfinance.com/privacy', '_system'); } }} className="text-emerald-400 underline">privacy policy</a>.
          </p>
          <div className="flex gap-3 pt-1">
            <button
              onClick={() => {
                try { localStorage.setItem(AI_CONSENT_HUSTLE_KEY, 'true'); localStorage.setItem(AI_CONSENT_KEY, 'true'); } catch {}
                setAiConsentGiven(true);
              }}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold"
            >
              I Agree — Continue
            </button>
            <button onClick={() => setAiConsentDeclined(true)} className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-400 text-sm">
              No Thanks
            </button>
          </div>
        </div>
      </div>
    );
  }

  const find = async () => {
    setLoading(true); setError(''); setHustles([]);
    logEvent('side_hustle_finder_used', { uid: auth.currentUser?.uid });
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 30000);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${CLOUD}/businessIdeaEngine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode: 'sideHustles', skills, hoursPerWeek: hours, incomeGoal: income }),
        signal: controller.signal,
      });
      clearTimeout(abortTimer);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Research failed');
      setHustles(data.hustles || []);
    } catch (e) {
      clearTimeout(abortTimer);
      setError(e.name === 'AbortError' ? 'Request timed out. Please try again.' : e.message);
    }
    finally { setLoading(false); }
  };

  const accentColors = [
    { border: 'border-amber-500/20', label: 'text-amber-400', badge: 'bg-amber-500/10 text-amber-400' },
    { border: 'border-emerald-500/20', label: 'text-emerald-400', badge: 'bg-emerald-500/10 text-emerald-400' },
    { border: 'border-cyan-500/20', label: 'text-cyan-400', badge: 'bg-cyan-500/10 text-cyan-400' },
    { border: 'border-violet-500/20', label: 'text-violet-400', badge: 'bg-violet-500/10 text-violet-400' },
    { border: 'border-pink-500/20', label: 'text-pink-400', badge: 'bg-pink-500/10 text-pink-400' },
    { border: 'border-indigo-500/20', label: 'text-indigo-400', badge: 'bg-indigo-500/10 text-indigo-400' },
  ];

  return (
    <div className="space-y-8" style={{ animation: 'cardFadeIn 0.4s ease-out both' }}>
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>Side Hustle Finder</h1>
        <p className="text-sm text-slate-500 mt-1">Tell Perplexity your skills and goals — it finds 6 trending opportunities matched to you right now.</p>
      </div>

      <div className="centris-card rounded-2xl p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2 block">Your skills or interests</label>
            <input value={skills} onChange={e => setSkills(e.target.value)} placeholder="e.g. writing, video editing, coding" className="w-full px-4 py-3 bg-slate-800 border border-white/[0.08] rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2 block">Hours per week available</label>
            <input value={hours} onChange={e => setHours(e.target.value)} placeholder="e.g. 10" type="number" min="1" className="w-full px-4 py-3 bg-slate-800 border border-white/[0.08] rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2 block">Monthly income goal ($)</label>
            <input value={income} onChange={e => setIncome(e.target.value)} placeholder="e.g. 1500" type="number" min="1" className="w-full px-4 py-3 bg-slate-800 border border-white/[0.08] rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
          </div>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button onClick={find} disabled={loading} className="w-full py-3.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:opacity-60 flex items-center justify-center gap-2 transition-all">
          {loading ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Searching trends...</span></> : <><Zap className="w-4 h-4" /><span>Find My Side Hustles</span></>}
        </button>
      </div>

      {hustles.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {hustles.map((h, i) => {
            const c = accentColors[i % accentColors.length];
            return (
              <div key={i} className={`centris-card rounded-2xl p-6 border ${c.border} flex flex-col gap-3`}>
                <div>
                  <p className={`text-[10px] uppercase tracking-widest font-bold ${c.label} mb-1`}>Side Hustle {i + 1}</p>
                  <h3 className="text-base font-bold text-white">{h.name}</h3>
                  <p className="text-sm text-slate-400 mt-1 leading-relaxed">{h.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-800/60 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Startup Cost</p>
                    <p className="text-xs font-semibold text-white mt-0.5">{h.startupCost}</p>
                  </div>
                  <div className="bg-slate-800/60 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Income (3mo)</p>
                    <p className="text-xs font-semibold text-white mt-0.5">{h.incomeRange}</p>
                  </div>
                  <div className="bg-slate-800/60 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">First Dollar</p>
                    <p className="text-xs font-semibold text-white mt-0.5">{h.timeToFirstDollar}</p>
                  </div>
                  <div className="bg-slate-800/60 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Skills Needed</p>
                    <p className="text-xs font-semibold text-white mt-0.5">{h.skillsNeeded}</p>
                  </div>
                </div>
                <div className={`rounded-lg px-3 py-2 ${c.badge} text-xs`}>
                  <span className="font-bold">Why now: </span>{h.whyNow}
                </div>
                <div className="bg-slate-800/40 border border-white/[0.05] rounded-lg px-3 py-2.5">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">First Step Today</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{h.firstStep}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- BusinessToolsHub.js ----
const BusinessToolsHub = ({ user, expenses, setExpenses, saveExpense, setShowUploadModal, invoices = [], setRevenue, withBackgroundTask, setTaxVaultReminder, showToast, perms = null, onCanProceed = null, paygPrices = null, initialTool = null }) => {
  const [selectedTool, setSelectedTool] = useState(initialTool);

  if (selectedTool === 'tax-writeoffs') {
    return (
      <div>
        <button
          onClick={() => setSelectedTool(null)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group"
          style={{ animation: 'pageSlideIn 0.3s ease-out both' }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]">
            <ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          </div>
          Back
        </button>
        <TaxWriteOffsHub
          user={user}
          expenses={expenses}
          setExpenses={setExpenses}
          saveExpense={saveExpense}
          setShowUploadModal={setShowUploadModal}
          invoices={invoices}
          setRevenue={setRevenue}
          withBackgroundTask={withBackgroundTask}
          setTaxVaultReminder={setTaxVaultReminder}
          showToast={showToast}
        />
      </div>
    );
  }

  if (selectedTool === 'invoices') {
    return (
      <div>
        <button
          onClick={() => setSelectedTool(null)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group"
          style={{ animation: 'pageSlideIn 0.3s ease-out both' }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]">
            <ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          </div>
          Back
        </button>
        <InvoiceGenerator user={user} setTaxVaultReminder={setTaxVaultReminder} showToast={showToast} perms={perms} onCanProceed={onCanProceed} paygPrices={paygPrices} />
      </div>
    );
  }

  if (selectedTool === 'mileage') {
    return (
      <div>
        <button
          onClick={() => setSelectedTool(null)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group"
          style={{ animation: 'pageSlideIn 0.3s ease-out both' }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]">
            <ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          </div>
          Back
        </button>
        <MileageTracker user={user} showToast={showToast} />
      </div>
    );
  }

  if (selectedTool === 'marketing') {
    return (
      <div>
        <button
          onClick={() => setSelectedTool(null)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group"
          style={{ animation: 'pageSlideIn 0.3s ease-out both' }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]">
            <ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          </div>
          Back
        </button>
        <React.Suspense fallback={<div className="text-slate-400 p-8 text-center">Loading...</div>}><MarketingHub user={user} invoices={invoices} expenses={expenses} /></React.Suspense>
      </div>
    );
  }

  if (selectedTool === 'idea-engine') {
    return (
      <div>
        <button onClick={() => setSelectedTool(null)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group" style={{ animation: 'pageSlideIn 0.3s ease-out both' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]"><ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" /></div>
          Back
        </button>
        <BusinessIdeaEngine user={user} />
      </div>
    );
  }

  if (selectedTool === 'website-builder') {
    return (
      <div>
        <button onClick={() => setSelectedTool(null)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group" style={{ animation: 'pageSlideIn 0.3s ease-out both' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]"><ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" /></div>
          Back to Business Tools
        </button>
        <React.Suspense fallback={<div className="text-slate-400 p-8 text-center">Loading...</div>}><WebsiteBuilder user={user} /></React.Suspense>
      </div>
    );
  }

  if (selectedTool === 'product-builder') {
    return (
      <div>
        <button onClick={() => setSelectedTool(null)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group" style={{ animation: 'pageSlideIn 0.3s ease-out both' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]"><ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" /></div>
          Back to Business Tools
        </button>
        <React.Suspense fallback={<div className="text-slate-400 p-8 text-center">Loading...</div>}><ProductBuilder user={user} /></React.Suspense>
      </div>
    );
  }

  if (selectedTool === 'side-hustles') {
    return (
      <div>
        <button onClick={() => setSelectedTool(null)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group" style={{ animation: 'pageSlideIn 0.3s ease-out both' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]"><ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" /></div>
          Back
        </button>
        <SideHustleFinder user={user} />
      </div>
    );
  }

  return (
    <div className="space-y-8" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
      <div className="mb-2">
        <h1 className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>Business Tools</h1>
        <p className="text-sm text-slate-500 mt-1">Invoices, mileage, tax write-offs, and marketing in one place</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { tool: 'tax-writeoffs', Icon: Receipt, color: 'indigo', label: 'Tax Write-Offs', desc: 'Scan receipts and find deductions.', sub: `${expenses.filter(e => e.taxDeductible).length} deductible items` },
          { tool: 'invoices', Icon: FileText, color: 'purple', label: 'Invoice Generator', desc: 'Create and send professional invoices.', sub: 'Send via email' },
          { tool: 'mileage', Icon: Car, color: 'emerald', label: 'Mileage Tracker', desc: 'Log trips and calculate IRS deductions.', sub: '$0.70/mi — 2026 rate' },
          { tool: 'idea-engine', Icon: Lightbulb, color: 'purple', label: 'Business Ideas', desc: 'Perplexity finds trending opportunities for you.', sub: 'Trend research + plan' },
          { tool: 'product-builder', Icon: Sparkles, color: 'violet', label: 'Product Builder', desc: 'Build websites and digital products with AI.', sub: 'Vibe coding' },
          { tool: 'side-hustles', Icon: Zap, color: 'amber', label: 'Side Hustles', desc: 'Find 6 matched side hustles for your skills.', sub: 'Income opportunities' },
        ].map(({ tool, Icon, color, label, desc, sub }) => {
          const borderMap = { indigo: 'hover:border-indigo-400/40', purple: 'hover:border-purple-400/40', emerald: 'hover:border-emerald-400/40', pink: 'hover:border-pink-400/40', violet: 'hover:border-violet-400/40', amber: 'hover:border-amber-400/40' };
          const textMap = { indigo: 'text-indigo-400', purple: 'text-purple-400', emerald: 'text-emerald-400', pink: 'text-pink-400', violet: 'text-violet-400', amber: 'text-amber-400' };
          return (
            <button key={tool} onClick={() => setSelectedTool(tool)} className={`centris-card rounded-xl p-4 text-left ${borderMap[color]} transition-all flex flex-col gap-2`}>
              <div className="flex items-center justify-between">
                <Icon className={`w-5 h-5 ${textMap[color]}`} />
                <ArrowRight className={`w-3.5 h-3.5 ${textMap[color]}`} />
              </div>
              <div>
                <p className="text-sm font-bold text-white">{label}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{desc}</p>
              </div>
              <p className={`text-[10px] font-semibold ${textMap[color]} mt-auto`}>{sub}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};
// ---- Tax calculation engine ----
const STATE_TAX_RATES = {
  AL:0.05,AK:0,AZ:0.025,AR:0.047,CA:0.093,CO:0.044,CT:0.069,DE:0.066,
  FL:0,GA:0.055,HI:0.11,ID:0.058,IL:0.0495,IN:0.03,IA:0.06,KS:0.057,
  KY:0.045,LA:0.06,ME:0.07,MD:0.0575,MA:0.05,MI:0.0425,MN:0.0985,
  MS:0.05,MO:0.054,MT:0.069,NE:0.068,NV:0,NH:0,NJ:0.1075,NM:0.059,
  NY:0.0882,NC:0.0475,ND:0.025,OH:0.04,OK:0.05,OR:0.099,PA:0.031,
  RI:0.0599,SC:0.07,SD:0,TN:0,TX:0,UT:0.0485,VT:0.0875,VA:0.0575,
  WA:0,WV:0.065,WI:0.077,WY:0,DC:0.1075
};
const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'Washington D.C.'
};
function calcTaxBreakdown({ grossIncome, incomeType, filingStatus, stateCode }) {
  if (!grossIncome || grossIncome <= 0) return { seTax:0,federalTax:0,stateTax:0,totalTax:0,effectiveRate:0,recommendedSaveRate:25 };
  // SE tax (1099/self-employed portion)
  const seShare = incomeType === 'w2' ? 0 : incomeType === 'mixed' ? 0.6 : 1;
  const seGross = grossIncome * seShare;
  const netSe = seGross * 0.9235; // IRS 92.35% rule
  const ssWageBase = 168600;
  const seTax = Math.min(netSe, ssWageBase) * 0.124 + netSe * 0.029; // 12.4% SS + 2.9% Medicare
  const seDeduction = seTax / 2;
  // Federal income tax
  const stdDed = filingStatus === 'married' ? 29200 : 14600;
  const taxableIncome = Math.max(0, grossIncome - stdDed - seDeduction);
  const mfjBrackets = [[23200,0.10],[94300,0.12],[201050,0.22],[383900,0.24],[487450,0.32],[731200,0.35],[Infinity,0.37]];
  const singleBrackets = [[11600,0.10],[47150,0.12],[100525,0.22],[191950,0.24],[243725,0.32],[609350,0.35],[Infinity,0.37]];
  const brackets = filingStatus === 'married' ? mfjBrackets : singleBrackets;
  let federalTax = 0, prev = 0;
  for (const [top, rate] of brackets) {
    if (taxableIncome <= prev) break;
    federalTax += (Math.min(taxableIncome, top) - prev) * rate;
    prev = top;
    if (top === Infinity) break;
  }
  const stateRate = STATE_TAX_RATES[stateCode] || 0;
  const stateTax = grossIncome * stateRate;
  const totalTax = seTax + federalTax + stateTax;
  const effectiveRate = totalTax / grossIncome;
  return { seTax, federalTax, stateTax, totalTax, effectiveRate, recommendedSaveRate: Math.max(15, Math.ceil(effectiveRate * 100)) };
}

// ---- Enhanced TaxVault ----
const TaxVault = ({ user, bankAccounts = [], liveBankImportActive: taxVaultLiveBank = false }) => {
  // Settings state
  const [settings, setSettings] = useState({
    reservePercent: 25,
    cushionPercent: 5,
    enabled: true,
    autoPullEnabled: false,
    targetDate: new Date(new Date().getFullYear(), 3, 15).toISOString().split('T')[0],
    taxBracket: 25,
    taxVaultAccountId: null,
    taxVaultAccountName: null,
    filingStatus: 'single',
    stateCode: 'TX',
    incomeType: 'self-employed',
  });

  // UI state
  const [transactions, setTransactions] = useState([]);
  const [incomeEntries, setIncomeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  // Stripe Financial Connections state
  const [fcLoading, setFcLoading] = useState(false);
  const [fcLinkedAccount, setFcLinkedAccount] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferDoneAmount, setTransferDoneAmount] = useState('');

  // Transaction entry state
  const [entry, setEntry] = useState({
    type: 'deposit',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    note: ''
  });

  // Withdrawal state
  const [withdrawalData, setWithdrawalData] = useState({
    amount: '',
    purpose: 'quarterly',
    note: '',
    taxYear: new Date().getFullYear()
  });

  // Calculate metrics
  const paidIncome = useMemo(
    () => (incomeEntries || []).reduce((sum, row) => sum + Math.max(0, Number(row.amount || 0)), 0),
    [incomeEntries]
  );

  const isConfirmedManualDeposit = (tx) => {
    if (tx?.type !== 'deposit') return false;
    const source = String(tx?.source || '').toLowerCase();
    const note = String(tx?.note || '').toLowerCase();
    if (source === 'manual' || source === 'manual_confirmed') return true;
    // Backward compatibility: prior reminder confirmations were saved as invoice_auto
    return source === 'invoice_auto' && note.startsWith('tax reserve from invoice');
  };

  const vaultBalance = useMemo(
    () => transactions.reduce((sum, tx) => {
      const amount = Number(tx.amount || 0);
      if (tx.type === 'withdrawal') return sum - amount;
      if (isConfirmedManualDeposit(tx)) return sum + amount;
      return sum;
    }, 0),
    [transactions]
  );

  const linkedAccount = useMemo(() => {
    if (!settings.taxVaultAccountId || !bankAccounts.length) return null;
    return bankAccounts.find((a) => (a.account_id || a.accountId) === settings.taxVaultAccountId) || null;
  }, [settings.taxVaultAccountId, bankAccounts]);

  const isExplicitTaxVaultAccount = (account) => {
    if (!account) return false;
    const accountText = [
      account.name,
      account.official_name,
      account.accountName,
      account.institutionName,
      settings.taxVaultAccountName,
    ].filter(Boolean).join(' ').toLowerCase();
    return /(tax vault|tax savings|tax reserve|tax account)/i.test(accountText);
  };

  const explicitTaxVaultLinked = isExplicitTaxVaultAccount(linkedAccount);

  const linkedAccountBalance = explicitTaxVaultLinked
    ? Number(linkedAccount.balances?.current || 0)
    : null;

  const effectiveVaultBalance = linkedAccountBalance !== null
    ? linkedAccountBalance
    : vaultBalance;

  const baseTarget = paidIncome * (Number(settings.reservePercent || 25) / 100);
  const cushionTarget = paidIncome * ((Number(settings.reservePercent || 25) + Number(settings.cushionPercent || 0)) / 100);
  const monthlyGoal = baseTarget / 12;
  const cushionGap = Math.max(0, cushionTarget - effectiveVaultBalance);
  const effectiveCoverage = cushionTarget > 0 ? Math.min(100, (effectiveVaultBalance / cushionTarget) * 100) : 0;

  // Tax deadline countdown
  const daysUntilTaxDay = useMemo(() => {
    const today = new Date();
    const taxDay = new Date(settings.targetDate);
    const diffTime = taxDay - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, [settings.targetDate]);

  // Quarterly tax estimates
  const quarterlyEstimate = baseTarget / 4;
  const currentQuarter = Math.floor((new Date().getMonth()) / 3) + 1;

  // Real tax calculation
  const taxCalc = useMemo(() => calcTaxBreakdown({
    grossIncome: paidIncome,
    incomeType: settings.incomeType || 'self-employed',
    filingStatus: settings.filingStatus || 'single',
    stateCode: settings.stateCode || 'TX',
  }), [paidIncome, settings.incomeType, settings.filingStatus, settings.stateCode]);

  // Stripe Financial Connections handler
  const handleLinkSavingsAccount = async () => {
    setFcLoading(true);
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const functions = getFunctions(getApp(), 'us-central1');
      const createSession = httpsCallable(functions, 'createFinancialConnectionsSession');
      const { data } = await createSession();
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      if (!stripe) { setStatus('[X] Stripe not configured — add REACT_APP_STRIPE_PUBLISHABLE_KEY'); setFcLoading(false); return; }
      const result = await stripe.collectFinancialConnectionsAccounts({ clientSecret: data.client_secret });
      if (result.error) { setStatus(`[X] ${result.error.message}`); setFcLoading(false); return; }
      const acct = result.financialConnectionsSession?.accounts?.[0];
      if (acct) {
        setFcLinkedAccount(acct);
        setSettings(s => ({ ...s, taxVaultAccountId: acct.id, taxVaultAccountName: acct.display_name || acct.institution_name }));
        await saveTaxVaultSettings(user.uid, { ...settings, taxVaultAccountId: acct.id, taxVaultAccountName: acct.display_name || acct.institution_name });
        setStatus('[OK] Savings account linked');
      } else {
        setStatus('[i] No account selected');
      }
    } catch (err) {
      setStatus(`[X] ${err.message}`);
    }
    setFcLoading(false);
    setTimeout(() => setStatus(''), 4000);
  };

  const handleMarkTransferDone = async (e) => {
    e.preventDefault();
    const amount = Number(transferDoneAmount);
    if (!(amount > 0)) { setStatus('[X] Enter an amount'); return; }
    const result = await addTaxVaultTransaction(user.uid, {
      type: 'deposit', amount, date: new Date().toISOString().split('T')[0],
      note: `Manual transfer to savings account`, source: 'manual_confirmed',
    });
    if (result.success) {
      await refreshTransactions();
      setShowTransferModal(false);
      setTransferDoneAmount('');
      setStatus('[OK] Transfer recorded');
      setTimeout(() => setStatus(''), 3000);
    }
  };

  // Withdrawal history by tax year
  const withdrawalsByYear = useMemo(() => {
    const byYear = {};
    transactions.filter(tx => tx.type === 'withdrawal').forEach(tx => {
      const year = tx.taxYear || new Date(tx.date).getFullYear();
      if (!byYear[year]) byYear[year] = [];
      byYear[year].push(tx);
    });
    return byYear;
  }, [transactions]);

  // Load data on mount
  useEffect(() => {
    if (!user?.uid) return;

    const loadData = async () => {
      setLoading(true);
      const [settingsResult, txResult, revenueResult] = await Promise.all([
        loadTaxVaultSettings(user.uid),
        loadTaxVaultTransactions(user.uid),
        loadRevenue(user.uid)
      ]);

      if (settingsResult.success) setSettings(prev => ({ ...prev, ...settingsResult.settings }));
      if (txResult.success) setTransactions(txResult.transactions);
      if (revenueResult.success) setIncomeEntries(revenueResult.revenue);
      setLoading(false);
    };

    loadData();
  }, [user]);

  const refreshTransactions = async () => {
    if (!user?.uid) return;
    const txResult = await loadTaxVaultTransactions(user.uid);
    if (txResult.success) setTransactions(txResult.transactions);
  };

  const handleSaveSettings = async () => {
    if (!user?.uid) return;
    setSavingSettings(true);
    const result = await saveTaxVaultSettings(user.uid, settings);
    setSavingSettings(false);
    setStatus(result.success ? '[OK] Settings saved' : `[X] Failed: ${result.error}`);
    setTimeout(() => setStatus(''), 3000);
  };

  const handleSyncIncome = async () => {
    if (!user?.uid) return;
    setSyncing(true);
    const revenueResult = await loadRevenue(user.uid);
    const incomeEntries = revenueResult.success ? revenueResult.revenue : [];
    if (revenueResult.success) setIncomeEntries(revenueResult.revenue);
    const result = await syncTaxVaultFromIncome(user.uid, incomeEntries, Number(settings.reservePercent || 25));
    setSyncing(false);
    if (result.success) {
      await refreshTransactions();
      setStatus(
        result.createdCount > 0
          ? `[OK] Synced ${result.createdCount} income entries ($${result.createdAmount.toFixed(2)})`
          : '[i] No new income to sync'
      );
    } else {
      setStatus(`[X] Sync failed: ${result.error}`);
    }
    setTimeout(() => setStatus(''), 5000);
  };

  const handleAddDeposit = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;

    const amount = Number(entry.amount);
    if (!(amount > 0)) {
      setStatus('[X] Amount must be greater than 0');
      return;
    }

    const result = await addTaxVaultTransaction(user.uid, {
      type: 'deposit',
      amount,
      date: entry.date || new Date().toISOString().split('T')[0],
      note: entry.note || 'Manual deposit',
      source: 'manual'
    });

    if (!result.success) {
      setStatus(`[X] Failed: ${result.error}`);
      return;
    }

    await refreshTransactions();
    setEntry({
      type: 'deposit',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      note: ''
    });
    setShowDepositModal(false);
    setStatus('[OK] Deposit added');
    setTimeout(() => setStatus(''), 3000);
  };

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;

    const amount = Number(withdrawalData.amount);
    if (!(amount > 0)) {
      setStatus('[X] Amount must be greater than 0');
      return;
    }

    if (amount > vaultBalance) {
      setStatus('[X] Insufficient vault balance');
      return;
    }

    const purposes = {
      quarterly: 'Quarterly Estimated Tax Payment',
      annual: 'Annual Tax Payment',
      adjustment: 'Tax Adjustment',
      other: withdrawalData.note || 'Tax Withdrawal'
    };

    const result = await addTaxVaultTransaction(user.uid, {
      type: 'withdrawal',
      amount,
      date: new Date().toISOString().split('T')[0],
      note: purposes[withdrawalData.purpose] || withdrawalData.note,
      source: 'manual',
      purpose: withdrawalData.purpose,
      taxYear: withdrawalData.taxYear
    });

    if (!result.success) {
      setStatus(`[X] Failed: ${result.error}`);
      return;
    }

    await refreshTransactions();
    setWithdrawalData({
      amount: '',
      purpose: 'quarterly',
      note: '',
      taxYear: new Date().getFullYear()
    });
    setShowWithdrawalModal(false);
    setStatus('[OK] Tax payment recorded');
    setTimeout(() => setStatus(''), 3000);
  };

  const txDateLabel = (tx) => {
    if (!tx?.date) return 'No date';
    if (typeof tx.date === 'string') return new Date(tx.date).toLocaleDateString();
    if (typeof tx.date?.toDate === 'function') return tx.date.toDate().toLocaleDateString();
    return String(tx.date);
  };

  if (loading) {
    return (
      <div className="centris-card rounded-xl p-6">
        <p className="text-sm text-slate-400">Loading Tax Vault...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Tax Calculator Card ── */}
      <div className="centris-card rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="bg-violet-500/10 p-1.5 rounded-lg"><Receipt className="w-3.5 h-3.5 text-violet-400" /></div>
            <div>
              <p className="text-sm font-semibold text-white">Tax Calculator</p>
              <p className="text-[10px] text-slate-500">Based on {paidIncome > 0 ? `$${paidIncome.toLocaleString()} logged income` : 'your income'}</p>
            </div>
          </div>
          {paidIncome > 0 && (
            <div className="text-right">
              <p className="text-xs text-slate-400">Effective rate</p>
              <p className="text-sm font-bold text-violet-400">{(taxCalc.effectiveRate * 100).toFixed(1)}%</p>
            </div>
          )}
        </div>
        <div className="p-4 space-y-3">
          {/* Filing inputs */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Income Type</label>
              <select value={settings.incomeType || 'self-employed'}
                onChange={e => setSettings(s => ({ ...s, incomeType: e.target.value }))}
                className="w-full bg-slate-800/60 border border-white/[0.06] rounded-lg px-2 py-1.5 text-xs text-white">
                <option value="self-employed">1099 / Self-Employed</option>
                <option value="w2">W-2 Employee</option>
                <option value="mixed">Both</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Filing Status</label>
              <select value={settings.filingStatus || 'single'}
                onChange={e => setSettings(s => ({ ...s, filingStatus: e.target.value }))}
                className="w-full bg-slate-800/60 border border-white/[0.06] rounded-lg px-2 py-1.5 text-xs text-white">
                <option value="single">Single</option>
                <option value="married">Married Filing Jointly</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">State</label>
              <select value={settings.stateCode || 'TX'}
                onChange={e => setSettings(s => ({ ...s, stateCode: e.target.value }))}
                className="w-full bg-slate-800/60 border border-white/[0.06] rounded-lg px-2 py-1.5 text-xs text-white">
                {Object.entries(STATE_NAMES).sort(([,a],[,b])=>a.localeCompare(b)).map(([code,name])=>(
                  <option key={code} value={code}>{code} — {name}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Tax breakdown */}
          {paidIncome > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: 'SE Tax (15.3%)', value: taxCalc.seTax, color: 'text-orange-400', show: settings.incomeType !== 'w2' },
                { label: 'Federal Income', value: taxCalc.federalTax, color: 'text-red-400', show: true },
                { label: `${settings.stateCode || 'State'} Tax`, value: taxCalc.stateTax, color: 'text-amber-400', show: true },
                { label: 'Total Owed', value: taxCalc.totalTax, color: 'text-white', show: true },
              ].filter(i => i.show).map(item => (
                <div key={item.label} className="bg-white/[0.03] rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-slate-500 mb-1 leading-tight">{item.label}</p>
                  <p className={`text-sm font-bold ${item.color}`}>${Math.round(item.value).toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white/[0.02] rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500">Add income entries to see your tax estimate</p>
            </div>
          )}
          {paidIncome > 0 && (
            <div className="flex items-center justify-between bg-emerald-500/8 border border-emerald-500/20 rounded-lg px-3 py-2">
              <p className="text-xs text-slate-300">Recommended save rate</p>
              <p className="text-sm font-bold text-emerald-400">{taxCalc.recommendedSaveRate}% of income</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Vault Card ── */}
      <div className="centris-card rounded-xl overflow-hidden">

      {/* Compact header row */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2.5">
          <div className="bg-emerald-500/10 p-1.5 rounded-lg">
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Tax Vault</p>
            <p className="text-[10px] text-slate-500">Set aside taxes from every income entry automatically</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-emerald-400">${effectiveVaultBalance.toFixed(2)}</span>
          <button
            onClick={handleSyncIncome}
            disabled={syncing}
            className="text-xs text-slate-400 hover:text-white border border-white/[0.06] px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </button>
          <button
            onClick={() => setShowSettings(s => !s)}
            className="text-xs text-slate-400 hover:text-white border border-white/[0.06] px-2.5 py-1.5 rounded-lg transition-all"
          >
            <Settings className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Status message */}
      {status && (
        <div className={`px-4 py-2 text-xs border-b border-white/[0.06] ${
          status.includes('[OK]') ? 'bg-emerald-500/10 text-emerald-300' :
          status.includes('[X]') ? 'bg-red-500/10 text-red-300' :
          'bg-blue-500/10 text-blue-300'
        }`}>{status}</div>
      )}

      {/* Main body */}
      <div className="p-4 space-y-3">
        {/* Balance + progress */}
        <div className="flex items-end justify-between mb-1">
          <div>
            <p className="text-[10px] text-slate-500 mb-0.5">{explicitTaxVaultLinked ? 'Savings Account' : 'Vault Balance'}</p>
            <p className="text-2xl font-semibold text-white">${effectiveVaultBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">{effectiveCoverage.toFixed(0)}% of goal</p>
            <p className="text-[10px] text-slate-500">{cushionGap > 0 ? `$${cushionGap.toFixed(0)} to go` : 'Goal met'}</p>
          </div>
        </div>
        <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{
            width: `${effectiveCoverage}%`,
            background: effectiveCoverage >= 100 ? '#10b981' : effectiveCoverage >= 60 ? '#f59e0b' : '#ef4444',
          }} />
        </div>
        {linkedAccount && !explicitTaxVaultLinked && (
          <p className="text-[10px] text-amber-400">Linked account not marked as tax account — using manual deposits only.</p>
        )}

        {/* Key stats */}
        <div className="flex items-center justify-between text-center pt-1">
          {[
            { label: 'Monthly Goal', value: `$${monthlyGoal.toFixed(0)}` },
            { label: `Q${currentQuarter} Est.`, value: `$${quarterlyEstimate.toFixed(0)}` },
            { label: 'Days Left', value: isNaN(daysUntilTaxDay) ? '--' : `${daysUntilTaxDay}d` },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-[10px] text-slate-500">{s.label}</p>
              <p className="text-sm font-medium text-white mt-0.5">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Move to Savings banner */}
        {taxCalc.totalTax > 0 && effectiveVaultBalance < taxCalc.totalTax && (
          <div className="bg-violet-500/8 border border-violet-500/20 rounded-lg px-3 py-2.5 flex items-center justify-between">
            <div>
              <p className="text-xs text-violet-300 font-medium">Move ${Math.round(taxCalc.totalTax - effectiveVaultBalance).toLocaleString()} to savings</p>
              <p className="text-[10px] text-slate-500">to fully cover estimated taxes</p>
            </div>
            <button
              onClick={() => { setTransferDoneAmount(Math.round(taxCalc.totalTax - effectiveVaultBalance).toString()); setShowTransferModal(true); }}
              className="text-xs bg-violet-600 hover:bg-violet-500 text-white rounded-lg px-3 py-1.5 font-medium transition-all"
            >
              Move Money
            </button>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowDepositModal(true)}
            className="flex-1 text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-lg px-3 py-2 font-medium transition-all flex items-center justify-center gap-1.5"
          >
            <ArrowDownRight className="w-3.5 h-3.5" /> Add Deposit
          </button>
          <button
            onClick={() => setShowWithdrawalModal(true)}
            disabled={vaultBalance <= 0}
            className="flex-1 text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg px-3 py-2 font-medium transition-all flex items-center justify-center gap-1.5 disabled:opacity-40"
          >
            <ArrowUpRight className="w-3.5 h-3.5" /> Pay Taxes
          </button>
          <button
            onClick={() => setActiveTab(t => t === 'history' ? null : 'history')}
            className="text-xs text-slate-400 hover:text-white border border-white/[0.06] rounded-lg px-3 py-2 transition-all"
          >
            History
          </button>
        </div>

        {/* Recent transactions (inline, no tab) */}
        {activeTab === 'history' && (
          <div className="space-y-1.5 border-t border-white/[0.06] pt-3">
            {transactions.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-3">No transactions yet - sync income to start</p>
            ) : (
              transactions.slice(0, 5).map(tx => (
                <div key={tx.id} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${tx.type === 'withdrawal' ? 'bg-red-400' : 'bg-emerald-400'}`} />
                    <div>
                      <p className="text-xs text-white">{tx.type === 'withdrawal' ? 'Tax Payment' : 'Deposit'}{['invoice_auto', 'income_auto', 'bank_auto'].includes(tx.source) && <span className="ml-1 text-[10px] text-emerald-500">auto</span>}</p>
                      <p className="text-[10px] text-slate-500">{txDateLabel(tx)}{tx.note ? ` - ${tx.note}` : ''}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold ${tx.type === 'withdrawal' ? 'text-red-400' : 'text-emerald-400'}`}>
                    {tx.type === 'withdrawal' ? '-' : '+'}${Number(tx.amount || 0).toFixed(2)}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Settings panel (inline collapsible) */}
        {showSettings && (
          <div className="border-t border-white/[0.06] pt-3 space-y-3">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Vault Settings</p>
            <div className="centris-card rounded-2xl p-5 mb-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Link Your Tax Savings Account</p>
                  <p className="text-xs text-slate-500">See your real savings balance &amp; get transfer guidance</p>
                </div>
              </div>

              {/* Stripe Financial Connections link */}
              {fcLinkedAccount || settings.taxVaultAccountName ? (
                <div className="flex items-center gap-3 p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/8 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{fcLinkedAccount?.display_name || settings.taxVaultAccountName}</p>
                    <p className="text-[10px] text-emerald-400">Linked via Stripe Financial Connections</p>
                  </div>
                  <button
                    onClick={() => { setFcLinkedAccount(null); setSettings(s => ({ ...s, taxVaultAccountId: null, taxVaultAccountName: null })); }}
                    className="text-[10px] text-slate-500 hover:text-red-400 transition-colors"
                  >Unlink</button>
                </div>
              ) : (
                <button
                  onClick={handleLinkSavingsAccount}
                  disabled={fcLoading}
                  className="w-full flex items-center justify-center gap-2 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 rounded-xl py-3 text-sm font-medium transition-all disabled:opacity-60"
                >
                  {fcLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
                  {fcLoading ? 'Connecting...' : 'Link Savings Account'}
                </button>
              )}

              <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                Powered by Stripe Financial Connections — bank-grade security, read-only access.
                CENTRIS never stores your login credentials.
              </p>

              {/* Also show existing Plaid bank accounts if available */}
              {bankAccounts.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Or select from connected accounts</p>
                  {bankAccounts.filter(a => a.type === 'depository').map(account => {
                    const accountId = account.account_id || account.accountId;
                    const isSelected = settings.taxVaultAccountId === accountId;
                    return (
                      <div key={accountId}
                        onClick={() => setSettings(s => ({ ...s, taxVaultAccountId: accountId, taxVaultAccountName: `${account.name} ****${account.mask}` }))}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? 'border-emerald-500/40 bg-emerald-500/8' : 'border-white/[0.05] hover:border-white/10'}`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-emerald-400 bg-emerald-400' : 'border-slate-600'}`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{account.name}</p>
                          <p className="text-[10px] text-slate-500">{account.institutionName} ****{account.mask}</p>
                        </div>
                        <p className="text-xs font-bold text-white">${Number(account.balances?.current || 0).toLocaleString()}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-400">Tax Reserve Rate</label>
                <span className="text-xs text-white font-medium">{settings.reservePercent}%</span>
              </div>
              <input type="range" min="10" max="40" value={settings.reservePercent}
                onChange={e => setSettings(p => ({ ...p, reservePercent: Number(e.target.value) }))}
                className="w-full accent-emerald-500 h-1.5" />
              <p className="text-[10px] text-slate-500 mt-1">Self-employed: 25-30% | High earner: 35-40%</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-400">Safety Cushion</label>
                <span className="text-xs text-white font-medium">+{settings.cushionPercent}%</span>
              </div>
              <input type="range" min="0" max="20" value={settings.cushionPercent}
                onChange={e => setSettings(p => ({ ...p, cushionPercent: Number(e.target.value) }))}
                className="w-full accent-emerald-500 h-1.5" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Tax Deadline</label>
              <input type="date" value={settings.targetDate}
                onChange={e => setSettings(p => ({ ...p, targetDate: e.target.value }))}
                className="w-full bg-slate-900/60 border border-white/[0.06] rounded-lg px-3 py-2 text-xs text-white" />
            </div>
            <button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="w-full text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-3 py-2 font-medium transition-all disabled:opacity-60 flex items-center justify-center gap-1.5"
            >
              <Check className="w-3 h-3" /> {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>
      </div>

      {/* Deposit Modal */}
      {showDepositModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl max-w-sm w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-white">Add to Tax Vault</p>
              <button onClick={() => setShowDepositModal(false)}><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
            </div>
            <form onSubmit={handleAddDeposit} className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400 text-sm">$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00" value={entry.amount}
                    onChange={e => setEntry(p => ({ ...p, amount: e.target.value }))}
                    className="w-full pl-7 pr-4 py-2.5 bg-slate-800 border border-white/[0.06] rounded-lg text-white text-sm" required />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Date</label>
                <input type="date" value={entry.date}
                  onChange={e => setEntry(p => ({ ...p, date: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-800 border border-white/[0.06] rounded-lg text-white text-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Note (optional)</label>
                <input type="text" placeholder="e.g. Q4 income" value={entry.note}
                  onChange={e => setEntry(p => ({ ...p, note: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-800 border border-white/[0.06] rounded-lg text-white text-sm" />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowDepositModal(false)}
                  className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg text-sm border border-white/[0.06] hover:bg-slate-700">Cancel</button>
                <button type="submit"
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500">Add Deposit</button>
              </div>
            </form>
          </div>
        </div>
      </ModalPortal>}

      {/* Transfer Guidance Modal */}
      {showTransferModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl max-w-sm w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-white">Move Money to Tax Savings</p>
              <button onClick={() => setShowTransferModal(false)}><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
            </div>
            <div className="bg-violet-500/8 border border-violet-500/20 rounded-xl p-4 mb-4 space-y-2">
              <p className="text-xs text-slate-300 font-medium">How to transfer:</p>
              <ol className="text-xs text-slate-400 space-y-1.5 list-decimal list-inside">
                <li>Open your bank app or website</li>
                <li>Initiate a transfer from checking → savings</li>
                <li>Enter the amount below and confirm</li>
                <li>Come back here and tap "I've Done It"</li>
              </ol>
            </div>
            <form onSubmit={handleMarkTransferDone} className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Amount you transferred</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400 text-sm">$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00" value={transferDoneAmount}
                    onChange={e => setTransferDoneAmount(e.target.value)}
                    className="w-full pl-7 pr-4 py-2.5 bg-slate-800 border border-white/[0.06] rounded-lg text-white text-sm" required />
                </div>
              </div>
              {settings.taxVaultAccountName && (
                <div className="flex items-center gap-2 bg-white/[0.03] rounded-lg px-3 py-2">
                  <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <p className="text-xs text-slate-400">To: <span className="text-white">{settings.taxVaultAccountName}</span></p>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowTransferModal(false)}
                  className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg text-sm border border-white/[0.06] hover:bg-slate-700">Cancel</button>
                <button type="submit"
                  className="flex-1 px-4 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-500">I've Done It</button>
              </div>
            </form>
          </div>
        </div>
      </ModalPortal>}

      {/* Withdrawal Modal */}
      {showWithdrawalModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl max-w-sm w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-white">Record Tax Payment</p>
              <button onClick={() => setShowWithdrawalModal(false)}><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
            </div>
            <div className="centris-card rounded-lg p-3 mb-3 flex justify-between items-center">
              <span className="text-xs text-slate-400">Available</span>
              <span className="text-sm font-bold text-white">${vaultBalance.toFixed(2)}</span>
            </div>
            <form onSubmit={handleWithdrawal} className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400 text-sm">$</span>
                  <input type="number" step="0.01" min="0" max={vaultBalance} placeholder="0.00"
                    value={withdrawalData.amount}
                    onChange={e => setWithdrawalData(p => ({ ...p, amount: e.target.value }))}
                    className="w-full pl-7 pr-4 py-2.5 bg-slate-800 border border-white/[0.06] rounded-lg text-white text-sm" required />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Purpose</label>
                <select value={withdrawalData.purpose}
                  onChange={e => setWithdrawalData(p => ({ ...p, purpose: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-800 border border-white/[0.06] rounded-lg text-white text-sm">
                  <option value="quarterly">Quarterly Estimated Payment</option>
                  <option value="annual">Annual Tax Payment</option>
                  <option value="adjustment">Tax Adjustment</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block">Tax Year</label>
                  <input type="number" min="2020" max="2030" value={withdrawalData.taxYear}
                    onChange={e => setWithdrawalData(p => ({ ...p, taxYear: Number(e.target.value) }))}
                    className="w-full px-3 py-2.5 bg-slate-800 border border-white/[0.06] rounded-lg text-white text-sm" />
                </div>
                {withdrawalData.purpose === 'other' && (
                  <div>
                    <label className="text-xs text-slate-400 mb-1.5 block">Note</label>
                    <input type="text" placeholder="Description" value={withdrawalData.note}
                      onChange={e => setWithdrawalData(p => ({ ...p, note: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-slate-800 border border-white/[0.06] rounded-lg text-white text-sm" />
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowWithdrawalModal(false)}
                  className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg text-sm border border-white/[0.06] hover:bg-slate-700">Cancel</button>
                <button type="submit"
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-500">Record Payment</button>
              </div>
            </form>
          </div>
        </div>
      </ModalPortal>}
    </div>
  );
};
// ---- UnifiedDashboard.js (Pulse) ----
const UnifiedDashboard = ({ user, expenses, invoices, mileageTrips, subscriptions, bankAccounts = [], revenue = [], taxVaultSettings = { enabled: true, reservePercent: 25 }, perms = null, onShowPricing = null, liveBankImportActive = false }) => {
  const [forecastMonths, setForecastMonths] = useState(3);
  const [showForecastDetail, setShowForecastDetail] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const maxForecastMonths = Math.min(12, Number(perms?.forecastMonthsMax || 3));

  // ??? Helpers ???
  const getMs = (data) => {
    if (!data?.length) return 1;
    const ds = data.map(e => new Date(e.date)).filter(d => !isNaN(d));
    if (!ds.length) return 1;
    const earliest = new Date(Math.min(...ds));
    const latest = new Date(Math.max(...ds));
    const span = (latest.getFullYear() - earliest.getFullYear()) * 12
      + (latest.getMonth() - earliest.getMonth())
      + 1;
    return Math.max(1, span);
  };
  const fmt = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(a);
  const monthlySubCost = (sub) => {
    if (!sub) return 0;
    if (sub.billingCycle === 'yearly') return (sub.cost || 0) / 12;
    if (sub.billingCycle === 'quarterly') return (sub.cost || 0) / 3;
    return sub.cost || 0;
  };

  // Core Metrics (revenue from BOTH invoices AND revenue entries)
  const invoiceRevenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const revenueEntryTotal = (revenue || []).reduce((s, r) => s + (r.amount || 0), 0);
  const totalRevenue = invoiceRevenue + revenueEntryTotal;

  const pendingRevenue = invoices.filter(i => i.status === 'sent').reduce((s, i) => s + (i.total || 0), 0);
  const overdueInvoices = invoices.filter(i => i.status === 'sent' && new Date(i.dueDate || i.date) < new Date());
  const overdueRevenue = overdueInvoices.reduce((s, i) => s + (i.total || 0), 0);

  // Expenses
  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const bizExpenses = expenses.filter(e => e.taxDeductible);
  const persExpenses = expenses.filter(e => !e.taxDeductible);
  const bizSpend = bizExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const persSpend = persExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const activeSubs = (subscriptions || []).filter(s => s.status === 'active');
  const subCost = activeSubs.reduce((s, sub) => s + monthlySubCost(sub), 0);
  const ms = getMs(expenses);
  const monthlyBurn = totalExpenses / Math.max(1, ms) + subCost;
  const netProfit = totalRevenue - totalExpenses;

  // Bank balance (for Cash display only, not runway math)
  const currentBalance = bankAccounts.reduce(
    (s, a) => s + Number(a?.balances?.current ?? a?.balance ?? 0),
    0
  );

  // Tax
  const taxPct = taxVaultSettings?.reservePercent || 25;
  const taxVault = totalRevenue * (taxPct / 100);
  const takeHome = totalRevenue - taxVault;
  const mileageDed = (mileageTrips || []).reduce((s, t) => s + (t.deduction || 0), 0);

  // Monthly revenue from all sources
  const paidInvoices = invoices.filter((i) => i.status === 'paid');
  const allRevenueDates = [
    ...paidInvoices.map(i => ({ date: i.paidDate || i.date })),
    ...(revenue || []).map(r => ({ date: r.date })),
  ].filter(d => d.date);
  const revenueMs = getMs(allRevenueDates.length ? allRevenueDates : paidInvoices);
  const monthlyRevenue = totalRevenue / Math.max(1, revenueMs);
  // ??? TIER 1: Unified Cash Runway (personal + business) ???
  const unifiedRunway = useMemo(() => {
    const monthlyPersonal = persSpend / Math.max(1, ms);
    const monthlyBusiness = bizSpend / Math.max(1, ms) + subCost;
    const totalMonthlyBurn = monthlyPersonal + monthlyBusiness;
    // Estimate available cash from revenue trends and pending AR only (ignore bank balances)
    const availableCash =
      Math.max(0, monthlyRevenue - totalMonthlyBurn) * 3 + pendingRevenue * 0.7; // ~3 months of net as proxy
    const months = totalMonthlyBurn > 0 ? Math.floor(availableCash / totalMonthlyBurn) : 999;
    return { months, totalBurn: totalMonthlyBurn, personalBurn: monthlyPersonal, businessBurn: monthlyBusiness, availableCash };
  }, [bizSpend, persSpend, subCost, pendingRevenue, ms, monthlyRevenue]);

  // ??? TIER 1: Smart Commingling Detector ???
  const comminglingFlags = useMemo(() => {
    const personalKeywords = ['grocery', 'groceries', 'restaurant', 'dining', 'uber eats', 'doordash', 'grubhub', 'netflix', 'spotify', 'hulu', 'gym', 'fitness', 'clothing', 'apparel', 'target', 'walmart', 'costco', 'amazon prime', 'pet', 'veterinar', 'pharmacy', 'cvs', 'walgreens', 'rent', 'mortgage', 'utilities', 'electric', 'gas bill', 'water bill', 'daycare', 'tuition'];
    const businessKeywords = ['adobe', 'aws', 'google cloud', 'stripe', 'paypal fee', 'quickbooks', 'slack', 'notion', 'figma', 'github', 'hosting', 'domain', 'mailchimp', 'zoom pro', 'canva pro', 'advertisement', 'marketing', 'consulting', 'contractor', 'office supply', 'coworking'];

    const flags = [];
    for (const exp of expenses) {
      const desc = String(exp.merchant || exp.description || '').toLowerCase();
      const cat = String(exp.category || '').toLowerCase();
      const isMarkedBiz = exp.taxDeductible === true;
      const isMarkedPersonal = exp.taxDeductible === false || !exp.taxDeductible;

      // Personal expense marked as business
      if (isMarkedBiz && personalKeywords.some(k => desc.includes(k) || cat.includes(k))) {
        flags.push({ expense: exp, issue: 'personal_as_business', suggestion: `"${exp.merchant || exp.description}" looks personal but is marked as business/deductible.` });
      }
      // Business expense not marked as deductible
      if (isMarkedPersonal && businessKeywords.some(k => desc.includes(k) || cat.includes(k))) {
        flags.push({ expense: exp, issue: 'business_as_personal', suggestion: `"${exp.merchant || exp.description}" looks like a business expense ? mark it as deductible.` });
      }
    }
    return flags.slice(0, 10); // cap at 10
  }, [expenses]);

  // ??? TIER 1: Client Concentration Warning ???
  const clientConcentration = useMemo(() => {
    const paid = invoices.filter(i => i.status === 'paid');
    const byClient = {};
    for (const inv of paid) {
      const name = String(inv.clientName || inv.customer || 'Unknown');
      byClient[name] = (byClient[name] || 0) + (inv.total || 0);
    }
    const sorted = Object.entries(byClient).sort(([, a], [, b]) => b - a);
    if (!sorted.length || totalRevenue === 0) return { risky: false, topClient: null, topPct: 0, sorted };
    const topPct = (sorted[0][1] / totalRevenue) * 100;
    return { risky: topPct > 40, topClient: sorted[0][0], topPct, sorted };
  }, [invoices, totalRevenue]);

  // ??? TIER 1: Contract Lock-in Alerts ???
  const lockInAlerts = useMemo(() => {
    const now = new Date();
    const alerts = [];
    for (const sub of activeSubs) {
      if (sub.billingCycle === 'yearly' && sub.renewalDate) {
        const renewal = new Date(sub.renewalDate);
        const daysUntil = Math.floor((renewal - now) / 864e5);
        // Alert if renewal is within 30 days
        if (daysUntil > 0 && daysUntil <= 30) {
          alerts.push({ sub, daysUntil, renewal, savings: sub.cost || 0 });
        }
      }
    }
    return alerts.sort((a, b) => a.daysUntil - b.daysUntil);
  }, [activeSubs]);

  // ??? TIER 1: Late Payment Shock Absorber ???
  const latePaymentImpact = useMemo(() => {
    if (!overdueInvoices.length) return null;
    const totalOverdue = overdueRevenue;
    const monthsOfBurnOverdue = monthlyBurn > 0 ? totalOverdue / monthlyBurn : 0;
    // Which subs are at risk if this money doesn't come in
    const nonCriticalSubs = activeSubs
      .filter(s => !s.critical)
      .sort((a, b) => (b.cost || 0) - (a.cost || 0))
      .slice(0, 3);
    const pauseSavings = nonCriticalSubs.reduce((s, sub) => s + monthlySubCost(sub), 0);
    return {
      totalOverdue,
      monthsOfBurnAtRisk: monthsOfBurnOverdue,
      overdueCount: overdueInvoices.length,
      suggestPause: nonCriticalSubs,
      pauseSavings,
    };
  }, [overdueInvoices, overdueRevenue, monthlyBurn, activeSubs]);

  const deductionCoach = useMemo(() => {
    const candidates = comminglingFlags
      .filter((f) => f.issue === 'business_as_personal')
      .map((f) => ({
        id: String(f.expense?.id || f.expense?.date || `${f.expense?.merchant || 'expense'}-${f.expense?.amount || 0}`),
        merchant: f.expense?.merchant || f.expense?.description || 'Expense',
        amount: Number(f.expense?.amount || 0),
        explanation: f.suggestion,
      }));
    const missedDeductionAmount = candidates.reduce((s, c) => s + c.amount, 0);
    const taxSavings = missedDeductionAmount * 0.25;
    return { candidates, missedDeductionAmount, taxSavings };
  }, [comminglingFlags]);

  const subAnalysis = useMemo(() => {
    const now = Date.now();
    const highRisk = activeSubs
      .map((sub) => {
        let lastUsed = null;
        if (sub.lastUsedAt && typeof sub.lastUsedAt.toDate === 'function') lastUsed = sub.lastUsedAt.toDate();
        else if (sub.lastUsedAt) lastUsed = new Date(sub.lastUsedAt);
        else if (sub.lastUsed) lastUsed = new Date(sub.lastUsed);
        if (!lastUsed || Number.isNaN(lastUsed.getTime())) return null;
        const daysUnused = Math.max(0, Math.floor((now - lastUsed.getTime()) / 86400000));
        return { ...sub, daysUnused, monthlyCost: monthlySubCost(sub) };
      })
      .filter(Boolean)
      .filter((sub) => sub.daysUnused >= 30)
      .sort((a, b) => b.monthlyCost - a.monthlyCost);
    const wastedMonthly = highRisk.reduce((sum, sub) => sum + sub.monthlyCost, 0);
    return { highRisk, wastedMonthly };
  }, [activeSubs]);

  const taxPlan = useMemo(() => {
    const taxableProfit = Math.max(0, totalRevenue - bizSpend - mileageDed);
    const seTax = taxableProfit * 0.153 * 0.5;
    const incomeTax = taxableProfit * 0.22;
    const totalOwed = seTax + incomeTax;
    const quarterlyPayment = totalOwed / 4;
    const reserved = taxVault;
    const shortfall = Math.max(0, totalOwed - reserved);

    const now = new Date();
    const year = now.getFullYear();
    const dueDates = [
      { label: 'Apr 15', date: new Date(year, 3, 15) },
      { label: 'Jun 15', date: new Date(year, 5, 15) },
      { label: 'Sep 15', date: new Date(year, 8, 15) },
      { label: 'Jan 15', date: new Date(year + 1, 0, 15) },
    ];
    let next = dueDates.find((d) => d.date >= now) || dueDates[dueDates.length - 1];
    if (next.date < now) next = { label: 'Apr 15', date: new Date(year + 1, 3, 15) };
    const daysUntilNext = Math.max(0, Math.ceil((next.date.getTime() - now.getTime()) / 86400000));
    const penaltyRisk = shortfall > 0 && daysUntilNext <= 45 ? shortfall * 0.03 : 0;

    return { seTax, incomeTax, totalOwed, quarterlyPayment, reserved, shortfall, next, daysUntilNext, penaltyRisk };
  }, [totalRevenue, bizSpend, mileageDed, taxVault]);

  // TIER 3 INTELLIGENCE
  const dailyPulse = useMemo(() => {
    const issues = [];
    if (unifiedRunway.months < 3) issues.push({ severity: 'critical', text: `Only ${unifiedRunway.months} months of runway left` });
    if (latePaymentImpact && latePaymentImpact.totalOverdue > monthlyBurn) issues.push({ severity: 'critical', text: `${fmt(latePaymentImpact.totalOverdue)} overdue - more than a month of burn` });
    if (taxPlan.shortfall > 500 && taxPlan.daysUntilNext <= 30) issues.push({ severity: 'warning', text: `Tax payment of ${fmt(taxPlan.quarterlyPayment)} due in ${taxPlan.daysUntilNext} days` });
    if (clientConcentration.risky) issues.push({ severity: 'warning', text: `${clientConcentration.topPct.toFixed(0)}% of revenue from one client` });
    if (deductionCoach.taxSavings > 200) issues.push({ severity: 'info', text: `${fmt(deductionCoach.taxSavings)} in potential tax savings unclaimed` });

    if (issues.length === 0) return { status: 'green', message: "You're on track. No urgent issues.", top: null };
    if (issues[0].severity === 'critical') return { status: 'red', message: issues[0].text, top: issues[0] };
    return { status: 'amber', message: `${issues.length} thing${issues.length > 1 ? 's' : ''} need${issues.length === 1 ? 's' : ''} attention`, top: issues[0] };
  }, [unifiedRunway, latePaymentImpact, taxPlan, clientConcentration, deductionCoach, monthlyBurn]);

  const feastFamine = useMemo(() => {
    const monthlyIncome = {};
    for (const inv of invoices.filter((i) => i.status === 'paid')) {
      const d = new Date(inv.paidDate || inv.date);
      if (Number.isNaN(d.getTime())) continue;
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthlyIncome[k] = (monthlyIncome[k] || 0) + (inv.total || 0);
    }
    for (const r of (revenue || [])) {
      const d = new Date(r.date);
      if (Number.isNaN(d.getTime())) continue;
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthlyIncome[k] = (monthlyIncome[k] || 0) + (r.amount || 0);
    }
    const values = Object.values(monthlyIncome);
    if (values.length < 3) return null;
    const avg = values.reduce((s, v) => s + v, 0) / values.length;
    const current = values[values.length - 1] || 0;
    const ratio = avg > 0 ? current / avg : 1;
    if (ratio > 1.25) return { phase: 'feast', ratio, avg, current, suggestion: `High-income month. Set aside ${fmt((current - avg) * 0.5)} extra for lean months.` };
    if (ratio < 0.75) return { phase: 'famine', ratio, avg, current, suggestion: `Lean month. Your reserves cover ${unifiedRunway.months} months at this level.` };
    return { phase: 'stable', ratio, avg, current, suggestion: 'Income is tracking near your average.' };
  }, [invoices, revenue, unifiedRunway]);

  const taxPrepScore = useMemo(() => {
    let score = 0;
    const items = [];

    const categorized = expenses.filter((e) => e.category && e.category !== 'Other' && e.category !== 'Uncategorized').length;
    const catPct = expenses.length > 0 ? categorized / expenses.length : 0;
    const catScore = Math.round(catPct * 25);
    score += catScore;
    items.push({ label: 'Expenses categorized', score: catScore, max: 25, pct: catPct });

    const deductibleCount = expenses.filter((e) => e.taxDeductible).length;
    const dedPct = expenses.length > 0 ? Math.min(1, deductibleCount / (expenses.length * 0.4)) : 0;
    const dedScore = Math.round(dedPct * 25);
    score += dedScore;
    items.push({ label: 'Deductions marked', score: dedScore, max: 25, pct: dedPct });

    const qScore = taxPlan.shortfall <= 0 ? 25 : taxPlan.shortfall < taxPlan.quarterlyPayment ? 15 : 5;
    score += qScore;
    items.push({ label: 'Tax reserves on track', score: qScore, max: 25, pct: qScore / 25 });

    const invScore = invoices.length > 0 ? 25 : 0;
    score += invScore;
    items.push({ label: 'Invoice records', score: invScore, max: 25, pct: invScore / 25 });

    return { score, maxScore: 100, items, grade: score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : 'D' };
  }, [expenses, invoices, taxPlan]);

  const invoiceEscalation = useMemo(() => {
    const now = new Date();
    return invoices
      .filter((i) => i.status === 'sent')
      .map((inv) => {
        const due = new Date(inv.dueDate || inv.date);
        const daysLate = Math.floor((now - due) / 864e5);
        let stage = 'current';
        let action = 'No action needed';
        let urgency = 'low';
        if (daysLate > 0 && daysLate <= 7) { stage = 'reminder'; action = 'Send a friendly payment reminder'; urgency = 'low'; }
        else if (daysLate > 7 && daysLate <= 14) { stage = 'followup'; action = 'Send a firmer follow-up email'; urgency = 'medium'; }
        else if (daysLate > 14 && daysLate <= 30) { stage = 'call'; action = 'Call the client directly'; urgency = 'medium'; }
        else if (daysLate > 30 && daysLate <= 60) { stage = 'final'; action = 'Send final notice with late fee'; urgency = 'high'; }
        else if (daysLate > 60) { stage = 'collections'; action = 'Consider collections or write-off'; urgency = 'critical'; }
        return { invoice: inv, daysLate, stage, action, urgency, amount: inv.total || 0 };
      })
      .filter((e) => e.daysLate > 0)
      .sort((a, b) => b.daysLate - a.daysLate);
  }, [invoices]);

  const clientScorecard = useMemo(() => {
    const clients = {};
    for (const inv of invoices) {
      const name = inv.clientName || inv.customer || 'Unknown';
      if (!clients[name]) clients[name] = { name, revenue: 0, invoiceCount: 0, paid: 0, avgDays: [], onTime: 0, late: 0 };
      clients[name].revenue += inv.total || 0;
      clients[name].invoiceCount++;
      if (inv.status === 'paid') {
        clients[name].paid++;
        const issued = new Date(inv.date);
        const paidDate = new Date(inv.paidDate || inv.date);
        const days = Math.max(0, Math.floor((paidDate - issued) / 864e5));
        clients[name].avgDays.push(days);
        const due = new Date(inv.dueDate || inv.date);
        if (paidDate <= due) clients[name].onTime++; else clients[name].late++;
      }
    }
    return Object.values(clients).map((c) => {
      const avgPayDays = c.avgDays.length ? c.avgDays.reduce((s, v) => s + v, 0) / c.avgDays.length : 99;
      const payScore = avgPayDays <= 15 ? 100 : avgPayDays <= 30 ? 80 : avgPayDays <= 45 ? 60 : avgPayDays <= 60 ? 40 : 20;
      const volumeScore = Math.min(100, (c.revenue / Math.max(totalRevenue, 1)) * 500);
      const reliabilityScore = c.invoiceCount > 0 ? (c.onTime / Math.max(c.invoiceCount, 1)) * 100 : 50;
      const overallScore = payScore * 0.35 + volumeScore * 0.35 + reliabilityScore * 0.30;
      const tier = overallScore >= 75 ? 'A' : overallScore >= 55 ? 'B' : 'C';
      return { ...c, avgPayDays: Math.round(avgPayDays), payScore, volumeScore, reliabilityScore, overallScore: Math.round(overallScore), tier };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [invoices, totalRevenue]);

  // Build alert list
  const alerts = useMemo(() => {
    const list = [];

    if (unifiedRunway.months < 3 && unifiedRunway.months > 0) {
      list.push({ id: 'runway-critical', severity: 'critical', icon: '!', title: 'Critical runway', message: `${unifiedRunway.months} months at ${fmt(unifiedRunway.totalBurn)}/mo total burn. Take action now.` });
    } else if (unifiedRunway.months < 6 && unifiedRunway.months > 0) {
      list.push({ id: 'runway-low', severity: 'warning', icon: '!', title: 'Low runway', message: `${unifiedRunway.months} months remaining. Build toward 6+ months.` });
    }

    if (latePaymentImpact && latePaymentImpact.totalOverdue > 0) {
      list.push({ id: 'late-payments', severity: 'warning', icon: '$', title: `${fmt(latePaymentImpact.totalOverdue)} in overdue invoices`, message: `That is ${latePaymentImpact.monthsOfBurnAtRisk.toFixed(1)} months of burn. ${latePaymentImpact.suggestPause.length ? `Pause ${latePaymentImpact.suggestPause.map((s) => s.name).join(', ')} to save ${fmt(latePaymentImpact.pauseSavings)}/mo while you collect.` : 'Send reminders now.'}` });
    }

    if (clientConcentration.risky) {
      list.push({ id: 'client-conc', severity: 'warning', icon: '*', title: `${clientConcentration.topPct.toFixed(0)}% revenue from ${clientConcentration.topClient}`, message: 'High dependency. Losing this client would be severe.' });
    }

    if (taxPlan.shortfall > 500 && taxPlan.daysUntilNext < 45) {
      list.push({ id: 'tax-shortfall', severity: 'warning', icon: 'T', title: `Tax shortfall ${fmt(taxPlan.shortfall)}`, message: `${taxPlan.daysUntilNext} days until ${taxPlan.next.label}. Potential penalty risk ${fmt(taxPlan.penaltyRisk)}.` });
    }

    if (deductionCoach.taxSavings > 100) {
      list.push({ id: 'missed-deductions', severity: 'info', icon: 'D', title: `${fmt(deductionCoach.taxSavings)} in potential tax savings`, message: `${deductionCoach.candidates.length} expenses may be deductible but are currently marked personal.` });
    }

    if (subAnalysis.wastedMonthly > 0) {
      list.push({ id: 'zombie-subs', severity: 'info', icon: 'S', title: `${fmt(subAnalysis.wastedMonthly)}/mo in potentially unused subscriptions`, message: `${subAnalysis.highRisk.length} active subscriptions appear unused for 30+ days.` });
    }

    if (comminglingFlags.length > 0) {
      const bizAsPersList = comminglingFlags.filter((f) => f.issue === 'personal_as_business');
      const persAsBizList = comminglingFlags.filter((f) => f.issue === 'business_as_personal');
      if (bizAsPersList.length > 0) {
        list.push({ id: 'commingling-pb', severity: 'info', icon: 'C', title: `${bizAsPersList.length} possible personal expense${bizAsPersList.length > 1 ? 's' : ''} marked as business`, message: bizAsPersList.slice(0, 2).map((f) => f.suggestion).join(' ') });
      }
      if (persAsBizList.length > 0) {
        list.push({ id: 'commingling-bp', severity: 'info', icon: 'C', title: `${persAsBizList.length} possible business expense${persAsBizList.length > 1 ? 's' : ''} not deducted`, message: persAsBizList.slice(0, 2).map((f) => f.suggestion).join(' ') });
      }
    }

    for (const alert of lockInAlerts) {
      list.push({ id: `lockin-${alert.sub.name}`, severity: 'info', icon: 'R', title: `${alert.sub.name} renews in ${alert.daysUntil} days`, message: `Annual renewal of ${fmt(alert.savings)}. Cancel before ${alert.renewal.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} if you want to switch.` });
    }

    for (const esc of invoiceEscalation.filter((e) => e.urgency !== 'low').slice(0, 3)) {
      list.push({
        id: `esc-${esc.invoice.id || esc.invoice.invoiceNumber || Math.random()}`,
        severity: esc.urgency === 'critical' ? 'critical' : 'warning',
        icon: esc.stage === 'collections' ? '[ALERT]' : esc.stage === 'final' ? '[!]' : '[MAIL]',
        title: `${esc.invoice.clientName || 'Client'}: ${fmt(esc.amount)} - ${esc.daysLate}d late`,
        message: esc.action,
      });
    }

    const cTier = clientScorecard.filter((c) => c.tier === 'C' && c.revenue > 500);
    if (cTier.length > 0) {
      list.push({
        id: 'ctier-clients',
        severity: 'info',
        icon: '[CLIENT]',
        title: `${cTier.length} low-scoring client${cTier.length > 1 ? 's' : ''}`,
        message: `${cTier.map((c) => c.name).join(', ')} - slow paying or low revenue. Consider raising rates or replacing.`,
      });
    }

    if (feastFamine && feastFamine.phase === 'feast') {
      list.push({ id: 'feast', severity: 'info', icon: '[UP]', title: 'High-income month detected', message: feastFamine.suggestion });
    }
    if (feastFamine && feastFamine.phase === 'famine') {
      list.push({ id: 'famine', severity: 'warning', icon: '[DOWN]', title: 'Below-average income month', message: feastFamine.suggestion });
    }

    if ((netProfit || 0) > 50000) {
      const seSavings = Math.round(netProfit * 0.4 * 0.153);
      list.push({
        id: 'entity-nudge',
        severity: 'info',
        icon: '[BIZ]',
        title: `S-Corp could save ~${fmt(seSavings)}/yr in SE taxes`,
        message: 'At your income level, an S-Corp election reduces self-employment tax. Talk to a CPA about whether it makes sense for you.',
      });
    }

    return list.filter((a) => !dismissedAlerts.has(a.id));
  }, [unifiedRunway, latePaymentImpact, clientConcentration, comminglingFlags, lockInAlerts, dismissedAlerts, taxPlan, deductionCoach, subAnalysis, invoiceEscalation, clientScorecard, feastFamine, netProfit]);

  // ??? Forecast ???
  const projection = useMemo(
    () => buildCashFlowProjection({ expenses, invoices, revenue, subscriptions, bankAccounts, taxVaultSettings, forecastMonths }),
    [expenses, invoices, revenue, subscriptions, bankAccounts, taxVaultSettings, forecastMonths]
  );

  // ??? Spending split ???
  const bizPct = totalExpenses > 0 ? Math.round((bizSpend / totalExpenses) * 100) : 50;
  // ??? Dismiss helper ???
  const dismiss = (id) => setDismissedAlerts(prev => new Set([...prev, id]));

  useEffect(() => {
    if (forecastMonths > maxForecastMonths) setForecastMonths(maxForecastMonths);
  }, [forecastMonths, maxForecastMonths]);

  const glassCard = 'centris-card rounded-2xl p-5';
  const glassOuter = glassCard;
  const sectionLabel = 'text-[11px] md:text-[12px] font-semibold tracking-[0.18em] uppercase text-slate-400 mb-3';

  return (
    <div className="space-y-4" style={{ animation: 'pageSlideIn 0.35s ease-out both' }}>
      <div className="pb-1">
        <h1 className="text-2xl font-semibold text-white">Pulse</h1>
        <p className="text-sm text-slate-500 mt-0.5">Financial health</p>
      </div>

      <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${
        dailyPulse.status === 'green' ? 'bg-emerald-500/[0.06] border border-emerald-500/15' :
        dailyPulse.status === 'amber' ? 'bg-amber-500/[0.06] border border-amber-500/15' :
        'bg-red-500/[0.06] border border-red-500/15'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 animate-pulse ${
          dailyPulse.status === 'green' ? 'bg-emerald-400' :
          dailyPulse.status === 'amber' ? 'bg-amber-400' : 'bg-red-400'
        }`} />
        <p className={`text-sm font-medium ${
          dailyPulse.status === 'green' ? 'text-emerald-300' :
          dailyPulse.status === 'amber' ? 'text-amber-300' : 'text-red-300'
        }`}>{dailyPulse.message}</p>
        {feastFamine && feastFamine.phase !== 'stable' && (
          <span className="ml-auto text-[10px] text-slate-500 whitespace-nowrap">
            {feastFamine.phase === 'feast' ? 'High month' : 'Lean month'}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {
            label: 'Cash',
            value: liveBankImportActive ? fmt(currentBalance) : (currentBalance > 0 ? fmt(currentBalance) : '—'),
            sub: liveBankImportActive
              ? `${bankAccounts.length} account${bankAccounts.length !== 1 ? 's' : ''}`
              : 'Statements & receipts (no live link)',
          },
          { label: 'Net Profit', value: fmt(netProfit), sub: `${fmt(totalRevenue)} revenue` },
          { label: 'Burn Rate', value: `${fmt(monthlyBurn)}/mo`, sub: `incl. ${fmt(subCost)} subs` },
          { label: 'Runway', value: unifiedRunway.months > 99 ? 'INF' : `${unifiedRunway.months}mo`, sub: 'est. months' },
        ].map((m, i) => (
          <div key={m.label} className={glassOuter} style={{ animation: `cardFadeIn 0.4s ease-out both`, animationDelay: `${i * 60}ms` }}>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1.5">{m.label}</p>
            <p className="text-xl md:text-2xl font-semibold text-white">{m.value}</p>
            <p className="text-[10px] text-slate-600 mt-1">{m.sub}</p>
          </div>
        ))}
      </div>

      {alerts.length > 0 && alerts[0].severity !== 'info' && (
        <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${alerts[0].severity === 'critical' ? 'bg-red-500/[0.05] border-red-500/15' : 'bg-amber-500/[0.05] border-amber-500/15'}`}>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${alerts[0].severity === 'critical' ? 'text-red-300' : 'text-amber-300'}`}>{alerts[0].title}</p>
            <p className="text-xs text-slate-500 mt-0.5">{alerts[0].message || alerts[0].msg}</p>
          </div>
          <button onClick={() => dismiss(alerts[0].id)} className="text-slate-600 hover:text-slate-400 text-xs shrink-0">&#x2715;</button>
        </div>
      )}

      <div className={glassOuter}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-slate-400">Spending breakdown</p>
          <p className="text-xs text-slate-500">{fmt(totalExpenses)}</p>
        </div>
        <div className="w-full bg-white/[0.04] rounded-full h-1.5 flex overflow-hidden mb-4">
          <div className="bg-indigo-500 h-1.5 rounded-l-full transition-all" style={{ width: `${bizPct}%` }} />
          <div className="bg-orange-500/80 h-1.5 rounded-r-full transition-all" style={{ width: `${100 - bizPct}%` }} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
          {[
            { label: 'Business', value: fmt(bizSpend), color: 'text-indigo-400' },
            { label: 'Personal', value: fmt(persSpend), color: 'text-orange-400' },
            { label: 'Subscriptions', value: `${fmt(subCost)}/mo`, color: 'text-purple-400' },
            { label: 'Pending AR', value: fmt(pendingRevenue), color: 'text-emerald-400' },
          ].map((i) => (
            <div key={i.label}>
              <p className="text-[10px] text-slate-500 mb-0.5">{i.label}</p>
              <p className={`text-sm font-semibold ${i.color}`}>{i.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className={`${glassOuter} text-center py-2`}>
          <p className="text-[10px] text-slate-500 mb-0.5">Take-Home</p>
          <p className="text-sm font-bold text-white">{fmt(takeHome)}</p>
          <p className="text-[10px] text-slate-600 mt-0.5">after {taxPct}% tax reserve</p>
        </div>
        <div className={`${glassOuter} text-center py-2`}>
          <p className="text-[10px] text-slate-500 mb-0.5">Cash + AR</p>
          <p className="text-sm font-bold text-emerald-400">{fmt(currentBalance + pendingRevenue)}</p>
          <p className="text-[10px] text-slate-600 mt-0.5">safety net</p>
        </div>
      </div>

      {/* ── Tax reserve snapshot ── */}
      <div className={glassOuter}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-slate-400">Tax reserve</p>
          {taxPlan.daysUntilNext <= 90 && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${taxPlan.shortfall <= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
              {taxPlan.shortfall <= 0 ? 'Covered' : `${fmt(taxPlan.shortfall)} short`}
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-[10px] text-slate-500 mb-0.5">Should reserve</p>
            <p className="text-sm font-bold text-amber-400">{fmt(taxVault)}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-0.5">Est. quarterly</p>
            <p className="text-sm font-bold text-white">{fmt(taxPlan.quarterlyPayment)}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-0.5">Next due</p>
            <p className="text-sm font-bold text-white">{taxPlan.next.label}</p>
            <p className="text-[9px] text-slate-600">{taxPlan.daysUntilNext}d away</p>
          </div>
        </div>
      </div>

      {/* ── Income volatility ── */}
      {feastFamine && (
        <div className={glassOuter}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-slate-400">Income this month</p>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              feastFamine.phase === 'feast' ? 'bg-emerald-500/10 text-emerald-400' :
              feastFamine.phase === 'famine' ? 'bg-red-500/10 text-red-400' :
              'bg-slate-500/10 text-slate-400'
            }`}>
              {feastFamine.phase === 'feast' ? 'High month' : feastFamine.phase === 'famine' ? 'Lean month' : 'On track'}
            </span>
          </div>
          <div className="flex items-end gap-1 mb-3" style={{ height: 48 }}>
            {(() => {
              const monthlyIncome = {};
              for (const inv of invoices.filter(i => i.status === 'paid')) {
                const d = new Date(inv.paidDate || inv.date);
                if (isNaN(d.getTime())) continue;
                const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
                monthlyIncome[k] = (monthlyIncome[k] || 0) + (inv.total || 0);
              }
              for (const r of (revenue || [])) {
                const d = new Date(r.date);
                if (isNaN(d.getTime())) continue;
                const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
                monthlyIncome[k] = (monthlyIncome[k] || 0) + (r.amount || 0);
              }
              const now = new Date();
              const months = Array.from({ length: 6 }, (_, i) => {
                const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
                const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
                return { k, label: d.toLocaleString('default', { month: 'short' }), val: monthlyIncome[k] || 0 };
              });
              const maxVal = Math.max(...months.map(m => m.val), 1);
              const avg = months.reduce((s, m) => s + m.val, 0) / months.length;
              return months.map((m, i) => (
                <div key={m.k} className="flex-1 flex flex-col items-center justify-end gap-0.5">
                  <div className="w-full rounded-t-sm" style={{
                    height: `${Math.max(4, (m.val / maxVal) * 44)}px`,
                    background: i === 5
                      ? (m.val >= avg * 1.1 ? '#34d399' : m.val < avg * 0.75 ? '#f87171' : '#818cf8')
                      : 'rgba(129,140,248,0.25)'
                  }} />
                  <span className="text-[8px] text-slate-600">{m.label}</span>
                </div>
              ));
            })()}
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-slate-500">{feastFamine.suggestion}</span>
            <span className="text-slate-500">avg {fmt(feastFamine.avg)}/mo</span>
          </div>
        </div>
      )}

      {/* ── Pending / overdue invoices ── */}
      {(invoiceEscalation.length > 0 || pendingRevenue > 0) && (
        <div className={glassOuter}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-slate-400">Money coming in</p>
            <span className="text-xs font-semibold text-emerald-400">{fmt(pendingRevenue)} pending</span>
          </div>
          {invoiceEscalation.length > 0 ? (
            <div className="space-y-2.5">
              {invoiceEscalation.slice(0, 4).map(esc => (
                <div key={esc.invoice.id || esc.invoice.invoiceNumber} className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{esc.invoice.clientName || 'Client'}</p>
                    <p className="text-[10px] text-slate-500">{esc.daysLate > 0 ? `${esc.daysLate}d late — ` : ''}{esc.action}</p>
                  </div>
                  <p className={`text-xs font-bold shrink-0 ${esc.urgency === 'critical' ? 'text-red-400' : esc.urgency === 'high' ? 'text-orange-400' : 'text-amber-400'}`}>
                    {fmt(esc.amount)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500">No overdue invoices — great!</p>
          )}
          {invoices.filter(i => i.status === 'sent' && new Date(i.dueDate || i.date) >= new Date()).slice(0, 2).map(inv => (
            <div key={inv.id} className="flex items-center justify-between pt-2 mt-2 border-t border-white/[0.04]">
              <div>
                <p className="text-xs text-slate-300">{inv.clientName}</p>
                <p className="text-[10px] text-slate-500">due {inv.dueDate || inv.date}</p>
              </div>
              <p className="text-xs font-semibold text-emerald-400">{fmt(inv.total)}</p>
            </div>
          ))}
        </div>
      )}

      <div className={glassOuter}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-medium text-slate-400">Cash flow forecast</p>
          <select value={forecastMonths} onChange={(e) => setForecastMonths(Number(e.target.value))} className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-2.5 py-1 text-white text-[11px]">
            <option value={3}>3 months</option>
            {maxForecastMonths >= 6 && <option value={6}>6 months</option>}
            {maxForecastMonths >= 12 && <option value={12}>12 months</option>}
          </select>
        </div>
        <div className="space-y-2">
          {projection.forecast.map((month, idx) => {
            const maxB = Math.max(...projection.forecast.map((m) => Math.abs(m.endingBalance)), Math.abs(projection.currentBalance), 1);
            const w = (Math.abs(month.endingBalance) / maxB) * 100;
            const neg = month.endingBalance < 0;
            return (
              <div key={month.month}>
                <div className="flex items-center gap-3">
                  <span className="text-slate-500 w-8 shrink-0 text-[11px]">{month.monthName}</span>
                  <div className="flex-1 bg-white/[0.03] rounded-full h-1.5 overflow-hidden">
                    <div className={`h-1.5 rounded-full transition-all ${neg ? 'bg-red-500/60' : 'bg-emerald-500/50'}`} style={{ width: `${w}%` }} />
                  </div>
                  <span className={`text-[11px] font-medium w-16 text-right ${neg ? 'text-red-400' : 'text-slate-300'}`}>{fmt(month.endingBalance)}</span>
                  <button onClick={() => setShowForecastDetail(showForecastDetail === idx ? null : idx)} className="text-slate-700 hover:text-slate-400">
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showForecastDetail === idx ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {showForecastDetail === idx && (
                  <div className="ml-11 mr-20 mt-2 bg-white/[0.02] rounded-lg p-3 border border-white/[0.04] text-xs grid grid-cols-2 gap-x-4 gap-y-1.5">
                    <div className="flex justify-between"><span className="text-slate-500">Income</span><span className="text-white">+{fmt(month.projectedIncome)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Expenses</span><span className="text-white">-{fmt(month.projectedExpenses)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Subs</span><span className="text-white">-{fmt(month.subscriptionCosts)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Tax</span><span className="text-white">-{fmt(month.taxReserve)}</span></div>
                    {month.quarterlyTaxPayment > 0 && <div className="col-span-2 flex justify-between"><span className="text-slate-500">Quarterly Tax</span><span className="text-white">-{fmt(month.quarterlyTaxPayment)}</span></div>}
                    <div className="col-span-2 flex justify-between pt-1.5 border-t border-white/[0.04]">
                      <span className="text-slate-500">Net</span>
                      <span className={`font-semibold ${month.netCashFlow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{month.netCashFlow >= 0 ? '+' : ''}{fmt(month.netCashFlow)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {(projection.historical.incomeGrowthRate !== 0 || projection.forecast.some((m) => m.quarterlyTaxPayment > 0)) && (
          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-white/[0.04]">
            {projection.historical.incomeGrowthRate > 0 && (
              <span className="flex items-center gap-1 text-[11px] text-emerald-400">
                <TrendingUp className="w-3 h-3" /> Growing {projection.historical.incomeGrowthRate.toFixed(1)}%
              </span>
            )}
            {projection.historical.incomeGrowthRate < 0 && (
              <span className="flex items-center gap-1 text-[11px] text-red-400">
                <TrendingDown className="w-3 h-3" /> Down {Math.abs(projection.historical.incomeGrowthRate).toFixed(1)}%
              </span>
            )}
            {projection.forecast.some((m) => m.quarterlyTaxPayment > 0) && (() => {
              const tMonth = projection.forecast.find((m) => m.quarterlyTaxPayment > 0);
              return (
                <span className="flex items-center gap-1 text-[11px] text-slate-500">
                  <Calendar className="w-3 h-3" /> {fmt(tMonth.quarterlyTaxPayment)} tax due {tMonth.monthName}
                </span>
              );
            })()}
          </div>
        )}
      </div>

      <div className={glassOuter}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-medium text-slate-400">Tax readiness</p>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${taxPrepScore.score >= 80 ? 'text-emerald-400 bg-emerald-500/10' : taxPrepScore.score >= 60 ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10'}`}>
            Score {taxPrepScore.score}/100
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {[
            { label: 'Missed Deductions', value: fmt(deductionCoach.missedDeductionAmount) },
            { label: 'Tax Savings', value: fmt(deductionCoach.taxSavings) },
            { label: 'Unused Subs', value: `${fmt(subAnalysis.wastedMonthly)}/mo` },
          ].map((i) => (
            <div key={i.label} className="text-center">
              <p className="text-[10px] text-slate-500 mb-1">{i.label}</p>
              <p className="text-sm font-medium text-white">{i.value}</p>
            </div>
          ))}
        </div>
        {taxPlan.daysUntilNext <= 90 && (
          <div className="flex items-center justify-between py-3 border-t border-white/[0.04]">
            <div>
              <p className="text-xs text-white font-medium">Quarterly tax due {taxPlan.next.label}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{taxPlan.daysUntilNext} days away · {fmt(taxPlan.quarterlyPayment)}</p>
            </div>
            <span className={`text-[11px] px-2.5 py-1 rounded-full ${taxPlan.shortfall <= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
              {taxPlan.shortfall <= 0 ? 'Covered' : `${fmt(taxPlan.shortfall)} short`}
            </span>
          </div>
        )}
        {deductionCoach.candidates.length > 0 && (
          <div className="pt-3 border-t border-white/[0.04]">
            <p className="text-[10px] text-slate-500 mb-2">Potential deductions</p>
            <div className="space-y-1.5">
              {deductionCoach.candidates.slice(0, 5).map((item) => (
                <div key={item.id} className="flex items-center justify-between">
                  <p className="text-xs text-slate-300 truncate">{item.merchant}</p>
                  <p className="text-xs font-medium text-emerald-400 shrink-0 ml-3">{fmt(item.amount)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {alerts.length > 0 && (
        <div className={glassOuter}>
          <p className="text-xs font-medium text-slate-400 mb-3">Alerts</p>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div key={alert.id} className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${alert.severity === 'critical' ? 'text-red-300' : alert.severity === 'warning' ? 'text-amber-300' : 'text-slate-300'}`}>{alert.title}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{alert.message || alert.msg}</p>
                </div>
                <button onClick={() => dismiss(alert.id)} className="text-slate-700 hover:text-slate-400 text-xs shrink-0">&#x2715;</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(deductionCoach.taxSavings > 0 || subAnalysis.wastedMonthly > 0) && (
        <div className={`${glassOuter} text-center`}>
          <p className="text-[10px] text-slate-500 mb-1">Potential annual savings</p>
          <p className="text-xl font-semibold text-white">{fmt(deductionCoach.taxSavings + subAnalysis.wastedMonthly * 12)}</p>
        </div>
      )}
    </div>
  );
};
const QUANTRA_STREAM_URL = 'https://us-central1-receiptwise-prod.cloudfunctions.net/cfoChatStream';

const quantraToISO = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toISOString();
  }
  return typeof value === 'string' ? value : null;
};

const quantraMoneyExact = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const quantraPct = (n) => `${Number(n || 0).toFixed(1)}%`;

const renderQuantraInline = (text) =>
  text.replace(/\[\d+\]/g, '').split(/\*\*(.*?)\*\*/g).map((part, j) =>
    j % 2 === 1 ? <strong key={j} className="text-white font-semibold">{part}</strong> : part
  );

const QuantraMarkdown = ({ text, streaming }) => {
  if (!text && !streaming) return null;
  const lines = (text || '').split('\n');
  return (
    <div className="text-sm space-y-1.5">
      {lines.map((line, i) => {
        const t = line.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (t.startsWith('## ')) return <p key={i} className="text-white font-bold mt-2 mb-1">{t.slice(3)}</p>;
        if (t.startsWith('### ')) return <p key={i} className="text-white font-semibold mt-2 mb-0.5">{t.slice(4)}</p>;
        const numbered = t.match(/^(\d+)\.\s+(.*)/);
        if (numbered) return (
          <div key={i} className="flex gap-2 pl-1">
            <span className="text-violet-400 font-semibold shrink-0">{numbered[1]}.</span>
            <span>{renderQuantraInline(numbered[2])}</span>
          </div>
        );
        if (t.startsWith('- ') || t.startsWith('* ')) return (
          <div key={i} className="flex gap-2 pl-1">
            <span className="text-violet-400 shrink-0 mt-0.5">•</span>
            <span>{renderQuantraInline(t.slice(2))}</span>
          </div>
        );
        return <p key={i}>{renderQuantraInline(line)}</p>;
      })}
      {streaming && <span className="inline-block w-1.5 h-4 bg-violet-400 ml-0.5 animate-pulse align-middle" />}
    </div>
  );
};

const quantraMonthSpan = (items) => {
  const dates = (items || [])
    .map((d) => new Date(d.date || d.paidDate || d.createdAt))
    .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()));
  if (dates.length < 2) return 1;
  return Math.max(1, Math.round((Math.max(...dates) - Math.min(...dates)) / (30.44 * 864e5)));
};

const buildFinancialSnapshot = ({
  expenses = [], invoices = [], mileageTrips = [], subscriptions = [],
  bankAccounts = [], revenue = [], bills = [], budgets = [],
  taxVaultSettings = {}, userName = '',
}) => {
  const now = new Date();
  const todayStr = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  // Revenue from BOTH sources
  const paidInvoices = invoices.filter((i) => i.status === 'paid');
  const sentInvoices = invoices.filter((i) => i.status === 'sent');
  const overdueInvoices = sentInvoices.filter((i) => new Date(i.dueDate || i.date) < now);
  const draftInvoices = invoices.filter((i) => i.status === 'draft');

  const invoiceRevenue = paidInvoices.reduce((s, i) => s + Number(i.total || 0), 0);
  const revenueEntries = (revenue || []).reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalRevenue = invoiceRevenue + revenueEntries;
  const pendingRevenue = sentInvoices.reduce((s, i) => s + Number(i.total || 0), 0);
  const overdueRevenue = overdueInvoices.reduce((s, i) => s + Number(i.total || 0), 0);

  const allRevenueDates = [
    ...paidInvoices.map((i) => new Date(i.paidDate || i.date)),
    ...(revenue || []).map((r) => new Date(r.date || r.createdAt)),
  ].filter((d) => d instanceof Date && !Number.isNaN(d.getTime()));
  const revenueMonths = allRevenueDates.length >= 2
    ? Math.max(1, Math.round((Math.max(...allRevenueDates) - Math.min(...allRevenueDates)) / (30.44 * 864e5)))
    : allRevenueDates.length === 1 ? 1 : 0;
  const monthlyRevenue = revenueMonths > 0 ? totalRevenue / revenueMonths : 0;

  const paidWithDates = paidInvoices.filter((i) => i.paidDate && i.date);
  const avgDaysToPay = paidWithDates.length > 0
    ? paidWithDates.reduce((s, i) => {
      const days = Math.max(0, Math.floor((new Date(i.paidDate) - new Date(i.date)) / 864e5));
      return s + (Number.isFinite(days) ? days : 0);
    }, 0) / paidWithDates.length
    : 0;

  const clientMap = {};
  paidInvoices.forEach((i) => {
    const name = i.clientName || i.customer || 'Unknown';
    clientMap[name] = (clientMap[name] || 0) + Number(i.total || 0);
  });
  const topClients = Object.entries(clientMap).map(([name, rev]) => ({ name, rev })).sort((a, b) => b.rev - a.rev);

  const monthlyIncome = {};
  paidInvoices.forEach((inv) => {
    const d = new Date(inv.paidDate || inv.date);
    if (Number.isNaN(d.getTime())) return;
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthlyIncome[k] = (monthlyIncome[k] || 0) + Number(inv.total || 0);
  });
  (revenue || []).forEach((r) => {
    const d = new Date(r.date || r.createdAt);
    if (Number.isNaN(d.getTime())) return;
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthlyIncome[k] = (monthlyIncome[k] || 0) + Number(r.amount || 0);
  });
  const incomeTrend = Object.entries(monthlyIncome).sort().slice(-6);

  // Expenses
  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const taxDeductible = expenses.filter((e) => e.taxDeductible).reduce((s, e) => s + Number(e.amount || 0), 0);
  const expDates = expenses.map((e) => new Date(e.date)).filter((d) => !Number.isNaN(d.getTime()));
  const expMonths = expDates.length >= 2
    ? Math.max(1, Math.round((Math.max(...expDates) - Math.min(...expDates)) / (30.44 * 864e5)))
    : 1;
  const monthlyExpenses = totalExpenses / Math.max(1, expMonths);

  const catSpend = {};
  expenses.forEach((e) => { catSpend[e.category || 'Other'] = (catSpend[e.category || 'Other'] || 0) + Number(e.amount || 0); });
  const sortedCats = Object.entries(catSpend).sort(([, a], [, b]) => b - a);

  const monthlyExpenseMap = {};
  expenses.forEach((e) => {
    const d = new Date(e.date);
    if (Number.isNaN(d.getTime())) return;
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthlyExpenseMap[k] = (monthlyExpenseMap[k] || 0) + Number(e.amount || 0);
  });
  const expenseTrend = Object.entries(monthlyExpenseMap).sort().slice(-6);

  // Subscriptions
  const activeSubs = subscriptions.filter((s) => s.status === 'active');
  const monthlySubCost = activeSubs.reduce((s, sub) => {
    const cost = Number(sub.cost || sub.amount || 0);
    if (sub.billingCycle === 'yearly' || sub.billingCycle === 'annual') return s + cost / 12;
    if (sub.billingCycle === 'quarterly') return s + cost / 3;
    return s + cost;
  }, 0);

  // Bank
  const connectedBanks = bankAccounts.filter((a) => Number(a.balances?.current || a.balance || 0) !== 0 || a.name);
  const totalBalance = connectedBanks.reduce((s, a) => s + Number(a.balances?.current || a.balance || 0), 0);

  // Derived metrics
  const monthlyBurn = monthlyExpenses + monthlySubCost;
  const netProfit = totalRevenue - totalExpenses;
  const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
  const runway = (monthlyBurn > 0 && totalBalance > 0) ? Math.floor(totalBalance / monthlyBurn) : null;
  const effectiveHourly = revenueMonths > 0 ? totalRevenue / (revenueMonths * 160) : 0;

  // Mileage
  const totalMiles = mileageTrips.reduce((s, t) => s + Number(t.distance || t.totalMiles || 0), 0);
  const totalMileageDeduction = mileageTrips.reduce((s, t) => s + Number(t.deduction || 0), 0);

  // Bills
  const upcomingBills = (bills || []).filter((b) => {
    if (b.paid) return false;
    const daysUntil = Math.floor((new Date(b.nextDueDate || b.dueDate) - now) / 864e5);
    return daysUntil >= 0 && daysUntil <= 14;
  });

  const reservePct = taxVaultSettings?.reservePercent || 25;

  // Build snapshot
  let snap = `TODAY: ${todayStr}\n`;
  if (userName) snap += `USER: ${userName}\n`;

  // Key metrics
  if (totalRevenue > 0 || totalExpenses > 0 || connectedBanks.length > 0) {
    snap += `\n=== KEY METRICS ===`;
    if (totalRevenue > 0 && revenueMonths > 0) {
      snap += `\nTotal Revenue: ${quantraMoneyExact(totalRevenue)} over ~${revenueMonths} month${revenueMonths !== 1 ? 's' : ''}`;
      snap += `\nMonthly Revenue (avg): ${quantraMoneyExact(monthlyRevenue)}/mo`;
    }
    if (totalExpenses > 0) {
      snap += `\nMonthly Expenses (avg): ${quantraMoneyExact(monthlyExpenses)}/mo`;
    }
    if (activeSubs.length > 0) {
      snap += `\nMonthly Subscriptions: ${quantraMoneyExact(monthlySubCost)}/mo`;
    }
    if (monthlyBurn > 0) {
      snap += `\nTotal Monthly Burn: ${quantraMoneyExact(monthlyBurn)}/mo`;
    }
    if (totalRevenue > 0 && monthlyBurn > 0) {
      snap += `\nNet Monthly: ${quantraMoneyExact(monthlyRevenue - monthlyBurn)}/mo`;
      snap += `\nProfit Margin: ${quantraPct(profitMargin)}`;
      if (revenueMonths > 0) snap += `\nEffective Hourly Rate (160hr/mo): ${quantraMoneyExact(effectiveHourly)}/hr`;
    }
    if (connectedBanks.length > 0 && totalBalance > 0) {
      snap += `\nCash Balance: ${quantraMoneyExact(totalBalance)}`;
      if (runway !== null) snap += `\nCash Runway: ${runway > 100 ? '100+' : runway} months`;
    }
  }

  // Revenue detail
  if (totalRevenue > 0) {
    snap += `\n\n=== REVENUE (${quantraMoneyExact(totalRevenue)} total) ===`;
    if (invoiceRevenue > 0) snap += `\nFrom Invoices: ${quantraMoneyExact(invoiceRevenue)}`;
    if (revenueEntries > 0) snap += `\nFrom Revenue Entries: ${quantraMoneyExact(revenueEntries)}`;
    if (paidInvoices.length > 0) {
      snap += `\nPaid Invoices: ${paidInvoices.length}`;
      snap += `\nAvg Invoice Value: ${quantraMoneyExact(invoiceRevenue / paidInvoices.length)}`;
      if (avgDaysToPay > 0) snap += `\nAvg Days to Payment: ${Math.round(avgDaysToPay)}`;
    }
    if (sentInvoices.length > 0) snap += `\nPending Invoices: ${sentInvoices.length} (${quantraMoneyExact(pendingRevenue)})`;
    if (overdueInvoices.length > 0) snap += `\nOverdue Invoices: ${overdueInvoices.length} (${quantraMoneyExact(overdueRevenue)})`;
    if (draftInvoices.length > 0) snap += `\nDraft Invoices: ${draftInvoices.length}`;
  }

  // Client breakdown
  if (topClients.length > 0) {
    const top3Rev = topClients.slice(0, 3).reduce((s, c) => s + c.rev, 0);
    const concentration = invoiceRevenue > 0 ? (top3Rev / invoiceRevenue) * 100 : 0;
    snap += `\n\n=== TOP CLIENTS (concentration: ${quantraPct(concentration)} from top 3) ===`;
    topClients.slice(0, 5).forEach((c, i) => {
      snap += `\n${i + 1}. ${c.name}: ${quantraMoneyExact(c.rev)}`;
    });
  }

  // Revenue trend
  if (incomeTrend.length >= 2) {
    snap += `\n\n=== MONTHLY REVENUE TREND ===`;
    incomeTrend.forEach(([month, amt]) => { snap += `\n${month}: ${quantraMoneyExact(amt)}`; });
  }

  // Expenses
  if (totalExpenses > 0) {
    snap += `\n\n=== EXPENSES (${quantraMoneyExact(totalExpenses)} total) ===`;
    snap += `\nCount: ${expenses.length}`;
    snap += `\nTax Deductible: ${quantraMoneyExact(taxDeductible)} (${quantraPct((taxDeductible / totalExpenses) * 100)})`;
    if (totalRevenue > 0) snap += `\nNet Profit: ${quantraMoneyExact(netProfit)}`;

    if (sortedCats.length > 0) {
      snap += `\n\nBy Category:`;
      sortedCats.slice(0, 10).forEach(([cat, amt]) => { snap += `\n- ${cat}: ${quantraMoneyExact(amt)}`; });
    }

    if (expenseTrend.length >= 2) {
      snap += `\n\nMonthly Expense Trend:`;
      expenseTrend.forEach(([month, amt]) => { snap += `\n${month}: ${quantraMoneyExact(amt)}`; });
    }
  }

  // Overdue invoices
  if (overdueInvoices.length > 0) {
    snap += `\n\n=== OVERDUE INVOICES ===`;
    overdueInvoices.slice(0, 8).forEach((inv) => {
      snap += `\n- ${inv.clientName || 'Unknown'}: ${quantraMoneyExact(inv.total)} - ${Math.floor((now - new Date(inv.dueDate || inv.date)) / 864e5)} days overdue`;
    });
  }

  // Subscriptions
  if (activeSubs.length > 0) {
    snap += `\n\n=== ACTIVE SUBSCRIPTIONS (${activeSubs.length}, ${quantraMoneyExact(monthlySubCost)}/mo) ===`;
    activeSubs.sort((a, b) => Number(b.cost || 0) - Number(a.cost || 0)).slice(0, 12).forEach((sub) => {
      const cost = Number(sub.cost || sub.amount || 0);
      const monthly = (sub.billingCycle === 'yearly' || sub.billingCycle === 'annual') ? cost / 12 : cost;
      const lastUsed = quantraToISO(sub.lastUsedAt || sub.lastUsed);
      let usageNote = '';
      if (lastUsed) {
        const daysSince = Math.floor((now - new Date(lastUsed)) / 864e5);
        usageNote = daysSince > 30 ? ' UNUSED 30+ days' : ` (used ${daysSince}d ago)`;
      }
      snap += `\n- ${sub.name || 'Unknown'}: ${quantraMoneyExact(monthly)}/mo${usageNote}`;
    });
  }

  // Bank accounts
  if (connectedBanks.length > 0) {
    snap += `\n\n=== BANK ACCOUNTS ===`;
    connectedBanks.forEach((acc) => {
      snap += `\n- ${acc.name || acc.officialName || 'Account'}: ${quantraMoneyExact(acc.balances?.current || acc.balance || 0)}`;
    });
  }

  // Mileage
  if (mileageTrips.length > 0) {
    snap += `\n\n=== MILEAGE ===`;
    snap += `\nTrips: ${mileageTrips.length}, Total Miles: ${totalMiles.toFixed(1)}, Deduction: ${quantraMoneyExact(totalMileageDeduction)}`;
  }

  // Upcoming bills
  if (upcomingBills.length > 0) {
    snap += `\n\n=== UPCOMING BILLS (next 14 days) ===`;
    upcomingBills.forEach((b) => {
      snap += `\n- ${b.name || b.category || 'Bill'}: ${quantraMoneyExact(b.amount)} - due in ${Math.floor((new Date(b.nextDueDate || b.dueDate) - now) / 864e5)} days`;
    });
  }

  // Budgets
  if ((budgets || []).length > 0) {
    const totalBudgeted = budgets.reduce((s, b) => {
      const amt = Number(b.amount || b.limit || 0);
      // Treat annual budgets as monthly (÷12) for a comparable monthly figure
      const monthly = (b.period === 'annual' || b.period === 'yearly') ? amt / 12 : amt;
      return s + monthly;
    }, 0);
    snap += `\n\n=== BUDGETS (${budgets.length} categories, ${quantraMoneyExact(totalBudgeted)}/mo total) ===`;
    budgets.slice(0, 10).forEach((b) => {
      const amt = Number(b.amount || b.limit || 0);
      const monthly = (b.period === 'annual' || b.period === 'yearly') ? amt / 12 : amt;
      const spent = Number(b.spent || b.currentSpend || 0);
      const pct = monthly > 0 ? Math.round((spent / monthly) * 100) : 0;
      snap += `\n- ${b.category || b.name || 'Budget'}: ${quantraMoneyExact(monthly)}/mo budget${spent > 0 ? `, ${quantraMoneyExact(spent)} spent (${pct}%)` : ''}`;
    });
  }

  // Tax
  if (totalExpenses > 0 || mileageTrips.length > 0) {
    snap += `\n\n=== TAX ===`;
    snap += `\nTax Reserve Rate: ${reservePct}%`;
    if (taxDeductible > 0) snap += `\nDeductible Expenses: ${quantraMoneyExact(taxDeductible)}`;
    if (totalMileageDeduction > 0) snap += `\nMileage Deduction: ${quantraMoneyExact(totalMileageDeduction)}`;
    snap += `\nTotal Deductions: ${quantraMoneyExact(taxDeductible + totalMileageDeduction)}`;
    if (totalRevenue > 0) snap += `\nEstimated Quarterly Tax (${reservePct}%): ~${quantraMoneyExact((netProfit * (reservePct / 100)) / 4)}`;
  }

  return snap;
};

const streamQuantraResponse = async ({ message, financialSnapshot, conversationHistory = [], onToken, onDone, onError, signal }) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Not authenticated');

    const res = await fetch(QUANTRA_STREAM_URL, {
      method: 'POST', signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message, financialContext: financialSnapshot, conversationHistory }),
    });

    if (!res.ok || !res.body) throw new Error(`Stream failed (${res.status})`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';
      for (const event of events) {
        const dataLine = event.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        const data = dataLine.slice(5).trim();
        if (data === '[DONE]') { onDone(fullText); return; }
        try {
          const parsed = JSON.parse(data);
          if (parsed.text) { fullText += parsed.text; onToken(fullText); }
        } catch {
          // skip malformed
        }
      }
    }
    onDone(fullText);
  } catch (err) {
    if (err.name !== 'AbortError') onError(err);
  }
};

const AI_CONSENT_KEY = 'centris_ai_consent_v1';
const AI_CONSENT_QUANTRA_KEY = 'centris_ai_consent_quantra_v1';
const AI_CONSENT_IDEAS_KEY = 'centris_ai_consent_ideas_v1';
const AI_CONSENT_HUSTLE_KEY = 'centris_ai_consent_hustle_v1';

const QuantraAgent = ({
  user, expenses = [], invoices = [], mileageTrips = [],
  subscriptions = [], bankAccounts = [], revenue = [],
  bills = [], budgets = [], taxVaultSettings = {},
  monthlyGoal = null,
  perms = null, onCanProceed = null, paygPrices = null,
  onInsights = null, showToast = null,
}) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [aiConsentGiven, setAiConsentGiven] = useState(() => {
    try { return localStorage.getItem(AI_CONSENT_QUANTRA_KEY) === 'true' || localStorage.getItem(AI_CONSENT_KEY) === 'true'; } catch { return false; }
  });
  const [aiConsentDeclined, setAiConsentDeclined] = useState(false);
  const [proactiveInsights, setProactiveInsights] = useState([]);
  const chatEndRef = useRef(null);
  const abortRef = useRef(null);

  const quickActions = useMemo(() => [
    { label: "How's my cash flow?", query: 'What is my current cash flow situation? Am I in good shape?', icon: DollarSign },
    { label: 'Am I profitable?', query: 'Break down my profitability - revenue vs expenses, margins, and trends.', icon: TrendingUp },
    { label: 'Tax deductions?', query: 'What are my total tax deductions? Am I missing any opportunities?', icon: Receipt },
    { label: 'Spending analysis', query: 'Analyze my spending patterns - what are my biggest costs and where can I optimize?', icon: AlertTriangle },
  ], []);

  const financialSnapshot = useMemo(() => {
    const base = buildFinancialSnapshot({
      expenses, invoices, mileageTrips, subscriptions, bankAccounts,
      revenue, bills, budgets, taxVaultSettings,
      userName: user?.name || user?.displayName || '',
    });
    return monthlyGoal
      ? base + `\n\nMONTHLY REVENUE GOAL: $${monthlyGoal.toLocaleString()}`
      : base;
  }, [expenses, invoices, mileageTrips, subscriptions, bankAccounts, revenue, bills, budgets, taxVaultSettings, user, monthlyGoal]);

  useEffect(() => {
    if (!user?.uid) return;
    const insights = [];
    const now = new Date();
    const paidInv = invoices.filter((i) => i.status === 'paid');
    const totalRev = paidInv.reduce((s, i) => s + Number(i.total || 0), 0);
    const totalExp = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
    const moExp = totalExp / Math.max(1, quantraMonthSpan(expenses));
    const moSub = subscriptions.filter((s) => s.status === 'active').reduce((s, sub) => s + (sub.billingCycle === 'yearly' ? Number(sub.cost || 0) / 12 : Number(sub.cost || 0)), 0);
    const moBurn = moExp + moSub;
    const bal = bankAccounts.reduce((s, a) => s + Number(a.balances?.current || 0), 0);
    const rwy = moBurn > 0 ? Math.floor(bal / moBurn) : 999;
    const moRev = totalRev / Math.max(1, quantraMonthSpan(paidInv));
    const margin = totalRev > 0 ? ((totalRev - totalExp) / totalRev) * 100 : 0;
    const deductible = expenses.filter((e) => e.taxDeductible).reduce((s, e) => s + Number(e.amount || 0), 0);

    if (rwy < 3 && bal > 0) insights.push({ type: 'urgent', icon: 'AlertCircle', title: 'Low Cash Runway', message: `${rwy} month${rwy !== 1 ? 's' : ''} of runway at current burn.`, priority: 1 });
    else if (rwy < 6 && rwy > 0) insights.push({ type: 'warning', icon: 'AlertTriangle', title: 'Cash Runway Warning', message: `${rwy} months of runway.`, priority: 2 });

    const overdue = invoices.filter((i) => i.status === 'sent' && new Date(i.dueDate || i.date) < now);
    if (overdue.length) insights.push({ type: 'warning', icon: 'DollarSign', title: 'Overdue Invoices', message: `${quantraMoneyExact(overdue.reduce((s, i) => s + Number(i.total || 0), 0))} in ${overdue.length} overdue invoice${overdue.length !== 1 ? 's' : ''}.`, priority: 2 });

    if (moRev > 0 && moSub > moRev * 0.1) insights.push({ type: 'info', icon: 'Bell', title: 'High Subscription Costs', message: `Subscriptions: ${quantraMoneyExact(moSub)}/mo (${quantraPct((moSub / moRev) * 100)} of revenue).`, priority: 3 });
    if (margin < 20 && totalRev > 0) insights.push({ type: 'warning', icon: 'TrendingDown', title: 'Low Profit Margin', message: `Margin at ${quantraPct(margin)}. Aim for 25-40%.`, priority: 2 });
    if (totalExp > 0 && deductible < totalExp * 0.7) insights.push({ type: 'info', icon: 'FileText', title: 'Tax Deduction Opportunity', message: `Only ${quantraPct((deductible / totalExp) * 100)} of expenses marked deductible.`, priority: 3 });

    const sorted = insights.sort((a, b) => a.priority - b.priority);
    setProactiveInsights(sorted);
    onInsights?.(sorted);
  }, [user, expenses, invoices, subscriptions, bankAccounts]);

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      const result = await loadQuantraChatHistory(user.uid);
      if (result.success && result.messages?.length) setChatMessages(result.messages);
    })();
  }, [user?.uid]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    if (!user?.uid || !chatMessages.length) return;
    const t = setTimeout(() => saveQuantraChatHistory(user.uid, chatMessages), 1000);
    return () => clearTimeout(t);
  }, [user?.uid, chatMessages]);

  const handleSend = useCallback(async (incoming) => {
    const message = (typeof incoming === 'string' ? incoming : chatInput)?.trim();
    if (!message || isThinking) return;
    logEvent('quantra_message_sent', { uid: user?.uid });

    if (user?.uid === 'demo-user') {
      setChatMessages((prev) => [...prev, { role: 'user', content: message, timestamp: new Date() }]);
      setChatInput('');
      setIsThinking(true);
      setTimeout(() => {
        setChatMessages((prev) => [...prev, { role: 'cfo', content: "I'm Quantra, your AI financial advisor. In this demo I can see Rivera Design Studio's data — $9.5k in revenue this month, $1.1k in expenses, and a solid 88% profit margin.\n\nTo get personalized AI advice based on your actual business, **create a free account** and I'll analyze your real numbers. I can help with cash flow, tax strategy, pricing, and more.", timestamp: new Date(), streaming: false }]);
        setIsThinking(false);
      }, 1200);
      return;
    }

    if (typeof onCanProceed === 'function') {
      const ok = await onCanProceed('quantraMessage', 'quantraMessagesPerMonth');
      if (!ok) return;
    }

    setChatMessages((prev) => [...prev, { role: 'user', content: message, timestamp: new Date() }]);
    setChatInput('');
    setIsThinking(true);
    setChatMessages((prev) => [...prev, { role: 'cfo', content: '', timestamp: new Date(), streaming: true }]);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);

    await streamQuantraResponse({
      message, financialSnapshot, signal: controller.signal,
      conversationHistory: chatMessages.filter(m => m.role && m.content && m.content.trim()).map(m => ({ role: m.role, content: m.content })),
      onToken: (fullText) => {
        setChatMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { ...u[u.length - 1], content: fullText };
          return u;
        });
      },
      onDone: (fullText) => {
        clearTimeout(timeout);
        setChatMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { ...u[u.length - 1], content: fullText || "I couldn't generate a response. Please try again.", streaming: false };
          return u;
        });
        setIsThinking(false);
      },
      onError: (err) => {
        clearTimeout(timeout);
        console.error('Quantra error:', err);
        setChatMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { ...u[u.length - 1], content: 'I ran into an issue connecting. Please try again in a moment.', streaming: false };
          return u;
        });
        setIsThinking(false);
      },
    });
  }, [chatInput, isThinking, financialSnapshot, onCanProceed]);

  const handleClear = useCallback(async () => {
    setChatMessages([]);
    if (user?.uid) await clearQuantraChatHistory(user.uid);
    showToast?.('Chat history cleared', 'success');
  }, [user?.uid]);

  const InsightIcon = ({ name, className }) => {
    const icons = { AlertCircle, AlertTriangle, DollarSign, Bell, TrendingDown, FileText, Clock };
    const I = icons[name] || AlertCircle;
    return <I className={className} />;
  };

  if (aiConsentDeclined) {
    return (
      <div className="max-w-lg mx-auto mt-8 text-center space-y-3 p-6">
        <Brain className="w-8 h-8 text-slate-600 mx-auto" />
        <p className="text-sm text-slate-400">Quantra AI is unavailable without data sharing consent.</p>
        <button onClick={() => setAiConsentDeclined(false)} className="text-xs text-violet-400 underline">Review consent again</button>
      </div>
    );
  }

  if (!aiConsentGiven) {
    return (
      <div className="max-w-lg mx-auto mt-8">
        <div className="centris-card rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <Brain className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">Quantra AI — Data Sharing Notice</p>
              <p className="text-xs text-slate-400">Permission required before continuing</p>
            </div>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-white">What data is shared:</p>
            <p className="text-xs text-slate-300 leading-relaxed">A summary of your financial totals — expenses, revenue, invoices, and mileage totals. No raw transactions, bank credentials, or personal identifiers beyond what you have entered in CENTRIS.</p>
            <p className="text-xs font-semibold text-white mt-2">Who it is shared with:</p>
            <p className="text-xs text-slate-300 leading-relaxed"><span className="text-white font-semibold">Anthropic</span> (claude.ai) — a third-party AI company. This data is sent to Anthropic's API to generate your financial answers. Anthropic does not use API data to train their models.</p>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            By tapping "I Agree", you consent to sharing the above data with Anthropic. You can review Anthropic's privacy policy at <span className="text-violet-400">anthropic.com/privacy</span> and our full privacy policy at <span className="text-violet-400">centrisfinance.com/privacy</span>.
          </p>
          <div className="flex gap-3 pt-1">
            <button
              onClick={() => {
                try { localStorage.setItem(AI_CONSENT_QUANTRA_KEY, 'true'); localStorage.setItem(AI_CONSENT_KEY, 'true'); } catch {}
                setAiConsentGiven(true);
              }}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold"
            >
              I Agree — Continue
            </button>
            <button onClick={() => setAiConsentDeclined(true)} className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-400 text-sm">
              No Thanks
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      <div className="flex flex-wrap gap-2 mb-1">
        {quickActions.map((action, idx) => (
          <button key={idx} onClick={() => handleSend(action.query)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-slate-300 border border-white/[0.08] hover:border-violet-500/40 hover:text-white transition-all" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <action.icon className="w-3 h-3 text-violet-400" />{action.label}
          </button>
        ))}
      </div>

      <div className="centris-card rounded-xl overflow-hidden">
        {chatMessages.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 border-b border-white/[0.04]">
            <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-violet-400" /><span className="text-xs font-medium text-slate-400">{chatMessages.length} messages</span></div>
            <button onClick={handleClear} className="text-xs text-slate-500 hover:text-red-400 transition-colors">Clear history</button>
          </div>
        )}

        <div className="h-96 overflow-y-auto p-6 space-y-4">
          {chatMessages.length === 0 && (
            <div className="text-center py-12">
              <div className="relative inline-block mb-5">
                <div className="absolute inset-0 rounded-2xl blur-xl opacity-30" style={{ background: 'linear-gradient(135deg, #8b5cf6, #ec4899)' }} />
                <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center mx-auto"><Brain className="w-8 h-8 text-white" /></div>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Hey{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! I'm Quantra.</h3>
              <p className="text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">Your AI financial advisor. I can see all your data - expenses, invoices, subscriptions, bank balances, everything. Ask me anything.</p>
              {perms?.priorityAICFO && (
                <div className="mt-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-semibold">
                    &#9733; Priority AI CFO
                  </span>
                </div>
              )}
            </div>
          )}

          {chatMessages.map((msg, idx) => (
            <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'cfo' && (
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div className="bg-gradient-to-br from-violet-600/20 to-purple-600/20 p-2 rounded-xl border border-violet-500/10"><Brain className={`w-5 h-5 text-violet-400 ${msg.streaming ? 'animate-pulse' : ''}`} /></div>
                  <span className="text-[9px] text-violet-400/60 font-medium">Quantra</span>
                </div>
              )}
              <div className={`max-w-[80%] rounded-lg p-4 ${msg.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-slate-700/50 text-slate-200'}`}>
                {msg.role === 'cfo'
                  ? <QuantraMarkdown text={msg.content} streaming={msg.streaming} />
                  : <p className="text-sm">{msg.content}</p>}
              </div>
            </div>
          ))}

          {isThinking && chatMessages.length > 0 && chatMessages[chatMessages.length - 1]?.content === '' && (
            <div className="flex gap-3">
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <div className="bg-gradient-to-br from-violet-600/20 to-purple-600/20 p-2 rounded-xl border border-violet-500/10"><Brain className="w-5 h-5 text-violet-400 animate-pulse" /></div>
                <span className="text-[9px] text-violet-400/60 font-medium">Quantra</span>
              </div>
              <div className="centris-card rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <p className="text-sm text-slate-400">Analyzing your data...</p>
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div className="border-t border-slate-700 p-4">
          {perms?.tier === 'payg' && (
            <div className="mb-2">
              <PaygCostBadge action="quantraMessage" user={user} paygPrices={paygPrices} />
            </div>
          )}
          <div className="flex gap-2">
            <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()} placeholder="Ask Quantra anything about your finances..." className="flex-1 min-w-0 centris-card rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-violet-500 focus:outline-none" />
            <button onClick={() => handleSend()} disabled={!chatInput.trim() || isThinking} className="bg-violet-600 text-white px-6 py-2 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Send className="w-4 h-4" /></button>
          </div>
          <p className="text-[10px] text-slate-600 mt-2 text-center">Quantra provides financial insights for informational purposes only. Not financial advice.</p>
        </div>
      </div>
    </div>
  );
};
const ReceiptReview = ({ ocrData, imageUrl, onConfirm, onCancel }) => {
  const [editedData, setEditedData] = useState({
    merchant: ocrData?.merchant || '',
    amount: Number(ocrData?.amount || ocrData?.total || 0),
    category: ocrData?.category || 'Other',
    date: ocrData?.date || new Date().toISOString().split('T')[0],
    taxDeductible: Boolean(ocrData?.taxDeductible),
    notes: ocrData?.notes || '',
  });

  useEffect(() => {
    setEditedData({
      merchant: ocrData?.merchant || '',
      amount: Number(ocrData?.amount || ocrData?.total || 0),
      category: ocrData?.category || 'Other',
      date: ocrData?.date || new Date().toISOString().split('T')[0],
      taxDeductible: Boolean(ocrData?.taxDeductible),
      notes: ocrData?.notes || '',
    });
  }, [ocrData]);

  const updateField = (field, value) => setEditedData((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    if (!editedData.merchant || !Number.isFinite(Number(editedData.amount)) || Number(editedData.amount) <= 0) return;
    onConfirm({
      merchant: editedData.merchant,
      amount: Number(editedData.amount),
      category: editedData.category,
      date: editedData.date,
      taxDeductible: editedData.taxDeductible,
      notes: editedData.notes,
    });
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
      <div className="centris-card rounded-2xl max-w-3xl w-full p-6">
        <h3 className="text-xl font-bold text-white mb-4">Review Scanned Receipt</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            {imageUrl ? (
              <img src={imageUrl} alt="Scanned receipt" className="w-full max-h-96 object-contain rounded-xl border border-slate-700" />
            ) : (
              <div className="centris-card w-full h-64 rounded-xl flex items-center justify-center text-slate-500 text-sm">No preview</div>
            )}
          </div>
          <div className="space-y-3">
            <input type="text" value={editedData.merchant} onChange={(e) => updateField('merchant', e.target.value)} placeholder="Merchant" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
            <input type="number" value={editedData.amount} onChange={(e) => updateField('amount', e.target.value)} placeholder="Amount" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
            <input type="date" value={editedData.date} onChange={(e) => updateField('date', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
            <input type="text" value={editedData.category} onChange={(e) => updateField('category', e.target.value)} placeholder="Category" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
            <textarea value={editedData.notes} onChange={(e) => updateField('notes', e.target.value)} placeholder="Notes (optional)" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm h-24 resize-none" />
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={editedData.taxDeductible} onChange={(e) => updateField('taxDeductible', e.target.checked)} />
              Tax deductible
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-200 border border-slate-700">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700" disabled={!editedData.merchant || Number(editedData.amount) <= 0}>
            Save Receipt
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const SubscriptionTracker = ({
  user,
  expenses,
  onUnknownPatterns,
  onOpenCfoPopup,
  onExtensionPairedChange,
  showToast,
  openBankStatementSignal = 0,
}) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('cost');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAutoDetect, setShowAutoDetect] = useState(false);
  const [showBankAnalyzer, setShowBankAnalyzer] = useState(false);
  useEffect(() => {
    if (openBankStatementSignal > 0) setShowBankAnalyzer(true);
  }, [openBankStatementSignal]);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showDetectDropdown, setShowDetectDropdown] = useState(false);
  const [showExtensionPanel, setShowExtensionPanel] = useState(false);
  const [learnedKeywords, setLearnedKeywords] = useState([]); // Learned keywords from Firebase
  const [learnedKeywordsLoaded, setLearnedKeywordsLoaded] = useState(false);
  const [resolvedUnknownNames, setResolvedUnknownNames] = useState([]); // Unknown patterns already answered
  const [resolvedUnknownLoaded, setResolvedUnknownLoaded] = useState(false);
  const [dismissedSubNames, setDismissedSubNames] = useState(new Set());
  const [pairingCode, setPairingCode] = useState('');
  const [pairingExpiresAt, setPairingExpiresAt] = useState(null);
  const [pairingStatus, setPairingStatus] = useState('');
  const [extensionPaired, setExtensionPaired] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const hasDetectedSubscriptions = React.useRef(false);
  const lastDetectKey = React.useRef('');
  const isDetecting = React.useRef(false);
  
  const [newSubscription, setNewSubscription] = useState({
    name: '',
    cost: 0,
    billingCycle: 'monthly',
    category: 'Software',
    status: 'active',
    lastUsed: new Date().toISOString().split('T')[0],
    renewalDate: '',
    website: '',
    notes: ''
  });

  // Load subscriptions from Firestore on mount
  useEffect(() => {
    if (user) {
      loadLearnedKeywordsFromFirestore(); // NEW: Load learned keywords
      loadResolvedUnknownPatterns();
      // Load dismissed subscription names
      (async () => {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db: firestoreDb } = await import('./firebase');
          const ref = doc(firestoreDb, 'users', user.uid, 'settings', 'dismissedSubscriptions');
          const snap = await getDoc(ref);
          if (snap.exists()) {
            setDismissedSubNames(new Set(snap.data().names || []));
          }
        } catch (e) {
          console.error('Failed to load dismissed subscriptions:', e);
        }
      })();
    }
  }, [user]);

  // Reload resolved patterns when Quantra popup confirms/declines
  useEffect(() => {
    if (!user?.uid) return undefined;
    const handler = () => { loadResolvedUnknownPatterns(); };
    window.addEventListener('quantra-resolved-updated', handler);
    return () => window.removeEventListener('quantra-resolved-updated', handler);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setSubscriptions([]);
      return undefined;
    }
    if (user.uid === 'demo-user') return undefined; // demo mode: data already set

    const subsRef = collection(db, 'users', user.uid, 'subscriptions');
    const unsubscribe = onSnapshot(
      subsRef,
      async (snapshot) => {
        const incoming = [];
        snapshot.forEach((docSnap) => {
          incoming.push({ id: docSnap.id, ...docSnap.data() });
        });
        const deduped = await dedupeSubscriptions(incoming);
        setSubscriptions(deduped);
      },
      (error) => {
        console.error('Realtime subscription listener failed:', error);
      }
    );

    return () => unsubscribe();
  }, [user]);
  // Listen for extension usage and merge "last used" onto in-memory subscriptions.
  // Primary source is subscription_usage, with extension_usage retained as fallback.
  useEffect(() => {
    if (!user?.uid) {
      setExtensionPaired(false);
      return undefined;
    }
    if (user.uid === 'demo-user') return undefined; // demo mode

    const buildUsageMap = (snapshot) => {
      const usageMap = new Map();
      snapshot.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const rawName = String(data.serviceName || data.service || decodeURIComponent(docSnap.id || "")).trim();
        const key = normalizeServiceKey(rawName);
        if (!key) return;

        const rawLastUsed = data.lastUsed || data.lastUsedAt;
        const parsedDate = rawLastUsed ? new Date(rawLastUsed) : null;
        if (!parsedDate || Number.isNaN(parsedDate.getTime())) return;

        const existing = usageMap.get(key);
        if (!existing || parsedDate > existing.lastUsedDate) {
          usageMap.set(key, {
            lastUsed: parsedDate.toISOString(),
            lastUsedDate: parsedDate,
            lastVisitedUrl: data.lastVisitedUrl || data.url || "",
          });
        }
      });
      return usageMap;
    };

    const findUsageForSubscription = (usageMap, subscriptionName) => {
      const subKey = normalizeServiceKey(subscriptionName || "");
      if (!subKey) return null;

      const exact = usageMap.get(subKey);
      if (exact) return exact;

      for (const [usageKey, usage] of usageMap.entries()) {
        if (!usageKey) continue;
        if (subKey.includes(usageKey) || usageKey.includes(subKey)) {
          return usage;
        }
      }

      return null;
    };

    const applyUsageMapToSubscriptions = (usageMap, sourceLabel) => {
      if (usageMap.size === 0) return;
      setSubscriptions((prevSubs) =>
        prevSubs.map((sub) => {
          const usage = findUsageForSubscription(usageMap, sub.name);
          if (!usage) return sub;

          const currentDate = sub.lastUsed ? new Date(sub.lastUsed) : null;
          const hasCurrent = currentDate && !Number.isNaN(currentDate.getTime());
          const nextLastUsed = hasCurrent && currentDate >= usage.lastUsedDate
            ? sub.lastUsed
            : usage.lastUsed;
          return {
            ...sub,
            lastUsed: nextLastUsed,
            lastUsedAt: usage.lastUsed,
            lastUsedSource: "extension",
            extensionTracked: true,
            extensionSync: true,
            extensionSourceCollection: sourceLabel,
            lastVisitedUrl: usage.lastVisitedUrl || sub.lastVisitedUrl || "",
          };
        })
      );
    };

    const subscriptionUsageRef = collection(db, "users", user.uid, "subscription_usage");
    const extensionUsageRef = collection(db, "users", user.uid, "extension_usage");
    let hasSubscriptionUsage = false;
    let hasExtensionUsage = false;

    const unsubscribeSubscriptionUsage = onSnapshot(
      subscriptionUsageRef,
      (snapshot) => {
        hasSubscriptionUsage = !snapshot.empty;
        setExtensionPaired(hasSubscriptionUsage || hasExtensionUsage);
        applyUsageMapToSubscriptions(buildUsageMap(snapshot), "subscription_usage");
      },
      (error) => {
        console.error("subscription_usage listener error:", error);
      }
    );

    const unsubscribeExtensionUsage = onSnapshot(
      extensionUsageRef,
      (snapshot) => {
        hasExtensionUsage = !snapshot.empty;
        setExtensionPaired(hasSubscriptionUsage || hasExtensionUsage);
        applyUsageMapToSubscriptions(buildUsageMap(snapshot), "extension_usage");
      },
      (error) => {
        console.error("extension_usage listener error:", error);
      }
    );

    return () => {
      unsubscribeSubscriptionUsage();
      unsubscribeExtensionUsage();
    };
  }, [user]);

  useEffect(() => {
    onExtensionPairedChange?.(extensionPaired);
  }, [extensionPaired, onExtensionPairedChange]);

  // NEW: Load learned keywords
  const loadLearnedKeywordsFromFirestore = async () => {
    const keywords = await LearnedKeywordsManager.getLearnedKeywords(user.uid);
    setLearnedKeywords(keywords);
    setLearnedKeywordsLoaded(true);
    console.log('Loaded learned keywords:', keywords.length);
  };

  const dedupeSubscriptions = async (subs) => {
    const groups = new Map();
    for (const sub of subs) {
      const key = normalizeServiceKey(sub.name);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(sub);
    }

    const deletions = [];
    const keepers = [];
    for (const [, group] of groups) {
      if (group.length === 1) {
        keepers.push(group[0]);
        continue;
      }
      const sorted = [...group].sort((a, b) => {
        const aTime = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.lastUsed ? new Date(a.lastUsed).getTime() : 0);
        const bTime = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.lastUsed ? new Date(b.lastUsed).getTime() : 0);
        return bTime - aTime;
      });
      const [keep, ...dupes] = sorted;
      keepers.push(keep);
      for (const dup of dupes) deletions.push(deleteSubscription(user.uid, dup.id));
    }

    if (deletions.length > 0) {
      await Promise.all(deletions);
      console.log('Deduped subscriptions deleted:', deletions.length);
    }

    return keepers;
  };

  const loadResolvedUnknownPatterns = async () => {
    const result = await loadSuggestedKeywords(user.uid);
    if (result.success) {
      const resolved = new Set();
      result.keywords
        .filter(k => k.status && k.status !== 'pending')
        .forEach((k) => {
          const raw = k.merchantName || k.serviceName || k.keyword || '';
          if (!raw) return;
          resolved.add(normalizeServiceKey(raw));
          resolved.add(raw.toLowerCase().replace(/[^a-z0-9]/g, ''));
          resolved.add(raw.toLowerCase().trim());
        });
      const resolvedList = [...resolved].filter(Boolean);
      setResolvedUnknownNames(resolvedList);
      setResolvedUnknownLoaded(true);
      return resolvedList;
    }
    setResolvedUnknownLoaded(true);
    return [];
  };

  // Close detect dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDetectDropdown && !event.target.closest('.detect-dropdown-container')) {
        setShowDetectDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDetectDropdown]);

  const loadSubscriptionsFromFirestore = async () => {
    console.log('Loading subscriptions from Firestore...');
    const result = await loadSubscriptions(user.uid);
    if (result.success) {
      console.log('Loaded subscriptions:', result.subscriptions.length);
      const deduped = await dedupeSubscriptions(result.subscriptions);
      setSubscriptions(deduped);
    } else {
      console.error('Failed to load subscriptions:', result.error);
    }
  };

  const generatePairingCode = async () => {
    if (!user) return;

    setPairingStatus('Generating...');
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setPairingStatus('Please log in again.');
        return;
      }

      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        'https://us-central1-receiptwise-prod.cloudfunctions.net/createPairingCode',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to create pairing code');
      }

      const code = String(payload.code || '').trim();
      const expires = payload.expiresAt
        ? new Date(payload.expiresAt)
        : new Date(Date.now() + 10 * 60 * 1000);
      if (!code) {
        throw new Error('Pairing service returned an empty code');
      }

      setPairingCode(code);
      setPairingExpiresAt(expires);
      localStorage.setItem('receiptwise_pairing_code', code);
      localStorage.setItem('receiptwise_user_id', user.uid);
      setPairingStatus('Code ready. Enter it in the extension within 10 minutes.');
    } catch (error) {
      console.error('Failed to create pairing code:', error);
      setPairingStatus(`Failed to create code: ${error.message || 'Please try again.'}`);
    }
  };

  // Master list of subscription service keywords
  // Used by both normalizeServiceName and isKnownSubscriptionService
  const serviceKeywords = SHARED_SERVICE_KEYWORDS;
  // Helper function to normalize and identify service names using keyword matching
  const normalizeServiceName = (merchantName) => {
    const merchant = normalizeForMatch(merchantName);

    // Check each service's keywords
    // Use safer matching for short/ambiguous keywords to avoid false positives (e.g., "max")
    const safeWordBoundaryMatch = (haystack, needle) => {
      if (!needle) return false;
      const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Word boundary on both sides when possible
      const re = new RegExp(`(^|\\b|\\s)${escaped}(\\b|\\s|$)`, 'i');
      return re.test(haystack);
    };

    for (const [serviceName, keywords] of serviceKeywords) {
      for (const keyword of keywords) {
        const k = normalizeForMatch(String(keyword || ''));
        if (!k) continue;

        // If the keyword is short/ambiguous, require word-boundary style match
        const isShort = k.length <= 3;
        const isAmbiguous = ['max'].includes(k);
        const matched = (isShort || isAmbiguous)
          ? safeWordBoundaryMatch(merchant, k)
          : merchant.includes(k);

        if (matched) return serviceName;
      }
    }

// If not a known service, try to extract clean company name
    // Remove common payment processor indicators
    let cleaned = merchant
      .replace(/\*+/g, ' ')
      .replace(/\d{10,}/g, '') // Remove long phone numbers
      .replace(/\b\d{3,4}\b/g, '') // Remove 3-4 digit codes at word boundaries
      .replace(/[^a-z0-9\s&'-]/g, ' ') // Keep &, ', - for company names
      .replace(/\s+/g, ' ')
      .trim();

    // Handle multi-word company names better
    // Take first 2-3 words for company names
    const words = cleaned.split(' ').filter(w => w.length > 1); // Minimum 2 chars per word
    
    if (words.length === 0) return 'Unknown Service';
    if (words.length === 1) {
      // Single word - capitalize it
      return words[0].charAt(0).toUpperCase() + words[0].slice(1);
    }
    
    // Multi-word - take up to 3 words and capitalize each
    const companyName = words
      .slice(0, 3)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return companyName;
  };

  const inferBillingCycle = (avgDaysBetween) => {
    if (!Number.isFinite(avgDaysBetween)) return 'unknown';
    if (avgDaysBetween >= 20 && avgDaysBetween <= 40) return 'monthly';
    if (avgDaysBetween >= 70 && avgDaysBetween <= 110) return 'quarterly';
    if (avgDaysBetween >= 320 && avgDaysBetween <= 420) return 'yearly';
    return 'unknown';
  };

  const median = (nums) => {
    const arr = [...nums].sort((a, b) => a - b);
    const mid = Math.floor(arr.length / 2);
    return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
  };

  // Auto-detect subscriptions from bank statements (once per new expense set)
  useEffect(() => {
    const detectKey = expenses.length > 0
      ? `${expenses.length}-${expenses[0]?.date || ''}-${expenses[expenses.length - 1]?.date || ''}`
      : '';

    if (detectKey && detectKey !== lastDetectKey.current) {
      // New expense set detected; allow re-detection automatically
      lastDetectKey.current = detectKey;
      hasDetectedSubscriptions.current = false;
    }

    if (user && expenses.length > 0 && learnedKeywordsLoaded && resolvedUnknownLoaded && !hasDetectedSubscriptions.current && !isDetecting.current) {
      hasDetectedSubscriptions.current = true;
      detectSubscriptionsFromExpenses();
    }
  }, [expenses, user, learnedKeywordsLoaded, resolvedUnknownLoaded]);

  // Helper function to check if a merchant is a known subscription service
  // NOW INCLUDES LEARNED KEYWORDS FROM DATABASE
  const isKnownSubscriptionService = (merchantName, rawDescription = '') => {
    const merchant = normalizeForMatch(merchantName);
    const description = normalizeForMatch(rawDescription);
    
    // Extract all keywords from the serviceKeywords array
    const allBuiltInKeywords = serviceKeywords.flatMap(([serviceName, keywords]) => keywords);
    
    // Check built-in keywords
    const matchesBuiltIn = allBuiltInKeywords.some(keyword => {
      const k = normalizeForMatch(String(keyword || ''));
      if (!k) return false;
      return merchant.includes(k) || description.includes(k);
    });
    if (matchesBuiltIn) return true;
    
    // Check learned keywords from database
    const matchesLearned = LearnedKeywordsManager.matchesLearnedKeywords(merchantName, learnedKeywords);
    if (matchesLearned.matched) {
      console.log('OK Matched learned keyword:', matchesLearned.keyword.keyword);
      return true;
    }
    
    return false;
  };

  const inferCategoryForService = (serviceName, merchantName = '') => {
    const key = normalizeServiceKey(serviceName || merchantName);
    if (!key) return 'Other';

    // Insurance
    if (key.includes('insurance') || ['statefarm', 'geico', 'progressive', 'allstate', 'farmers', 'libertymutual', 'nationwide', 'usaa'].some(k => key.includes(k))) {
      return 'Professional';
    }

    // Phone / Internet
    if (['verizon', 'att', 'tmobile', 't-mobile', 'comcast', 'xfinity', 'spectrum', 'charter', 'cox', 'mintmobile', 'cricket', 'boostmobile', 'googlefi'].some(k => key.includes(normalizeServiceKey(k)))) {
      return 'Communication';
    }

    // Utilities
    if (['electric', 'electricity', 'power', 'energy', 'water', 'sewer', 'waste', 'trash', 'gasutility', 'gascompany'].some(k => key.includes(normalizeServiceKey(k)))) {
      return 'Other';
    }

    return 'Other';
  };

  const detectSubscriptionsFromExpenses = async () => {
    if (isDetecting.current) {
      console.log('Detection already in progress, skipping.');
      return;
    }
    isDetecting.current = true;
    console.log('Starting subscription detection from expenses...');
    console.log('Total expenses:', expenses.length);

    // Only consider expenses from the last 90 days
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    const recentExpenses = expenses.filter(e => {
      const d = new Date(e.date);
      return !Number.isNaN(d.getTime()) && d >= ninetyDaysAgo;
    });
    console.log('Recent expenses (last 90 days):', recentExpenses.length);

    if (recentExpenses.length === 0) {
      console.log('No recent expenses found - skipping detection.');
      isDetecting.current = false;
      return;
    }

    const resolvedNameSet = new Set((await loadResolvedUnknownPatterns()).map(normalizeServiceKey));
    
    try {
      // Group expenses by normalized service name
      const serviceGroups = {};
      
      recentExpenses.forEach(expense => {
        const rawName = expense.description || expense.merchant;
        const key = normalizeServiceKey(rawName);
        if (!serviceGroups[key]) {
          serviceGroups[key] = { name: normalizeServiceName(rawName), transactions: [] };
        }
        serviceGroups[key].transactions.push({
          date: expense.date,
          amount: expense.amount,
          merchant: expense.merchant,
          description: expense.description
        });
      });

    console.log('Service groups found:', Object.keys(serviceGroups).length);

    const detectedSubs = [];
    const unknownPatterns = []; // Store unknown patterns to ask user
    const existingSubNames = subscriptions.map(s => normalizeServiceKey(s.name));
    console.log('Existing subscriptions:', existingSubNames);

    // Analyze each service group
    for (const [serviceKey, group] of Object.entries(serviceGroups)) {
      const serviceName = group.name;
      const transactions = group.transactions;

      // Skip if already exists OR was previously deleted by user
      if (existingSubNames.includes(serviceKey) || dismissedSubNames.has(serviceKey)) {
        console.log('Skipping (already exists or was dismissed):', serviceName);
        continue;
      }

      transactions.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      // CHECK IF IT'S A KNOWN SERVICE FIRST (before pattern checking!)
      const isKnown = isKnownSubscriptionService(
        transactions[0].merchant,
        transactions[0].description || transactions[0].merchant
      );
      
      if (isKnown && transactions.length >= 2) {
        // KNOWN SERVICE - Just need 2+ transactions, no strict pattern required!
        console.log('OK Found KNOWN service with', transactions.length, 'transactions:', serviceName);
        
        const dates = transactions.map(t => new Date(t.date));
        const amounts = transactions
          .map(t => Math.abs(Number(t.amount)))
          .filter(a => Number.isFinite(a) && a > 0);
        if (amounts.length < 2) {
          console.log('Skipping (invalid amounts):', serviceName, amounts);
          continue;
        }
        const centerAmount = median(amounts);
        
        // Calculate average days between (for billing cycle estimation)
        let avgDaysBetween = 30; // default monthly
        if (dates.length > 1) {
          const daysBetweenTransactions = [];
          for (let i = 1; i < dates.length; i++) {
            const days = (dates[i] - dates[i-1]) / (1000 * 60 * 60 * 24);
            daysBetweenTransactions.push(days);
          }
          avgDaysBetween = daysBetweenTransactions.reduce((a, b) => a + b, 0) / daysBetweenTransactions.length;
        }
        
        // Determine billing cycle (wider window)
        let billingCycle = inferBillingCycle(avgDaysBetween);
        if (billingCycle === 'unknown') billingCycle = 'monthly';
        
        const totalDays = (dates[dates.length - 1] - dates[0]) / (1000 * 60 * 60 * 24);
        const subscriptionAgeMonths = Math.floor(totalDays / 30);
        
        const nextRenewal = new Date(dates[dates.length - 1]);
        if (billingCycle === 'monthly') {
          nextRenewal.setMonth(nextRenewal.getMonth() + 1);
        } else if (billingCycle === 'quarterly') {
          nextRenewal.setMonth(nextRenewal.getMonth() + 3);
        } else {
          nextRenewal.setFullYear(nextRenewal.getFullYear() + 1);
        }

        console.log('OK Auto-adding KNOWN subscription:', serviceName);

        detectedSubs.push({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: serviceName,
          cost: centerAmount,
          billingCycle,
          category: inferCategoryForService(serviceName, transactions[0].merchant),
          status: 'active',
          lastUsed: dates[dates.length - 1].toISOString().split('T')[0],
          autoDetected: true,
          renewalDate: nextRenewal.toISOString().split('T')[0],
          transactionCount: transactions.length,
          firstCharge: dates[0].toISOString().split('T')[0],
          subscriptionAge: `${subscriptionAgeMonths} months`,
          notes: `Auto-detected known service: ${transactions.length} charges`
        });
        
        continue; // Skip pattern detection for known services
      }
      
      // UNKNOWN SERVICE - Apply strict pattern detection
      // Need at least 2 transactions for unknown patterns
      if (transactions.length < 2) {
        console.log('Skipping unknown service (need 2+ transactions):', serviceName, transactions.length);
        continue;
      }
      
      // Check if amounts are consistent (within $2 or 5%)
      const amounts = transactions
        .map(t => Math.abs(Number(t.amount)))
        .filter(a => Number.isFinite(a) && a > 0);
      if (amounts.length < 2) {
        console.log('Skipping (invalid amounts):', serviceName, amounts);
        continue;
      }
      const centerAmount = median(amounts);
      const tolerance = Math.max(2, centerAmount * 0.05);
      const isConsistent = amounts.every(amt => Math.abs(amt - centerAmount) <= tolerance);

      if (!isConsistent) {
        console.log('Skipping (inconsistent amounts):', serviceName, amounts);
        continue;
      }
      
      // Calculate days between transactions
      const dates = transactions.map(t => new Date(t.date));
      const daysBetweenTransactions = [];
      for (let i = 1; i < dates.length; i++) {
        const days = (dates[i] - dates[i-1]) / (1000 * 60 * 60 * 24);
        daysBetweenTransactions.push(days);
      }
      
      const avgDaysBetween = daysBetweenTransactions.reduce((a, b) => a + b, 0) / daysBetweenTransactions.length;
      
      // Check pattern consistency (looser; allow 2-txns)
      const isConsistentPattern = daysBetweenTransactions.length <= 1 || daysBetweenTransactions.every(days =>
        Math.abs(days - avgDaysBetween) < 10
      );
      
      if (!isConsistentPattern) {
        console.log('Skipping (inconsistent pattern):', serviceName, daysBetweenTransactions);
        continue;
      }
      
      // Determine billing cycle for unknown services (wider window + allow unknown)
      let billingCycle = inferBillingCycle(avgDaysBetween);
      if (isKnown && billingCycle === 'unknown') billingCycle = 'monthly';

      // Unknown service with good pattern - save to ask user (unless already answered)
      if (resolvedNameSet.has(serviceKey)) {
        console.log('Skipping unknown pattern (already answered):', serviceName);
        continue;
      }

      console.log('? Found UNKNOWN pattern:', serviceName, billingCycle, transactions.length, 'transactions');
      unknownPatterns.push({
        name: serviceName,
        cost: centerAmount,
        billingCycle,
        transactionCount: transactions.length,
        avgDaysBetween: Math.round(avgDaysBetween),
        transactions: transactions
      });
    }

    console.log('Total detected (known) subscriptions:', detectedSubs.length);
    console.log('Total unknown patterns found:', unknownPatterns.length);

    // Save known subscriptions
    let savedCount = 0;
    // Load existing subs ONCE (not per-iteration!)
    const currentSubs = await loadSubscriptions(user.uid);
    const existingNames = new Set(
      (currentSubs.success ? currentSubs.subscriptions : [])
        .map(s => normalizeServiceName(s.name).toLowerCase())
    );

    for (const sub of detectedSubs) {
      const subKey = sub.name.toLowerCase();
      if (existingNames.has(subKey)) {
        console.log('Already exists in DB:', sub.name);
        continue;
      }

      console.log('Saving subscription:', sub.name);
      const result = await saveSubscription(user.uid, sub);
      if (result.success) {
        savedCount++;
        existingNames.add(subKey); // Prevent duplicates within same batch
        console.log('Successfully saved:', sub.name);
      } else {
        console.error('Failed to save:', sub.name, result.error);
      }
    }

      console.log('Saved count:', savedCount);

      // Always reload subscriptions after detection
      await loadSubscriptionsFromFirestore();
      
      // Show feedback and ask about unknown patterns via CFO
      if (unknownPatterns.length > 0) {
        // Trigger CFO popup to ask about unknown patterns
        const question = `I found ${unknownPatterns.length} recurring charge${unknownPatterns.length !== 1 ? 's' : ''} that might be subscription${unknownPatterns.length !== 1 ? 's' : ''}:\n\n${unknownPatterns.map(p => `- ${p.name}: $${p.cost.toFixed(2)} every ~${p.avgDaysBetween} days (${p.transactionCount} charges)`).join('\n')}\n\nAre any of these subscriptions you want to track?`;
        
        // Store unknown patterns for user confirmation
        if (typeof onUnknownPatterns === 'function') {
          onUnknownPatterns(unknownPatterns, question);
        } else if (window.cfoAskQuestion) {
          window.cfoAskQuestion(question, unknownPatterns);
        } else {
          // Fallback to alert if CFO not available
          showToast?.('Found potential unknown subscriptions to review', 'info');
        }
      }
      
      if (savedCount > 0 && unknownPatterns.length === 0) {
        showToast?.(`Added ${savedCount} subscription${savedCount !== 1 ? 's' : ''}`, 'success');
      }
    } finally {
      isDetecting.current = false;
    }
  };

  // Handle subscriptions from bank analyzer
  const handleBankStatementSubscriptions = async (transactions) => {
    console.log('Bank analyzer transactions received:', transactions.length);

    const resolvedNameSet = new Set((await loadResolvedUnknownPatterns()).map(normalizeServiceKey));

    const currentSubs = await loadSubscriptions(user.uid);
    const existingNames = new Set(
      (currentSubs.success ? currentSubs.subscriptions : [])
        .map(s => normalizeServiceKey(s.name))
    );

    const serviceGroups = {};

    transactions.forEach(t => {
      const rawName = t.description || t.merchant;
      const key = normalizeServiceKey(rawName);
      console.log('Grouping:', t.merchant, '->', key);
      if (!serviceGroups[key]) {
        serviceGroups[key] = { name: normalizeServiceName(rawName), txns: [] };
      }
      serviceGroups[key].txns.push(t);
    });

    console.log('Service groups:', Object.keys(serviceGroups));

    let addedCount = 0;
    let skippedCount = 0;
    const unknownPatterns = [];
    const addedThisBatch = new Set(); // Track names saved in THIS run

    for (const [serviceKey, group] of Object.entries(serviceGroups)) {
      const serviceName = group.name;
      const txns = group.txns;

      // Sort by date
      txns.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Check against current state AND what we've already added in this batch
      const normalizedKey = serviceKey;
      const existsInState = existingNames.has(normalizedKey);
      const addedAlready = addedThisBatch.has(normalizedKey);
      const wasDismissed = dismissedSubNames.has(normalizedKey);

      if (existsInState || addedAlready || wasDismissed) {
        console.log('Already exists or dismissed (state/batch/dismissed):', serviceName);
        skippedCount++;
        continue;
      }

      // Known services get lighter matching; unknown still require recurring evidence
      const isKnown = isKnownSubscriptionService(txns[0].merchant, txns[0].description || txns[0].merchant);

      const minTxnCount = 2;
      if (txns.length < minTxnCount) {
        console.log(`Skipping (< ${minTxnCount} transactions):`, serviceName);
        continue;
      }

      // Use absolute amounts (expenses often come in as negatives in bank exports)
      const amounts = txns
        .map(t => Math.abs(Number(t.amount)))
        .filter(a => Number.isFinite(a) && a > 0);

      if (amounts.length < minTxnCount) {
        console.log('Skipping (invalid/empty amounts):', serviceName);
        continue;
      }

      // Amount consistency (robust to small tax/fee variation)
      const center = median(amounts);
      const tol = Math.max(2, center * 0.05); // $2 or 5%, whichever is larger
      const withinTol = amounts.filter(a => Math.abs(a - center) <= tol).length;
      const matchRatio = withinTol / amounts.length;

      // Known: allow 70% within tolerance; Unknown: require 80%
      const requiredRatio = isKnown ? 0.70 : 0.80;
      if (matchRatio < requiredRatio) {
        console.log('Skipping (inconsistent amounts):', serviceName, { amounts, center, tol, matchRatio });
        continue;
      }

      // Calculate days between transactions
      const dates = txns.map(t => new Date(t.date));
      const daysBetweenTransactions = [];
      for (let i = 1; i < dates.length; i++) {
        const days = (dates[i] - dates[i - 1]) / (1000 * 60 * 60 * 24);
        if (Number.isFinite(days) && days > 0) daysBetweenTransactions.push(days);
      }

      if (daysBetweenTransactions.length === 0) {
        console.log('Skipping (not enough spacing info):', serviceName);
        continue;
      }

      const avgDaysBetween = daysBetweenTransactions.reduce((a, b) => a + b, 0) / daysBetweenTransactions.length;

      // Check pattern consistency (looser; allow 2-txns)
      const isConsistentPattern = daysBetweenTransactions.length <= 1 || daysBetweenTransactions.every(days =>
        Math.abs(days - avgDaysBetween) < 10
      );

      if (!isConsistentPattern) {
        console.log('Skipping (inconsistent pattern):', serviceName);
        continue;
      }

      // Determine billing cycle (wider window + allow unknown)
      const billingCycle = inferBillingCycle(avgDaysBetween);

      // If it's a known subscription service, auto-add. Otherwise record it as a candidate.
      if (isKnown) {
        const costEstimate = center;

        const sub = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: serviceName,
          cost: costEstimate,
          billingCycle,
          category: inferCategoryForService(serviceName, txns[0]?.merchant),
          status: 'active',
          autoDetected: true,
          transactionCount: txns.length,
          lastUsed: dates[dates.length - 1].toISOString().split('T')[0],
          renewalDate: new Date(dates[dates.length - 1].getTime() + avgDaysBetween * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0],
          notes: `Auto-detected (known): ${txns.length} charges ~every ${Math.round(avgDaysBetween)} days; $${costEstimate.toFixed(2)} typical`
        };

        console.log('Saving KNOWN subscription:', sub.name, billingCycle);
        const result = await saveSubscription(user.uid, sub);
        if (result.success) {
          addedCount++;
          addedThisBatch.add(normalizedKey);
          existingNames.add(normalizedKey);
          console.log('OK Saved:', sub.name);
        } else {
          console.error('X Failed to save:', sub.name, result.error);
        }
      } else {
        console.log('? Found UNKNOWN pattern:', serviceName);
        if (resolvedNameSet.has(serviceName.toLowerCase())) {
          console.log('Skipping unknown pattern (already answered):', serviceName);
          continue;
        }
        unknownPatterns.push({
          name: serviceName,
          cost: center,
          billingCycle,
          transactionCount: txns.length,
          avgDaysBetween: Math.round(avgDaysBetween),
          matchRatio: Math.round(matchRatio * 100)
        });
      }
    }

    console.log('Bank analyzer complete - Added:', addedCount, 'Skipped:', skippedCount);

    // Reload subscriptions after detection
    await loadSubscriptionsFromFirestore();

    // Show result and ask about unknown patterns via CFO
    if (unknownPatterns.length > 0) {
      const question = `I found ${unknownPatterns.length} recurring charge${unknownPatterns.length !== 1 ? 's' : ''} that might be subscription${unknownPatterns.length !== 1 ? 's' : ''}:\n\n${unknownPatterns.map(p => `- ${p.name}: $${p.cost.toFixed(2)} every ~${p.avgDaysBetween} days (${p.transactionCount} charges)`).join('\n')}\n\nAre any of these subscriptions you want to track?`;

      if (typeof onUnknownPatterns === 'function') {
        onUnknownPatterns(unknownPatterns, question);
      } else if (window.cfoAskQuestion) {
        window.cfoAskQuestion(question, unknownPatterns);
      } else {
        showToast?.('Found potential unknown subscriptions to review', 'info');
      }
    }

    if (addedCount > 0) {
      if (unknownPatterns.length === 0) {
        showToast?.(`Added ${addedCount} subscription${addedCount !== 1 ? 's' : ''}`, 'success');
      }
    }
  };

  const addSubscription = async () => {
    if (!newSubscription.name || newSubscription.cost <= 0) return;

    const result = await saveSubscription(user.uid, {
      ...newSubscription,
      id: Date.now().toString()
    });

    if (result.success) {
      await loadSubscriptionsFromFirestore();
      setShowAddModal(false);
      resetForm();
    }
  };

  const deleteSubscriptionHandler = async (subOrId) => {
    const sub = typeof subOrId === 'object'
      ? subOrId
      : subscriptions.find(s => s.id === subOrId);
    const subId = typeof subOrId === 'object' ? subOrId.id : subOrId;

    const wasPromptConfirmed = sub?.learned || (sub?.notes && sub.notes.includes('User confirmed'));
    if (wasPromptConfirmed && sub?.name) {
      await LearnedKeywordsManager.deleteLearnedKeywordsForService(user.uid, sub.name);
      await loadLearnedKeywordsFromFirestore();
    }

    // Remember this name so auto-detection won't re-add it
    if (sub?.name) {
      const normalizedName = normalizeServiceKey(sub.name);
      setDismissedSubNames(prev => new Set([...prev, normalizedName]));
      try {
        const { doc, setDoc, getDoc } = await import('firebase/firestore');
        const { db: firestoreDb } = await import('./firebase');
        const ref = doc(firestoreDb, 'users', user.uid, 'settings', 'dismissedSubscriptions');
        const existing = await getDoc(ref);
        const names = existing.exists() ? (existing.data().names || []) : [];
        if (!names.includes(normalizedName)) {
          await setDoc(ref, { names: [...names, normalizedName] }, { merge: true });
        }
      } catch (e) {
        console.error('Failed to save dismissed subscription name:', e);
      }
    }
    await deleteSubscription(user.uid, subId);
    await loadSubscriptionsFromFirestore();
  };

  const handleDeleteAllSubscriptions = async () => {
    setShowDeleteAllConfirm(false);

    // Remember all names so auto-detection won't re-add them
    const normalizedNames = subscriptions.map(s => normalizeServiceKey(s.name)).filter(Boolean);
    setDismissedSubNames(prev => new Set([...prev, ...normalizedNames]));
    try {
      const { doc, setDoc, getDoc } = await import('firebase/firestore');
      const { db: firestoreDb } = await import('./firebase');
      const ref = doc(firestoreDb, 'users', user.uid, 'settings', 'dismissedSubscriptions');
      const existing = await getDoc(ref);
      const existingNames = existing.exists() ? (existing.data().names || []) : [];
      const merged = [...new Set([...existingNames, ...normalizedNames])];
      await setDoc(ref, { names: merged }, { merge: true });
    } catch (e) {
      console.error('Failed to save dismissed subscription names:', e);
    }

    const deletePromises = subscriptions.map(sub => deleteSubscription(user.uid, sub.id));
    await Promise.all(deletePromises);
    await loadSubscriptionsFromFirestore();
    showToast?.('Deleted all subscriptions', 'info');
  };

  const updateSubscriptionStatus = async (subId, newStatus) => {
    await updateSubscription(user.uid, subId, { status: newStatus });
    await loadSubscriptionsFromFirestore();
  };

  const resetForm = () => {
    setNewSubscription({
      name: '',
      cost: 0,
      billingCycle: 'monthly',
      category: 'Software',
      status: 'active',
      lastUsed: new Date().toISOString().split('T')[0],
      renewalDate: '',
      website: '',
      notes: ''
    });
  };

  const calculateMonthlyTotal = () => {
    return subscriptions
      .filter(sub => sub.status === 'active')
      .reduce((sum, sub) => {
        let monthlyCost = sub.cost;
        if (sub.billingCycle === 'yearly') monthlyCost = sub.cost / 12;
        else if (sub.billingCycle === 'quarterly') monthlyCost = sub.cost / 3;
        return sum + monthlyCost;
      }, 0);
  };

  const getExtensionLastUsedDate = (sub) => {
    if (!sub) return null;
    const hasExtensionFlag = sub.lastUsedSource === 'extension' || sub.extensionTracked || sub.extensionSync;
    if (!hasExtensionFlag) return null;
    if (sub.lastUsedAt && typeof sub.lastUsedAt.toDate === 'function') {
      return sub.lastUsedAt.toDate();
    }
    if (sub.lastUsedAt instanceof Date) return sub.lastUsedAt;
    if (typeof sub.lastUsedAt === 'string') {
      const parsed = new Date(sub.lastUsedAt);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    if (sub.lastUsed) {
      const parsed = new Date(sub.lastUsed);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return null;
  };

  const getLastUsedSource = (sub) => {
    if (!sub) return null;
    if (sub.lastUsedSource === 'extension' || sub.extensionTracked || sub.extensionSync) return 'extension';
    if (sub.lastUsed) return 'bank';
    return null;
  };

  const categories = ['all', 'Software', 'Entertainment', 'Productivity', 'Communication', 'Professional', 'Other'];

  const filteredSubscriptions = subscriptions
    .filter(sub => filterStatus === 'all' || sub.status === filterStatus)
    .filter(sub => selectedCategory === 'all' || sub.category === selectedCategory)
    .filter(sub => sub.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'cost') {
        const aCost = a.billingCycle === 'yearly' ? a.cost / 12 : a.billingCycle === 'quarterly' ? a.cost / 3 : a.cost;
        const bCost = b.billingCycle === 'yearly' ? b.cost / 12 : b.billingCycle === 'quarterly' ? b.cost / 3 : b.cost;
        return bCost - aCost;
      }
      return a.name.localeCompare(b.name);
    });

  const zombieCount = subscriptions.filter(sub => {
    if (sub.status !== 'active') return false;
    const lastUsedDate = getExtensionLastUsedDate(sub);
    if (!lastUsedDate) return false;
    const daysSince = (Date.now() - lastUsedDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSince > 30;
  }).length;

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const target = document.getElementById(sectionId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  if (showBankAnalyzer) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => {
            setShowBankAnalyzer(false);
            loadSubscriptionsFromFirestore();
          }}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group"
          style={{ animation: 'pageSlideIn 0.3s ease-out both' }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]">
            <ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          </div>
          Back
        </button>
        <BankAnalyzer
          user={user}
          onCancel={() => { setShowBankAnalyzer(false); loadSubscriptionsFromFirestore(); }}
          onExpensesExtracted={async (transactions, analysis) => {
            const txList = Array.isArray(transactions)
              ? transactions
              : (analysis?.transactions || transactions?.transactions || []);
            const expenseTransactions = (analysis?.expenseTransactions || txList).filter((t) => t.flow !== 'income');
            await handleBankStatementSubscriptions(expenseTransactions);
            setShowBankAnalyzer(false);
            await loadSubscriptionsFromFirestore();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Compact header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">{subscriptions.length} subscriptions</p>
          <p className="text-lg lg:text-2xl font-bold text-white">${calculateMonthlyTotal().toFixed(2)}<span className="text-xs lg:text-sm text-slate-500 font-normal">/mo</span></p>
        </div>
        <div className="flex items-center gap-2">
          {subscriptions.length > 0 && (
            <button onClick={() => setShowDeleteAllConfirm(true)} className="p-1.5 text-red-400/50 hover:text-red-400 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <div className="relative detect-dropdown-container">
            <button
              onClick={() => setShowDetectDropdown(!showDetectDropdown)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold"
            >
              <Sparkles className="w-3 h-3" />
              Add
              <ChevronDown className={`w-3 h-3 transition-transform ${showDetectDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showDetectDropdown && (
                <div className="centris-card absolute right-0 mt-2 w-80 rounded-xl z-50 overflow-hidden">
                  <div className="p-2 space-y-1">
                    <button
                      onClick={() => {
                        setShowDetectDropdown(false);
                        setShowAddModal(true);
                      }}
                      className="w-full text-left px-4 py-3.5 hover:bg-slate-700/50 rounded-lg transition-all flex items-center space-x-3 group"
                    >
                      <div className="bg-blue-500/20 p-2.5 rounded-lg group-hover:bg-blue-500/30 transition-colors">
                        <Plus className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold">Add Manually</p>
                        <p className="text-xs text-slate-400">Enter details yourself</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowDetectDropdown(false);
                        hasDetectedSubscriptions.current = false;
                        detectSubscriptionsFromExpenses();
                      }}
                      className="w-full text-left px-4 py-3.5 hover:bg-slate-700/50 rounded-lg transition-all flex items-center space-x-3 group"
                    >
                      <div className="bg-green-500/20 p-2.5 rounded-lg group-hover:bg-green-500/30 transition-colors">
                        <RefreshCw className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold">Scan Receipts</p>
                        <p className="text-xs text-slate-400">Detect from uploaded receipts</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowDetectDropdown(false);
                        setShowBankAnalyzer(true);
                      }}
                      className="w-full text-left px-4 py-3.5 hover:bg-slate-700/50 rounded-lg transition-all flex items-center space-x-3 group"
                    >
                      <div className="bg-purple-500/20 p-2.5 rounded-lg group-hover:bg-purple-500/30 transition-colors">
                        <Upload className="w-5 h-5 text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold flex items-center gap-2">
                          <span>Upload Bank Statement</span>
                          <HelpBadge id="bank-csv" />
                        </p>
                        <p className="text-xs text-slate-400">CSV, Excel, or PDF</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowDetectDropdown(false);
                        setShowAutoDetect(true);
                      }}
                      className="w-full text-left px-4 py-3.5 hover:bg-slate-700/50 rounded-lg transition-all flex items-center space-x-3 group"
                    >
                      <div className="bg-amber-500/20 p-2.5 rounded-lg group-hover:bg-amber-500/30 transition-colors">
                        <Mail className="w-5 h-5 text-amber-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold">Scan Gmail</p>
                        <p className="text-xs text-slate-400">Find subscription emails</p>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setShowDetectDropdown(false);
                        setShowExtensionPanel(true);
                      }}
                      className="w-full text-left px-4 py-3.5 hover:bg-slate-700/50 rounded-lg transition-all flex items-center space-x-3 group"
                    >
                      <div className="bg-indigo-500/20 p-2.5 rounded-lg group-hover:bg-indigo-500/30 transition-colors">
                        <CreditCard className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold flex items-center gap-2">Browser Extension <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-full">Optional</span></p>
                        <p className="text-xs text-slate-400">Passively tracks subscription sites you visit</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Compact stats row */}
      <div className="grid grid-cols-3 gap-3 lg:gap-4">
        <div className="centris-card rounded-xl p-3 lg:p-5 text-center">
          <p className="text-xs text-slate-500 mb-0.5">Monthly</p>
          <p className="text-sm lg:text-2xl font-bold text-white">${calculateMonthlyTotal().toFixed(2)}</p>
        </div>
        <div className="centris-card rounded-xl p-3 lg:p-5 text-center">
          <p className="text-xs text-slate-500 mb-0.5">Yearly</p>
          <p className="text-sm lg:text-2xl font-bold text-white">${(calculateMonthlyTotal() * 12).toFixed(2)}</p>
        </div>
        <div className="centris-card rounded-xl p-3 lg:p-5 text-center">
          <p className="text-xs text-slate-500 mb-0.5">Zombies</p>
          <p className="text-sm lg:text-2xl font-bold text-orange-400">{zombieCount}</p>
        </div>
      </div>

      {/* Gmail Scanner Section */}
      {showAutoDetect && (
        <div className="centris-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-amber-500/20 p-2 rounded-lg">
                <Mail className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Gmail Scanner</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Scan your email for subscription receipts
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAutoDetect(false)}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <GmailSubscriptionScanner 
            user={user}
            onSubscriptionsFound={async (detectedSubs) => {
              // Filter out subs that already exist or were previously resolved
              const resolvedResult = await loadSuggestedKeywords(user.uid);
              const resolvedNames = new Set();
              if (resolvedResult.success) {
                resolvedResult.keywords
                  .filter(k => k.status && k.status !== 'pending')
                  .forEach((k) => {
                    const name = (k.merchantName || k.serviceName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                    if (name) resolvedNames.add(name);
                  });
              }

              const existingNames = new Set(
                subscriptions.map(s => (s.name || '').toLowerCase().replace(/[^a-z0-9]/g, ''))
              );

              const needsReview = detectedSubs.filter((sub) => {
                const key = (sub.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                return key && !existingNames.has(key) && !resolvedNames.has(key);
              });

              // Update existing subs that Gmail has new info for (status changes etc)
              const existingUpdates = detectedSubs.filter((sub) => {
                const key = (sub.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                return key && existingNames.has(key);
              });

              for (const sub of existingUpdates) {
                const existing = subscriptions.find(
                  (s) => normalizeServiceName(s.name).toLowerCase() === normalizeServiceName(sub.name).toLowerCase()
                );
                if (existing) {
                  const shouldUpdate = existing.status !== sub.status
                    || existing.lastEmailDate !== sub.lastEmailDate
                    || existing.renewalDate !== sub.renewalDate;
                  if (shouldUpdate) {
                    await updateSubscription(user.uid, existing.id, {
                      status: sub.status,
                      statusReason: sub.statusReason || '',
                      renewalDate: sub.renewalDate,
                      lastEmailDate: sub.lastEmailDate,
                      lastUsed: sub.lastUsed || existing.lastUsed,
                    });
                  }
                }
              }

              if (needsReview.length > 0) {
                // Route ALL new detections through Quantra checklist
                if (typeof onOpenCfoPopup === 'function') {
                  onOpenCfoPopup({
                    type: 'gmail-checklist',
                    gmailSubs: needsReview,
                  });
                }
              } else if (existingUpdates.length > 0) {
                await loadSubscriptionsFromFirestore();
              }

              setShowAutoDetect(false);
            }}
          />
        </div>
      )}

      <p className="text-[10px] text-slate-600 px-1">Detection uses last 90 days of transactions only.</p>

      {/* Extension pairing panel (shown when selected from dropdown) */}
      {showExtensionPanel && (
        <div className="centris-card rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="bg-indigo-500/20 p-1.5 rounded-lg">
                <CreditCard className="w-4 h-4 text-indigo-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Browser Extension</p>
                <p className="text-[11px] text-slate-400">Passively tracks subscription sites you visit</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${extensionPaired ? 'bg-green-500/20 text-green-300' : 'bg-slate-700/60 text-slate-400'}`}>
                {extensionPaired ? '✓ Connected' : 'Not Paired'}
              </div>
              <button onClick={() => setShowExtensionPanel(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              {extensionPaired ? 'Syncing usage automatically.' : 'Generate a code, then enter it in the extension popup.'}
            </p>
            <button onClick={generatePairingCode} className="px-3 py-1.5 bg-indigo-600/20 text-indigo-400 rounded-lg hover:bg-indigo-600/30 border border-indigo-500/30 text-xs font-medium flex-shrink-0 ml-3">
              Generate Code
            </button>
          </div>
          {pairingStatus && <p className="mt-2 text-xs text-slate-400">{pairingStatus}</p>}
          {pairingCode && (
            <div className="mt-3 bg-slate-800/60 flex items-center justify-between rounded-lg px-4 py-3">
              <div>
                <p className="text-[10px] text-slate-400 mb-1">Pairing Code (expires in 10 min)</p>
                <p className="text-2xl font-bold text-white tracking-widest font-mono">{pairingCode}</p>
              </div>
              <button onClick={() => navigator.clipboard.writeText(pairingCode)} className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-xs font-medium">Copy</button>
            </div>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center space-x-2 flex-1 min-w-[160px] centris-card rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search subscriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent border-none text-white placeholder-slate-500 focus:outline-none text-sm"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-slate-900/50 border border-white/[0.06] rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 bg-slate-900/50 border border-white/[0.06] rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
          ))}
        </select>
      </div>

      {/* Subscription List */}
      {filteredSubscriptions.length === 0 ? (
        <div className="text-center py-8 centris-card rounded-xl">
          <Bell className="w-6 h-6 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No subscriptions yet</p>
        </div>
      ) : (
        <div className="grid gap-2 lg:grid-cols-2 lg:gap-3">
          {filteredSubscriptions.map((sub) => {
            const lastUsedDate = getExtensionLastUsedDate(sub);
            const daysSinceUsed = lastUsedDate
              ? Math.max(0, Math.floor((Date.now() - lastUsedDate.getTime()) / (1000 * 60 * 60 * 24)))
              : null;
            const isZombie = daysSinceUsed !== null && daysSinceUsed > 30;
            const isCancelled = sub.status === 'cancelled';
            return (
              <div key={sub.id}
                className={`centris-card rounded-xl flex items-center gap-3 px-3 py-3 lg:px-5 lg:py-4 ${isZombie ? 'border-orange-500/30' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className={`text-sm lg:text-base font-semibold truncate ${isCancelled ? 'line-through text-slate-500' : 'text-white'}`}>{sub.name}</p>
                    {isZombie && <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded-full">zombie</span>}
                    {isCancelled && <span className="text-xs px-1.5 py-0.5 bg-slate-700 text-slate-500 rounded-full">cancelled</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{sub.category} · {sub.renewalDate ? `renews ${new Date(sub.renewalDate).toLocaleDateString('en-US',{month:'short',day:'numeric'})}` : 'no renewal set'}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm lg:text-base font-bold text-white">${sub.cost.toFixed(2)}</p>
                  <p className="text-xs text-slate-500">/{sub.billingCycle === 'yearly' ? 'yr' : sub.billingCycle === 'quarterly' ? 'qtr' : 'mo'}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isCancelled ? (
                    <button onClick={() => updateSubscriptionStatus(sub.id, 'active')} className="p-1.5 text-green-400/60 hover:text-green-400 transition-colors" title="Reactivate"><RefreshCw className="w-3.5 h-3.5" /></button>
                  ) : (
                    <button onClick={() => updateSubscriptionStatus(sub.id, 'cancelled')} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors" title="Cancel"><X className="w-3.5 h-3.5" /></button>
                  )}
                  <button onClick={() => deleteSubscriptionHandler(sub)} className="p-1.5 text-slate-600 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Subscription Modal */}
      {showAddModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl max-w-md w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Add Subscription</h3>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="text-slate-400 hover:text-white transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Service Name</label>
                <input
                  type="text"
                  placeholder="e.g., Netflix, Spotify"
                  value={newSubscription.name}
                  onChange={(e) => setNewSubscription({...newSubscription, name: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Cost</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={newSubscription.cost || ''}
                    onChange={(e) => setNewSubscription({...newSubscription, cost: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Billing</label>
                  <select
                    value={newSubscription.billingCycle}
                    onChange={(e) => setNewSubscription({...newSubscription, billingCycle: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Category</label>
                <select
                  value={newSubscription.category}
                  onChange={(e) => setNewSubscription({...newSubscription, category: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                >
                  <option value="Software">Software</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Productivity">Productivity</option>
                  <option value="Communication">Communication</option>
                  <option value="Professional">Professional</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="flex-1 px-6 py-3 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-all font-semibold border border-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={addSubscription}
                  disabled={!newSubscription.name || newSubscription.cost <= 0}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllConfirm && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl border border-red-500/30 max-w-md w-full p-8">
            <div className="flex items-center space-x-3 mb-5">
              <div className="bg-red-500/20 p-3 rounded-xl">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Delete All Subscriptions?</h3>
            </div>
            
            <p className="text-slate-300 mb-6 text-lg">
              This will permanently delete <span className="font-bold text-white">{subscriptions.length}</span> subscription{subscriptions.length !== 1 ? 's' : ''}. This action cannot be undone.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteAllConfirm(false)}
                className="flex-1 px-6 py-3 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-all font-semibold border border-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAllSubscriptions}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all flex items-center justify-center space-x-2 font-semibold"
              >
                <Trash2 className="w-5 h-5" />
                <span>Delete All</span>
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>}
    </div>
  );
};


// ============ FLOATING CFO POPUP COMPONENT ============
const CFOPopup = ({ user, data, onClose, onConfirm }) => {
  const isScentRedirect = data?.type === 'scent-redirect';
  const isGmailChecklist = data?.type === 'gmail-checklist';
  const items = isGmailChecklist ? (data?.gmailSubs || []) : (data?.unknownPatterns || []);
  const [selections, setSelections] = React.useState(
    () => items.reduce((acc, p, i) => ({ ...acc, [i]: false }), {})
  );
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    const popupItems = (data?.type === 'gmail-checklist')
      ? (data?.gmailSubs || [])
      : (data?.unknownPatterns || []);
    setSelections(popupItems.reduce((acc, p, i) => ({ ...acc, [i]: false }), {}));
    setSaving(false);
    setSaved(false);
  }, [data]);

  const togglePattern = (idx) => {
    setSelections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleConfirm = async () => {
    if (isScentRedirect) {
      if (typeof data?.onNavigate === 'function') {
        data.onNavigate(data.destinationView);
      }
      onClose();
      return;
    }

    // ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬ Gmail Checklist Mode ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬
    if (isGmailChecklist) {
      setSaving(true);
      const gmailSubs = data?.gmailSubs || [];
      const confirmed = gmailSubs.filter((_, i) => selections[i]);
      const declined = gmailSubs.filter((_, i) => !selections[i]);

      // Save confirmed subscriptions
      for (const sub of confirmed) {
        await saveSubscription(user.uid, {
          ...sub,
          id: `gmail-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          userConfirmed: true,
        });

        // Learn keywords so bank statement scanner recognizes it next time
        const keywords = LearnedKeywordsManager.extractKeywords(sub.name);
        for (const keyword of keywords) {
          await LearnedKeywordsManager.addLearnedKeyword(user.uid, keyword, sub.name, sub.name);
        }

        // Mark as resolved
        await saveSuggestedKeyword(user.uid, {
          merchantName: sub.name,
          keywords: LearnedKeywordsManager.extractKeywords(sub.name),
          cost: sub.cost,
          billingCycle: sub.billingCycle,
          source: 'gmail_user_confirmed',
          status: 'approved',
        });
      }

      // Mark declined as resolved so they never come back
      for (const sub of declined) {
        await saveSuggestedKeyword(user.uid, {
          merchantName: sub.name,
          keywords: LearnedKeywordsManager.extractKeywords(sub.name),
          cost: sub.cost,
          billingCycle: sub.billingCycle,
          source: 'gmail_user_declined',
          status: 'rejected',
        });
      }

      // Notify parent to reload
      if (confirmed.length > 0 && typeof onConfirm === 'function') {
        await onConfirm(confirmed);
      } else {
        window.dispatchEvent(new CustomEvent('quantra-resolved-updated'));
      }

      setSaving(false);
      setSaved(true);
      setTimeout(() => onClose(), 1500);
      return;
    }

    setSaving(true);
    const patterns = data?.unknownPatterns || [];
    const confirmed = patterns.filter((_, i) => selections[i]);
    const declined = patterns.filter((_, i) => !selections[i]);

    if (confirmed.length > 0) {
      // Save confirmed as subscriptions + learn keywords
      for (const pattern of confirmed) {
        const keywords = LearnedKeywordsManager.extractKeywords(pattern.name);
        for (const keyword of keywords) {
          await LearnedKeywordsManager.addLearnedKeyword(user.uid, keyword, pattern.name, pattern.name);
        }
        // Also save to suggested_keywords DB for future version updates
        await saveSuggestedKeyword(user.uid, {
          merchantName: pattern.name,
          keywords: keywords,
          cost: pattern.cost,
          billingCycle: pattern.billingCycle,
          transactionCount: pattern.transactionCount,
          avgDaysBetween: pattern.avgDaysBetween,
          source: 'user_confirmed_unknown_pattern',
          status: 'approved'
        });
      }
      await onConfirm(confirmed);
    }

    // Save declined patterns to suggested_keywords as "rejected" so we know
    for (const pattern of declined) {
      await saveSuggestedKeyword(user.uid, {
        merchantName: pattern.name,
        keywords: LearnedKeywordsManager.extractKeywords(pattern.name),
        cost: pattern.cost,
        billingCycle: pattern.billingCycle,
        transactionCount: pattern.transactionCount,
        avgDaysBetween: pattern.avgDaysBetween,
        source: 'user_declined_unknown_pattern',
        status: 'rejected'
      });
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => onClose(), 1500);
  };

  const selectedCount = Object.values(selections).filter(Boolean).length;

  if (isScentRedirect) {
    return (
      <div className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[420px] flex flex-col centris-card border border-violet-500/30 rounded-2xl overflow-hidden animate-in">
        <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Brain className="w-5 h-5 text-white" />
            <div>
              <p className="text-white font-semibold text-sm">Quantra</p>
              <p className="text-violet-200 text-xs">Scent redirect suggestion</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-3">
            <p className="text-sm text-violet-200">{data?.prompt || 'I found a better place to handle this request.'}</p>
          </div>
          {data?.reason && (
            <div className="centris-card rounded-xl p-3">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Reason</p>
              <p className="text-sm text-slate-200">{data.reason}</p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-700 px-5 py-3 flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-700 transition-colors border border-slate-700"
          >
            Dismiss
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
          >
            {`Go to ${data?.destinationLabel || 'Destination'}`}
          </button>
        </div>
      </div>
    );
  }

  // ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬ Gmail Checklist Render ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬
  if (isGmailChecklist) {
    const gmailSubs = data?.gmailSubs || [];
    const selectedCountGmail = Object.values(selections).filter(Boolean).length;
    const selectAll = () => setSelections(gmailSubs.reduce((acc, _, i) => ({ ...acc, [i]: true }), {}));
    const selectNone = () => setSelections(gmailSubs.reduce((acc, _, i) => ({ ...acc, [i]: false }), {}));

    return (
      <div
        className="fixed bottom-6 right-6 z-50 w-[420px] max-h-[600px] flex flex-col centris-card border border-violet-500/30 rounded-2xl overflow-hidden"
        style={{ animation: 'quantraSlideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both' }}
      >
        <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <Brain className="w-5 h-5 text-white" />
            <div>
              <p className="text-white font-semibold text-sm">Quantra</p>
              <p className="text-violet-200 text-xs">Email scan results</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-4 pb-2">
          <p className="text-sm text-slate-300 leading-relaxed">
            I found <strong className="text-white">{gmailSubs.length}</strong> possible subscription{gmailSubs.length !== 1 ? 's' : ''} in your email.
            Check the ones that are real subscriptions. I will ignore the rest from now on.
          </p>
        </div>

        <div className="px-5 py-2 flex items-center gap-3 border-b border-white/[0.04]">
          <button onClick={selectAll} className="text-xs text-violet-400 hover:text-violet-300 font-medium">
            Select all
          </button>
          <span className="text-slate-600">|</span>
          <button onClick={selectNone} className="text-xs text-slate-500 hover:text-slate-300 font-medium">
            Clear all
          </button>
          <span className="flex-1" />
          <span className="text-xs text-slate-500">{selectedCountGmail} of {gmailSubs.length} selected</span>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2" style={{ maxHeight: 340 }}>
          {saved ? (
            <div className="text-center py-10">
              <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-white font-semibold">
                {selectedCountGmail > 0 ? `Added ${selectedCountGmail} subscription${selectedCountGmail !== 1 ? 's' : ''}!` : 'Got it, none added.'}
              </p>
              <p className="text-xs text-slate-400 mt-1">I will not ask about the others again.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {gmailSubs.map((sub, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelections((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all ${
                    selections[idx]
                      ? 'bg-violet-500/10 border border-violet-500/25'
                      : 'bg-transparent border border-transparent hover:bg-white/[0.02]'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                    selections[idx]
                      ? 'bg-violet-500 border-violet-500'
                      : 'bg-transparent border-2 border-slate-600'
                  }`}>
                    {selections[idx] && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${selections[idx] ? 'text-white' : 'text-slate-300'}`}>
                      {sub.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {sub.cost > 0 && (
                        <span className="text-xs text-slate-500">
                          ${sub.cost.toFixed(2)}/{sub.billingCycle}
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        sub.status === 'active'
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-red-500/10 text-red-400'
                      }`}>
                        {sub.status}
                      </span>
                      {sub.category && sub.category !== 'Other' && (
                        <span className="text-[10px] text-slate-600">{sub.category}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {!saved && (
          <div className="border-t border-slate-700 px-5 py-3 flex space-x-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-700 transition-colors border border-slate-700"
            >
              Skip for now
            </button>
            <button
              onClick={handleConfirm}
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Confirm {selectedCountGmail > 0 ? `(${selectedCountGmail})` : ''}</>
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] max-h-[520px] flex flex-col centris-card border border-violet-500/30 rounded-2xl overflow-hidden animate-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-3">
          <Brain className="w-5 h-5 text-white" />
          <div>
            <p className="text-white font-semibold text-sm">Quantra</p>
            <p className="text-violet-200 text-xs">Unknown charges detected</p>
          </div>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {saved ? (
          <div className="text-center py-8">
            <div className="bg-green-500/20 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-7 h-7 text-green-400" />
            </div>
            <p className="text-white font-semibold">Got it!</p>
            <p className="text-slate-400 text-sm mt-1">
              {selectedCount > 0 
                ? `Added ${selectedCount} subscription${selectedCount !== 1 ? 's' : ''} and learned the keywords for next time.`
                : 'Noted your preferences. I\'ll be smarter next time!'}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-3">
              <p className="text-sm text-violet-200">
                [?] I found <span className="font-bold text-white">{data.unknownPatterns.length}</span> recurring charge{data.unknownPatterns.length !== 1 ? 's' : ''} that look like subscriptions. Tap the ones you want to track:
              </p>
            </div>

            <div className="space-y-2">
              {data.unknownPatterns.map((pattern, idx) => (
                <button
                  key={idx}
                  onClick={() => togglePattern(idx)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    selections[idx]
                      ? 'bg-violet-500/20 border-violet-500/40'
                      : 'centris-card hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                        selections[idx] ? 'bg-violet-500 border-violet-500' : 'border-slate-600'
                      }`}>
                        {selections[idx] && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{pattern.name}</p>
                        <p className="text-slate-400 text-xs">{pattern.transactionCount} charges - ~every {pattern.avgDaysBetween} days</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-semibold text-sm">${pattern.cost.toFixed(2)}</p>
                      <p className="text-slate-500 text-xs">{pattern.billingCycle}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {!saved && (
        <div className="border-t border-slate-700 px-5 py-3 flex space-x-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-700 transition-colors border border-slate-700"
          >
            Dismiss
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Saving...</span></>
            ) : (
              <span>{selectedCount > 0 ? `Add ${selectedCount} & Learn` : 'Skip All'}</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== PLAID LINK COMPONENT ====================
// Plaid Link is web-only — usePlaidLink's event hub crashes in Capacitor WKWebView
const PlaidLinkButtonWeb = ({ user, onSuccess, onError, buttonText = "Connect Bank Account" }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateLinkToken = React.useCallback(async () => {
    if (!user?.uid) return;

    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('No authenticated user');
        setLoading(false);
        return;
      }
      const idToken = await currentUser.getIdToken();

      const response = await fetch(
        'https://us-central1-receiptwise-prod.cloudfunctions.net/plaidCreateLinkToken',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ userId: user.uid })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const err = new Error(data.error || 'Failed to create link token');
        err.plaidError = data.plaidError;
        err.status = response.status;
        throw err;
      }

      if (data.link_token) {
        setLinkToken(data.link_token);
      } else {
        const err = new Error(data.error || 'Failed to create link token');
        err.plaidError = data.plaidError;
        throw err;
      }
    } catch (error) {
      console.error('Error generating link token:', error);
      onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [user, onError]);

  const onPlaidSuccess = React.useCallback(async (publicToken, metadata) => {
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('No authenticated user');
        setLoading(false);
        return;
      }
      const idToken = await currentUser.getIdToken();

      const response = await fetch(
        'https://us-central1-receiptwise-prod.cloudfunctions.net/plaidExchangeToken',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            publicToken,
            metadata,
            userId: user.uid
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const err = new Error(data.error || 'Failed to exchange token');
        err.plaidError = data.plaidError;
        err.status = response.status;
        throw err;
      }

      if (data.success) {
        onSuccess?.(data);
      } else {
        const err = new Error(data.error || 'Failed to exchange token');
        err.plaidError = data.plaidError;
        throw err;
      }
    } catch (error) {
      console.error('Error exchanging token:', error);
      onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [user, onSuccess, onError]);

  const config = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err, metadata) => {
      if (err) {
        console.error('Plaid Link exited with error:', err);
        const enriched = { ...err, plaidMetadata: metadata };
        onError?.(enriched);
      }
    }
  };

  const { open, ready } = usePlaidLink(config);

  const handleClick = async () => {
    if (!linkToken) {
      await generateLinkToken();
      setTimeout(() => {
        if (ready && linkToken) open();
      }, 500);
    } else {
      if (ready) open();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading || !user}
      className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading ? (
        <>
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Connecting...</span>
        </>
      ) : (
        <>
          <Building2 className="w-4 h-4" />
          <span>{buttonText}</span>
        </>
      )}
    </button>
  );
};

const PlaidLinkButton = (props) => {
  if (isNative) return null; // Plaid OAuth not supported in native WKWebView
  return <PlaidLinkButtonWeb {...props} />;
};

// ==================== BANK TRANSACTION REVIEW MODAL ====================
const BankTransactionReviewModal = ({
  transactions,
  onConfirm,
  onCancel,
  existingExpenses,
  learnedKeywords
}) => {
  const smartCategorize = (transaction, existing, keywords) => {
    const merchantLower = String(transaction.name || transaction.merchantName || '').toLowerCase();

    const keywordMatch = (keywords || []).find((k) =>
      merchantLower.includes(String(k.keyword || '').toLowerCase()) ||
      k.additionalKeywords?.some((ak) => merchantLower.includes(String(ak || '').toLowerCase()))
    );
    if (keywordMatch?.category) return keywordMatch.category;

    const primaryWord = merchantLower.split(' ')[0];
    const similar = (existing || []).find((exp) =>
      String(exp.merchant || '').toLowerCase().includes(primaryWord)
    );
    if (similar) return similar.category;

    if (merchantLower.includes('uber') || merchantLower.includes('lyft')) return 'Transportation';
    if (merchantLower.includes('amazon') || merchantLower.includes('ebay')) return 'Office Supplies';
    if (merchantLower.includes('starbucks') || merchantLower.includes('restaurant')) return 'Meals & Entertainment';
    if (merchantLower.includes('gas') || merchantLower.includes('shell') || merchantLower.includes('chevron')) return 'Transportation';

    return 'Other';
  };

  const detectIfSubscription = (transaction) => {
    const merchantLower = String(transaction.name || transaction.merchantName || '').toLowerCase();
    const subKeywords = ['subscription', 'monthly', 'annual', 'netflix', 'spotify',
      'adobe', 'microsoft', 'google', 'dropbox', 'github', 'slack'];
    return subKeywords.some((kw) => merchantLower.includes(kw));
  };

  const [selectedTransactions, setSelectedTransactions] = useState(
    (transactions || []).map((tx, idx) => ({
      ...tx,
      id: idx,
      selected: true,
      category: tx.category || smartCategorize(tx, existingExpenses, learnedKeywords),
      categoryConfidence: Number(tx.categoryConfidence || 0),
      categorySource: tx.categorySource || 'fallback',
      taxDeductible: false,
      isSubscription: detectIfSubscription(tx),
      notes: ''
    }))
  );

  const toggleTransaction = (id) => {
    setSelectedTransactions((prev) =>
      prev.map((tx) => (tx.id === id ? { ...tx, selected: !tx.selected } : tx))
    );
  };

  const toggleAll = () => {
    const allSelected = selectedTransactions.every((tx) => tx.selected);
    setSelectedTransactions((prev) =>
      prev.map((tx) => ({ ...tx, selected: !allSelected }))
    );
  };

  const updateTransaction = (id, field, value) => {
    setSelectedTransactions((prev) =>
      prev.map((tx) => (tx.id === id ? { ...tx, [field]: value } : tx))
    );
  };

  const handleConfirm = () => {
    const toImport = selectedTransactions.filter((tx) => tx.selected);
    onConfirm(toImport);
  };

  const selectedCount = selectedTransactions.filter((tx) => tx.selected).length;
  const totalAmount = selectedTransactions
    .filter((tx) => tx.selected)
    .reduce((sum, tx) => sum + Math.abs(tx.amount || 0), 0);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
      <div className="centris-card rounded-2xl border border-indigo-500/30 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-white">Review Bank Transactions</h2>
              <p className="text-sm text-slate-400 mt-1">
                {selectedCount} of {transactions.length} selected - ${totalAmount.toFixed(2)} total
              </p>
            </div>
            <button onClick={onCancel} className="text-slate-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex gap-3">
            <button
              onClick={toggleAll}
              className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 text-sm font-medium"
            >
              {selectedTransactions.every((tx) => tx.selected) ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {selectedTransactions.map((tx) => (
              <div
                key={tx.id}
                className={`centris-card rounded-lg p-4 border transition-all ${
                  tx.selected ? 'border-indigo-500/50 bg-indigo-500/5' : 'border-slate-700'
                }`}
              >
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    checked={tx.selected}
                    onChange={() => toggleTransaction(tx.id)}
                    className="mt-1 w-5 h-5 rounded border-slate-600 text-indigo-600 focus:ring-indigo-500"
                  />

                  <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-white">{tx.name || tx.merchantName}</p>
                      <p className="text-xs text-slate-400">{new Date(tx.date).toLocaleDateString()}</p>
                      {tx.isSubscription && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                          Possible Subscription
                        </span>
                      )}
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 mb-1">Amount</p>
                      <p className="text-sm font-semibold text-white">
                        ${Math.abs(tx.amount || 0).toFixed(2)}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 mb-1">Category</p>
                      <select
                        value={tx.category}
                        onChange={(e) => updateTransaction(tx.id, 'category', e.target.value)}
                        className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                      >
                        <option value="Computer & Software">Computer & Software</option>
                        <option value="Office Supplies">Office Supplies</option>
                        <option value="Meals & Entertainment">Meals & Entertainment</option>
                        <option value="Transportation">Transportation</option>
                        <option value="Travel & Lodging">Travel & Lodging</option>
                        <option value="Professional Services">Professional Services</option>
                        <option value="Marketing & Advertising">Marketing & Advertising</option>
                        <option value="Utilities">Utilities</option>
                        <option value="Equipment">Equipment</option>
                        <option value="Insurance">Insurance</option>
                        <option value="Education & Training">Education & Training</option>
                        <option value="Shipping & Postage">Shipping & Postage</option>
                        <option value="Other">Other</option>
                      </select>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] ${
                          tx.categoryConfidence > 80
                            ? 'bg-green-500/20 text-green-300'
                            : tx.categoryConfidence > 60
                              ? 'bg-yellow-500/20 text-yellow-300'
                              : 'bg-red-500/20 text-red-300'
                        }`}>
                          {tx.categoryConfidence || 0}% confident
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {tx.categorySource === 'learned_pattern' ? 'learned pattern' : tx.categorySource}
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 mb-1">Deductible</p>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tx.taxDeductible}
                          onChange={(e) => updateTransaction(tx.id, 'taxDeductible', e.target.checked)}
                          className="w-4 h-4 rounded border-slate-600 text-indigo-600"
                        />
                        <span className="ml-2 text-xs text-slate-300">Business</span>
                      </label>
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 mb-1">Notes</p>
                      <input
                        type="text"
                        value={tx.notes}
                        onChange={(e) => updateTransaction(tx.id, 'notes', e.target.value)}
                        placeholder="Optional"
                        className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-slate-700 centris-card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-400">
              Importing <span className="text-white font-semibold">{selectedCount}</span> transactions
              worth <span className="text-white font-semibold">${totalAmount.toFixed(2)}</span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="px-6 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedCount === 0}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Import {selectedCount} Transaction{selectedCount !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const getCashFlowItemDate = (item) => {
  const raw = item?.paidDate || item?.date;
  const parsed = raw ? new Date(raw) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const calculateCashFlowGrowthRate = (items = []) => {
  if (!items.length) return 0;
  const sorted = [...items]
    .map((item) => ({ item, date: getCashFlowItemDate(item) }))
    .filter((entry) => entry.date)
    .sort((a, b) => a.date - b.date);
  if (sorted.length < 2) return 0;

  const midpoint = Math.floor(sorted.length / 2);
  const firstHalf = sorted.slice(0, midpoint);
  const lastHalf = sorted.slice(midpoint);
  if (!firstHalf.length || !lastHalf.length) return 0;

  const averageValue = (rows) => (
    rows.reduce((sum, row) => sum + Number(row.item.total ?? row.item.amount ?? 0), 0) / rows.length
  );

  const firstAvg = averageValue(firstHalf);
  const lastAvg = averageValue(lastHalf);
  if (!Number.isFinite(firstAvg) || Math.abs(firstAvg) < 0.0001) return 0;
  return ((lastAvg - firstAvg) / firstAvg) * 100;
};

const calculateHistoricalCashFlowData = (expenses = [], invoices = [], revenue = []) => {
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);

  const recentExpenses = expenses.filter((exp) => {
    const date = getCashFlowItemDate(exp);
    return date && date >= sixMonthsAgo;
  });

  const recentInvoiceIncome = invoices.filter((inv) => {
    const date = getCashFlowItemDate(inv);
    return inv.status === 'paid' && date && date >= sixMonthsAgo;
  });
  const recentRevenueEntries = (revenue || []).filter((r) => {
    const date = r.date ? new Date(r.date) : null;
    return date && date >= sixMonthsAgo;
  });

  const totalRecentIncome =
    recentInvoiceIncome.reduce((sum, inv) => sum + Number(inv.total || 0), 0)
    + recentRevenueEntries.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  const monthlyExpenseAvg = recentExpenses.length
    ? recentExpenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0) / 6
    : 0;
  const monthlyIncomeAvg = totalRecentIncome > 0 ? totalRecentIncome / 6 : 0;

  const categoryBuckets = {};
  recentExpenses.forEach((exp) => {
    const category = exp.category || 'Uncategorized';
    if (!categoryBuckets[category]) categoryBuckets[category] = [];
    categoryBuckets[category].push(Number(exp.amount || 0));
  });

  const categoryAverages = {};
  Object.keys(categoryBuckets).forEach((category) => {
    const bucket = categoryBuckets[category];
    categoryAverages[category] = bucket.length
      ? bucket.reduce((sum, value) => sum + value, 0) / bucket.length
      : 0;
  });

  const allIncomeItems = [
    ...recentInvoiceIncome.map(i => ({ date: i.paidDate || i.date, amount: i.total })),
    ...recentRevenueEntries.map(r => ({ date: r.date, amount: r.amount })),
  ];

  return {
    monthlyExpenseAvg,
    monthlyIncomeAvg,
    categoryAverages,
    incomeGrowthRate: calculateCashFlowGrowthRate(allIncomeItems),
    expenseGrowthRate: calculateCashFlowGrowthRate(recentExpenses),
  };
};

const calculateSubscriptionCostsForMonth = (monthDate, subscriptions = []) => {
  let total = 0;
  subscriptions
    .filter((sub) => sub.status === 'active')
    .forEach((sub) => {
      const cost = Number(sub.cost || 0);
      if (sub.billingCycle === 'monthly') {
        total += cost;
        return;
      }
      if (sub.billingCycle === 'yearly') {
        const renewalDate = new Date(sub.renewalDate);
        if (!Number.isNaN(renewalDate.getTime()) && renewalDate.getMonth() === monthDate.getMonth()) {
          total += cost;
        }
      }
    });
  return total;
};

const buildCashFlowProjection = ({
  expenses = [],
  invoices = [],
  revenue = [],
  subscriptions = [],
  bankAccounts = [],
  taxVaultSettings = { enabled: true, reservePercent: 25 },
  forecastMonths = 3,
} = {}) => {
  const historical = calculateHistoricalCashFlowData(expenses, invoices, revenue);
  const currentBalance = bankAccounts.reduce(
    (sum, acc) => sum + Number(acc?.balances?.current ?? acc?.balance ?? 0),
    0
  );
  const months = Math.max(1, Number(forecastMonths || 3));
  const forecast = [];
  let runningBalance = currentBalance;

  const estimatedAnnualIncome = historical.monthlyIncomeAvg * 12;
  const annualTaxOwed = estimatedAnnualIncome * (Number(taxVaultSettings?.reservePercent ?? 25) / 100);
  const quarterlyPaymentAmount = annualTaxOwed / 4;

  for (let i = 0; i < months; i += 1) {
    const forecastDate = new Date();
    forecastDate.setMonth(forecastDate.getMonth() + i);
    const monthKey = `${forecastDate.getFullYear()}-${String(forecastDate.getMonth() + 1).padStart(2, '0')}`;

    const monthsOut = i + 1;
    const incomeGrowthMultiplier = 1 + (historical.incomeGrowthRate / 100) * (monthsOut / 12);
    const expenseGrowthMultiplier = 1 + (historical.expenseGrowthRate / 100) * (monthsOut / 12);
    const projectedIncome = historical.monthlyIncomeAvg * incomeGrowthMultiplier;
    const projectedExpenses = historical.monthlyExpenseAvg * expenseGrowthMultiplier;
    const subscriptionCosts = calculateSubscriptionCostsForMonth(forecastDate, subscriptions);
    const taxReserve = taxVaultSettings?.enabled
      ? projectedIncome * (Number(taxVaultSettings?.reservePercent ?? 25) / 100)
      : 0;
    const isQuarterlyMonth = [3, 5, 8].includes(forecastDate.getMonth())
      || (forecastDate.getMonth() === 0 && i > 0);
    const quarterlyTaxPayment = isQuarterlyMonth ? quarterlyPaymentAmount : 0;
    const totalIn = projectedIncome;
    const totalOut = projectedExpenses + subscriptionCosts + taxReserve + quarterlyTaxPayment;
    const netCashFlow = totalIn - totalOut;

    runningBalance += netCashFlow;

    forecast.push({
      month: monthKey,
      monthName: forecastDate.toLocaleString('default', { month: 'short', year: 'numeric' }),
      date: forecastDate,
      projectedIncome,
      projectedExpenses,
      subscriptionCosts,
      taxReserve,
      quarterlyTaxPayment,
      totalIn,
      totalOut,
      netCashFlow,
      endingBalance: runningBalance,
      categoryBreakdown: historical.categoryAverages,
      confidence: i === 0 ? 95 : Math.max(15, 95 - i * 10),
    });
  }

  let runway = months;
  for (let i = 0; i < forecast.length; i += 1) {
    if (forecast[i].endingBalance <= 0) {
      runway = i;
      break;
    }
  }

  const worstMonth = forecast.reduce(
    (worst, month) => (month.endingBalance < worst.endingBalance ? month : worst),
    forecast[0]
  );
  const bestMonth = forecast.reduce(
    (best, month) => (month.endingBalance > best.endingBalance ? month : best),
    forecast[0]
  );

  return { forecast, currentBalance, historical, runway, worstMonth, bestMonth };
};

const CashFlowForecast = ({
  user,
  expenses = [],
  invoices = [],
  revenue = [],
  subscriptions = [],
  bankAccounts = [],
  taxVaultSettings = { enabled: true, reservePercent: 25 },
  perms = null,
}) => {
  const [forecastMonths, setForecastMonths] = useState(3);
  const maxForecastMonths = Math.min(12, Number(perms?.forecastMonthsMax || 3));
  const [showDetails, setShowDetails] = useState(null);

  const projection = useMemo(
    () => buildCashFlowProjection({
      expenses,
      invoices,
      revenue,
      subscriptions,
      bankAccounts,
      taxVaultSettings,
      forecastMonths,
    }),
    [expenses, invoices, revenue, subscriptions, bankAccounts, taxVaultSettings, forecastMonths]
  );

  const {
    forecast,
    currentBalance,
    historical,
    runway,
    worstMonth,
    bestMonth,
  } = projection;

  const endingBalance = forecast.length ? forecast[forecast.length - 1].endingBalance : currentBalance;

  return (
    <div className="space-y-6">
      <div className="centris-card rounded-xl border border-blue-500/30 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/20 p-3 rounded-xl">
              <TrendingUp className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Cash Flow Forecast</h2>
              <p className="text-sm text-blue-300">Hi {user?.name || 'there'}, next {forecastMonths} months projection</p>
            </div>
          </div>

          <select
            value={forecastMonths}
            onChange={(e) => setForecastMonths(Number(e.target.value))}
            className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm"
          >
            <option value={3}>3 Months</option>
            {maxForecastMonths >= 6 && <option value={6}>6 Months</option>}
            {maxForecastMonths >= 12 && <option value={12}>12 Months</option>}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="centris-card rounded-lg p-4">
            <p className="text-xs text-slate-400 mb-1">Current Balance</p>
            <p className="text-2xl font-bold text-white">${currentBalance.toLocaleString()}</p>
          </div>
          <div className="centris-card rounded-lg p-4">
            <p className="text-xs text-slate-400 mb-1">Projected at {forecastMonths} Months</p>
            <p className={`text-2xl font-bold ${endingBalance > currentBalance ? 'text-green-400' : 'text-red-400'}`}>
              ${endingBalance.toLocaleString()}
            </p>
          </div>
          <div className="centris-card rounded-lg p-4">
            <p className="text-xs text-slate-400 mb-1">Runway</p>
            <p className={`text-2xl font-bold ${runway < 3 ? 'text-red-400' : runway < 6 ? 'text-yellow-400' : 'text-green-400'}`}>
              {runway >= forecastMonths ? `${forecastMonths}+` : runway} months
            </p>
          </div>
          <div className="centris-card rounded-lg p-4">
            <p className="text-xs text-slate-400 mb-1">Avg Monthly Income</p>
            <p className="text-2xl font-bold text-white">${historical.monthlyIncomeAvg.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {runway < 3 && (
        <div className="bg-rose-500/15 border border-rose-500/40 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-300 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-rose-200">Cash Flow Warning</p>
              <p className="text-sm text-rose-100 mt-1">
                Your projected balance will hit zero in {runway} months. Consider reducing expenses or increasing income.
              </p>
            </div>
          </div>
        </div>
      )}

      {worstMonth && worstMonth.endingBalance < currentBalance * 0.3 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-yellow-300">Lean Month Ahead</p>
              <p className="text-sm text-yellow-200 mt-1">
                {worstMonth.monthName} looks tight with projected balance of ${worstMonth.endingBalance.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="centris-card rounded-xl p-6">
        <h3 className="text-base font-semibold text-white mb-4">Balance Projection</h3>
        <div className="space-y-3">
          {forecast.map((month, idx) => {
            const maxBalance = Math.max(...forecast.map((m) => Math.abs(m.endingBalance)), Math.abs(currentBalance), 1);
            const barWidth = (Math.abs(month.endingBalance) / maxBalance) * 100;
            const isNegative = month.endingBalance < 0;

            return (
              <div key={month.month} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400 w-20">{month.monthName}</span>
                  <div className="flex-1 mx-4 relative">
                    <div className="w-full bg-slate-700 rounded-full h-8 overflow-hidden">
                      <div
                        className={`h-8 rounded-full transition-all ${isNegative ? 'bg-gradient-to-r from-rose-700 to-red-600' : 'bg-gradient-to-r from-green-600 to-emerald-500'}`}
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    <div className="absolute -top-1 -right-1">
                      <div
                        className="w-3 h-3 rounded-full border-2 border-slate-900"
                        style={{
                          backgroundColor: month.confidence > 80 ? '#10b981' : month.confidence > 60 ? '#f59e0b' : '#ef4444',
                        }}
                        title={`${month.confidence}% confidence`}
                      />
                    </div>
                  </div>
                  <span className={`font-semibold w-32 text-right ${isNegative ? 'text-rose-300' : 'text-white'}`}>
                    ${month.endingBalance.toLocaleString()}
                  </span>
                  <button onClick={() => setShowDetails(showDetails === idx ? null : idx)} className="ml-2 text-slate-400 hover:text-white">
                    <ChevronDown className={`w-4 h-4 transition-transform ${showDetails === idx ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {showDetails === idx && (
                  <div className="ml-24 mr-36 centris-card rounded-lg p-4 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-green-400 mb-1">Income</p>
                        <p className="text-white font-medium">+${month.projectedIncome.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-rose-300 mb-1">Expenses</p>
                        <p className="text-white font-medium">-${month.projectedExpenses.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-purple-400 mb-1">Subscriptions</p>
                        <p className="text-white font-medium">-${month.subscriptionCosts.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-yellow-400 mb-1">Tax Reserve</p>
                        <p className="text-white font-medium">-${month.taxReserve.toLocaleString()}</p>
                      </div>
                      {month.quarterlyTaxPayment > 0 && (
                        <div className="col-span-2">
                          <p className="text-xs text-orange-400 mb-1">Quarterly Tax Payment</p>
                          <p className="text-white font-medium">-${month.quarterlyTaxPayment.toLocaleString()}</p>
                        </div>
                      )}
                      <div className="col-span-2 pt-2 border-t border-slate-700">
                        <p className="text-xs text-slate-400 mb-1">Net Cash Flow</p>
                        <p className={`font-bold ${month.netCashFlow >= 0 ? 'text-green-400' : 'text-rose-300'}`}>
                          {month.netCashFlow >= 0 ? '+' : ''}${month.netCashFlow.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 pt-4 border-t border-slate-700 flex items-center justify-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-slate-400">High Confidence (90%+)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-slate-400">Medium Confidence (70-90%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-slate-400">Low Confidence (&lt;70%)</span>
          </div>
        </div>
      </div>

      <div className="centris-card rounded-xl p-6">
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-400" />
          AI Insights
        </h3>
        <div className="space-y-3">
          {historical.incomeGrowthRate > 0 && (
            <div className="flex items-start gap-3 bg-green-500/10 rounded-lg p-3 border border-green-500/20">
              <TrendingUp className="w-4 h-4 text-green-400 mt-0.5" />
              <p className="text-sm text-green-200">Your income is growing at {historical.incomeGrowthRate.toFixed(1)}% recently. Keep it up.</p>
            </div>
          )}
          {historical.incomeGrowthRate < 0 && (
            <div className="flex items-start gap-3 bg-rose-500/15 rounded-lg p-3 border border-rose-500/35">
              <TrendingDown className="w-4 h-4 text-rose-300 mt-0.5" />
              <p className="text-sm text-rose-100">Income has declined {Math.abs(historical.incomeGrowthRate).toFixed(1)}% recently. Consider boosting sales.</p>
            </div>
          )}
          {forecast.some((m) => m.quarterlyTaxPayment > 0) && (
            <div className="flex items-start gap-3 bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
              <Calendar className="w-4 h-4 text-blue-400 mt-0.5" />
              <p className="text-sm text-blue-200">
                Quarterly tax payment of ${forecast.find((m) => m.quarterlyTaxPayment > 0).quarterlyTaxPayment.toLocaleString()} due in {forecast.find((m) => m.quarterlyTaxPayment > 0).monthName}
              </p>
            </div>
          )}
          {bestMonth && (
            <div className="flex items-start gap-3 bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
              <Target className="w-4 h-4 text-purple-400 mt-0.5" />
              <p className="text-sm text-purple-200">
                Best projected month: {bestMonth.monthName} with ${bestMonth.endingBalance.toLocaleString()} balance
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="centris-card rounded-lg p-4">
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-400 hover:text-white font-medium">How is this calculated?</summary>
          <div className="mt-3 text-xs text-slate-500 space-y-2">
            <p>
              <strong className="text-slate-400">Income:</strong> Average of last 6 months of paid invoices, adjusted for trend.
            </p>
            <p>
              <strong className="text-slate-400">Expenses:</strong> Average of last 6 months of expenses, adjusted for trend.
            </p>
            <p>
              <strong className="text-slate-400">Subscriptions:</strong> Active renewal dates and billing cycles.
            </p>
            <p>
              <strong className="text-slate-400">Tax Reserve:</strong> {taxVaultSettings?.reservePercent || 25}% of gross income {taxVaultSettings?.enabled ? '(Auto-reserved)' : '(Manual)'}.
            </p>
            <p>
              <strong className="text-slate-400">Confidence:</strong> Decreases over time as forecasts extend farther out.
            </p>
          </div>
        </details>
      </div>
    </div>
  );
};

// Supported currencies for the Settings > Currency preference. Symbol is used
// for the compact/manual-concat formats; code feeds Intl.NumberFormat directly.
const CURRENCIES = {
  USD: { symbol: '$', locale: 'en-US' },
  EUR: { symbol: '€', locale: 'de-DE' },
  GBP: { symbol: '£', locale: 'en-GB' },
  CAD: { symbol: 'CA$', locale: 'en-CA' },
  AUD: { symbol: 'AU$', locale: 'en-AU' },
  INR: { symbol: '₹', locale: 'en-IN' },
  JPY: { symbol: '¥', locale: 'ja-JP' },
};
const getCurrencySymbol = (code) => CURRENCIES[code]?.symbol || CURRENCIES.USD.symbol;

const moneyCompact = (n, currencyCode = 'USD') => {
  const v = Number(n || 0);
  const symbol = getCurrencySymbol(currencyCode);
  if (Math.abs(v) >= 1000) return `${symbol}${(v / 1000).toFixed(1)}k`;
  return `${symbol}${v.toFixed(0)}`;
};

const pctChange = (a, b) => {
  const av = Number(a || 0);
  const bv = Number(b || 0);
  if (!bv) return 0;
  return ((av - bv) / bv) * 100;
};

const buildMonthlyDashboardSeries = (expenses = [], invoices = [], revenue = []) => {
  const now = new Date();
  const months = Array.from({ length: 12 }, (_, idx) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - idx), 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return {
      key,
      month: d.toLocaleString('default', { month: 'short' }),
      revenue: 0,
      expenses: 0,
      invoiced: 0,
    };
  });
  const indexByKey = months.reduce((acc, m, i) => { acc[m.key] = i; return acc; }, {});

  expenses.forEach((e) => {
    const date = getCashFlowItemDate(e);
    if (!date) return;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const idx = indexByKey[key];
    if (typeof idx !== 'number') return;
    months[idx].expenses += Number(e.amount || 0);
  });

  invoices.forEach((inv) => {
    const baseDate = getCashFlowItemDate(inv);
    if (!baseDate) return;
    const key = `${baseDate.getFullYear()}-${String(baseDate.getMonth() + 1).padStart(2, '0')}`;
    const idx = indexByKey[key];
    if (typeof idx !== 'number') return;
    const amount = Number(inv.total || 0);
    months[idx].invoiced += amount;
    if (inv.status === 'paid') months[idx].revenue += amount;
  });

  revenue.forEach((r) => {
    const date = getCashFlowItemDate(r);
    if (!date) return;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const idx = indexByKey[key];
    if (typeof idx !== 'number') return;
    months[idx].revenue += Number(r.amount || 0);
  });

  return months;
};

function TrendLineGraph({ data, keys, colors, height = 150, showDots = true, lightMode = false }) {
  const [hovered, setHovered] = useState(null);
  const [W, setW] = useState(600);
  const containerRef = useRef(null);
  const gradId = useMemo(() => Math.random().toString(36).slice(2, 9), []);

  useEffect(() => {
    if (!containerRef.current) return;
    const initial = containerRef.current.getBoundingClientRect().width;
    if (initial > 0) setW(Math.round(initial));
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0) setW(Math.round(w));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const H = height;
  const allVals = data.flatMap((d) => keys.map((k) => Number(d[k] || 0)));
  const minV = Math.min(...allVals, 0) * 0.9;
  const maxV = Math.max(...allVals, 1) * 1.05;
  const range = maxV - minV || 1;
  const x = (i) => (i / Math.max(1, data.length - 1)) * W;
  const y = (v) => H - (((Number(v || 0) - minV) / range) * H * 0.92) - H * 0.04;
  const pathFor = (key) => data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d[key]).toFixed(1)}`).join(' ');
  const areaFor = (key) => `${pathFor(key)} L ${x(data.length - 1).toFixed(1)} ${H} L 0 ${H} Z`;

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: H + 28 }}>
      {W > 0 && (
        <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full" style={{ height: H }} onMouseLeave={() => setHovered(null)}>
          {[0.25, 0.5, 0.75].map((p) => (
            <line key={p} x1="0" y1={H * p} x2={W} y2={H * p} stroke={lightMode ? 'rgba(15,23,42,0.07)' : 'rgba(255,255,255,0.06)'} strokeWidth="1" />
          ))}
          {keys.map((k, i) => (
            <path key={k} d={pathFor(k)} fill="none" stroke={colors[i]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          ))}
          {data.map((_, i) => (
            <rect key={i} x={Math.max(0, x(i) - W / data.length / 2)} y={0} width={W / data.length} height={H} fill="transparent" onMouseEnter={() => setHovered(i)} />
          ))}
          {hovered !== null && (
            <>
              <line x1={x(hovered)} y1={0} x2={x(hovered)} y2={H} stroke={lightMode ? 'rgba(15,23,42,0.15)' : 'rgba(148,163,184,0.3)'} strokeWidth="1" strokeDasharray="3,3" />
              {keys.map((k, i) => (
                <circle key={`${k}-hover`} cx={x(hovered)} cy={y(data[hovered][k])} r="4" fill={colors[i]} stroke={lightMode ? 'rgba(255,255,255,0.95)' : 'rgba(15,23,42,0.85)'} strokeWidth="1.5" />
              ))}
            </>
          )}
          {showDots && keys.map((k, i) => (
            <circle key={`${k}-last`} cx={x(data.length - 1)} cy={y(data[data.length - 1][k])} r="3" fill={colors[i]} stroke={lightMode ? 'rgba(255,255,255,0.9)' : 'rgba(15,23,42,0.5)'} strokeWidth="1" />
          ))}
        </svg>
      )}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
        {data.map((d, i) => {
          const step = data.length <= 4 ? 1 : data.length <= 7 ? 2 : 3;
          const show = i === 0 || i === data.length - 1 || i % step === 0;
          if (!show) return null;
          const leftPct = (i / Math.max(1, data.length - 1)) * 100;
          const transform = i === 0 ? 'translateX(0%)' : i === data.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)';
          return (
            <span key={i} className="text-[9px] text-slate-500 absolute whitespace-nowrap" style={{ left: `${leftPct}%`, transform }}>
              {d.month}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function MiniSparkline({ data, color = '#6366f1', height = 30 }) {
  const gradId = useMemo(() => Math.random().toString(36).slice(2, 9), []);
  const vals = data.map((n) => Number(n || 0));
  const min = Math.min(...vals, 0) * 0.9;
  const max = Math.max(...vals, 1) * 1.05;
  const range = max - min || 1;
  const W = 100;
  const H = height;
  const pts = vals.map((v, i) => [(i / Math.max(1, vals.length - 1)) * W, H - (((v - min) / range) * H)]);
  const d = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const area = `${d} L ${pts[pts.length - 1][0]} ${H} L 0 ${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
      <defs>
        <linearGradient id={`sp-${gradId}-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#sp-${gradId}-${color.replace('#', '')})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SegmentDonut({ segments, size = 80, thickness = 10 }) {
  const r = (size / 2) - thickness;
  const circ = 2 * Math.PI * r;
  const total = Math.max(1, segments.reduce((s, seg) => s + Number(seg.value || 0), 0));
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={thickness} />
      {segments.map((seg, i) => {
        const dash = (Number(seg.value || 0) / total) * circ;
        const gap = circ - dash;
        const el = (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            strokeLinecap="round"
          />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

function HorizontalBar({ label, value, max, color }) {
  const w = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-[11px] text-slate-500 w-8 shrink-0">{label}</span>
      <div className="flex-1 bg-slate-700/30 rounded-full h-1.5 overflow-hidden">
        <div className="h-1.5 rounded-full transition-all duration-700" style={{ width: `${w}%`, background: color }} />
      </div>
      <span className="text-[11px] text-white w-12 text-right">{moneyCompact(value)}</span>
    </div>
  );
}

const RevenueChartsPanel = ({ expenses = [], invoices = [], revenue = [], lightMode = false }) => {
  const [range, setRange] = useState('12m');
  const monthly = useMemo(() => buildMonthlyDashboardSeries(expenses, invoices, revenue), [expenses, invoices, revenue]);
  const data = range === '3m' ? monthly.slice(-3) : range === '6m' ? monthly.slice(-6) : monthly;
  const rawData = data.length ? data : [{ month: 'Now', revenue: 0, expenses: 0, invoiced: 0 }];
  const safeData = rawData.map(d => ({ ...d, net: (Number(d.revenue || 0)) - (Number(d.expenses || 0)) }));

  const current = safeData[safeData.length - 1];
  const prev = safeData[safeData.length - 2] || safeData[safeData.length - 1];
  const avg = Math.round(safeData.reduce((s, m) => s + Number(m.revenue || 0), 0) / Math.max(1, safeData.length));
  const totalRev = safeData.reduce((s, d) => s + Number(d.revenue || 0), 0);
  const totalExp = safeData.reduce((s, d) => s + Number(d.expenses || 0), 0);
  const netProfit = totalRev - totalExp;
  const margin = totalRev > 0 ? ((netProfit / totalRev) * 100) : 0;
  const revDelta = pctChange(current.revenue, prev.revenue);
  const maxMonth = safeData.reduce((m, d) => (Number(d.revenue || 0) > Number(m.revenue || 0) ? d : m), safeData[0]);

  const categoryTotals = Object.entries(
    expenses.reduce((acc, e) => {
      const key = e.category || 'Other';
      acc[key] = (acc[key] || 0) + Number(e.amount || 0);
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]).slice(0, 4);
  const donutPalette = lightMode ? ['#2563eb', '#059669', '#f97316', '#06b6d4'] : ['#6366f1', '#8b5cf6', '#f97316', '#06b6d4'];
  const spendSegments = categoryTotals.length
    ? categoryTotals.map(([label, value], i) => ({ label, value, color: donutPalette[i % donutPalette.length] }))
    : [
      { label: 'Business', value: 38, color: lightMode ? '#2563eb' : '#6366f1' },
      { label: 'Subs', value: 22, color: lightMode ? '#059669' : '#8b5cf6' },
      { label: 'Personal', value: 28, color: '#f97316' },
      { label: 'Travel', value: 12, color: '#06b6d4' },
    ];
  const spendTotal = spendSegments.reduce((s, seg) => s + Number(seg.value || 0), 0);

  return (
    <div className="space-y-4">
      <div className="centris-card rounded-2xl p-5 shadow-lg shadow-black/10">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Revenue</p>
            <div className="flex items-end gap-3 flex-wrap">
              <p className="text-2xl font-bold text-white">{moneyCompact(totalRev)}</p>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mb-0.5 flex items-center gap-1 ${revDelta >= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                {revDelta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(revDelta).toFixed(1)}% vs last month
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">avg {moneyCompact(avg)}/mo - best {maxMonth.month} ({moneyCompact(maxMonth.revenue)})</p>
          </div>
          <div className="flex gap-1 centris-card rounded-lg p-0.5">
            {['3m', '6m', '12m'].map((r) => (
              <button key={r} onClick={() => setRange(r)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${range === r ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-4 mb-3">
          {[['Revenue', lightMode ? '#2563eb' : '#38bdf8'], ['Expenses', lightMode ? '#e11d48' : '#fb7185'], ['Invoiced', lightMode ? '#0891b2' : '#4ade80'], ['Net Profit', lightMode ? '#16a34a' : '#fde047']].map(([label, color]) => (
            <div key={label} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: color }} />
              <span className="text-[11px] text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <TrendLineGraph data={safeData} keys={['revenue', 'expenses', 'invoiced', 'net']} colors={[lightMode ? '#2563eb' : '#38bdf8', lightMode ? '#e11d48' : '#fb7185', lightMode ? '#0891b2' : '#4ade80', lightMode ? '#16a34a' : '#fde047']} height={160} lightMode={lightMode} />
      </div>

    </div>
  );
};

const BankingHub = ({
  user,
  bankAccounts = [],
  onConnectBank,
  onImportTransactions,
  plaidConnectEnabled = true,
}) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [recentTx, setRecentTx] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);

  const totalBalance = bankAccounts.reduce((s, a) => s + Number(a?.balances?.current || 0), 0);
  const checkingAccounts = bankAccounts.filter((a) => a.type === 'depository' && a.subtype === 'checking');
  const savingsAccounts = bankAccounts.filter((a) => a.type === 'depository' && a.subtype === 'savings');
  const creditCards = bankAccounts.filter((a) => a.type === 'credit');
  const fmt = (n) => `$${Math.abs(Number(n || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const loadTransactions = async (account) => {
    if (!user?.uid) return;
    setSelectedAccount(account);
    setLoadingTx(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const end = new Date().toISOString().split('T')[0];
      const start = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
      const response = await fetch(
        `https://us-central1-receiptwise-prod.cloudfunctions.net/plaidGetTransactions?userId=${user.uid}&startDate=${start}&endDate=${end}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const data = await response.json();
      if (data.success) {
        const accountId = account.account_id || account.accountId;
        const filtered = (data.transactions || []).filter((tx) => (tx.account_id || tx.accountId) === accountId);
        setRecentTx(filtered.slice(0, 50));
      }
    } catch (err) {
      console.error('Load account transactions error:', err);
      setRecentTx([]);
    }
    setLoadingTx(false);
  };

  if (selectedAccount) {
    const isCreditCard = selectedAccount.type === 'credit';
    const limit = Number(selectedAccount?.balances?.limit || 0);
    const current = Math.abs(Number(selectedAccount?.balances?.current || 0));
    const utilization = limit > 0 ? (current / limit) * 100 : 0;
    const bankDomain = String(selectedAccount?.institutionName || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

    return (
      <div className="space-y-6" style={{ animation: 'pageSlideIn 0.4s ease-out both' }}>
        <button onClick={() => setSelectedAccount(null)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-white transition-colors group">
          <ArrowRight className="w-4 h-4 rotate-180 group-hover:-translate-x-1 transition-transform" />
          Back to Banking
        </button>

        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-slate-500">{selectedAccount.institutionName} ****{selectedAccount.mask}</p>
              <h2 className="text-2xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>{selectedAccount.name}</h2>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">{isCreditCard ? 'Balance Owed' : 'Available'}</p>
              <p className={`text-3xl font-bold ${isCreditCard ? 'text-red-400' : 'text-emerald-400'}`} style={{ letterSpacing: '-0.03em' }}>
                {fmt(selectedAccount?.balances?.current || 0)}
              </p>
            </div>
          </div>

          {isCreditCard && limit > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-2">
                <span className="text-slate-500">Credit Utilization</span>
                <span className={`font-semibold ${utilization > 75 ? 'text-red-400' : utilization > 30 ? 'text-amber-400' : 'text-emerald-400'}`}>{utilization.toFixed(0)}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-white/[0.04]">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(utilization, 100)}%`,
                    background: utilization > 75
                      ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                      : utilization > 30
                        ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                        : 'linear-gradient(90deg, #10b981, #059669)',
                  }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-slate-600 mt-1">
                <span>{fmt(current)} used</span>
                <span>{fmt(limit)} limit</span>
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-5">
            {isCreditCard && bankDomain && (
              <a
                href={`https://${bankDomain}.com`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/15 hover:bg-red-500/20 transition-all"
              >
                <Lock className="w-3.5 h-3.5" />
                Freeze Card (via Bank)
              </a>
            )}
            <button
              onClick={() => onImportTransactions?.()}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium bg-violet-500/10 text-violet-400 border border-violet-500/15 hover:bg-violet-500/20 transition-all"
            >
              <Download className="w-3.5 h-3.5" />
              Import to Expenses
            </button>
          </div>
        </div>

        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold text-white mb-4">Last 30 Days</h3>
          {loadingTx ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-slate-500">Loading transactions...</p>
            </div>
          ) : recentTx.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No recent transactions</p>
          ) : (
            <div className="space-y-1">
              {recentTx.map((tx, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/[0.03] last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{tx.name || tx.merchant_name}</p>
                    <p className="text-[11px] text-slate-600">{tx.date} · {tx.category?.[0] || 'Other'}</p>
                  </div>
                  <p className={`text-sm font-semibold ${tx.amount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {tx.amount > 0 ? '-' : '+'}{fmt(tx.amount)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const AccountCard = ({ account, colorClass = 'emerald' }) => (
    <button
      onClick={() => loadTransactions(account)}
      className="centris-card rounded-xl p-5 text-left hover:border-white/[0.2] transition-all"
    >
      <div className="flex items-center justify-between mb-3">
        <Wallet className={`w-5 h-5 ${colorClass === 'red' ? 'text-red-400' : colorClass === 'cyan' ? 'text-cyan-400' : 'text-emerald-400'}`} />
        <ArrowRight className="w-4 h-4 text-slate-500" />
      </div>
      <p className="text-sm font-semibold text-white">{account.name}</p>
      <p className="text-xs text-slate-500 mt-1">{account.institutionName} ****{account.mask}</p>
      <p className="text-lg font-bold text-white mt-3">{fmt(account?.balances?.current || 0)}</p>
    </button>
  );

  return (
    <div className="relative space-y-8" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>

      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-slate-500">{bankAccounts.length} account{bankAccounts.length !== 1 ? 's' : ''}</p>
            <p className="text-2xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>{fmt(totalBalance)}</p>
          </div>
          <button type="button" onClick={() => onImportTransactions?.()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600/20 text-violet-400 border border-violet-500/20 text-xs font-medium">
            <Upload className="w-3 h-3" /> Import Statement
          </button>
        </div>

        {checkingAccounts.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-emerald-400 mb-3 tracking-wide uppercase">Checking</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {checkingAccounts.map((acc, i) => <AccountCard key={acc.account_id || acc.accountId || i} account={acc} colorClass="emerald" />)}
            </div>
          </div>
        )}

        {savingsAccounts.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-cyan-400 mb-3 tracking-wide uppercase">Savings</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {savingsAccounts.map((acc, i) => <AccountCard key={acc.account_id || acc.accountId || i} account={acc} colorClass="cyan" />)}
            </div>
          </div>
        )}

        {creditCards.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-red-400 mb-3 tracking-wide uppercase">Credit Cards</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {creditCards.map((acc, i) => <AccountCard key={acc.account_id || acc.accountId || i} account={acc} colorClass="red" />)}
            </div>
          </div>
        )}

        {plaidConnectEnabled && (
          <button type="button" onClick={onConnectBank} className="w-full centris-card p-3 text-center rounded-xl flex items-center justify-center gap-2 text-xs text-slate-500 hover:text-white transition-all">
            <Plus className="w-3.5 h-3.5" /> Connect Another Account
          </button>
        )}
      </div>
    </div>
  );
};

const TaxVaultReminderModal = ({ data, userId, onConfirm, onSkip }) => {
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    await addTaxVaultTransaction(userId, {
      type: 'deposit',
      amount: data.amount,
      date: new Date().toISOString().split('T')[0],
      note: `Tax reserve from Invoice ${data.invoiceNumber} - ${data.client}`,
      source: 'manual_confirmed',
      sourceInvoiceId: String(data.invoiceId),
    });
    setConfirming(false);
    setDone(true);
    setTimeout(() => onConfirm(), 1500);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center z-[999] p-4">
      <div className="centris-card rounded-2xl w-full max-w-sm overflow-hidden" style={{ boxShadow: '0 40px 100px rgba(0,0,0,0.6)' }}>
        <div className="h-1 w-full bg-gradient-to-r from-emerald-500 to-teal-500" />
        <div className="p-6">
          {done ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              </div>
              <p className="text-white font-semibold">Added to Tax Vault</p>
              <p className="text-xs text-slate-500 mt-1">${data.amount.toFixed(2)} reserved for taxes</p>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Invoice {data.invoiceNumber} just got paid</p>
                  <p className="text-xs text-slate-400 mt-0.5">{data.client} paid ${data.invoiceTotal.toLocaleString()}</p>
                </div>
              </div>
              <div className="bg-emerald-500/8 border border-emerald-500/20 rounded-xl p-4 mb-5">
                <p className="text-sm text-emerald-300 font-medium mb-1">Set aside ${data.amount.toFixed(2)} for taxes</p>
                <p className="text-xs text-slate-400 leading-relaxed">
                  That's {data.reservePct}% of this payment - your current tax reserve rate. Transfer this to a separate savings account and log it in your Tax Vault so you're never caught short at tax time.
                </p>
              </div>
              <div className="centris-card rounded-xl p-3 mb-5">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-semibold">What to do right now</p>
                {[
                  'Open your bank app',
                  `Transfer $${data.amount.toFixed(2)} to your tax savings account`,
                  'Tap "Yes, I moved it" below to log it',
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <span className="w-4 h-4 rounded-full bg-slate-700 text-slate-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <p className="text-xs text-slate-300">{step}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={onSkip} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 centris-card hover:text-white transition-colors">
                  Remind me later
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:shadow-lg hover:shadow-emerald-500/20 transition-all disabled:opacity-60 flex items-center justify-center gap-1.5"
                >
                  {confirming ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Saving...</> : <>Yes, I moved it</>}
                </button>
              </div>
              <p className="text-center text-[11px] text-slate-600 mt-3">You can always log this manually in the Tax Vault tab</p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const PaygCostBadge = ({ action, user, paygPrices }) => {
  const cost = paygPrices?.[action];
  if (!cost) return null;
  return (
    <div className="flex items-center gap-2 text-[10px] text-amber-400 bg-amber-500/5 rounded-lg px-3 py-1.5 border border-amber-500/10">
      <Zap className="w-3 h-3" />
      <span>Costs ${(cost / 100).toFixed(2)} · Balance: ${((user?.paygCredits || 0) / 100).toFixed(2)}</span>
    </div>
  );
};

const AddCreditsModal = ({ user, onClose, showToast }) => {
  const [loading, setLoading] = useState(null);

  const handleBuy = async (priceKey) => {
    setLoading(priceKey);
    try {
      const result = await createCheckoutSession(priceKey);
      if (result.url) window.location.href = result.url;
      else if (showToast) showToast('Failed to start purchase: ' + (result.error || 'Unknown error'), 'error');
    } catch (err) {
      if (showToast) showToast('Purchase error: ' + err.message, 'error');
    } finally {
      setLoading(null);
    }
  };

  const packs = [
    { key: 'credits_5', amount: '$5', credits: '100 messages or 50 scans', bonus: '' },
    { key: 'credits_10', amount: '$10', credits: '200 messages or 100 scans', bonus: 'Most Popular' },
    { key: 'credits_25', amount: '$25', credits: '500 messages or 250 scans', bonus: 'Best Value' },
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[999] flex items-center justify-center p-4">
      <div className="centris-card rounded-2xl max-w-md w-full">
        <div className="p-6 text-center border-b border-white/[0.06]">
          <h2 className="text-lg font-bold text-white">Add Credits</h2>
          <p className="text-xs text-slate-400 mt-1">
            Current balance: <span className="text-amber-400 font-semibold">${((user?.paygCredits || 0) / 100).toFixed(2)}</span>
          </p>
        </div>
        <div className="p-6 space-y-3">
          {packs.map((pack) => (
            <button
              key={pack.key}
              onClick={() => handleBuy(pack.key)}
              disabled={Boolean(loading)}
              className="w-full flex items-center justify-between centris-card rounded-xl p-4 transition-all disabled:opacity-50"
            >
              <div className="text-left">
                <p className="text-lg font-bold text-white">{pack.amount}</p>
                <p className="text-[10px] text-slate-500">{pack.credits}</p>
              </div>
              <div className="text-right">
                {pack.bonus && <span className="text-[10px] text-cyan-400 font-semibold">{pack.bonus}</span>}
                {loading === pack.key && <Loader className="w-4 h-4 text-white animate-spin" />}
              </div>
            </button>
          ))}
        </div>
        <div className="px-6 pb-6">
          <button onClick={onClose} className="w-full py-2.5 rounded-xl text-xs font-semibold bg-white/[0.04] text-slate-400 border border-white/[0.06] hover:bg-white/[0.06] transition-all">
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Native IAP Pricing Modal (iOS only — uses cordova-plugin-purchase) ───────

const NATIVE_PLANS = [
  {
    key: 'plus',
    productId: 'com.centrisfinance.plus.monthly',
    annualProductId: 'com.centrisfinance.plus.annual',
    name: 'Plus',
    monthlyPrice: '$19.99',
    annualPrice: '$199.99',
    annualMonthly: '$16.67',
    color: 'cyan',
    features: ['Unlimited receipt scans', 'Unlimited invoices', 'Unlimited Quantra AI', 'Advanced reports & exports', 'Branded invoices', 'Smart alerts', '6-month forecast'],
  },
  {
    key: 'pro',
    productId: 'com.centrisfinance.pro.monthly',
    annualProductId: 'com.centrisfinance.pro.annual',
    name: 'Pro',
    monthlyPrice: '$39.99',
    annualPrice: '$399.99',
    annualMonthly: '$33.33',
    color: 'violet',
    popular: true,
    features: ['Everything in Plus', '12-month forecast', 'Client scorecards', 'Business analytics tools', 'Marketing hub (50/mo)', '5 bank connections'],
  },
  {
    key: 'elite',
    productId: 'com.centrisfinance.elite.monthly',
    annualProductId: 'com.centrisfinance.elite.annual',
    name: 'Elite',
    monthlyPrice: '$69.99',
    annualPrice: '$699.99',
    annualMonthly: '$58.33',
    color: 'amber',
    features: ['Everything in Pro', 'Unlimited marketing tools', '10 bank connections', 'Video clips included', 'Priority AI CFO', 'White-glove support'],
  },
];

const NativePricingModal = ({ currentPlan, onClose, onPurchaseSuccess, userId }) => {
  const [loading, setLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [error, setError] = useState('');
  const [productsLoaded, setProductsLoaded] = useState(false);

  const colorMap = {
    cyan: { border: 'border-cyan-500/40', text: 'text-cyan-400', bg: 'bg-cyan-500/10', btn: 'from-cyan-600 to-blue-600' },
    violet: { border: 'border-violet-500/40', text: 'text-violet-400', bg: 'bg-violet-500/10', btn: 'from-violet-600 to-purple-600' },
    amber: { border: 'border-amber-500/40', text: 'text-amber-400', bg: 'bg-amber-500/10', btn: 'from-amber-500 to-orange-500' },
  };

  const getIAP = () => Capacitor.isNativePlatform() ? IAPPlugin : null;

  useEffect(() => {
    const prefetch = async () => {
      try {
        const IAP = getIAP();
        if (!IAP) return;
        const allIds = NATIVE_PLANS.flatMap(p => [p.productId, p.annualProductId]);
        await IAPPlugin.getProducts({ productIds: allIds });
        setProductsLoaded(true);
      } catch {
        setProductsLoaded(true);
      }
    };
    prefetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePurchase = async (plan) => {
    setError('');
    setLoading(plan.key);
    let purchaseTimerId = null;
    try {
      const IAP = getIAP();
      if (!IAP) throw new Error('IAP not available');
      const productId = billingCycle === 'annual' ? plan.annualProductId : plan.productId;

      // Write a one-time UUID token to Firestore so the server can verify this purchase
      const purchaseToken = crypto.randomUUID();
      if (userId) {
        const { doc, setDoc } = await import('firebase/firestore');
        const { db: fdb } = await import('./firebase');
        const now = Date.now();
        await setDoc(doc(fdb, 'iapTokens', purchaseToken), {
          uid: userId,
          productId,
          planKey: plan.key,
          createdAt: now,
          expiresAt: now + 24 * 60 * 60 * 1000,
        });
      }

      const purchasePromise = IAP.purchase({ productId, userId: purchaseToken });
      const timeoutPromise = new Promise((_, reject) => {
        purchaseTimerId = setTimeout(() => reject(new Error('Purchase timed out. Please try again.')), 60000);
      });
      const result = await Promise.race([purchasePromise, timeoutPromise]);
      clearTimeout(purchaseTimerId);
      await onPurchaseSuccess(plan.key, result?.receiptData, productId);
      onClose();
    } catch (err) {
      clearTimeout(purchaseTimerId);
      const msg = err?.message?.toLowerCase() ?? '';
      if (msg === 'deferred') {
        setError('Purchase is pending approval (Ask to Buy). It will complete once approved.');
      } else if (!msg.includes('cancel')) {
        setError(err?.message || 'Purchase failed. Please try again.');
      }
    } finally {
      setLoading(null);
    }
  };

  const handleRestore = async () => {
    setError('');
    setLoading('restore');
    let restoreTimerId = null;
    try {
      const IAP = getIAP();
      if (!IAP) throw new Error('IAP not available');
      const restorePromise = IAP.restorePurchases();
      const restoreTimeoutPromise = new Promise((_, reject) => {
        restoreTimerId = setTimeout(() => reject(new Error('Restore timed out. Please try again.')), 20000);
      });
      const { restoredProductIds = [] } = await Promise.race([restorePromise, restoreTimeoutPromise]);
      clearTimeout(restoreTimerId);
      const PLAN_RANK = { elite: 3, pro: 2, plus: 1 };
      const planKey = restoredProductIds
        .map(id => id.replace('com.centrisfinance.', '').replace('.monthly', '').replace('.annual', ''))
        .filter(k => ['plus', 'pro', 'elite'].includes(k))
        .sort((a, b) => (PLAN_RANK[b] || 0) - (PLAN_RANK[a] || 0))[0];
      if (planKey) {
        await onPurchaseSuccess(planKey);
        onClose();
      } else {
        setError('No previous purchases found.');
      }
    } catch (err) {
      clearTimeout(restoreTimerId);
      setError(err?.message || 'Could not restore purchases.');
    } finally {
      setLoading(null);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-2xl rounded-t-3xl sm:rounded-2xl border border-white/[0.08] bg-slate-900 overflow-hidden" style={{ maxHeight: '92vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 px-5 pt-5 pb-3 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Upgrade CENTRIS</h2>
            <p className="text-xs text-slate-400 mt-0.5">Unlock your full financial picture</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-1 px-5 py-4">
          <div className="flex bg-white/[0.04] border border-white/[0.06] rounded-xl p-1 gap-1">
            <button onClick={() => setBillingCycle('monthly')} className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${billingCycle === 'monthly' ? 'bg-white/[0.1] text-white' : 'text-slate-400'}`}>Monthly</button>
            <button onClick={() => setBillingCycle('annual')} className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${billingCycle === 'annual' ? 'bg-white/[0.1] text-white' : 'text-slate-400'}`}>
              Annual <span className="text-[10px] text-emerald-400 font-bold">Save 34%</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="px-4 pb-2 space-y-3">
          {NATIVE_PLANS.map(plan => {
            const c = colorMap[plan.color];
            const isCurrent = currentPlan === plan.key;
            return (
              <div key={plan.key} className={`rounded-xl border p-4 ${plan.popular ? c.border : 'border-white/[0.06]'} relative`} style={{ background: 'rgba(255,255,255,0.02)' }}>
                {plan.popular && <span className="absolute -top-2.5 left-4 text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-600 text-white">MOST POPULAR</span>}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className={`text-sm font-bold ${c.text}`}>{plan.name}</p>
                    <p className="text-xl font-bold text-white mt-0.5">
                      {billingCycle === 'annual' ? plan.annualMonthly : plan.monthlyPrice}
                      <span className="text-xs text-slate-400 font-normal">/mo</span>
                    </p>
                    {billingCycle === 'annual' && <p className="text-[10px] text-slate-500">{plan.annualPrice}/yr billed annually</p>}
                  </div>
                  <button
                    onClick={() => handlePurchase(plan)}
                    disabled={!!loading || isCurrent}
                    className={`px-4 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r ${c.btn} disabled:opacity-50 transition-all`}
                  >
                    {loading === plan.key ? <Loader className="w-3.5 h-3.5 animate-spin" /> : isCurrent ? 'Current' : 'Subscribe'}
                  </button>
                </div>
                <ul className="space-y-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <Check className={`w-3 h-3 ${c.text} flex-shrink-0`} />{f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {error && <p className="text-xs text-red-400 text-center px-5 py-2">{error}</p>}

        {/* Footer */}
        <div className="px-5 py-4 space-y-3 border-t border-white/[0.06]">
          <button onClick={handleRestore} disabled={!!loading} className="w-full text-center text-xs text-slate-500 hover:text-slate-300 transition-colors py-1">
            {loading === 'restore' ? 'Restoring...' : 'Restore previous purchases'}
          </button>
          <p className="text-[10px] text-slate-600 text-center">Payment charged to your Apple ID. Subscriptions auto-renew unless cancelled at least 24 hours before the end of the current period. Manage or cancel in Settings &rarr; Apple ID &rarr; Subscriptions.</p>
          <div className="flex items-center justify-center gap-3 pt-1">
            <a href="https://centrisfinance.com/privacy" target="_blank" rel="noreferrer" onClick={(e) => { if (window.Capacitor?.isNativePlatform?.()) { e.preventDefault(); window.open('https://centrisfinance.com/privacy', '_system'); } }} className="text-[10px] text-slate-500 hover:text-slate-300 underline">Privacy Policy</a>
            <span className="text-slate-700 text-[10px]">·</span>
            <a href="https://centrisfinance.com/terms" target="_blank" rel="noreferrer" onClick={(e) => { if (window.Capacitor?.isNativePlatform?.()) { e.preventDefault(); window.open('https://centrisfinance.com/terms', '_system'); } }} className="text-[10px] text-slate-500 hover:text-slate-300 underline">Terms of Use</a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const PricingModal = ({ user, currentPlan, onClose }) => {
  const [loading, setLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [modalError, setModalError] = useState('');

  const handleUpgrade = async (priceKey) => {
    setLoading(priceKey);
    setModalError('');
    try {
      logEvent('subscription_started', { price_key: priceKey, billing_cycle: billingCycle });
      const result = await createCheckoutSession(priceKey);
      if (result.url) {
        window.location.href = result.url;
      } else if (result.success && result.plan === 'payg') {
        window.location.reload();
      } else {
        setModalError('Failed to start checkout: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      setModalError('Checkout error: ' + err.message);
    } finally {
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setLoading('portal');
    setModalError('');
    try {
      const result = await createBillingPortal();
      if (result.url) window.location.href = result.url;
      else setModalError('Unable to open billing portal');
    } catch (err) {
      setModalError('Billing error: ' + err.message);
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      name: 'Free',
      key: 'free',
      price: 0,
      annualPrice: 0,
      tagline: 'Track finances for free, forever',
      features: [
        'Unlimited expense & mileage tracking',
        'Bank statement import (CSV/PDF)',
        '10 receipt scans/mo',
        '5 invoices/mo',
        '15 Quantra AI messages/mo',
      ],
      limits: [
        'No live bank sync',
        'No AI builders',
        'CENTRIS watermark on invoices',
      ],
    },
    {
      name: 'Plus',
      key: 'plus',
      price: 19,
      annualPrice: 152,
      tagline: 'Full toolkit for freelancers',
      popular: true,
      features: [
        'Unlimited receipts & invoices',
        'Unlimited Quantra AI CFO',
        'Full Pulse dashboard & smart alerts',
        'Marketing Hub — scripts & trends',
        '2 bank connections (live sync)',
        'Deduction coach & export reports',
      ],
      monthlyKey: 'plus_monthly',
      annualKey: 'plus_annual',
    },
    {
      name: 'Pro',
      key: 'pro',
      price: 39,
      annualPrice: 312,
      tagline: 'For growing businesses',
      features: [
        'Everything in Plus',
        '5 bank connections',
        'Client scorecards & invoice escalation',
        'Income smoothing & rate card',
        'AI Visual Generator (5/mo)',
        'Lead Generator & advanced forecasting',
      ],
      monthlyKey: 'pro_monthly',
      annualKey: 'pro_annual',
    },
    {
      name: 'Elite',
      key: 'elite',
      price: 69,
      annualPrice: 552,
      tagline: 'Unlimited AI for power users',
      badge: 'Best Value',
      features: [
        'Everything in Pro',
        'Unlimited Marketing Hub',
        'AI Visual Generator (20/mo)',
        'Video Ad Generator (5 sets/mo)',
        'Website Builder — unlimited',
        'Priority AI — fastest responses',
      ],
      monthlyKey: 'elite_monthly',
      annualKey: 'elite_annual',
    },
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[999] flex items-center justify-center p-4">
      <div className="centris-card rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8 pb-6 text-center border-b border-white/[0.06]">
          <h2 className="text-2xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>Choose Your Plan</h2>
          <div className="mt-5 inline-flex items-center centris-card rounded-lg p-1">
            <button onClick={() => setBillingCycle('monthly')} className={`px-4 py-2 rounded-md text-xs font-semibold transition-all ${billingCycle === 'monthly' ? 'bg-white/[0.08] text-white' : 'text-slate-500'}`}>Monthly</button>
            <button onClick={() => setBillingCycle('annual')} className={`px-4 py-2 rounded-md text-xs font-semibold transition-all ${billingCycle === 'annual' ? 'bg-white/[0.08] text-white' : 'text-slate-500'}`}>Annual <span className="ml-1.5 text-emerald-400 text-[10px]">Save ~17%</span></button>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const isCurrent = currentPlan === plan.key;
            const price = billingCycle === 'annual' && plan.annualPrice > 0 ? Math.round(plan.annualPrice / 12) : plan.price;
            const priceKey = billingCycle === 'annual' ? plan.annualKey : plan.monthlyKey;

            return (
              <div key={plan.key} className={`rounded-xl border p-5 flex flex-col ${plan.popular ? 'border-cyan-500/40 bg-cyan-500/5 ring-1 ring-cyan-500/20' : plan.badge ? 'border-violet-500/40 bg-violet-500/5 ring-1 ring-violet-500/20' : 'border-white/[0.06] bg-slate-800/40'}`}>
                {plan.popular && <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400 mb-1">Most Popular</span>}
                {plan.badge && <span className="text-[10px] font-bold uppercase tracking-widest text-violet-400 mb-1">{plan.badge}</span>}
                <h3 className="text-base font-bold text-white">{plan.name}</h3>
                <p className="text-[10px] text-slate-500 mt-0.5 mb-3">{plan.tagline}</p>
                <div className="mb-4">
                  {price === 0 ? (
                    <div><span className="text-2xl font-bold text-white">Free</span></div>
                  ) : (
                    <div>
                      <span className="text-2xl font-bold text-white">${price}</span><span className="text-xs text-slate-500">/mo</span>
                      {billingCycle === 'annual' && plan.annualPrice > 0 && <p className="text-[10px] text-slate-600 mt-0.5">${plan.annualPrice}/yr billed annually</p>}
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-1.5 mb-4">
                  {plan.features.map((f, i) => <div key={i} className="flex items-start gap-2"><Check className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" /><span className="text-[11px] text-slate-300 leading-tight">{f}</span></div>)}
                  {plan.limits && plan.limits.map((f, i) => <div key={`l${i}`} className="flex items-start gap-2"><X className="w-3 h-3 text-slate-600 mt-0.5 shrink-0" /><span className="text-[11px] text-slate-600 leading-tight">{f}</span></div>)}
                </div>

                {isCurrent ? (
                  <button disabled className="w-full py-2.5 rounded-xl text-xs font-semibold bg-white/[0.04] text-slate-500 border border-white/[0.06]">Current Plan</button>
                ) : plan.key === 'free' ? (
                  <button disabled={currentPlan === 'free'} onClick={currentPlan !== 'free' ? handleManageBilling : undefined} className="w-full py-2.5 rounded-xl text-xs font-semibold bg-white/[0.04] text-slate-400 border border-white/[0.06] hover:bg-white/[0.06] transition-all disabled:opacity-50">
                    {currentPlan === 'free' ? 'Current Plan' : 'Manage Subscription'}
                  </button>
                ) : (
                  <button onClick={() => handleUpgrade(priceKey)} disabled={Boolean(loading)} className={`w-full py-2.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-50 ${plan.popular ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:shadow-lg hover:shadow-cyan-500/20' : plan.badge ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:shadow-lg hover:shadow-violet-500/20' : 'bg-white/[0.06] text-white border border-white/[0.06] hover:bg-white/[0.10]'}`}>
                    {loading === priceKey ? <span className="flex items-center justify-center gap-2"><Loader className="w-3.5 h-3.5 animate-spin" /> Redirecting...</span> : `Upgrade to ${plan.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {modalError && <p className="text-xs text-red-400 text-center px-8 pb-2">{modalError}</p>}
        <div className="px-8 pb-6 flex items-center justify-between">
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-300 transition-colors">Close</button>
          {!['free', 'payg'].includes(currentPlan) && (
            <button onClick={handleManageBilling} disabled={loading === 'portal'} className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
              {loading === 'portal' ? 'Opening...' : 'Manage Billing ->'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const InputField = ({ label, value, onChange, type = 'text', placeholder, disabled }) => (
  <div>
    <label className="text-[11px] text-slate-500 font-medium uppercase tracking-wider block mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      onTouchEnd={(e) => e.target.focus()}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      className="w-full centris-card rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none transition-all disabled:opacity-50"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-[11px] text-slate-500 font-medium uppercase tracking-wider block mb-1.5">{label}</label>
    <select value={value} onChange={onChange} className="w-full centris-card rounded-xl px-4 py-3 text-sm text-white focus:border-cyan-500/40 focus:outline-none transition-all appearance-none">
      {options.map(([val, text]) => <option key={val} value={val}>{text}</option>)}
    </select>
  </div>
);

const SettingsPage = ({
  user,
  currentPlan,
  perms,
  profileForm,
  setProfileForm,
  profileSaving,
  setProfileSaving,
  profileSaved,
  setProfileSaved,
  setShowPricingModal,
  setShowAddCreditsModal,
  twoFactorEnabled,
  setShowTwoFactorModal,
  handleLogout,
  settingsTab,
  setSettingsTab,
  bankImportMode,
  setBankImportMode,
  onResendVerification,
  onRefreshVerification,
  onDeleteAccount,
  showToast,
}) => {
  const [usageData, setUsageData] = useState(null);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  useEffect(() => {
    if (settingsTab !== 'usage' || !user?.uid) return;
    let mounted = true;
    getUsage(user.uid).then(r => { if (mounted) setUsageData(r || {}); }).catch(err => console.error('getUsage error:', err));
    return () => { mounted = false; };
  }, [settingsTab, user]);

  const tabs = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'banking', label: 'Banking', icon: Wallet },
    { key: 'billing', label: 'Plan & Billing', icon: CreditCard },
    { key: 'security', label: 'Security', icon: Shield },
    { key: 'notifications', label: 'Notifications', icon: Bell },
    { key: 'usage', label: 'Usage', icon: Activity },
    { key: 'data', label: 'Data & Privacy', icon: Database },
  ];

  const handleSaveProfile = async () => {
    if (!user?.uid) return;
    setProfileSaving(true);
    setProfileSaved(false);
    const result = await saveUserProfile(user.uid, {
      name: profileForm.name,
      companyName: profileForm.companyName,
      phone: profileForm.phone,
      address: profileForm.address,
      industry: profileForm.industry,
      entityType: profileForm.entityType,
      taxId: profileForm.taxId,
      fiscalYearEnd: profileForm.fiscalYearEnd,
      currency: profileForm.currency,
    });
    setProfileSaving(false);
    if (result.success) {
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    }
  };

  const handleManageBilling = async () => {
    const result = await createBillingPortal();
    if (result.url) window.location.href = result.url;
  };

  const planColors = {
    free: 'text-slate-400', payg: 'text-amber-400', plus: 'text-cyan-400', pro: 'text-violet-400', admin: 'text-emerald-400',
  };
  const planLabels = { free: 'Free', payg: 'Pay As You Go', plus: 'Plus', pro: 'Pro', elite: 'Elite', admin: 'Admin' };


  return (
    <>
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account, plan, and preferences</p>
      </div>

      <div className="flex gap-1 mb-8 centris-card rounded-xl p-1 overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setSettingsTab(tab.key)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${settingsTab === tab.key ? 'bg-white/[0.06] text-white' : 'text-slate-500 hover:text-slate-300'}`}>
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {settingsTab === 'profile' && (
        <div className="space-y-6">
          <div className="centris-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-white mb-5">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Full Name" value={profileForm.name} onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))} placeholder="Jane Smith" />
              <InputField label="Email" value={profileForm.email} disabled onChange={() => {}} placeholder="you@example.com" />
              <InputField label="Phone" value={profileForm.phone} onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" />
              <InputField label="Address" value={profileForm.address} onChange={(e) => setProfileForm((p) => ({ ...p, address: e.target.value }))} placeholder="City, State" />
            </div>
          </div>

          <div className="centris-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-white mb-5">Business Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Business Name" value={profileForm.companyName} onChange={(e) => setProfileForm((p) => ({ ...p, companyName: e.target.value }))} placeholder="ACME Consulting LLC" />
              <InputField label="Industry" value={profileForm.industry} onChange={(e) => setProfileForm((p) => ({ ...p, industry: e.target.value }))} placeholder="Marketing, Design, Software" />
              <SelectField
                label="Business Entity"
                value={profileForm.entityType}
                onChange={(e) => setProfileForm((p) => ({ ...p, entityType: e.target.value }))}
                options={[
                  ['sole_prop', 'Sole Proprietorship'],
                  ['llc_single', 'LLC (Single Member)'],
                  ['llc_multi', 'LLC (Multi Member)'],
                  ['s_corp', 'S-Corp'],
                  ['c_corp', 'C-Corp'],
                  ['partnership', 'Partnership'],
                ]}
              />
              <InputField label="EIN / Tax ID (optional)" value={profileForm.taxId} onChange={(e) => setProfileForm((p) => ({ ...p, taxId: e.target.value }))} placeholder="XX-XXXXXXX" />
              <SelectField
                label="Fiscal Year End"
                value={profileForm.fiscalYearEnd}
                onChange={(e) => setProfileForm((p) => ({ ...p, fiscalYearEnd: e.target.value }))}
                options={Array.from({ length: 12 }, (_, i) => [String(i + 1), new Date(2025, i, 1).toLocaleString('default', { month: 'long' })])}
              />
              <SelectField
                label="Currency"
                value={profileForm.currency || 'USD'}
                onChange={(e) => setProfileForm((p) => ({ ...p, currency: e.target.value }))}
                options={[
                  ['USD', 'US Dollar ($)'],
                  ['EUR', 'Euro (€)'],
                  ['GBP', 'British Pound (£)'],
                  ['CAD', 'Canadian Dollar (CA$)'],
                  ['AUD', 'Australian Dollar (AU$)'],
                  ['INR', 'Indian Rupee (₹)'],
                  ['JPY', 'Japanese Yen (¥)'],
                ]}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={handleSaveProfile} disabled={profileSaving} className="px-6 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-cyan-500/20 transition-all disabled:opacity-50 flex items-center gap-2">
              {profileSaving ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Saving...</> : <><Check className="w-3.5 h-3.5" /> Save Profile</>}
            </button>
            {profileSaved && <span className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>}
          </div>
        </div>
      )}

      {settingsTab === 'banking' && (
        <div className="space-y-6">
          <div className="centris-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-white mb-1">How you import bank activity</h3>
            <p className="text-xs text-slate-500 mb-5">Pick one primary method. You can change anytime; statement uploads and receipt scans always stay available.</p>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setBankImportMode(BANK_IMPORT_STATEMENTS)}
                className={`w-full text-left rounded-xl border p-4 transition-all ${
                  bankImportMode === BANK_IMPORT_STATEMENTS
                    ? 'border-violet-500/40 bg-violet-500/10'
                    : 'border-white/[0.06] hover:border-white/10'
                }`}
              >
                <p className="text-sm font-semibold text-white">Bank statements &amp; receipt scans</p>
                <p className="text-xs text-slate-400 mt-1">Upload CSV/PDF under Subscriptions or scan receipts under Expenses. Works without Plaid.</p>
              </button>
              <button
                type="button"
                disabled={!PLAID_BANK_CONNECT_ENABLED}
                onClick={() => setBankImportMode(BANK_IMPORT_LIVE)}
                className={`w-full text-left rounded-xl border p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  bankImportMode === BANK_IMPORT_LIVE
                    ? 'border-cyan-500/40 bg-cyan-500/10'
                    : 'border-white/[0.06] hover:border-white/10'
                }`}
              >
                <p className="text-sm font-semibold text-white">Live bank connection (Plaid)</p>
                <p className="text-xs text-slate-400 mt-1">
                  {PLAID_BANK_CONNECT_ENABLED
                    ? 'Connect accounts in Banking for live balances and automatic transaction import.'
                    : 'Coming soon — available after production approval.'}
                </p>
              </button>
            </div>
          </div>
        </div>
      )}

      {settingsTab === 'billing' && (
        <div className="space-y-6">
          <div className="centris-card rounded-2xl p-6">
            {isNative ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white">Current Plan</h3>
                  <span className={`text-xs font-bold uppercase tracking-wider ${planColors[currentPlan] || 'text-slate-400'}`}>
                    {planLabels[currentPlan] || currentPlan}
                  </span>
                </div>
                {currentPlan === 'free' && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-400">Upgrade to unlock unlimited AI features, receipt scanning, and more.</p>
                    <button onClick={() => setShowPricingModal(true)} className="px-5 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl text-sm font-semibold">View Plans &amp; Upgrade</button>
                  </div>
                )}
                {currentPlan !== 'free' && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{planLabels[currentPlan] || currentPlan} Plan</p>
                      <p className="text-xs text-slate-400 mt-0.5">Manage your subscription in Apple Settings &rarr; Subscriptions.</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white">Current Plan</h3>
              <span className={`text-xs font-bold uppercase tracking-wider ${planColors[currentPlan] || 'text-slate-400'}`}>
                {planLabels[currentPlan] || currentPlan}
              </span>
            </div>
            {currentPlan === 'free' && (
              <div className="space-y-3">
                <p className="text-xs text-slate-400">
                  {PLAID_BANK_CONNECT_ENABLED
                    ? 'You are on Free. Upgrade to unlock unlimited AI features and bank connections.'
                    : 'You are on Free. Upgrade to unlock unlimited AI features. Live bank sync will be available when that feature launches.'}
                </p>
                <button onClick={() => setShowPricingModal(true)} className="px-5 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl text-sm font-semibold">View Plans & Upgrade</button>
              </div>
            )}
            {currentPlan === 'payg' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                  <div>
                    <p className="text-xs text-amber-400 font-semibold">Credit Balance</p>
                    <p className="text-2xl font-bold text-white mt-1">${((user?.paygCredits || 0) / 100).toFixed(2)}</p>
                  </div>
                  <button onClick={() => setShowAddCreditsModal(true)} className="px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-xl text-xs font-semibold text-amber-400">+ Add Credits</button>
                </div>
                <button onClick={() => setShowPricingModal(true)} className="text-xs text-cyan-400">Compare subscription plans -&gt;</button>
              </div>
            )}
            {['plus', 'pro'].includes(currentPlan) && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="centris-card rounded-xl p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wider">Plan</p><p className="text-sm font-bold text-white mt-0.5">{planLabels[currentPlan]}</p></div>
                  <div className="centris-card rounded-xl p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wider">Billing</p><p className="text-sm font-bold text-white mt-0.5 capitalize">{user?.billingInterval || 'Monthly'}</p></div>
                  <div className="centris-card rounded-xl p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wider">{PLAID_BANK_CONNECT_ENABLED ? 'Bank Connections' : 'Live bank link'}</p><p className="text-sm font-bold text-white mt-0.5">{PLAID_BANK_CONNECT_ENABLED ? `${perms.plaidAccounts} allowed` : 'Coming soon'}</p></div>
                  <div className="centris-card rounded-xl p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wider">Status</p><p className="text-sm font-bold text-emerald-400 mt-0.5 capitalize">{user?.subscriptionStatus || 'active'}</p></div>
                </div>
                <div className="flex gap-3">
                  <button onClick={handleManageBilling} className="px-5 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-xl text-sm text-white">Manage Subscription</button>
                  {currentPlan === 'plus' && <button onClick={() => setShowPricingModal(true)} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-sm font-semibold">Upgrade to Pro</button>}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      )}

      {settingsTab === 'security' && (
        <div className="space-y-6">
          <div className="centris-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-white mb-5">Authentication</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-white/[0.03]">
                <div><p className="text-sm text-white">Email</p><p className="text-xs text-slate-500">{user?.email}</p></div>
                {user?.emailVerified ? <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-md">Verified</span> : <span className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded-md">Not Verified</span>}
              </div>
              {!user?.emailVerified && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                  <p className="text-xs text-amber-300 font-medium mb-1">Email not verified</p>
                  <p className="text-xs text-slate-400">Check your inbox for a verification email from CENTRIS. If you didn't receive one, try signing out and signing back in — a new verification email will be sent automatically.</p>
                </div>
              )}
              {/* 2FA UI temporarily hidden — code preserved, re-enable by uncommenting */}
              {false && (
              <div className="flex items-center justify-between py-3 border-b border-white/[0.03]">
                <div><p className="text-sm text-white">Two-Factor Authentication</p><p className="text-xs text-slate-500">{twoFactorEnabled ? 'SMS verification enabled' : 'Add an extra layer of security'}</p></div>
                <button onClick={() => { setShowTwoFactorModal(true); }} className={`text-xs px-3 py-1.5 rounded-lg ${twoFactorEnabled ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`}>{twoFactorEnabled ? 'Manage' : 'Enable'}</button>
              </div>
              )}
            </div>
          </div>

        </div>
      )}

      {settingsTab === 'notifications' && (
        <div className="space-y-6">
          <div className="centris-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-white mb-5">Push Notifications</h3>
            <p className="text-xs text-slate-400 mb-4">Get notified about overdue invoices, budget alerts, and tax deadlines.</p>
            <button onClick={async () => {
              const result = await requestNotificationPermission(user.uid);
              if (result.success) {
                showToast('Push notifications enabled!', 'success');
              } else if (result.reason === 'denied') {
                showToast('Enable notifications in your device Settings, then try again.', 'info');
              } else if (result.reason === 'unsupported') {
                // iOS WKWebView — silent, no toast needed
              } else {
                showToast('Could not enable notifications. Please try again.', 'error');
              }
            }} className="px-5 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-xl text-sm text-white">
              Enable Push Notifications
            </button>
          </div>
        </div>
      )}

      {settingsTab === 'usage' && (
        <div className="space-y-6">
          <div className="centris-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-white mb-1">Monthly Usage</h3>
            <p className="text-xs text-slate-500 mb-5">Resets every day at midnight. Upgrade in Plan &amp; Billing to increase limits.</p>
            <div className="space-y-4">
              {[
                { label: 'Receipt Scans', key: 'receiptScansPerMonth', usageKey: 'receiptScan', icon: '🧾' },
                { label: 'Invoices Sent', key: 'invoicesPerMonth', usageKey: 'invoice', icon: '📄' },
                { label: 'Quantra AI Messages', key: 'quantraMessagesPerMonth', usageKey: 'quantraMessage', icon: '🤖' },
                { label: 'Marketing Scripts', key: 'marketingScripts', usageKey: 'marketingScript', icon: '📝' },
                { label: 'Content Atomizer', key: 'marketingAtomizer', usageKey: 'marketingAtomizer', icon: '⚡' },
                { label: 'Trend Searches', key: 'marketingTrends', usageKey: 'marketingTrend', icon: '📈' },
                { label: 'Audience Finder', key: 'marketingDemographics', usageKey: 'marketingDemographic', icon: '👥' },
              ].map(({ label, key, usageKey, icon }) => {
                const limit = perms[key];
                const used = usageData?.[usageKey] || 0;
                const isUnlimited = !Number.isFinite(limit) || limit === Infinity;
                const isLocked = limit === 0;
                const pct = isUnlimited || isLocked ? 0 : Math.min((used / limit) * 100, 100);
                const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-violet-500';
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{icon}</span>
                        <span className="text-xs text-slate-300">{label}</span>
                      </div>
                      <span className={`text-xs font-semibold ${isLocked ? 'text-slate-600' : isUnlimited ? 'text-emerald-400' : pct >= 90 ? 'text-red-400' : 'text-white'}`}>
                        {isLocked ? 'Not included' : isUnlimited ? `${used} / ∞` : `${used} / ${limit}`}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-700/40 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${isLocked ? 'bg-slate-700/40' : barColor}`} style={{ width: isUnlimited ? '100%' : `${pct}%`, opacity: isUnlimited ? 0.3 : 1 }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {!usageData && (
              <p className="text-xs text-slate-500 mt-4 text-center">Loading usage data...</p>
            )}
          </div>
          <div className="centris-card rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Feature Access</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Full Pulse Dashboard', key: 'fullPulse' },
                { label: 'Smart Alerts', key: 'smartAlerts' },
                { label: 'Deduction Coach', key: 'deductionCoach' },
                { label: 'Branded Invoices', key: 'brandedInvoices' },
                { label: 'Export Reports', key: 'exportReports' },
                { label: 'Live Bank Sync', key: 'plaidAccounts', check: v => v > 0 },
                { label: 'Client Scorecards', key: 'clientScorecards' },
                { label: 'Income Smoothing', key: 'incomeSmoothing' },
              ].map(({ label, key, check }) => {
                const val = perms[key];
                const enabled = check ? check(val) : Boolean(val);
                return (
                  <div key={key} className="flex items-center gap-2">
                    {enabled
                      ? <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      : <X className="w-3.5 h-3.5 text-slate-600 shrink-0" />}
                    <span className={`text-xs ${enabled ? 'text-slate-300' : 'text-slate-600'}`}>{label}</span>
                  </div>
                );
              })}
            </div>
            {!isNative && (
              <button onClick={() => setShowPricingModal(true)} className="mt-4 w-full py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                View Plans &amp; Upgrade &rarr;
              </button>
            )}
          </div>
        </div>
      )}

      {settingsTab === 'data' && (
        <div className="space-y-6">
          {/* Trust banner */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 flex gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-emerald-300 mb-0.5">Your data stays yours</p>
              <p className="text-xs text-slate-400 leading-relaxed">CENTRIS never sells, rents, or shares your personal or financial data with advertisers or data brokers. Period.</p>
            </div>
          </div>

          {/* What we collect */}
          <div className="centris-card rounded-2xl p-6 space-y-4">
            <h3 className="text-sm font-semibold text-white">What we collect</h3>
            <div className="space-y-2">
              {[
                ['📊', 'Financial records', 'Expenses, revenue, invoices, mileage trips, subscriptions, and bank statements you enter or import. Stored securely in your account — only you can access it.'],
                ['👤', 'Account info', 'Your email address, display name, and subscription plan. Used only to identify your account and send you product updates (which you can opt out of).'],
                ['🖼️', 'Receipt images', 'Photos you upload are sent to our AI for data extraction only. Images are not stored on our servers after processing.'],
                ['💬', 'AI conversations', "Messages to Quantra and other AI features are sent to Anthropic's Claude API. Anthropic does not use API data to train their models. Conversation history is saved to your secure account so you can resume sessions across devices."],
                ['📈', 'Usage counts', 'Feature usage (e.g. receipt scans per month) to enforce your plan limits. Never shared externally.'],
                ['🔔', 'Push notification token', 'A device token used to deliver bill reminders and financial alerts. Never used for marketing without consent.'],
              ].map(([icon, title, desc]) => (
                <div key={title} className="flex gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                  <span className="text-base shrink-0">{icon}</span>
                  <div>
                    <p className="text-xs font-semibold text-white mb-0.5">{title}</p>
                    <p className="text-xs text-slate-400 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Where data goes */}
          <div className="centris-card rounded-2xl p-6 space-y-4">
            <h3 className="text-sm font-semibold text-white">Where your data goes</h3>
            <div className="space-y-3">
              {[
                { icon: '🔒', label: 'Firebase (Google Cloud)', desc: 'All financial data is stored in Google Firestore — encrypted at rest (AES-256) and in transit (TLS). Google is SOC 2 and ISO 27001 certified.' },
                { icon: '🤖', label: 'Anthropic (Claude AI)', desc: "AI features send a financial summary to Claude to generate insights. Anthropic does not retain or train on API data." },
                { icon: '💳', label: 'Stripe', desc: 'Web subscription payments only. CENTRIS never sees your card number — Stripe handles it entirely.' },
                { icon: '🍎', label: 'Apple', desc: 'iOS In-App Purchases are processed entirely by Apple. CENTRIS receives only a receipt token to verify your plan status.' },
              ].map(({ icon, label, desc }) => (
                <div key={label} className="flex gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                  <span className="text-base mt-0.5 shrink-0">{icon}</span>
                  <div>
                    <p className="text-xs font-semibold text-white mb-0.5">{label}</p>
                    <p className="text-xs text-slate-400 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4">
            <p className="text-xs text-amber-300 font-semibold mb-1">Financial Advice Disclaimer</p>
            <p className="text-xs text-slate-400 leading-relaxed">All insights, suggestions, forecasts, and recommendations generated by CENTRIS — including Quantra AI — are for informational purposes only. They do not constitute financial, tax, legal, or investment advice. CENTRIS LLC is not a licensed financial adviser. Always consult a qualified professional before making important financial decisions.</p>
          </div>

          {perms?.whiteGloveSupport && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <h3 className="text-sm font-semibold text-amber-300">White-glove Support</h3>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mb-3">As an Elite member you have access to priority support. Our team will personally assist you with setup, data imports, and any questions.</p>
              <a href="mailto:support@centrisfinance.com?subject=Elite%20Support%20Request" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 underline">Email Elite Support &rarr;</a>
            </div>
          )}

          <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-red-400 mb-3">Danger Zone</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-white">Sign Out</p><p className="text-xs text-slate-500">Sign out of your account</p></div>
                <button onClick={handleLogout} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20">Sign Out</button>
              </div>
              <div className="border-t border-red-500/10 pt-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm text-white">Delete Account</p>
                    <p className="text-xs text-slate-500 mt-0.5">Permanently delete your account and all data. This cannot be undone.</p>
                    {isNative && <p className="text-xs text-slate-600 mt-0.5">Cancel your subscription first in Settings &rarr; Apple ID &rarr; Subscriptions.</p>}
                  </div>
                  <button
                    onClick={() => setShowDeleteAccountConfirm(true)}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    {showDeleteAccountConfirm && <ModalPortal>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] p-4">
        <div className="centris-card rounded-2xl border border-red-500/20 max-w-sm w-full p-6">
          <div className="flex items-center space-x-3 mb-3"><AlertTriangle className="w-5 h-5 text-red-400" /><h3 className="text-lg font-bold text-white">Delete Account?</h3></div>
          <p className="text-sm text-slate-300 mb-5">This will permanently delete your account and all data. This cannot be undone.</p>
          <div className="flex space-x-3">
            <button onClick={() => setShowDeleteAccountConfirm(false)} className="flex-1 bg-slate-800 text-slate-300 px-4 py-2.5 rounded-xl font-medium hover:bg-slate-700 border border-slate-700 text-sm">Cancel</button>
            <button onClick={() => { setShowDeleteAccountConfirm(false); onDeleteAccount(); }} className="flex-1 bg-red-600 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-red-700 text-sm">Delete Account</button>
          </div>
        </div>
      </div>
    </ModalPortal>}
    </>
  );
};

// ============ DEMO DATA ============
const DEMO_DATA = {
  user: {
    uid: 'demo-user',
    email: 'demo@centrisfinance.com',
    name: 'Alex Rivera',
    companyName: 'Rivera Design Studio',
    plan: 'pro',
    emailVerified: true,
    onboardingCompleted: true,
  },
  expenses: [
    { id: 'demo-e1', merchant: 'Adobe Creative Cloud', amount: 54.99, date: '2026-04-01', category: 'Software & Tools', taxDeductible: true, notes: '' },
    { id: 'demo-e2', merchant: 'Notion', amount: 16.00, date: '2026-04-01', category: 'Software & Tools', taxDeductible: true, notes: '' },
    { id: 'demo-e3', merchant: 'Figma', amount: 45.00, date: '2026-04-01', category: 'Software & Tools', taxDeductible: true, notes: '' },
    { id: 'demo-e4', merchant: 'WeWork Office', amount: 350.00, date: '2026-04-02', category: 'Office & Workspace', taxDeductible: true, notes: 'Coworking space' },
    { id: 'demo-e5', merchant: 'Staples', amount: 89.43, date: '2026-04-05', category: 'Office Supplies', taxDeductible: true, notes: 'Printer ink, notebooks' },
    { id: 'demo-e6', merchant: 'Google Ads', amount: 450.00, date: '2026-04-07', category: 'Marketing', taxDeductible: true, notes: '' },
    { id: 'demo-e7', merchant: 'Uber (client meeting)', amount: 24.50, date: '2026-04-10', category: 'Travel', taxDeductible: true, notes: '' },
    { id: 'demo-e8', merchant: 'LinkedIn Premium', amount: 39.99, date: '2026-04-01', category: 'Marketing', taxDeductible: true, notes: '' },
    { id: 'demo-e9', merchant: 'Zoom Pro', amount: 15.99, date: '2026-04-01', category: 'Software & Tools', taxDeductible: true, notes: '' },
    { id: 'demo-e10', merchant: 'Client Lunch', amount: 87.40, date: '2026-04-12', category: 'Meals & Entertainment', taxDeductible: true, notes: 'Lunch with TechCorp team (50% deductible)' },
    { id: 'demo-e11', merchant: 'AWS', amount: 32.17, date: '2026-03-01', category: 'Software & Tools', taxDeductible: true, notes: '' },
    { id: 'demo-e12', merchant: 'Adobe Creative Cloud', amount: 54.99, date: '2026-03-01', category: 'Software & Tools', taxDeductible: true, notes: '' },
    { id: 'demo-e13', merchant: 'Google Ads', amount: 380.00, date: '2026-03-07', category: 'Marketing', taxDeductible: true, notes: '' },
    { id: 'demo-e14', merchant: 'WeWork Office', amount: 350.00, date: '2026-03-02', category: 'Office & Workspace', taxDeductible: true, notes: '' },
    { id: 'demo-e15', merchant: 'Coursera', amount: 49.00, date: '2026-03-15', category: 'Professional Development', taxDeductible: true, notes: 'UX Design course' },
    { id: 'demo-e16', merchant: 'Google Ads', amount: 420.00, date: '2026-02-07', category: 'Marketing', taxDeductible: true, notes: '' },
    { id: 'demo-e17', merchant: 'Adobe Creative Cloud', amount: 54.99, date: '2026-02-01', category: 'Software & Tools', taxDeductible: true, notes: '' },
    { id: 'demo-e18', merchant: 'WeWork Office', amount: 350.00, date: '2026-02-02', category: 'Office & Workspace', taxDeductible: true, notes: '' },
  ],
  revenue: [
    { id: 'demo-r1', source: 'TechCorp Inc.', amount: 5500.00, date: '2026-04-08', description: 'Website redesign — Phase 1', status: 'received', createdAt: '2026-04-08T10:00:00Z' },
    { id: 'demo-r2', source: 'Bright Media', amount: 2800.00, date: '2026-04-03', description: 'Brand identity package', status: 'received', createdAt: '2026-04-03T10:00:00Z' },
    { id: 'demo-r3', source: 'StartupXYZ', amount: 1200.00, date: '2026-04-15', description: 'Monthly retainer — April', status: 'received', createdAt: '2026-04-15T10:00:00Z' },
    { id: 'demo-r4', source: 'TechCorp Inc.', amount: 4800.00, date: '2026-03-12', description: 'Website redesign — Discovery', status: 'received', createdAt: '2026-03-12T10:00:00Z' },
    { id: 'demo-r5', source: 'Peak Fitness', amount: 3200.00, date: '2026-03-20', description: 'App UI/UX design', status: 'received', createdAt: '2026-03-20T10:00:00Z' },
    { id: 'demo-r6', source: 'StartupXYZ', amount: 1200.00, date: '2026-03-01', description: 'Monthly retainer — March', status: 'received', createdAt: '2026-03-01T10:00:00Z' },
    { id: 'demo-r7', source: 'Green Valley Co.', amount: 6500.00, date: '2026-02-15', description: 'E-commerce redesign', status: 'received', createdAt: '2026-02-15T10:00:00Z' },
    { id: 'demo-r8', source: 'StartupXYZ', amount: 1200.00, date: '2026-02-01', description: 'Monthly retainer — February', status: 'received', createdAt: '2026-02-01T10:00:00Z' },
  ],
  invoices: [
    { id: 'demo-i1', invoiceNumber: 'INV-2026-041', clientName: 'TechCorp Inc.', clientEmail: 'accounts@techcorp.io', amount: 7500.00, total: 7500.00, status: 'sent', dueDate: '2026-04-30', issueDate: '2026-04-01', items: [{ description: 'Website Redesign — Phase 2', quantity: 1, rate: 7500 }], notes: 'Net 30', createdAt: '2026-04-01T10:00:00Z' },
    { id: 'demo-i2', invoiceNumber: 'INV-2026-040', clientName: 'Bright Media', clientEmail: 'billing@brightmedia.com', amount: 2800.00, total: 2800.00, status: 'paid', dueDate: '2026-04-10', issueDate: '2026-03-25', items: [{ description: 'Brand Identity Package', quantity: 1, rate: 2800 }], notes: '', createdAt: '2026-03-25T10:00:00Z' },
    { id: 'demo-i3', invoiceNumber: 'INV-2026-039', clientName: 'Peak Fitness', clientEmail: 'finance@peakfitness.com', amount: 3200.00, total: 3200.00, status: 'paid', dueDate: '2026-03-28', issueDate: '2026-03-14', items: [{ description: 'App UI/UX Design — Full Delivery', quantity: 1, rate: 3200 }], notes: '', createdAt: '2026-03-14T10:00:00Z' },
    { id: 'demo-i4', invoiceNumber: 'INV-2026-042', clientName: 'StartupXYZ', clientEmail: 'cfo@startupxyz.com', amount: 1200.00, total: 1200.00, status: 'draft', dueDate: '2026-05-01', issueDate: '2026-04-18', items: [{ description: 'Monthly Retainer — May', quantity: 1, rate: 1200 }], notes: 'Recurring monthly', createdAt: '2026-04-18T10:00:00Z' },
  ],
  mileageTrips: [
    { id: 'demo-m1', date: '2026-04-10', startLocation: 'Home Office', endLocation: 'TechCorp HQ', miles: 14.2, totalMiles: 14.2, purpose: 'Client meeting', category: 'Client Meeting', deductible: true, deduction: 9.51, createdAt: '2026-04-10T10:00:00Z' },
    { id: 'demo-m2', date: '2026-04-08', startLocation: 'Home Office', endLocation: 'WeWork Downtown', miles: 8.6, totalMiles: 8.6, purpose: 'Coworking space', category: 'Business Travel', deductible: true, deduction: 5.76, createdAt: '2026-04-08T10:00:00Z' },
    { id: 'demo-m3', date: '2026-03-28', startLocation: 'WeWork Downtown', endLocation: 'Peak Fitness Studio', miles: 11.3, totalMiles: 11.3, purpose: 'Project delivery meeting', category: 'Client Meeting', deductible: true, deduction: 7.57, createdAt: '2026-03-28T10:00:00Z' },
    { id: 'demo-m4', date: '2026-03-20', startLocation: 'Home Office', endLocation: 'Bright Media Office', miles: 22.7, totalMiles: 22.7, purpose: 'Brand review presentation', category: 'Client Meeting', deductible: true, deduction: 15.21, createdAt: '2026-03-20T10:00:00Z' },
  ],
  subscriptions: [
    { id: 'demo-s1', name: 'Adobe Creative Cloud', cost: 54.99, billingCycle: 'monthly', category: 'Software & Tools', status: 'active', renewalDate: '2026-05-01' },
    { id: 'demo-s2', name: 'Figma Professional', cost: 45.00, billingCycle: 'monthly', category: 'Software & Tools', status: 'active', renewalDate: '2026-05-01' },
    { id: 'demo-s3', name: 'Notion Plus', cost: 16.00, billingCycle: 'monthly', category: 'Productivity', status: 'active', renewalDate: '2026-05-01' },
    { id: 'demo-s4', name: 'Zoom Pro', cost: 15.99, billingCycle: 'monthly', category: 'Communication', status: 'active', renewalDate: '2026-05-01' },
    { id: 'demo-s5', name: 'LinkedIn Premium', cost: 39.99, billingCycle: 'monthly', category: 'Marketing', status: 'active', renewalDate: '2026-05-01' },
  ],
  bills: [
    { id: 'demo-b1', name: 'AWS Hosting', amount: 32.17, nextDueDate: '2026-05-01', frequency: 'monthly', category: 'Software', paid: false, reminderDays: 3, notes: '' },
    { id: 'demo-b2', name: 'WeWork Coworking', amount: 350.00, nextDueDate: '2026-05-02', frequency: 'monthly', category: 'Office', paid: false, reminderDays: 5, notes: '' },
    { id: 'demo-b3', name: 'Business Insurance', amount: 89.00, nextDueDate: '2026-05-15', frequency: 'monthly', category: 'Insurance', paid: false, reminderDays: 7, notes: '' },
  ],
  budgets: [
    { id: 'demo-bu1', category: 'Marketing', label: 'Ads & Promotion', amount: 500, frequency: 'monthly', createdAt: '2026-01-01T00:00:00Z' },
    { id: 'demo-bu2', category: 'Software & Tools', label: 'SaaS Tools', amount: 200, frequency: 'monthly', createdAt: '2026-01-01T00:00:00Z' },
    { id: 'demo-bu3', category: 'Professional Development', label: 'Courses & Books', amount: 100, frequency: 'monthly', createdAt: '2026-01-01T00:00:00Z' },
  ],
};

const CentrisFinanceApp = () => {
  const RECAPTCHA_SIGNIN_TRIGGER_ID = 'mfa-signin-recaptcha';
  const RECAPTCHA_ENROLL_TRIGGER_ID = 'mfa-enroll-recaptcha';

  // ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬ RBAC ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬
  const ADMIN_EMAILS = ['augie.bohnhoff@gmail.com', 'bohnhoffaugie@gmail.com'];
  const ADMIN_EMAIL = ADMIN_EMAILS[0]; // legacy single-email references

  const INTERNAL_BETA_EMAILS = [
    'alejandromata.scf@gmail.com',
    'amin.r.dobani@gmail.com',
    'angelo.aviles.ortega@gmail.com',
    'anushramachandran@gmail.com',
    'augie.bohnhoff@gmail.com',
    'bobgnarley323@gmail.com',
    'bohnhoffaugie@gmail.com',
    'bohnhoffp@gmail.com',
    'choudhury.prjt.p@gmail.com',
    'deltasingh@outlook.com',
    'dxltasingh@gmail.com',
    'ertugrul1923@gmail.com',
    'estripeautrene@gmail.com',
    'ga112992233@gmail.com',
    'glbohnhoff@yahoo.com',
    'hasan.diwan@gmail.com',
    'maurice.hollink@gmail.com',
    'moshun11@gmail.com',
    'official@centrisfinance.com',
    'pank9j@gmail.com',
    'prjt777@gmail.com',
    'qinying626@gmail.com',
    'rahul731999@gmail.com',
    'rbezerra18@gmail.com',
    'shastrula.pradyumna@gmail.com',
    'tfitts1212@gmail.com',
    'tvp249@gmail.com',
    'vishalstartup1@gmail.com',
    'ziartrlapztrlapz@gmail.com',
    'ziartriapztrlapz@gmail.com',
  ];

  const ROLES = {
    admin: {
      isPro: true,
      tier: 'admin',
      marketingTier: 'marketing_studio',
      canAccessAdmin: true,
      canManageUsers: true,
      receiptScansPerMonth: Infinity,
      invoicesPerMonth: Infinity,
      quantraMessagesPerMonth: Infinity,
      plaidAccounts: Infinity,
      videoClipsFree: false,
      marketingScripts: Infinity,
      marketingAtomizer: Infinity,
      marketingTrends: Infinity,
      marketingDemographics: Infinity,
      marketingPricing: Infinity,
      fullPulse: true,
      subscriptionAutoDetect: true,
      clientScorecards: true,
      invoiceEscalation: true,
      incomeSmoothing: true,
      rateCard: true,
      profitFirst: true,
      exportReports: true,
      brandedInvoices: true,
      smartAlerts: true,
      deductionCoach: true,
      forecastMonthsMax: 12,
      canPayPerUse: false,
    },
    // ── INTERNAL BETA — all features, strict rate limits ─────────────────────
    internal_beta: {
      isPro: true,
      tier: 'elite',
      marketingTier: 'marketing_studio',
      canAccessAdmin: false,
      canManageUsers: false,
      receiptScansPerMonth: 20,
      invoicesPerMonth: 10,
      quantraMessagesPerMonth: 25,
      plaidAccounts: 2,
      videoClipsFree: false,
      marketingScripts: 10,
      marketingAtomizer: 8,
      marketingTrends: 8,
      marketingDemographics: 5,
      marketingPricing: 5,
      fullPulse: true,
      subscriptionAutoDetect: true,
      clientScorecards: true,
      invoiceEscalation: true,
      incomeSmoothing: true,
      rateCard: true,
      profitFirst: true,
      exportReports: true,
      brandedInvoices: true,
      smartAlerts: true,
      deductionCoach: true,
      forecastMonthsMax: 12,
      canPayPerUse: false,
    },
    // ── ELITE $69.99/mo — everything, unlimited ──────────────────────────────
    elite: {
      isPro: true,
      tier: 'elite',
      marketingTier: 'marketing_studio',
      canAccessAdmin: false,
      canManageUsers: false,
      receiptScansPerMonth: Infinity,
      invoicesPerMonth: Infinity,
      quantraMessagesPerMonth: Infinity,
      plaidAccounts: 10,
      videoClipsFree: true,
      priorityAICFO: true,
      whiteGloveSupport: true,
      marketingScripts: Infinity,
      marketingAtomizer: Infinity,
      marketingTrends: Infinity,
      marketingDemographics: Infinity,
      marketingPricing: Infinity,
      fullPulse: true,
      subscriptionAutoDetect: true,
      clientScorecards: true,
      invoiceEscalation: true,
      incomeSmoothing: true,
      rateCard: true,
      profitFirst: true,
      exportReports: true,
      brandedInvoices: true,
      smartAlerts: true,
      deductionCoach: true,
      forecastMonthsMax: 12,
      canPayPerUse: false,
    },
    // ── PRO $39.99/mo — Plus + advanced forecasting + business tools ──────────
    // "Everything in Plus, plus advanced forecasting and business tools"
    pro: {
      isPro: true,
      tier: 'pro',
      marketingTier: 'marketing_studio',
      canAccessAdmin: false,
      canManageUsers: false,
      receiptScansPerMonth: Infinity,
      invoicesPerMonth: Infinity,
      quantraMessagesPerMonth: Infinity,
      plaidAccounts: 5,
      videoClipsFree: false,
      marketingScripts: 50,
      marketingAtomizer: 40,
      marketingTrends: 30,
      marketingDemographics: 20,
      marketingPricing: 10,
      fullPulse: true,
      subscriptionAutoDetect: true,
      clientScorecards: true,
      invoiceEscalation: true,
      incomeSmoothing: true,
      rateCard: true,
      profitFirst: true,
      exportReports: true,
      brandedInvoices: true,
      smartAlerts: true,
      deductionCoach: true,
      forecastMonthsMax: 12,
      canPayPerUse: false,
    },
    // ── PLUS $19.99/mo — unlimited AI + advanced reports ─────────────────────
    // "Unlimited AI features, advanced reports, and priority support"
    plus: {
      isPro: false,
      tier: 'plus',
      marketingTier: 'marketing_lite',
      canAccessAdmin: false,
      canManageUsers: false,
      receiptScansPerMonth: Infinity,
      invoicesPerMonth: Infinity,
      quantraMessagesPerMonth: Infinity,
      plaidAccounts: 0,
      videoClipsFree: false,
      marketingScripts: 10,
      marketingAtomizer: 5,
      marketingTrends: 5,
      marketingDemographics: 0,
      marketingPricing: 0,
      fullPulse: true,
      subscriptionAutoDetect: true,
      clientScorecards: false,
      invoiceEscalation: false,
      incomeSmoothing: false,
      rateCard: false,
      profitFirst: false,
      exportReports: true,
      brandedInvoices: true,
      smartAlerts: true,
      deductionCoach: true,
      forecastMonthsMax: 6,
      canPayPerUse: false,
    },
    // ── FREE — very limited, enough to see the value ──────────────────────────
    free: {
      isPro: false,
      tier: 'free',
      marketingTier: 'free',
      canAccessAdmin: false,
      canManageUsers: false,
      receiptScansPerMonth: 10,
      invoicesPerMonth: 5,
      quantraMessagesPerMonth: 15,
      plaidAccounts: 0,
      videoClipsFree: false,
      marketingScripts: 0,
      marketingAtomizer: 0,
      marketingTrends: 0,
      marketingDemographics: 0,
      marketingPricing: 0,
      fullPulse: true,
      subscriptionAutoDetect: false,
      clientScorecards: false,
      invoiceEscalation: false,
      incomeSmoothing: false,
      rateCard: false,
      profitFirst: false,
      exportReports: false,
      brandedInvoices: false,
      smartAlerts: false,
      deductionCoach: false,
      forecastMonthsMax: 3,
      canPayPerUse: false,
    },
    payg: {
      isPro: false,
      tier: 'payg',
      marketingTier: 'free',
      canAccessAdmin: false,
      canManageUsers: false,
      receiptScansPerMonth: 10,
      invoicesPerMonth: 5,
      quantraMessagesPerMonth: 15,
      plaidAccounts: 0,
      videoClipsFree: false,
      marketingScripts: 0,
      marketingAtomizer: 0,
      marketingTrends: 0,
      marketingDemographics: 0,
      marketingPricing: 0,
      fullPulse: false,
      subscriptionAutoDetect: false,
      clientScorecards: false,
      invoiceEscalation: false,
      incomeSmoothing: false,
      rateCard: false,
      profitFirst: false,
      exportReports: false,
      brandedInvoices: false,
      smartAlerts: false,
      deductionCoach: false,
      forecastMonthsMax: 1,
      canPayPerUse: true,
    },
  };

  const PAYG_PRICES = {
    quantraMessage: 5,
    receiptScan: 10,
    invoice: 25,
    plaidAccount: 200,
    marketingScript: 15,
    marketingAtomizer: 15,
    marketingTrend: 25,
    marketingDemographic: 25,
    marketingPricing: 50,
    removeWatermark: 100,
    fullPulse: 200,
    subscriptionDetect: 100,
    videoGeneration: 300,
    websiteBuilder: 50,
  };

  const getRolePerms = (role, email) => {
    if (ADMIN_EMAILS.some(e => String(email || '').toLowerCase() === e.toLowerCase())) return ROLES.admin;
    if (INTERNAL_BETA_EMAILS.some(e => String(email || '').toLowerCase() === e.toLowerCase())) return ROLES.internal_beta;
    const normalizedRole = role === 'lite'
      ? 'plus'
      : role === 'plus_annual'
      ? 'plus'
      : role === 'pro_annual'
      ? 'pro'
      : role === 'elite_annual'
      ? 'elite'
      : role === 'pay_as_you_go'
      ? 'payg'
      : role === 'payg'
      ? 'payg'
      : role;
    return ROLES[normalizedRole] || ROLES.free;
  };

  const [user, setUser] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const isDemoModeRef = useRef(false);
  useEffect(() => { isDemoModeRef.current = isDemoMode; }, [isDemoMode]);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Signal native Swift overlay to dismiss the moment React mounts
  useEffect(() => {
    // Dismiss the HTML loader
    if (typeof window.__hideNativeLoader === 'function') window.__hideNativeLoader();
    // Dismiss the native Swift overlay via WKScriptMessageHandler
    try {
      window.webkit?.messageHandlers?.appReady?.postMessage('ready');
    } catch (_) {}
  }, []);

  // Absolute fallback: always show app within 5s regardless of Firebase state
  useEffect(() => {
    const t = setTimeout(() => setAuthInitialized(true), 5000);
    return () => clearTimeout(t);
  }, []);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState('profile');
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    companyName: '',
    phone: '',
    address: '',
    industry: '',
    entityType: 'sole_prop',
    taxId: '',
    fiscalYearEnd: '12',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaHint, setMfaHint] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [nativeMfaData, setNativeMfaData] = useState(null); // { sessionInfo, mfaPendingCredential } for Capacitor MFA
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorPhone, setTwoFactorPhone] = useState('');
  const [twoFactorVerificationId, setTwoFactorVerificationId] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [reauthRequired, setReauthRequired] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);
  const [recaptchaSolved, setRecaptchaSolved] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [bills, setBills] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [scheduledTx, setScheduledTx] = useState([]);
  const [smartAlerts, setSmartAlerts] = useState([]);
  const [dismissedSmartAlerts, setDismissedSmartAlerts] = useState(new Set());
  const [mileageTrips, setMileageTrips] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [expensesSubTab, setExpensesSubTab] = useState('expenses');
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [view, setView] = useState('overview');
  const [displayedView, setDisplayedView] = useState('overview');
  const [businessInitialTool, setBusinessInitialTool] = useState(null);

  // Handle deep-link URL params (e.g. ?action=setup2fa from Android/iOS)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'setup2fa') {
      setView('settings');
      setDisplayedView('settings');
      setSettingsTab('security');
      setShowTwoFactorModal(true);
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [pageAnimClass, setPageAnimClass] = useState('centris-page-enter');
  const pageTransTimer = useRef(null);
  useEffect(() => {
    if (view === displayedView) return;
    clearTimeout(pageTransTimer.current);
    setPageAnimClass('centris-page-exit');
    pageTransTimer.current = setTimeout(() => {
      setDisplayedView(view);
      setPageAnimClass('centris-page-enter');
    }, 210);
    return () => clearTimeout(pageTransTimer.current);
  }, [view, displayedView]);

  // Status bar — overlay so env(safe-area-inset-top) works, white icons on dark
  useEffect(() => {
    if (!isNative) return;
    StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});
  }, []);



  // Pull-to-refresh state
  const [pullRefreshing, setPullRefreshing] = useState(false);
  const [pullProgress, setPullProgress] = useState(0); // 0–1 drag progress before threshold
  const pullStartY = useRef(null);
  const pullMainRef = useRef(null);

  const handleRefreshAll = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const [expR, invR, tripsR, subsR, revR, billsR, budgetsR] = await Promise.all([
        loadExpenses(user.uid),
        loadInvoices(user.uid),
        loadMileageTrips(user.uid),
        loadSubscriptions(user.uid),
        loadRevenue(user.uid),
        loadBills(user.uid),
        loadBudgets(user.uid),
      ]);
      if (expR.success) setExpenses(expR.expenses);
      if (invR.success) setInvoices(invR.invoices);
      if (tripsR.success) setMileageTrips(tripsR.trips);
      if (subsR.success) setSubscriptions(subsR.subscriptions);
      if (revR.success) setRevenue(revR.revenue);
      if (billsR.success) setBills(billsR.bills);
      if (budgetsR.success) setBudgets(budgetsR.budgets);
    } catch (_) {}
  }, [user?.uid]);

  const handlePullStart = (e) => {
    // Only arm when truly at the top — prevents triggering during normal upward scroll
    if ((pullMainRef.current?.scrollTop ?? 1) < 4) pullStartY.current = e.touches[0].clientY;
  };
  const handlePullMove = (e) => {
    if (pullStartY.current === null) return;
    const delta = e.touches[0].clientY - pullStartY.current;
    if (delta <= 0) { pullStartY.current = null; setPullProgress(0); return; } // upward — disarm
    const THRESHOLD = 90; // px to trigger refresh (reduced from 280 — more responsive)
    setPullProgress(Math.min(delta / THRESHOLD, 1));
    if (delta > THRESHOLD && !pullRefreshing) setPullRefreshing(true);
  };
  const handlePullEnd = () => {
    setPullProgress(0);
    if (pullRefreshing) {
      haptic(ImpactStyle.Medium);
      handleRefreshAll().finally(() => setTimeout(() => setPullRefreshing(false), 600));
    }
    pullStartY.current = null;
  };

  const [budgetAlerts, setBudgetAlerts] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [scanningReceipt, setScanningReceipt] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showDeleteAllRevenueConfirm, setShowDeleteAllRevenueConfirm] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lightMode, setLightMode] = useState(() => {
    try { return localStorage.getItem('centris-theme') === 'light'; } catch { return false; }
  });
  const toggleTheme = () => setLightMode(prev => {
    const next = !prev;
    try { localStorage.setItem('centris-theme', next ? 'light' : 'dark'); } catch {}
    return next;
  });
  useEffect(() => {
    const bg = lightMode ? '#f5f6fa' : 'linear-gradient(135deg, #050913, #0c1428, #071020)';
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
  }, [lightMode]);
  const [quantraPopupOpen, setQuantraPopupOpen] = useState(false);
  const [cfoPopupOpen, setCfoPopupOpen] = useState(false);
  const [cfoPopupData, setCfoPopupData] = useState(null); // unknown-subscriptions | scent-redirect | gmail-checklist
  const [alertPopupQueue, setAlertPopupQueue] = useState([]);
  const [currentAlertPopup, setCurrentAlertPopup] = useState(null);
  const [toasts, setToasts] = useState([]);
  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);
  const [showQuantraGreeting, setShowQuantraGreeting] = useState(false);
  const [quantraGreetingMsg, setQuantraGreetingMsg] = useState('');
  const [quantraGreetingDismissed, setQuantraGreetingDismissed] = useState(false);
  
  const [showReceiptReview, setShowReceiptReview] = useState(false);
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);

  // First-time-user spotlight tour. Replaces the old multi-step concept modal —
  // running both back to back was exactly the "information overload" failure
  // mode onboarding research warns against. Mobile-only since its targets are
  // the mobile bottom tab bar.
  useEffect(() => {
    if (!user?.uid || user.uid === 'demo-user') return;
    if (localStorage.getItem('centris_onboarding_v1')) return;
    if (!isMobileDevice) return;
    const t = setTimeout(() => {
      setView('overview');
      setShowOnboardingTour(true);
    }, 800);
    return () => clearTimeout(t);
  }, [user?.uid]);
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);

  // Bank account state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [showBankConnectComingSoon, setShowBankConnectComingSoon] = useState(false);
  const [bankStatementOpenSignal, setBankStatementOpenSignal] = useState(0);
  const [showTopLevelBankImport, setShowTopLevelBankImport] = useState(false);
  const [showBankOnboarding, setShowBankOnboarding] = useState(false);
  const [taxVaultReminder, setTaxVaultReminder] = useState(null);
  const [bankImportMode, setBankImportMode] = useState(readBankImportPref);
  const [showSwitchToLiveBankModal, setShowSwitchToLiveBankModal] = useState(false);

  const currentPlan = useMemo(() => {
    if (!user) return 'free';
    if (ADMIN_EMAILS.some(e => String(user.email || '').toLowerCase() === e.toLowerCase())) return 'admin';
    return user.plan || user.planName || user.role || 'free';
  }, [user]);

  const perms = useMemo(() => getRolePerms(currentPlan, user?.email), [currentPlan, user?.email]);

  /** When true, load Plaid accounts and use live transaction sync; otherwise statements + receipt path. */
  const liveBankImportActive = useMemo(
    () => PLAID_BANK_CONNECT_ENABLED && bankImportMode === BANK_IMPORT_LIVE,
    [bankImportMode]
  );

  useEffect(() => {
    if (!PLAID_BANK_CONNECT_ENABLED && bankImportMode === BANK_IMPORT_LIVE) {
      setBankImportMode(BANK_IMPORT_STATEMENTS);
    }
  }, [PLAID_BANK_CONNECT_ENABLED, bankImportMode]);

  useEffect(() => {
    try {
      localStorage.setItem(BANK_IMPORT_PREF_KEY, bankImportMode);
    } catch { /* ignore */ }
  }, [bankImportMode]);

  useEffect(() => {
    if (bankImportMode === BANK_IMPORT_STATEMENTS) {
      setBankAccounts([]);
    }
  }, [bankImportMode]);

  // taxVaultReminder shape: { amount: number, invoiceNumber: string, client: string }
  const [bankOnboardingDismissed, setBankOnboardingDismissed] = useState(false);
  const [showTransactionReview, setShowTransactionReview] = useState(false);
  const [bankTransactionsToReview, setBankTransactionsToReview] = useState([]);
  const [importingTransactions, setImportingTransactions] = useState(false);
  const [lastBankSync, setLastBankSync] = useState(null);
  const [extensionPaired, setExtensionPaired] = useState(false);
  const [bankLearnedKeywords, setBankLearnedKeywords] = useState([]);
  const [bankInsights, setBankInsights] = useState([]);
  const [quantraInsights, setQuantraInsights] = useState([]);
  const [forecastTaxVaultSettings, setForecastTaxVaultSettings] = useState({ enabled: true, reservePercent: 25 });
  const [showGoalTracker, setShowGoalTracker] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [goalAmount, setGoalAmount] = useState(() => {
    try { const v = parseFloat(localStorage.getItem('centris_monthly_goal')); return v > 0 ? v : null; } catch { return null; }
  });
  const [toolSearch, setToolSearch] = useState('');
  const [showAddRevenue, setShowAddRevenue] = useState(false);
  const [newRevenue, setNewRevenue] = useState({
    source: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    status: 'received'
  });
  const [showHealthBreakdown, setShowHealthBreakdown] = useState(false);
  const [dashboardRange, setDashboardRange] = useState('90d');
  const [dashboardMetric, setDashboardMetric] = useState('net');
  const [heatmapSelectedDay, setHeatmapSelectedDay] = useState(null);
  const [heatmapHoverDay, setHeatmapHoverDay] = useState(null);
  const [backgroundTasks, setBackgroundTasks] = useState([]);
  const backgroundTaskIdRef = useRef(0);
  const mfaSignInRecaptchaRef = useRef(null);

  // Collaboration state
  const [publicInvoiceToken, setPublicInvoiceToken] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('invoice') || null;
  });
  const [pendingInviteToken, setPendingInviteToken] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('invite') || null;
  });
  const [inviteDetails, setInviteDetails] = useState(null);
  const [inviteAccepting, setInviteAccepting] = useState(false);
  const mfaEnrollRecaptchaRef = useRef(null);

  const startBackgroundTask = useCallback((label = 'Working...') => {
    const id = `bg-${Date.now()}-${backgroundTaskIdRef.current++}`;
    setBackgroundTasks((prev) => [...prev, { id, label }]);
    return id;
  }, []);

  const finishBackgroundTask = useCallback((id) => {
    setBackgroundTasks((prev) => prev.filter((task) => task.id !== id));
  }, []);

  const withBackgroundTask = useCallback(async (label, runner) => {
    const taskId = startBackgroundTask(label);
    try {
      return await runner();
    } finally {
      finishBackgroundTask(taskId);
    }
  }, [startBackgroundTask, finishBackgroundTask]);
 
  // Offline detection
  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (currentUser) => {
      // If user signed in via REST API bypass, don't let SDK's null override it
      if (!currentUser && getRestUser && getRestUser()) return;
      const timeout = new Promise((resolve) => setTimeout(resolve, 8000));
      const flow = (async () => {
      try {
      if (currentUser) {
        // Load role from Firestore profile — fall back to bare auth user if profile load fails
        let profileData = {};
        try { profileData = await loadUserProfile(currentUser.uid); } catch (_) {}

        setUser({
          ...currentUser,
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName,
          name: currentUser.displayName || currentUser.email.split('@')[0],
          emailVerified: Boolean(currentUser.emailVerified),
          ...profileData,
        });
        setProfileForm((prev) => ({
          ...prev,
          name: currentUser.displayName || profileData.name || '',
          email: currentUser.email || '',
          companyName: profileData.companyName || '',
          phone: profileData.phone || '',
          address: profileData.address || '',
          industry: profileData.industry || '',
          entityType: profileData.entityType || 'sole_prop',
          taxId: profileData.taxId || '',
          fiscalYearEnd: profileData.fiscalYearEnd || '12',
          currency: profileData.currency || 'USD',
        }));
        const [expensesResult, invoicesResult, tripsResult, subsResult, revenueResult, billsResult, budgetsResult, scheduledResult] = await Promise.all([
          loadExpenses(currentUser.uid),
          loadInvoices(currentUser.uid),
          loadMileageTrips(currentUser.uid),
          loadSubscriptions(currentUser.uid),
          loadRevenue(currentUser.uid),
          loadBills(currentUser.uid),
          loadBudgets(currentUser.uid),
          loadScheduledTx(currentUser.uid)
        ]);
        if (expensesResult.success) setExpenses(expensesResult.expenses);
        if (invoicesResult.success) setInvoices(invoicesResult.invoices);
        if (tripsResult.success) setMileageTrips(tripsResult.trips);
        if (subsResult.success) setSubscriptions(subsResult.subscriptions);
        if (revenueResult.success) setRevenue(revenueResult.revenue);
        if (billsResult.success) setBills(billsResult.bills);
        if (budgetsResult.success) setBudgets(budgetsResult.budgets);
        if (scheduledResult.success) setScheduledTx(scheduledResult.txs);
      } else {
        // Don't clear state if we're in demo mode (no real Firebase user signed in)
        if (isDemoModeRef.current) return;
        setUser(null);
        setProfileForm({
          name: '',
          email: '',
          companyName: '',
          phone: '',
          address: '',
          industry: '',
          entityType: 'sole_prop',
          taxId: '',
          fiscalYearEnd: '12',
          currency: 'USD',
        });
        setExpenses([]);
        setInvoices([]);
        setMileageTrips([]);
        setSubscriptions([]);
        setRevenue([]);
        setBills([]);
        setBudgets([]);
        setScheduledTx([]);
        setExtensionPaired(false);
        setSmartAlerts([]);
        setDismissedSmartAlerts(new Set());
        setAlertPopupQueue([]);
        setCurrentAlertPopup(null);
      }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        setAuthInitialized(true);
      }
      })();
      await Promise.race([flow, timeout]);
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgradeResult = params.get('upgrade');
    const creditsResult = params.get('credits');
    const videoResult = params.get('video');
    if (upgradeResult === 'success' || creditsResult === 'success' || videoResult === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      if (user?.uid) {
        loadUserProfile(user.uid).then((profile) => {
          setUser((prev) => ({ ...(prev || {}), ...profile }));
        }).catch(err => console.error('loadUserProfile error:', err));
      }
      if (typeof showToast === 'function') {
        if (videoResult === 'success') {
          showToast('Video purchase completed. You can now generate your clip batch.', 'success', 5000);
        } else {
          showToast(
            creditsResult === 'success' ? 'Credits added to your account!' : 'Welcome to your upgraded plan!',
            'success',
            5000
          );
        }
      }
    }
  }, [user?.uid, showToast]);

  // Clean collaboration tokens from URL on mount
  useEffect(() => {
    if (publicInvoiceToken || pendingInviteToken) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load invite details when a pending invite token is present
  useEffect(() => {
    if (!pendingInviteToken) return;
    getWorkspaceInvite(pendingInviteToken).then((res) => {
      if (res.success) setInviteDetails(res.invite);
      else { setPendingInviteToken(null); showToast(res.error || 'Invite not found', 'error'); }
    });
  }, [pendingInviteToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load bank accounts when user logs in or switches to live bank import
  useEffect(() => {
    if (user?.uid) {
      loadBankAccounts();
    } else {
      setBankAccounts([]);
    }
  }, [user, liveBankImportActive]);

  // Check if should show onboarding
  useEffect(() => {
    if (user?.uid && !loadingBankAccounts) {
      checkBankOnboarding();
    }
  }, [user, loadingBankAccounts, bankAccounts]);

  useEffect(() => {
    if (!user?.uid) return;
    if (liveBankImportActive && bankAccounts.length > 0) {
      syncBankIncomeToTaxVault();
      generateBankInsights();
    } else if (liveBankImportActive) {
      setBankInsights([]);
    } else {
      syncStatementIncomeToTaxVault();
      generateStatementInsights();
    }
  }, [user?.uid, bankAccounts, liveBankImportActive, revenue, expenses]);




  useEffect(() => {
    if (!user?.uid) {
      setForecastTaxVaultSettings({ enabled: true, reservePercent: 25 });
      return;
    }
    (async () => {
      const settings = await loadTaxVaultSettings(user.uid);
      if (settings.success && settings.settings) {
        setForecastTaxVaultSettings({
          enabled: settings.settings.enabled !== false,
          reservePercent: Number(settings.settings.reservePercent ?? 25),
        });
      }
    })();
  }, [user]);

  // ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬ Quantra Greeting Generator ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬
  const generateQuantraGreeting = useCallback(() => {
    const firstName = user?.name?.split(' ')[0] || 'there';
    const hour = new Date().getHours();
    const day = new Date().toLocaleDateString('en-US', { weekday: 'long' });

    let timeGreeting;
    if (hour < 12) timeGreeting = 'Good morning';
    else if (hour < 17) timeGreeting = 'Good afternoon';
    else timeGreeting = 'Good evening';

    const unpaidInvoices = invoices.filter((i) => i.status !== 'paid');
    const totalUnpaid = unpaidInvoices.reduce((s, i) => s + (i.total || 0), 0);
    const thisMonthExpenses = expenses.filter((e) => {
      const d = new Date(e.date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    const monthSpend = thisMonthExpenses.reduce((s, e) => s + (e.amount || 0), 0);
    const paidRevenue = invoices.filter((i) => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);

    const greetings = [];
    greetings.push(`${timeGreeting}, ${firstName}! Happy ${day}. I'm here whenever you need me.`);

    if (totalUnpaid > 500) {
      greetings.push(`${timeGreeting}, ${firstName}! Quick heads up - you've got $${totalUnpaid.toLocaleString()} in unpaid invoices. Want me to help you follow up?`);
    }
    if (paidRevenue > 0 && monthSpend > 0) {
      const margin = ((paidRevenue - monthSpend) / paidRevenue) * 100;
      if (margin > 30) {
        greetings.push(`${timeGreeting}, ${firstName}! Your profit margin is looking healthy at ${margin.toFixed(0)}%. You're crushing it!`);
      }
    }
    if (monthSpend > 2000) {
      greetings.push(`${timeGreeting}, ${firstName}! You've spent $${monthSpend.toLocaleString()} this month so far. Want me to break down where it's going?`);
    }
    if (expenses.length === 0) {
      greetings.push(`${timeGreeting}, ${firstName}! Looks like you're just getting started. Upload a receipt or bank statement and I'll take it from there!`);
    }
    if (day === 'Monday') {
      greetings.push(`${timeGreeting}, ${firstName}! New week, fresh start. Let's make this one count!`);
    }
    if (day === 'Friday') {
      greetings.push(`${timeGreeting}, ${firstName}! Happy Friday! Want a quick rundown of how the week went financially?`);
    }

    if (greetings.length > 1) {
      const useContextual = Math.random() > 0.3 && greetings.length > 1;
      return useContextual ? greetings[1 + Math.floor(Math.random() * (greetings.length - 1))] : greetings[0];
    }
    return greetings[0];
  }, [user, invoices, expenses]);

  // ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬ Quantra Greeting on App Open ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€š¬
  useEffect(() => {
    if (!user?.uid || quantraGreetingDismissed) return;
    const timer = setTimeout(() => {
      const msg = generateQuantraGreeting();
      setQuantraGreetingMsg(msg);
      setShowQuantraGreeting(true);
      setTimeout(() => setShowQuantraGreeting(false), 12000);
    }, 1500);
    return () => clearTimeout(timer);
  }, [user?.uid, generateQuantraGreeting, quantraGreetingDismissed]);

  // Push notification registration (only if already granted, web only)
  useEffect(() => {
    if (!user?.uid) return;
    // Skip on native iOS — WKWebView does not support browser Notification API
    if (window.Capacitor?.isNativePlatform?.()) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      requestNotificationPermission(user.uid);
    }
  }, [user?.uid]);

  // Foreground push messages route to alert popup
  useEffect(() => {
    if (!user?.uid) return () => {};
    const seenKey = `centris_seen_push_notifications_${user.uid}`;
    const readSeen = () => {
      try {
        const parsed = JSON.parse(localStorage.getItem(seenKey) || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    };
    const writeSeen = (ids) => {
      // Keep storage bounded while preserving recency.
      const trimmed = ids.slice(-250);
      localStorage.setItem(seenKey, JSON.stringify(trimmed));
    };

    const unsubscribe = onForegroundMessage((payload) => {
      const stableId =
        String(payload?.messageId || '').trim() ||
        String(payload?.data?.alertId || '').trim() ||
        String(payload?.data?.notificationId || '').trim() ||
        [
          payload?.data?.type,
          payload?.data?.billId,
          payload?.notification?.title,
          payload?.notification?.body,
        ]
          .filter(Boolean)
          .join('|');

      if (stableId) {
        const seen = readSeen();
        if (seen.includes(stableId)) return;
        writeSeen([...seen, stableId]);
      }

      const popupAlert = {
        id: stableId ? `push-${stableId}` : `push-${Date.now()}`,
        severity: payload?.data?.severity || 'warning',
        category: 'notification',
        title: payload?.notification?.title || 'Alert',
        message: payload?.notification?.body || '',
      };
      setAlertPopupQueue((prev) => {
        if (prev.some((a) => a.id === popupAlert.id)) return prev;
        return [...prev, popupAlert];
      });
      setCurrentAlertPopup((prev) => prev || popupAlert);
    });
    return unsubscribe;
  }, [user?.uid]);

  const clearRecaptcha = useCallback((refObj) => {
    try {
      if (refObj.current) refObj.current.clear();
    } catch (_) {
      // no-op
    }
    refObj.current = null;
  }, []);

  const ensureRecaptcha = useCallback(async (triggerElementId, refObj, opts = {}) => {
    const targetEl = document.getElementById(triggerElementId);
    if (!targetEl) {
      throw new Error('Security check could not initialize. Please reopen the 2FA modal and try again.');
    }
    if (refObj.current) return refObj.current;
    const size = opts.size || 'invisible';
    const verifier = new RecaptchaVerifier(auth, triggerElementId, {
      size,
      callback: () => {},
      'expired-callback': () => {
        clearRecaptcha(refObj);
      },
    });
    await verifier.render();
    refObj.current = verifier;
    return verifier;
  }, [clearRecaptcha]);

  const normalizePhoneToE164 = useCallback((raw) => {
    const input = String(raw || '').trim();
    if (!input) return '';
    const cleaned = input.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+')) {
      const digits = `+${cleaned.slice(1).replace(/\D/g, '')}`;
      return digits.length >= 8 ? digits : '';
    }
    const digits = cleaned.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return '';
  }, []);

  const refreshTwoFactorStatus = useCallback(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setTwoFactorEnabled(false);
      return;
    }
    setTwoFactorEnabled((multiFactor(currentUser).enrolledFactors || []).length > 0);
  }, []);

  useEffect(() => {
    refreshTwoFactorStatus();
  }, [user?.uid, refreshTwoFactorStatus]);

  useEffect(() => () => {
    clearRecaptcha(mfaSignInRecaptchaRef);
    clearRecaptcha(mfaEnrollRecaptchaRef);
  }, [clearRecaptcha]);

  // Pre-render a visible reCAPTCHA when the phone-input step is shown so the user
  // can solve it before clicking "Send code". Invisible reCAPTCHA fails on localhost
  // (auth/invalid-app-credential) because Google can't assess behavioral signals.
  useEffect(() => {
    const shouldRender = showTwoFactorModal && !twoFactorEnabled && !twoFactorVerificationId;
    if (!shouldRender) {
      clearRecaptcha(mfaEnrollRecaptchaRef);
      setRecaptchaSolved(false);
      return;
    }
    if (mfaEnrollRecaptchaRef.current) return; // already rendered
    const el = document.getElementById(RECAPTCHA_ENROLL_TRIGGER_ID);
    if (!el) return;
    setRecaptchaSolved(false);
    const verifier = new RecaptchaVerifier(auth, RECAPTCHA_ENROLL_TRIGGER_ID, {
      size: 'normal',
      callback: () => { setRecaptchaSolved(true); },
      'expired-callback': () => { setRecaptchaSolved(false); clearRecaptcha(mfaEnrollRecaptchaRef); },
    });
    verifier.render().then(() => {
      mfaEnrollRecaptchaRef.current = verifier;
    }).catch(() => {});
  }, [showTwoFactorModal, twoFactorEnabled, twoFactorVerificationId, reauthRequired, clearRecaptcha, RECAPTCHA_ENROLL_TRIGGER_ID]);

  const startMfaChallenge = useCallback(async (rawError) => {
    const resolver = getMultiFactorResolver(auth, rawError);
    const hint = resolver?.hints?.[0];
    if (!hint) throw new Error('No enrolled 2FA method found for this account.');
    try {
      const recaptchaVerifier = await ensureRecaptcha(RECAPTCHA_SIGNIN_TRIGGER_ID, mfaSignInRecaptchaRef);
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        recaptchaVerifier
      );
      setMfaResolver(resolver);
      setMfaHint(hint.phoneNumber || 'your phone');
      setMfaVerificationId(verificationId);
      setMfaRequired(true);
      setMfaCode('');
      setMfaError('');
    } catch (error) {
      clearRecaptcha(mfaSignInRecaptchaRef);
      const msg = String(error?.message || '');
      if (msg.includes('reCAPTCHA') || error?.code === 'auth/captcha-check-failed') {
        throw new Error('reCAPTCHA verification failed. Check authorized domains in Firebase Auth and try again.');
      }
      throw error;
    }
  }, [RECAPTCHA_SIGNIN_TRIGGER_ID, clearRecaptcha, ensureRecaptcha]);

  const handleMfaSignInConfirm = async () => {
    if (!mfaCode.trim()) return;
    // Native Capacitor path — use REST v2 finalize + custom token exchange
    if (nativeMfaData) {
      if (!nativeMfaData.sessionInfo || !nativeMfaData.mfaPendingCredential) return;
      setAuthLoading(true);
      setMfaError('');
      try {
        const result = await completeNativeMfa(nativeMfaData.sessionInfo, nativeMfaData.mfaPendingCredential, mfaCode.trim());
        if (result.success) {
          const profileData = await Promise.race([
            loadUserProfile(result.user.uid),
            new Promise(resolve => setTimeout(() => resolve({}), 3000))
          ]).catch(() => ({}));
          if (profileData?.onboardingCompleted) {
            localStorage.setItem('centris_onboarding_v1', 'done');
          }
          setAuthInitialized(true);
          setUser({
            ...result.user,
            name: result.user.displayName || result.user.email?.split('@')[0] || '',
            emailVerified: Boolean(result.user.emailVerified),
            ...profileData,
          });
          loadExpenses(result.user.uid).then(r => { if (r.success) setExpenses(r.expenses); }).catch(() => {});
          loadInvoices(result.user.uid).then(r => { if (r.success) setInvoices(r.invoices); }).catch(() => {});
          loadRevenue(result.user.uid).then(r => { if (r.success) setRevenue(r.revenue); }).catch(() => {});
          loadBills(result.user.uid).then(r => { if (r.success) setBills(r.bills); }).catch(() => {});
          loadBudgets(result.user.uid).then(r => { if (r.success) setBudgets(r.budgets); }).catch(() => {});
          setMfaRequired(false);
          setNativeMfaData(null);
          setMfaCode('');
          setShowAuth(false);
        } else {
          setMfaError(result.error || 'Unable to verify the code. Please try again.');
        }
      } catch (error) {
        setMfaError(error?.message || 'Unable to verify the code. Please try again.');
      } finally {
        setAuthLoading(false);
      }
      return;
    }
    // Web path — reCAPTCHA-based SDK MFA
    if (!mfaResolver || !mfaVerificationId) return;
    setAuthLoading(true);
    setMfaError('');
    try {
      const cred = PhoneAuthProvider.credential(mfaVerificationId, mfaCode.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await mfaResolver.resolveSignIn(assertion);
      setMfaRequired(false);
      setMfaResolver(null);
      setMfaVerificationId('');
      setMfaCode('');
      setShowAuth(false);
      clearRecaptcha(mfaSignInRecaptchaRef);
    } catch (error) {
      setMfaError(error?.message || 'Unable to verify the code. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const startTwoFactorEnrollment = async () => {
    if (!twoFactorPhone.trim()) return;
    setTwoFactorLoading(true);
    setTwoFactorError('');
    try {
      const normalizedPhone = normalizePhoneToE164(twoFactorPhone);
      if (!normalizedPhone) {
        throw new Error('Enter a valid phone number (example: +15551234567).');
      }

      // Native Capacitor path — use Cloud Function to send SMS without browser reCAPTCHA
      if (isNative) {
        console.log('[2FA] Starting native enrollment, currentUser:', auth.currentUser?.uid || 'null');
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('Session expired — please sign out and sign back in, then try again.');
        }
        const idToken = await currentUser.getIdToken(true);
        console.log('[2FA] Got idToken, calling enrollMfaPhone CF');
        const resp = await fetch(
          'https://us-central1-receiptwise-prod.cloudfunctions.net/enrollMfaPhone',
          { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken, phoneNumber: normalizedPhone }) }
        );
        const json = await resp.json();
        console.log('[2FA] enrollMfaPhone response:', resp.status, JSON.stringify(json));
        if (!resp.ok || !json.sessionInfo) {
          throw new Error(json.error || 'Failed to send verification SMS.');
        }
        setTwoFactorVerificationId(json.sessionInfo);
        return;
      }

      // Web path — reCAPTCHA + Firebase SDK
      const session = await multiFactor(auth.currentUser).getSession();
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const requestSmsCode = async (retry = false) => {
        if (retry) clearRecaptcha(mfaEnrollRecaptchaRef);
        const recaptchaVerifier = await ensureRecaptcha(RECAPTCHA_ENROLL_TRIGGER_ID, mfaEnrollRecaptchaRef, { size: 'normal' });
        return phoneAuthProvider.verifyPhoneNumber(
          { phoneNumber: normalizedPhone, session },
          recaptchaVerifier
        );
      };
      let verificationId;
      try {
        verificationId = await requestSmsCode(false);
      } catch (firstError) {
        const msg = String(firstError?.message || '').toLowerCase();
        if (firstError?.code !== 'auth/timeout' && !msg.includes('timeout')) throw firstError;
        clearRecaptcha(mfaEnrollRecaptchaRef);
        verificationId = await requestSmsCode(true);
      }
      setTwoFactorVerificationId(verificationId);
      setRecaptchaSolved(false);
    } catch (error) {
      setTwoFactorVerificationId('');
      setRecaptchaSolved(false);
      const msg = String(error?.message || '');
      if (error?.code === 'auth/requires-recent-login') {
        setReauthRequired(true);
        setTwoFactorError('Please confirm your password to continue.');
      } else if (error?.code === 'auth/timeout' || msg.toLowerCase().includes('timeout')) {
        setTwoFactorError('reCAPTCHA timed out. Please try again.');
        clearRecaptcha(mfaEnrollRecaptchaRef);
      } else if (msg.includes('reCAPTCHA') || error?.code === 'auth/captcha-check-failed') {
        setTwoFactorError('reCAPTCHA verification failed. Please try again.');
        clearRecaptcha(mfaEnrollRecaptchaRef);
      } else {
        setTwoFactorError(error?.message || 'Could not send verification code.');
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleReauth = async () => {
    if (!auth.currentUser || !reauthPassword) return;
    setReauthLoading(true);
    setTwoFactorError('');
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, reauthPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      // Clear the expired reCAPTCHA token — the useEffect will re-render a fresh one
      // once reauthRequired becomes false and the phone screen is shown again.
      clearRecaptcha(mfaEnrollRecaptchaRef);
      setRecaptchaSolved(false);
      setReauthRequired(false);
      setReauthPassword('');
      setTwoFactorError('Identity confirmed. Please complete the reCAPTCHA and send the code.');
    } catch (error) {
      setTwoFactorError(error?.code === 'auth/wrong-password' || error?.code === 'auth/invalid-credential'
        ? 'Incorrect password. Please try again.'
        : error?.message || 'Re-authentication failed.');
    } finally {
      setReauthLoading(false);
    }
  };

  const confirmTwoFactorEnrollment = async () => {
    if (!auth.currentUser || !twoFactorVerificationId || !twoFactorCode.trim()) return;
    setTwoFactorLoading(true);
    setTwoFactorError('');
    try {
      // Native Capacitor path — finalize via Cloud Function (twoFactorVerificationId holds sessionInfo)
      if (isNative) {
        const idToken = await auth.currentUser.getIdToken();
        const resp = await fetch(
          'https://us-central1-receiptwise-prod.cloudfunctions.net/finalizeEnrollMfa',
          { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken, sessionInfo: twoFactorVerificationId, code: twoFactorCode.trim() }) }
        );
        const json = await resp.json();
        if (!resp.ok) throw new Error(json.error || 'Could not enable 2FA.');
        // Reload user so enrolledFactors reflects the new enrollment
        await auth.currentUser.reload();
        setTwoFactorEnabled(true);
        setTwoFactorVerificationId('');
        setTwoFactorCode('');
        setTwoFactorPhone('');
        setShowTwoFactorModal(false);
        return;
      }
      // Web path — SDK enrollment
      const cred = PhoneAuthProvider.credential(twoFactorVerificationId, twoFactorCode.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(auth.currentUser).enroll(assertion, 'Primary phone');
      setTwoFactorEnabled(true);
      setTwoFactorVerificationId('');
      setTwoFactorCode('');
      setTwoFactorPhone('');
      setShowTwoFactorModal(false);
      clearRecaptcha(mfaEnrollRecaptchaRef);
    } catch (error) {
      setTwoFactorError(error?.message || 'Could not enable 2FA.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const disableTwoFactor = async () => {
    if (!auth.currentUser) return;
    setTwoFactorLoading(true);
    setTwoFactorError('');
    try {
      const factors = multiFactor(auth.currentUser).enrolledFactors || [];
      if (factors.length > 0) {
        await multiFactor(auth.currentUser).unenroll(factors[0]);
      }
      setTwoFactorEnabled(false);
      setShowTwoFactorModal(false);
    } catch (error) {
      setTwoFactorError(error?.message || 'Could not disable 2FA.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const result = authMode === 'signup' ? await signUp(email, password) : await signIn(email, password);
      if (result.success) {
        logEvent(authMode === 'signup' ? 'user_signed_up' : 'user_signed_in', { uid: result.user?.uid });
        if (result.user?.uid) posthog.identify(result.user.uid, { email: result.user.email });
        if (result.user) {
          const profileData = await Promise.race([
            loadUserProfile(result.user.uid),
            new Promise(resolve => setTimeout(() => resolve({}), 3000))
          ]).catch(() => ({}));
          if (profileData?.onboardingCompleted) {
            localStorage.setItem('centris_onboarding_v1', 'done');
          }
          setAuthInitialized(true);
          setUser({
            ...result.user,
            name: result.user.displayName || result.user.email?.split('@')[0] || '',
            emailVerified: Boolean(result.user.emailVerified),
            ...profileData,
          });
          loadExpenses(result.user.uid).then(r => { if (r.success) setExpenses(r.expenses); }).catch(() => {});
          loadInvoices(result.user.uid).then(r => { if (r.success) setInvoices(r.invoices); }).catch(() => {});
          loadRevenue(result.user.uid).then(r => { if (r.success) setRevenue(r.revenue); }).catch(() => {});
          loadBills(result.user.uid).then(r => { if (r.success) setBills(r.bills); }).catch(() => {});
          loadBudgets(result.user.uid).then(r => { if (r.success) setBudgets(r.budgets); }).catch(() => {});
        }
        setShowAuth(false);
      } else {
        setAuthError(result.error || 'Sign in failed. Please check your email and password.');
      }
    } catch (error) {
      setAuthError(error.message || 'An unexpected error occurred.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        logEvent('user_signed_in', { uid: result.user?.uid, method: 'google' });
        if (result.user?.uid) posthog.identify(result.user.uid, { email: result.user.email });
        const profileData = await Promise.race([
          loadUserProfile(result.user.uid),
          new Promise(resolve => setTimeout(() => resolve({}), 3000))
        ]).catch(() => ({}));
        if (profileData?.onboardingCompleted) {
          localStorage.setItem('centris_onboarding_v1', 'done');
        }
        setAuthInitialized(true);
        setUser({
          ...result.user,
          name: result.user.displayName || result.user.email?.split('@')[0] || '',
          emailVerified: Boolean(result.user.emailVerified),
          ...profileData,
        });
        loadExpenses(result.user.uid).then(r => { if (r.success) setExpenses(r.expenses); }).catch(() => {});
        loadInvoices(result.user.uid).then(r => { if (r.success) setInvoices(r.invoices); }).catch(() => {});
        loadRevenue(result.user.uid).then(r => { if (r.success) setRevenue(r.revenue); }).catch(() => {});
        loadBills(result.user.uid).then(r => { if (r.success) setBills(r.bills); }).catch(() => {});
        loadBudgets(result.user.uid).then(r => { if (r.success) setBudgets(r.budgets); }).catch(() => {});
        setShowAuth(false);
      } else if (result.code !== 'auth/popup-closed-by-user') {
        setAuthError(result.error || 'Google sign-in failed.');
      }
    } catch (error) {
      setAuthError(error.message || 'An unexpected error occurred.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleDemoMode = () => {
    // Suppress onboarding for demo session; restore on exit
    const prevOnboarding = localStorage.getItem('centris_onboarding_v1');
    localStorage.setItem('centris_onboarding_v1', 'done');
    window.__centris_demo_prev_onboarding = prevOnboarding;
    setIsDemoMode(true);
    setAuthInitialized(true);
    setUser(DEMO_DATA.user);
    setExpenses(DEMO_DATA.expenses);
    setRevenue(DEMO_DATA.revenue);
    setInvoices(DEMO_DATA.invoices);
    setMileageTrips(DEMO_DATA.mileageTrips);
    setSubscriptions(DEMO_DATA.subscriptions);
    setBills(DEMO_DATA.bills);
    setBudgets(DEMO_DATA.budgets);
  };


  const handleLogout = async () => {
    if (isDemoMode) {
      // Restore onboarding state
      if (window.__centris_demo_prev_onboarding === null) {
        localStorage.removeItem('centris_onboarding_v1');
      }
      window.__centris_demo_prev_onboarding = undefined;
      setIsDemoMode(false);
      setUser(null);
      setExpenses([]); setInvoices([]); setMileageTrips([]); setSubscriptions([]); setRevenue([]);
      setBills([]); setBudgets([]);
      return;
    }
    await logOut(); setUser(null); setExpenses([]); setInvoices([]); setMileageTrips([]); setSubscriptions([]); setRevenue([]);
    setMfaRequired(false);
    setMfaResolver(null);
    setMfaVerificationId('');
    setMfaCode('');
    setNativeMfaData(null);
    setShowTwoFactorModal(false);
    setTwoFactorEnabled(false);
    setReauthRequired(false);
    setReauthPassword('');
    clearRecaptcha(mfaSignInRecaptchaRef);
    clearRecaptcha(mfaEnrollRecaptchaRef);
  };

  const handleSendVerificationEmail = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        showToast('You need to be signed in first.', 'error');
        return;
      }
      if (currentUser.emailVerified) {
        showToast('Your email is already verified.', 'info');
        return;
      }
      await sendEmailVerification(currentUser, {
        url: window.location.origin + '/app',
      });
      showToast('Check your inbox for the verification email!', 'success');
    } catch (error) {
      showToast(error?.message || 'Could not send verification email.', 'error');
    }
  };

  const refreshEmailVerificationStatus = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      await currentUser.reload();
      const refreshedUser = auth.currentUser;
      if (!refreshedUser) return;
      setUser((prev) => prev ? ({ ...prev, emailVerified: Boolean(refreshedUser.emailVerified) }) : prev);
      showToast(refreshedUser.emailVerified ? 'Email verified! You can now enroll in SMS 2FA.' : 'Still unverified. Check your inbox, then try again.', refreshedUser.emailVerified ? 'success' : 'info');
    } catch (error) {
      showToast(error?.message || 'Could not refresh verification status.', 'error');
    }
  };

  const handleDeleteAccount = async () => {
    // No window.confirm — caller (SettingsPage) shows a confirmation dialog before calling this
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      // Use a fresh token to authenticate the server-side deletion request.
      // The Cloud Function deletes all Firestore subcollections AND the Auth user
      // server-side, satisfying Apple Guideline 5.1.1 (account deletion).
      const idToken = await currentUser.getIdToken(true);
      const resp = await fetch(
        'https://us-central1-receiptwise-prod.cloudfunctions.net/deleteAccount',
        { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` } }
      );
      if (!resp.ok) {
        const json = await resp.json().catch(() => ({}));
        throw new Error(json.error || 'Account deletion failed on server.');
      }
      setUser(null);
    } catch (error) {
      showToast(error?.message || 'Could not delete account. You may need to sign out and sign back in first.', 'error');
    }
  };

  const canProceed = useCallback(async (action, limitKey) => {
    if (!user?.uid) return false;

    if (perms.tier === 'payg' && perms.canPayPerUse) {
      const result = await deductPaygCredits(action);
      if (!result.allowed) {
        if (result.reason === 'insufficient_credits') setShowAddCreditsModal(true);
        return false;
      }
      setUser((prev) => ({ ...(prev || {}), paygCredits: result.remainingCredits }));
      return true;
    }

    if (limitKey && perms[limitKey] !== Infinity) {
      const usage = await getUsage(user.uid);
      const current = usage[action] || 0;
      if (current >= perms[limitKey]) {
        setShowPricingModal(true);
        return false;
      }
      await incrementUsage(user.uid, action);
    }

    return true;
  }, [user?.uid, perms, setUser]);

  const handleOpenPlaidModal = useCallback(() => {
    if (!perms || !user?.uid) return;
    if (!PLAID_BANK_CONNECT_ENABLED) {
      setShowBankConnectComingSoon(true);
      return;
    }
    if (bankImportMode === BANK_IMPORT_STATEMENTS) {
      setShowSwitchToLiveBankModal(true);
      return;
    }
    if (perms.plaidAccounts <= 0) {
      setShowPricingModal(true);
      return;
    }
    if (perms.plaidAccounts !== Infinity && bankAccounts.length >= perms.plaidAccounts) {
      setShowPricingModal(true);
      return;
    }
    setShowPlaidModal(true);
  }, [perms, user?.uid, bankAccounts.length, bankImportMode]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0]; if (!file) return;
    if (!(await canProceed('receiptScan', 'receiptScansPerMonth'))) return;
    setScanningReceipt(true); setShowUploadModal(false);
    try {
      const imageUrl = URL.createObjectURL(file); setReceiptImageUrl(imageUrl);
      const result = await scanReceipt(file);
      if (result.success) { setOcrResult(result.data); setShowReceiptReview(true); }
      else showToast('Failed to scan receipt: ' + (result.error || 'Unknown error'), 'error');
    } catch (error) { showToast('Error scanning receipt: ' + error.message, 'error'); }
    finally { setScanningReceipt(false); }
  };

  const handleNativeCamera = async (source) => {
    if (!(await canProceed('receiptScan', 'receiptScansPerMonth'))) return;
    setScanningReceipt(true); setShowUploadModal(false);
    try {
      const { Camera, CameraSource, CameraResultType } = await import('@capacitor/camera');
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
      });
      setReceiptImageUrl(photo.dataUrl);
      const res = await fetch(photo.dataUrl);
      const blob = await res.blob();
      const file = new File([blob], 'receipt.jpg', { type: 'image/jpeg' });
      const result = await scanReceipt(file);
      if (result.success) { setOcrResult(result.data); setShowReceiptReview(true); }
      else showToast('Failed to scan receipt: ' + (result.error || 'Unknown error'), 'error');
    } catch (error) {
      if (!String(error).includes('cancelled') && !String(error).includes('canceled')) {
        showToast('Error capturing photo: ' + (error.message || 'Unknown error'), 'error');
      }
    } finally { setScanningReceipt(false); }
  };

  const handleReceiptConfirm = async (expenseData) => {
    if (isDemoMode) {
      setExpenses(prev => [{ ...expenseData, id: 'demo-e-' + Date.now() }, ...prev]);
      setShowReceiptReview(false); setOcrResult(null); setReceiptImageUrl(null);
      showToast('Expense added (demo)', 'success');
      return;
    }
    const isDuplicate = await checkDuplicateExpense(user.uid, expenseData);
    if (isDuplicate) { showToast('Possible duplicate expense detected — saved anyway.', 'warning'); }
    const result = await saveExpense(user.uid, expenseData);
    if (result.success) {
      logEvent('expense_logged', { amount: expenseData.amount, category: expenseData.category });
      const updated = await loadExpenses(user.uid); if (updated.success) setExpenses(updated.expenses);
    }
    setShowReceiptReview(false); setOcrResult(null);
  };
  const handleReceiptCancel = () => { setShowReceiptReview(false); setOcrResult(null); setReceiptImageUrl(null); };

  const handleDeleteExpense = async (expenseId) => { setExpenseToDelete(expenseId); setShowDeleteConfirm(true); };
  const confirmDelete = async () => {
    haptic(ImpactStyle.Heavy);
    if (expenseToDelete) { const result = await deleteExpense(user.uid, expenseToDelete); if (result.success) { const updated = await loadExpenses(user.uid); if (updated.success) setExpenses(updated.expenses); } }
    setShowDeleteConfirm(false); setExpenseToDelete(null);
  };
  const handleDeleteAll = async () => { const result = await deleteAllExpenses(user.uid); if (result.success) setExpenses([]); setShowDeleteAllConfirm(false); };
  const handleDeleteAllRevenue = async () => {
    if (!user?.uid || revenue.length === 0) {
      setShowDeleteAllRevenueConfirm(false);
      return;
    }
    const results = await Promise.all(revenue.map((entry) => deleteRevenue(user.uid, entry.id)));
    const failedCount = results.filter((result) => !result?.success).length;
    if (failedCount > 0) {
      const refreshed = await loadRevenue(user.uid);
      if (refreshed.success) setRevenue(refreshed.revenue);
      showToast(`Deleted with issues: ${failedCount} revenue entr${failedCount === 1 ? 'y' : 'ies'} could not be removed.`, 'warning');
    } else {
      setRevenue([]);
    }
    setShowDeleteAllRevenueConfirm(false);
  };

  // Bank account functions
  const loadBankAccounts = async () => {
    if (!user?.uid) {
      setBankAccounts([]);
      return;
    }
    if (!liveBankImportActive) {
      setBankAccounts([]);
      setLoadingBankAccounts(false);
      return;
    }

    setLoadingBankAccounts(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('No authenticated user');
        setLoadingBankAccounts(false);
        return;
      }
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `https://us-central1-receiptwise-prod.cloudfunctions.net/plaidGetAccounts?userId=${user.uid}`,
        {
          headers: { 'Authorization': `Bearer ${idToken}` }
        }
      );

      const data = await response.json();
      if (data.success) {
        setBankAccounts(data.accounts || []);

        if (data.accounts && data.accounts.length > 0) {
          setShowBankOnboarding(false);
        }
      }
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  const checkBankOnboarding = () => {
    if (!liveBankImportActive) return;
    const dismissed = localStorage.getItem('bankOnboardingDismissed');
    if (!dismissed && bankAccounts.length === 0 && !bankOnboardingDismissed) {
      setTimeout(() => {
        setShowBankOnboarding(true);
      }, 3000);
    }
  };

  const handleBankConnected = async (data) => {
    console.log('Bank connected successfully:', data);
    await loadBankAccounts();
    setShowPlaidModal(false);
    setShowBankOnboarding(false);
  };

  const handleBankError = (error) => {
    console.error('Bank connection error:', error);
    const nested = error?.plaidError || {};
    const message =
      error?.display_message ||
      nested?.display_message ||
      error?.error_message ||
      nested?.error_message ||
      error?.message ||
      'Failed to connect bank account. Please try again.';
    const code = error?.error_code || nested?.error_code || '';
    const details = [];
    if (code) details.push(`Code: ${code}`);
    if (/phone/i.test(String(message)) || /PHONE|INVALID_PHONE/i.test(String(code))) {
      details.push('Phone tips: use your mobile number in US format (10 digits) or E.164 (+1XXXXXXXXXX).');
      details.push('Some institutions reject VoIP/landline numbers for MFA.');
    }
    showToast(`Bank connection error: ${message}${details.length ? ' — ' + details.join(' ') : ''}`, 'error', 8000);
  };

  const smartCategorizeTransaction = async (transaction, userId) => {
    const merchantName = transaction.name || transaction.merchantName || '';
    const similarPatterns = await findSimilarPatterns(merchantName, userId);

    if (similarPatterns.length > 0 && (similarPatterns[0].confidence || 0) > 70) {
      return {
        category: similarPatterns[0].pattern.category || 'Other',
        confidence: similarPatterns[0].confidence || 0,
        source: 'learned_pattern',
        autoApply: true,
      };
    }

    const prediction = await predictCategory(merchantName, userId);
    return {
      category: prediction.category || 'Other',
      confidence: prediction.confidence || 0,
      source: 'prediction',
      autoApply: (prediction.confidence || 0) > 80,
    };
  };

  // Import bank transactions (Plaid) or route to bank statement upload when Plaid is off
  const importBankTransactions = async (startDate, endDate) => {
    if (!user?.uid) return;
    if (!liveBankImportActive) {
      setShowTopLevelBankImport(true);
      return;
    }
    if (bankAccounts.length === 0) {
      showToast('Please connect a bank account first.', 'info');
      return;
    }

    setImportingTransactions(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const idToken = await currentUser.getIdToken();
      const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const end = endDate || new Date().toISOString().split('T')[0];

      const response = await fetch(
        `https://us-central1-receiptwise-prod.cloudfunctions.net/plaidGetTransactions?userId=${user.uid}&startDate=${start}&endDate=${end}`,
        {
          headers: { 'Authorization': `Bearer ${idToken}` }
        }
      );

      const data = await response.json();
      if (data.success && data.transactions) {
        const candidateTransactions = data.transactions.filter((tx) =>
          !tx.pending &&
          !tx.category?.some((cat) => String(cat).toLowerCase().includes('transfer'))
        );

        const uniqueTransactions = await filterOutDuplicates(candidateTransactions);
        if (uniqueTransactions.length === 0) {
          showToast('No new transactions to import. All transactions have already been added.', 'info');
          return;
        }

        const enrichedTransactions = await Promise.all(
          uniqueTransactions.map(async (tx) => {
            const smart = await smartCategorizeTransaction(tx, user.uid);
            return {
              ...tx,
              category: smart.category,
              categoryConfidence: smart.confidence,
              categorySource: smart.source,
              categoryAutoApplied: smart.autoApply,
            };
          })
        );

        const keywords = await LearnedKeywordsManager.getLearnedKeywords(user.uid);
        setBankLearnedKeywords(keywords || []);
        setBankTransactionsToReview(enrichedTransactions);
        setShowTransactionReview(true);
        setLastBankSync(new Date().toISOString());
      } else {
        showToast('Failed to fetch transactions: ' + (data.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error importing transactions:', error);
      showToast('Failed to import transactions. Please try again.', 'error');
    } finally {
      setImportingTransactions(false);
    }
  };

  const filterOutDuplicates = async (transactions) => {
    const unique = [];
    for (const tx of transactions) {
      const txName = String(tx.name || tx.merchantName || '').toLowerCase();
      const txPrimary = txName.split(' ')[0];
      const hasPrimary = txPrimary.length > 0;
      const txAmount = Math.abs(Number(tx.amount || 0));
      const existsInExpenses = expenses.some((exp) =>
        Math.abs((exp.amount || 0) - txAmount) < 0.01 &&
        exp.date === tx.date &&
        (!hasPrimary || String(exp.merchant || '').toLowerCase().includes(txPrimary))
      );
      const existsInRevenue = revenue.some((rev) =>
        Math.abs((rev.amount || 0) - txAmount) < 0.01 &&
        rev.date === tx.date &&
        (!hasPrimary || String(rev.source || '').toLowerCase().includes(txPrimary))
      );
      const isDuplicate = existsInExpenses || existsInRevenue;
      if (!isDuplicate) unique.push(tx);
    }
    return unique;
  };

  const handleConfirmTransactionImport = async (selectedTransactions) => {
    if (isDemoMode) {
      showToast('Bank import is not available in demo mode', 'info');
      setShowTransactionReview(false);
      return;
    }
    setImportingTransactions(true);
    try {
      let importedCount = 0;
      const detectedSubscriptions = [];

      for (const tx of selectedTransactions) {
        const confidenceText = tx.categoryConfidence ? ` (${tx.categoryConfidence}% confidence)` : '';
        const isExpense = Number(tx.amount || 0) > 0;

        if (isExpense) {
          const expenseData = {
            merchant: tx.name || tx.merchantName,
            amount: Math.abs(tx.amount),
            date: tx.date,
            category: tx.category,
            taxDeductible: tx.taxDeductible,
            notes: tx.notes || `Imported from ${tx.institutionName || 'bank'} via ${tx.categorySource || 'manual'}${confidenceText}`,
            source: 'bank_import',
            bankTransactionId: tx.transactionId,
            categoryConfidence: tx.categoryConfidence || 0,
            categorySource: tx.categorySource || 'manual',
          };

          const result = await saveExpense(user.uid, expenseData);
          if (result.success) {
            importedCount += 1;
            if (tx.isSubscription) {
              detectedSubscriptions.push({
                name: tx.name || tx.merchantName,
                cost: Math.abs(tx.amount),
                billingCycle: 'monthly',
                category: tx.category
              });
            }

            const keywords = LearnedKeywordsManager.extractKeywords(tx.name || tx.merchantName);
            for (const keyword of keywords) {
              await LearnedKeywordsManager.addLearnedKeyword(
                user.uid,
                keyword,
                tx.name || tx.merchantName,
                tx.name || tx.merchantName
              );
            }
          }
        } else {
          const revenueData = {
            source: tx.name || tx.merchantName || 'Bank Payment',
            amount: Math.abs(Number(tx.amount || 0)),
            date: tx.date,
            description: tx.notes || tx.originalDescription || '',
            status: 'received',
            fromBankStatement: true,
            sourceType: 'bank_import',
            bankTransactionId: tx.transactionId,
            createdAt: new Date().toISOString(),
          };
          const result = await saveRevenue(user.uid, revenueData);
          if (result.success) {
            importedCount += 1;

            // Tax vault reminder for manually logged income
            const vaultSettings = await loadTaxVaultSettings(user.uid);
            const reservePct = vaultSettings?.settings?.reservePercent || 25;
            const vaultAmount = parseFloat((revenueData.amount * (reservePct / 100)).toFixed(2));
            if (vaultAmount > 0) {
              setTaxVaultReminder({
                amount: vaultAmount,
                invoiceTotal: revenueData.amount,
                reservePct,
                invoiceNumber: revenueData.description || 'Income',
                client: revenueData.source || 'income entry',
                invoiceId: revenueData.id || revenueData.sourceInvoiceId || Date.now(),
              });
            }
          }
        }
      }

      const updated = await loadExpenses(user.uid);
      if (updated.success) setExpenses(updated.expenses);
      const updatedRevenue = await loadRevenue(user.uid);
      if (updatedRevenue.success) setRevenue(updatedRevenue.revenue);

      setShowTransactionReview(false);
      setBankTransactionsToReview([]);
      showToast(`Imported ${importedCount} transaction${importedCount !== 1 ? 's' : ''} successfully.`, 'success');

      if (detectedSubscriptions.length > 0) {
        // Auto-add detected subscriptions and notify rather than using window.confirm
        for (const sub of detectedSubscriptions) {
          await saveSubscription(user.uid, {
            ...sub,
            id: `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            status: 'active',
            autoDetected: true,
            renewalDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          });
        }
        const subsResult = await loadSubscriptions(user.uid);
        if (subsResult.success) setSubscriptions(subsResult.subscriptions);
        showToast(`${detectedSubscriptions.length} subscription${detectedSubscriptions.length !== 1 ? 's' : ''} auto-detected and added to your tracker.`, 'info', 6000);
      }
    } catch (error) {
      console.error('Error importing transactions:', error);
      showToast('Failed to import some transactions. Please try again.', 'error');
    } finally {
      setImportingTransactions(false);
    }
  };

  const checkIfTaxVaultTransactionExists = async (_bankTransactionId) => false;

  const syncBankIncomeToTaxVault = async () => {
    if (!user?.uid || bankAccounts.length === 0) return;
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const idToken = await currentUser.getIdToken();
      const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const end = new Date().toISOString().split('T')[0];

      const response = await fetch(
        `https://us-central1-receiptwise-prod.cloudfunctions.net/plaidGetTransactions?userId=${user.uid}&startDate=${start}&endDate=${end}`,
        { headers: { 'Authorization': `Bearer ${idToken}` } }
      );
      const data = await response.json();
      if (!data.success || !data.transactions) return;

      const deposits = data.transactions.filter((tx) =>
        tx.amount < 0 &&
        !tx.pending &&
        !tx.category?.some((cat) => String(cat).toLowerCase().includes('transfer')) &&
        Math.abs(tx.amount) > 50
      );

      const settings = await loadTaxVaultSettings(user.uid);
      const reservePercent = settings.success ? settings.settings.reservePercent : 25;

      for (const deposit of deposits) {
        const existingTx = await checkIfTaxVaultTransactionExists(deposit.transactionId);
        if (existingTx) continue;

        const incomeAmount = Math.abs(deposit.amount);
        const reserveAmount = incomeAmount * (reservePercent / 100);
        await addTaxVaultTransaction(user.uid, {
          type: 'deposit',
          amount: reserveAmount,
          incomeAmount,
          reservePercent,
          source: 'bank_auto',
          sourceBankTransactionId: deposit.transactionId,
          merchantName: deposit.merchantName || deposit.name,
          date: deposit.date,
          note: `Auto reserve from ${deposit.merchantName || 'bank deposit'}`
        });
      }
    } catch (error) {
      console.error('Error syncing income to tax vault:', error);
    }
  };

  const generateBankInsights = async () => {
    if (!user?.uid || bankAccounts.length === 0) return [];
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return [];
      const idToken = await currentUser.getIdToken();
      const today = new Date();
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const start = thirtyDaysAgo.toISOString().split('T')[0];
      const end = today.toISOString().split('T')[0];

      const response = await fetch(
        `https://us-central1-receiptwise-prod.cloudfunctions.net/plaidGetTransactions?userId=${user.uid}&startDate=${start}&endDate=${end}`,
        { headers: { 'Authorization': `Bearer ${idToken}` } }
      );
      const data = await response.json();
      const insights = [];
      if (data.success && data.transactions) {
        const transactions = data.transactions;
        const now = new Date();
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - 7);
        const prevWeekStart = new Date(now);
        prevWeekStart.setDate(now.getDate() - 14);

        const thisWeekSpending = transactions
          .filter((tx) => tx.amount > 0 && new Date(tx.date) >= weekStart)
          .reduce((sum, tx) => sum + tx.amount, 0);
        const lastWeekSpending = transactions
          .filter((tx) => tx.amount > 0 && new Date(tx.date) >= prevWeekStart && new Date(tx.date) < weekStart)
          .reduce((sum, tx) => sum + tx.amount, 0);

        if (lastWeekSpending > 0 && thisWeekSpending > lastWeekSpending * 1.5) {
          insights.push({
            type: 'warning',
            message: `Spending is up this week ($${thisWeekSpending.toFixed(2)} vs $${lastWeekSpending.toFixed(2)})`
          });
        }

        const largeTransactions = transactions.filter((tx) => tx.amount > 500);
        if (largeTransactions.length > 0) {
          insights.push({
            type: 'info',
            message: `${largeTransactions.length} large transaction${largeTransactions.length > 1 ? 's' : ''} detected in the last 30 days`
          });
        }

        const deposits = transactions.filter((tx) => tx.amount < 0);
        const totalIncome = deposits.reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
        insights.push({
          type: 'success',
          message: `$${totalIncome.toFixed(2)} in deposits over the last 30 days`
        });
      }
      setBankInsights(insights);
      return insights;
    } catch (error) {
      console.error('Error generating bank insights:', error);
      return [];
    }
  };

  /** Tax vault + pulse-style insights from revenue/expenses when not using live Plaid. */
  const syncStatementIncomeToTaxVault = async () => {
    if (!user?.uid) return;
    try {
      const settings = await loadTaxVaultSettings(user.uid);
      const rp = settings.success ? Number(settings.settings.reservePercent || 25) : 25;
      await syncTaxVaultFromIncome(user.uid, revenue || [], rp);
    } catch (error) {
      console.error('Error syncing statement income to tax vault:', error);
    }
  };

  const generateStatementInsights = async () => {
    if (!user?.uid) {
      setBankInsights([]);
      return;
    }
    const insights = [];
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - 7);
    const prevWeekStart = new Date(now);
    prevWeekStart.setDate(now.getDate() - 14);

    const thisWeekSpend = expenses
      .filter((e) => e.date && !Number.isNaN(new Date(e.date).getTime()) && new Date(e.date) >= weekStart)
      .reduce((s, e) => s + Number(e.amount || 0), 0);
    const lastWeekSpend = expenses
      .filter((e) => e.date && !Number.isNaN(new Date(e.date).getTime()) && new Date(e.date) >= prevWeekStart && new Date(e.date) < weekStart)
      .reduce((s, e) => s + Number(e.amount || 0), 0);

    if (lastWeekSpend > 0 && thisWeekSpend > lastWeekSpend * 1.5) {
      insights.push({
        type: 'warning',
        message: `Spending is up this week ($${thisWeekSpend.toFixed(2)} vs $${lastWeekSpend.toFixed(2)}) — from imported expenses`,
      });
    }

    const largeExp = expenses.filter((e) => Number(e.amount || 0) > 500);
    if (largeExp.length > 0) {
      insights.push({
        type: 'info',
        message: `${largeExp.length} expense${largeExp.length > 1 ? 's' : ''} over $500 in your ledger`,
      });
    }

    const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
    const inc30 = (revenue || [])
      .filter((r) => r.date && r.date >= thirtyAgo)
      .reduce((s, r) => s + Number(r.amount || 0), 0);
    if (inc30 > 0) {
      insights.push({
        type: 'success',
        message: `$${inc30.toFixed(2)} income recorded in the last 30 days`,
      });
    }

    setBankInsights(insights);
  };

  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const totalRevenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.total || 0), 0) + (revenue || []).reduce((s, r) => s + Number(r.amount || 0), 0);
  const taxDeductibleTotal = expenses.filter(e => e.taxDeductible).reduce((sum, e) => sum + e.amount, 0);
  const categoryTotals = expenses.reduce((acc, expense) => {
    const key = expense.category || 'Other';
    acc[key] = (acc[key] || 0) + Number(expense.amount || 0);
    return acc;
  }, {});
  const donutData = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const donutTotal = donutData.reduce((sum, [, v]) => sum + Number(v || 0), 0);
  const donutColors = lightMode ? ['#2563eb', '#06b6d4', '#f59e0b', '#34d399', '#ef4444', '#60a5fa'] : ['#6366f1', '#22d3ee', '#f59e0b', '#34d399', '#f87171', '#a78bfa'];
  const quickForecastProjection = useMemo(() => buildCashFlowProjection({
    expenses,
    invoices,
    revenue,
    subscriptions,
    bankAccounts,
    taxVaultSettings: forecastTaxVaultSettings,
    forecastMonths: 3,
  }), [expenses, invoices, revenue, subscriptions, bankAccounts, forecastTaxVaultSettings]);
  const paidInvoices = invoices.filter((i) => i.status === 'paid');
  const reservePct = Number(forecastTaxVaultSettings?.reservePercent || 25) / 100;

  const dashboardSeries = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    if (dashboardRange === '30d') start.setDate(now.getDate() - 29);
    else if (dashboardRange === '90d') start.setDate(now.getDate() - 89);
    else if (dashboardRange === '12m') start.setMonth(now.getMonth() - 11, 1);
    else start.setMonth(0, 1); // ytd

    const keyOf = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const points = [];
    const map = {};
    const cursor = new Date(start);
    while (cursor <= now) {
      const k = keyOf(cursor);
      map[k] = {
        key: k,
        label: cursor.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: 0,
        expenses: 0,
        taxReserve: 0,
        net: 0,
      };
      points.push(map[k]);
      cursor.setDate(cursor.getDate() + 1);
    }

    expenses.forEach((e) => {
      const d = new Date(e.date);
      const k = keyOf(d);
      if (map[k]) map[k].expenses += Number(e.amount || 0);
    });

    paidInvoices.forEach((inv) => {
      const dateVal = inv.paidDate || inv.date || inv.createdAt;
      if (!dateVal) return;
      const d = new Date(dateVal);
      const k = keyOf(d);
      if (map[k]) map[k].revenue += Number(inv.total || 0);
    });

    points.forEach((p) => {
      p.taxReserve = p.revenue * reservePct;
      p.net = p.revenue - p.expenses - p.taxReserve;
    });

    return points;
  }, [dashboardRange, expenses, paidInvoices, reservePct]);

  const chartMetricConfig = {
    revenue: { label: 'Revenue', key: 'revenue', color: '#34d399' },
    expenses: { label: 'Expenses', key: 'expenses', color: '#f87171' },
    net: { label: 'Net Cash Flow', key: 'net', color: '#60a5fa' },
    taxReserve: { label: 'Tax Set-Aside', key: 'taxReserve', color: '#fbbf24' },
  };

  const activeMetricConfig = chartMetricConfig[dashboardMetric] || chartMetricConfig.net;
  const metricValues = dashboardSeries.map((p) => Number(p[activeMetricConfig.key] || 0));
  const metricMax = metricValues.length ? Math.max(...metricValues) : 0;
  const metricMin = metricValues.length ? Math.min(...metricValues) : 0;
  const metricLatest = metricValues.length ? metricValues[metricValues.length - 1] : 0;
  const metricFirst = metricValues.length ? metricValues[0] : 0;
  const metricDeltaPct = Math.abs(metricFirst) > 0.01 ? ((metricLatest - metricFirst) / Math.abs(metricFirst)) * 100 : 0;

  const deductiblePct = totalExpenses > 0 ? (taxDeductibleTotal / totalExpenses) * 100 : 0;
  const categorizedPct = expenses.length > 0
    ? (expenses.filter((e) => e.category && e.category !== 'Uncategorized').length / expenses.length) * 100
    : 0;
  const taxReadinessScore = Math.round(
    Math.min(100,
      Math.min(40, deductiblePct * 0.5) +
      Math.min(25, categorizedPct * 0.25) +
      Math.min(20, paidInvoices.length * 2) +
      (forecastTaxVaultSettings?.enabled ? 15 : 5)
    )
  );
  const estimatedAnnualTax = paidInvoices.reduce((s, i) => s + Number(i.total || 0), 0) * reservePct;
  const estimatedQuarterly = estimatedAnnualTax / 4;

  const monthStart = new Date();
  monthStart.setDate(1);
  const monthlyRevenue = paidInvoices
    .filter((inv) => {
      const d = new Date(inv.paidDate || inv.date || inv.createdAt || 0);
      return d >= monthStart;
    })
    .reduce((sum, inv) => sum + Number(inv.total || 0), 0);
  const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
  const dayOfMonth = Math.max(1, new Date().getDate());
  const goalProjected = (monthlyRevenue / dayOfMonth) * daysInMonth;

  const smartAlertsComputed = useMemo(() => {
    const list = [];
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const fmt = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const largeTxs = expenses.filter((e) => e.amount >= 500 && new Date(e.date) >= weekAgo);
    largeTxs.forEach((tx) => {
      list.push({
        id: `large-tx-${tx.id || tx.date + tx.amount}`,
        severity: tx.amount >= 2000 ? 'critical' : 'warning',
        category: 'transactions',
        icon: '💳',
        title: `Large transaction: ${fmt(tx.amount)} at ${tx.merchant}`,
        message: `${new Date(tx.date).toLocaleDateString()} · ${tx.category}${tx.taxDeductible ? ' · tax deductible' : ''}`,
      });
    });

    const balance = bankAccounts.reduce((s, a) => s + (a.balances?.current || 0), 0);
    const monthlySpend = expenses.reduce((s, e) => s + e.amount, 0) / Math.max(1, 3);
    if (balance > 0 && balance < monthlySpend * 1.5) {
      list.push({
        id: 'low-balance',
        severity: balance < monthlySpend ? 'critical' : 'warning',
        category: 'balance',
        icon: '💦',
        title: `Low balance: ${fmt(balance)} in accounts`,
        message: `That's ${(balance / Math.max(1, monthlySpend)).toFixed(1)} months of avg spending. Consider transferring funds.`,
      });
    }

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const thisMonthExpenses = expenses.filter((e) => e.date >= monthStart);
    budgets.forEach((b) => {
      const monthlyLimit = Number(b.amount || 0) * (FREQ_TO_MONTHLY[b.frequency] || 1);
      const spent = thisMonthExpenses
        .filter((e) => e.category === b.category || e.category === b.label)
        .reduce((s, e) => s + e.amount, 0);
      const pct = monthlyLimit > 0 ? spent / monthlyLimit : 0;
      if (pct >= 1) {
        list.push({
          id: `budget-over-${b.id}`,
          severity: 'warning',
          category: 'budget',
          icon: '✅',
          title: `Over budget: ${b.label || b.category}`,
          message: `Spent ${fmt(spent)} of ${fmt(monthlyLimit)} limit this month (${Math.round(pct * 100)}% used).`,
        });
      } else if (pct >= 0.8) {
        list.push({
          id: `budget-near-${b.id}`,
          severity: 'info',
          category: 'budget',
          icon: '✅',
          title: `Approaching budget: ${b.label || b.category}`,
          message: `${Math.round(pct * 100)}% used — ${fmt(monthlyLimit - spent)} remaining this month.`,
        });
      }
    });

    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    const lastMonthTotal = expenses
      .filter((e) => e.date >= lastMonthStart && e.date <= lastMonthEnd)
      .reduce((s, e) => s + e.amount, 0);
    const thisMonthTotal = thisMonthExpenses.reduce((s, e) => s + e.amount, 0);
    const dayOfMonthLocal = now.getDate();
    const daysInMonthLocal = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const projectedThisMonth = (thisMonthTotal / dayOfMonthLocal) * daysInMonthLocal;
    if (lastMonthTotal > 0 && projectedThisMonth > lastMonthTotal * 1.4) {
      list.push({
        id: 'spending-spike',
        severity: 'warning',
        category: 'unusual',
        icon: '🔥',
        title: 'Unusual spending this month',
        message: `On pace for ${fmt(projectedThisMonth)} vs ${fmt(lastMonthTotal)} last month (+${Math.round((projectedThisMonth / lastMonthTotal - 1) * 100)}%).`,
      });
    }

    const sorted = [...expenses].sort((a, b) => a.date.localeCompare(b.date));
    const dupeIds = new Set();
    for (let i = 0; i < sorted.length; i += 1) {
      for (let j = i + 1; j < sorted.length; j += 1) {
        const a = sorted[i];
        const b = sorted[j];
        const daysDiff = (new Date(b.date) - new Date(a.date)) / 86400000;
        if (daysDiff > 3) break;
        if (Math.abs(a.amount - b.amount) < 0.01 && a.merchant === b.merchant && !dupeIds.has(a.id)) {
          dupeIds.add(b.id);
          list.push({
            id: `dupe-${a.id}-${b.id}`,
            severity: 'warning',
            category: 'transactions',
            icon: '⚠️',
            title: `Possible duplicate: ${a.merchant} ${fmt(a.amount)}`,
            message: `Same amount charged on ${a.date} and ${b.date}. May be a double charge.`,
          });
        }
      }
    }

    const in7 = new Date();
    in7.setDate(in7.getDate() + 7);
    const in7Str = in7.toISOString().split('T')[0];
    bills
      .filter((b) => b.nextDueDate >= todayStr && b.nextDueDate <= in7Str)
      .forEach((b) => {
        const days = Math.round((new Date(b.nextDueDate) - now) / 86400000);
        list.push({
          id: `bill-due-${b.id}`,
          severity: days <= 2 ? 'critical' : 'warning',
          category: 'bills',
          icon: '💸',
          title: `${b.name} due ${days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`}`,
          message: `${fmt(b.amount)} · ${b.category || 'Bill'}`,
        });
      });

    bills
      .filter((b) => b.nextDueDate < todayStr)
      .forEach((b) => {
        list.push({
          id: `bill-overdue-${b.id}`,
          severity: 'critical',
          category: 'bills',
          icon: '💰',
          title: `Overdue: ${b.name}`,
          message: `${fmt(b.amount)} was due ${b.nextDueDate}. Mark as paid when complete.`,
        });
      });

    return list.filter((a) => !dismissedSmartAlerts.has(a.id));
  }, [expenses, bankAccounts, budgets, bills, dismissedSmartAlerts]);

  useEffect(() => {
    setSmartAlerts(smartAlertsComputed);
  }, [smartAlertsComputed]);

  const dismissSmartAlert = useCallback((id) => {
    setDismissedSmartAlerts((prev) => new Set([...prev, id]));
  }, []);

  // Alert popup trigger for critical/warning alerts
  useEffect(() => {
    if (!user?.uid || smartAlerts.length === 0) return;

    const popupWorthy = smartAlerts.filter((a) => a.severity === 'critical' || a.severity === 'warning');
    if (popupWorthy.length === 0) return;

    const shownKey = 'centris_shown_alert_popups';
    const shown = new Set(JSON.parse(sessionStorage.getItem(shownKey) || '[]'));
    const newAlerts = popupWorthy.filter((a) => !shown.has(a.id));
    if (newAlerts.length === 0) return;

    const updatedShown = [...shown, ...newAlerts.map((a) => a.id)];
    sessionStorage.setItem(shownKey, JSON.stringify(updatedShown));

    setAlertPopupQueue((prev) => {
      const existingIds = new Set(prev.map((a) => a.id));
      const merged = [...prev, ...newAlerts.filter((a) => !existingIds.has(a.id))];
      if (!currentAlertPopup && merged.length > 0) setCurrentAlertPopup(merged[0]);
      return merged;
    });
  }, [smartAlerts, user?.uid, currentAlertPopup]);

  useEffect(() => {
    if (!currentAlertPopup && alertPopupQueue.length > 0) {
      setCurrentAlertPopup(alertPopupQueue[0]);
    }
  }, [alertPopupQueue, currentAlertPopup]);

  const dismissCurrentAlertPopup = (markDismissed = false) => {
    if (!currentAlertPopup) return;
    if (markDismissed) dismissSmartAlert(currentAlertPopup.id);
    const remaining = alertPopupQueue.filter((a) => a.id !== currentAlertPopup.id);
    setAlertPopupQueue(remaining);
    setCurrentAlertPopup(remaining[0] || null);
  };

  const navSections = [
    { label: 'Overview', items: [
      { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'liquidity', label: 'Pulse', icon: Activity },
    ]},
    { label: 'Money', items: [
      { id: 'expenses', label: 'Expenses and Revenue', icon: Receipt },
      { id: 'subscriptions', label: 'Subscriptions', icon: Bell },
      { id: 'reports', label: 'Tax & Reports', icon: PieChart },
    ]},
    { label: 'Tools', items: [
      { id: 'business-tools', label: 'Business Tools', icon: Briefcase },
      { id: 'personal-tools', label: 'Personal Tools', icon: Home },
      { id: 'marketing', label: 'Marketing Tools', icon: TrendingUp },
      { id: 'social-media', label: 'Social Media Tools', icon: Zap },
    ]},
    { label: '', items: [
      { id: 'settings', label: 'Settings', icon: Settings },
    ]},
  ];

  const viewBackgroundAccent = {
    overview: 'rgba(99,102,241,',
    liquidity: 'rgba(6,182,212,',
    expenses: 'rgba(236,72,153,',
    subscriptions: 'rgba(99,102,241,',
    reports: 'rgba(16,185,129,',
    'tools-hub': 'rgba(168,85,247,',
    'business-tools': 'rgba(168,85,247,',
    'personal-tools': 'rgba(245,158,11,',
    'marketing': 'rgba(236,72,153,',
    'social-media': 'rgba(99,102,241,',
    quantra: 'rgba(168,85,247,',
    banking: 'rgba(16,185,129,',
    'website-builder': 'rgba(16,185,129,',

    settings: 'rgba(148,163,184,',
  };
  const activeMeshAccent = viewBackgroundAccent[view] || 'rgba(99,102,241,';

  // ── Public invoice portal — no auth required ──────────────────────────────
  if (publicInvoiceToken) {
    return <React.Suspense fallback={<div className="text-slate-400 p-8 text-center">Loading...</div>}><ClientInvoiceView token={publicInvoiceToken} /></React.Suspense>;
  }

  // ============ SPLASH / AUTH INIT ============
  if (!authInitialized) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ animation: 'cardFadeIn 0.6s ease-out both', background: '#000000', borderRadius: 24, width: 160, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <img src={centrisLogo} alt="CENTRIS" style={{ width: 160, height: 160, objectFit: 'cover', mixBlendMode: 'normal' }} />
        </div>
      </div>
    );
  }

  // ============ SIGN IN / SIGN UP PAGE ============
  if (!user) {
    // If showAuth is false we show the landing; if true we show a full-screen auth page
    if (showAuth) {
      return (
        <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #02012E 0%, #0c0f2c 45%, #1a1060 100%)' }}>
          {/* top bar */}
          <div className="flex items-center justify-between px-5" style={{ paddingTop: 'max(2.5rem, env(safe-area-inset-top, 2.5rem))', paddingBottom: '0.75rem', background: 'rgba(2,1,46,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={() => setShowAuth(false)} className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors">
              <ArrowRight className="w-4 h-4 rotate-180" />
              <span className="text-sm">Back</span>
            </button>
            <button onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')} className="text-xs text-slate-400 hover:text-white transition-colors">
              {authMode === 'login' ? 'Sign up' : 'Sign in'}
            </button>
          </div>

          {/* centered auth form */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
            {/* big logo */}
            <img src={centrisLogo} alt="CENTRIS" style={{ width: 88, height: 88, objectFit: 'contain', marginBottom: 20, mixBlendMode: 'normal' }} />
            <h1 className="text-2xl font-black text-white mb-1 tracking-tight font-brand-display">CENTRIS</h1>
            <p className="text-sm text-slate-500 mb-8">{authMode === 'login' ? 'Sign in to your account' : 'Create your free account'}</p>

            <div className="w-full max-w-sm rounded-2xl p-6 shadow-2xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}>
              {forgotPasswordMode ? (
                forgotPasswordSent ? (
                  <div className="text-center space-y-4">
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                      <Check className="w-6 h-6 text-green-400" />
                    </div>
                    <p className="text-white font-semibold text-sm">Reset email sent</p>
                    <p className="text-slate-400 text-xs">Check your inbox for a password reset link.</p>
                    <button onClick={() => { setForgotPasswordMode(false); setForgotPasswordSent(false); setForgotPasswordEmail(''); }} className="text-indigo-400 text-xs hover:text-indigo-300 transition-colors">Back to sign in</button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-white font-semibold text-sm text-center mb-4">Reset your password</p>
                    <input type="text" inputMode="email" autoComplete="email" placeholder="Your email address" value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl text-white placeholder-slate-600 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', WebkitUserSelect: 'text', userSelect: 'text' }} />
                    <button disabled={forgotPasswordLoading || !forgotPasswordEmail} onClick={async () => { setForgotPasswordLoading(true); await resetPassword(forgotPasswordEmail); setForgotPasswordLoading(false); setForgotPasswordSent(true); }}
                      className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold text-sm hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                      {forgotPasswordLoading ? <Loader className="w-4 h-4 animate-spin" /> : 'Send Reset Email'}
                    </button>
                    <button onClick={() => { setForgotPasswordMode(false); setForgotPasswordEmail(''); }} className="w-full text-slate-500 text-xs hover:text-slate-300 transition-colors pt-1">Back to sign in</button>
                  </div>
                )
              ) : (
                <>
                  <div className="flex gap-2 mb-5 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <button onClick={() => { setAuthMode('login'); setAuthError(''); }} className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-all ${authMode === 'login' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}>Sign In</button>
                    <button onClick={() => { setAuthMode('signup'); setAuthError(''); }} className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-all ${authMode === 'signup' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}>Sign Up</button>
                  </div>
                  {authError && (
                    <div className="mb-3 px-3 py-2 rounded-lg text-xs text-red-300 text-center" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                      {authError}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={authLoading}
                    className="w-full flex items-center justify-center gap-2.5 py-3 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-60 mb-3"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 18 18">
                      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.85 2.09-1.81 2.73v2.27h2.92c1.71-1.57 2.69-3.89 2.69-6.64z" />
                      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.27c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.34A8.997 8.997 0 0 0 9 18z" />
                      <path fill="#FBBC05" d="M3.97 10.7A5.4 5.4 0 0 1 3.68 9c0-.59.1-1.17.29-1.7V4.96H.96A8.997 8.997 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.34z" />
                      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.59-2.59C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58z" />
                    </svg>
                    Continue with Google
                  </button>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
                    <span className="text-[11px] text-slate-600">or</span>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
                  </div>
                  <form onSubmit={handleAuth} className="space-y-3">
                    <input type="text" inputMode="email" autoComplete="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                      onTouchEnd={(e) => e.target.focus()}
                      className="w-full px-4 py-3 rounded-xl text-white placeholder-slate-600 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', WebkitUserSelect: 'text', userSelect: 'text' }} required />
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                        onTouchEnd={(e) => e.target.focus()}
                        className="w-full px-4 py-3 pr-11 rounded-xl text-white placeholder-slate-600 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', WebkitUserSelect: 'text', userSelect: 'text' }} required />
                      <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors p-1">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {authMode === 'login' && (
                      <div className="text-right">
                        <button type="button" onClick={() => { setForgotPasswordMode(true); setForgotPasswordEmail(email); }} className="text-xs text-slate-500 hover:text-indigo-400 transition-colors">Forgot password?</button>
                      </div>
                    )}
                    <button type="submit" disabled={authLoading} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold text-sm hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                      {authLoading ? (<><Loader className="w-4 h-4 animate-spin" /><span>Signing in...</span></>) : (<span>{authMode === 'login' ? 'Sign In' : 'Create Account'}</span>)}
                    </button>
                    {authMode === 'signup' && (
                      <div className="pt-1 space-y-1.5">
                        <p className="text-[11px] text-slate-600 text-center leading-relaxed">
                          By creating an account you agree to our{' '}
                          <a href="https://centrisfinance.com/terms" target="_blank" rel="noreferrer" onClick={(e) => { if (window.Capacitor?.isNativePlatform?.()) { e.preventDefault(); window.open('https://centrisfinance.com/terms', '_system'); } }} className="text-slate-400 underline">Terms of Use</a>
                          {' '}and{' '}
                          <a href="https://centrisfinance.com/privacy" target="_blank" rel="noreferrer" onClick={(e) => { if (window.Capacitor?.isNativePlatform?.()) { e.preventDefault(); window.open('https://centrisfinance.com/privacy', '_system'); } }} className="text-slate-400 underline">Privacy Policy</a>.
                        </p>
                        <p className="text-[10px] text-slate-700 text-center leading-relaxed">
                          All financial insights are suggestions only — not professional financial, tax, or legal advice. CENTRIS is not liable for decisions made based on app data. Your data is stored securely in Google Firebase (US servers) and only shared with Anthropic AI when you explicitly consent to use AI features.
                        </p>
                      </div>
                    )}
                  </form>
                </>
              )}
            </div>

            <div className="flex items-center gap-5 mt-6">
              {[{ icon: Shield, text: 'Bank-level encryption' }, { icon: Lock, text: 'End-to-end encrypted' }, { icon: Check, text: 'Free plan' }].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-1.5 text-slate-700 text-xs">
                  <Icon className="w-3 h-3" />{text}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // ============ LANDING PAGE ============
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #02012E 0%, #0c0f2c 45%, #1a1060 100%)' }}>
        {/* top bar */}
        <div className="flex items-center justify-between px-6 pb-3" style={{ paddingTop: 'max(4rem, env(safe-area-inset-top, 4rem))', background: 'rgba(2,1,46,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2">
            <img src={centrisLogo} alt="CENTRIS" style={{ width: 30, height: 30, objectFit: 'contain', mixBlendMode: 'normal' }} />
            <span className="text-sm font-black text-white tracking-tight font-brand-display">CENTRIS</span>
          </div>
          <button onClick={() => { setShowAuth(true); setAuthMode('login'); }} className="text-sm font-semibold text-white px-4 py-2 rounded-lg transition-all" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}>
            Sign in
          </button>
        </div>

        {/* center hero — logo + headline + buttons */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" style={{ gap: 0 }}>
          {/* big logo */}
          <img src={centrisLogo} alt="CENTRIS" style={{ width: 130, height: 130, objectFit: 'contain', marginBottom: 24, mixBlendMode: 'normal' }} />
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight font-brand-display" style={{ marginBottom: 12 }}>CENTRIS</h1>
          <p className="text-base text-slate-400 max-w-xs leading-relaxed" style={{ marginBottom: 40 }}>
            The all-in-one finance app for solopreneurs and small businesses.
          </p>
          <div className="flex flex-col gap-3 w-full max-w-xs">
            <button
              onClick={() => { setShowAuth(true); setAuthMode('signup'); }}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold text-base"
            >
              Get Started Free
            </button>
            <button
              onClick={() => { setShowAuth(true); setAuthMode('login'); }}
              className="w-full text-slate-300 py-4 rounded-2xl font-semibold text-base transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              Sign In
            </button>
          </div>
        </div>

        {/* footer */}
        <div className="text-center py-6 px-6" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}>
          <p className="text-slate-700 text-xs">Free plan available · Encrypted · Private</p>
        </div>
      </div>
    );
  }

  const activeLoadingMessages = [
    authLoading ? (mfaRequired ? 'Verifying sign-in code...' : 'Signing in...') : null,
    loadingBankAccounts ? 'Syncing bank accounts...' : null,
    importingTransactions ? 'Importing bank transactions...' : null,
    scanningReceipt ? 'Scanning receipt...' : null,
    twoFactorLoading ? 'Updating security settings...' : null,
    backgroundTasks.length > 0 ? (backgroundTasks[backgroundTasks.length - 1]?.label || 'Processing background task...') : null,
  ].filter(Boolean);
  const isSecurityCompliant = true;
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || Boolean(window?.Capacitor?.isNativePlatform?.()) || new URLSearchParams(window.location.search).get('forcemobile') === '1' || window.innerWidth < 768;

  // ============ MAIN APP SHELL ============
  return (
    <div className={`min-h-screen flex relative centris-shell${isMobileDevice ? ' is-mobile' : ''}${lightMode ? ' light-mode' : ''}`} data-view={view} data-theme={lightMode ? 'light' : 'dark'} style={{ background: 'transparent' }}>
      <GlobalStyles />
      <AmbientBg darkMode={!lightMode} />
      {isDemoMode && (
        <div className="fixed top-0 left-0 right-0 z-[201] flex items-center justify-between px-4 py-2.5" style={{ background: 'linear-gradient(90deg, rgba(99,102,241,0.95), rgba(139,92,246,0.95))', backdropFilter: 'blur(8px)' }}>
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-white/80" />
            <span className="text-white text-xs font-semibold">Demo Mode — exploring Rivera Design Studio</span>
          </div>
          <button onClick={handleLogout} className="text-white/80 text-xs font-semibold hover:text-white transition-colors flex items-center gap-1">
            Exit Demo <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 z-[200] bg-amber-500/90 backdrop-blur-sm text-white text-xs font-semibold text-center py-2 flex items-center justify-center gap-2">
          <Wifi className="w-3.5 h-3.5" /> No internet connection — some features may be unavailable
        </div>
      )}
      {activeLoadingMessages.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-4 py-2 rounded-xl border border-white/[0.1] bg-slate-900/80 backdrop-blur-xl flex items-center gap-2 shadow-2xl">
          <Spinner size="w-4 h-4" />
          <span className="text-xs text-slate-200">{activeLoadingMessages[0]}</span>
        </div>
      )}

      {/* ===== DESKTOP SIDEBAR ===== */}
      <aside className={`centris-sidebar hidden md:flex flex-col fixed top-0 left-0 h-full z-50 transition-all duration-500 ${sidebarCollapsed ? 'w-[68px]' : 'w-60'} border-r border-white/[0.06]`}>
        <div className={`flex items-center border-b border-white/[0.06] shrink-0 ${sidebarCollapsed ? 'justify-center px-2' : 'px-5'}`} style={{ minHeight: 56, paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="flex-shrink-0"><img src={centrisLogo} alt="CENTRIS logo" className="w-10 h-10 object-contain" style={{ mixBlendMode: 'normal' }} /></div>
          {!sidebarCollapsed && (
            <div className="ml-3 flex flex-col justify-center">
              <span className="text-[14px] font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 tracking-tight leading-none">CENTRIS</span>
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase leading-none mt-1" style={{ color: 'var(--text-muted)' }}>Finance</span>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {navSections.map((section) => (
            <div key={section.label} className="mb-5">
              {!sidebarCollapsed && <p className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{section.label}</p>}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = view === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (!isSecurityCompliant && item.id !== 'personal-tools') {
                          setView('personal-tools');
                          return;
                        }
                        logEvent('view_feature', { feature: item.id, uid: user?.uid });
                        setView(item.id);
                      }}
                      title={sidebarCollapsed ? item.label : undefined}
                      className={`w-full flex items-center rounded-xl transition-all duration-200 group ${sidebarCollapsed ? 'justify-center p-2.5' : 'px-3 py-2.5'} ${active ? 'centris-nav-active text-white shadow-sm' : 'text-slate-400 hover:text-white hover:bg-white/[0.05]'}`}>
                      <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-colors ${active ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-200'}`} />
                      {!sidebarCollapsed && <span className="ml-3 text-[13px] font-medium truncate">{item.label}</span>}
                      {!sidebarCollapsed && active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_6px_rgba(99,102,241,0.8)]"></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className={`border-t p-3 space-y-2 shrink-0 border-white/[0.06]`}>
          <div className={`flex gap-1 ${sidebarCollapsed ? 'flex-col items-center' : ''}`}>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`flex-1 flex items-center justify-center p-2 rounded-lg transition-all text-slate-500 hover:text-white hover:bg-white/[0.06]`}>
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
            <button
              onClick={toggleTheme}
              title={lightMode ? 'Switch to dark mode' : 'Switch to bright mode'}
              className="flex items-center justify-center p-2 rounded-lg transition-all text-slate-500 hover:text-white hover:bg-white/[0.06]">
              {lightMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>
          </div>
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3 px-1'}`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{(user.name || user.email || '?')[0].toUpperCase()}</div>
            {!sidebarCollapsed && <div className="flex-1 min-w-0"><p className={`text-xs font-medium truncate text-white`}>{user.name}</p><p className="text-[11px] text-slate-500 truncate">{user.email}</p></div>}
            {!sidebarCollapsed && <button onClick={handleLogout} className="text-slate-500 hover:text-red-400 transition-colors p-1 flex-shrink-0" title="Sign out"><LogOut className="w-4 h-4" /></button>}
          </div>
        </div>
      </aside>

      {/* ===== MOBILE TOP BAR ===== */}
      {isMobileDevice && <header className="centris-top-bar flex fixed top-0 left-0 right-0 z-[90]"
        style={{ background: 'var(--topbar-bg)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderBottom: '1px solid var(--topbar-border)', paddingTop: 'env(safe-area-inset-top, 0px)', alignItems: 'center', justifyContent: 'space-between', paddingLeft: 16, paddingRight: 8, minHeight: 44 }}>
        <img src={centrisLogo} alt="CENTRIS" style={{ width: 28, height: 28, objectFit: 'contain', mixBlendMode: 'normal' }} />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button onClick={() => { haptic(); toggleTheme(); }} title={lightMode ? 'Dark mode' : 'Bright mode'} className="mobile-tap" style={{ padding: '8px 10px', background: 'none', border: 'none', color: 'var(--topbar-icon)' }}>
            {lightMode ? <Moon style={{ width: 18, height: 18 }} /> : <Sun style={{ width: 18, height: 18 }} />}
          </button>
          <button onClick={() => { haptic(); setView('settings'); }} className="mobile-tap" style={{ padding: '8px 10px', background: 'none', border: 'none', color: 'var(--topbar-icon)' }}>
            <Settings style={{ width: 18, height: 18 }} />
          </button>
          <button onClick={handleLogout} className="mobile-tap" style={{ padding: '8px 10px', background: 'none', border: 'none', color: 'var(--topbar-icon)' }}>
            <LogOut style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </header>}

      {/* ===== MOBILE SUB-NAV PILLS (mobile only) ===== */}
      {isMobileDevice && (() => {
        const subNavs = {
          overview:      [{ key: 'overview', label: 'Dashboard' }, { key: 'liquidity', label: 'Pulse' }],
          liquidity:     [{ key: 'overview', label: 'Dashboard' }, { key: 'liquidity', label: 'Pulse' }],
          expenses:      [{ key: 'expenses', label: 'Expenses' }, { key: 'banking', label: 'Banking' }, { key: 'reports', label: 'Reports' }, { key: 'subscriptions', label: 'Subscriptions' }],
          banking:       [{ key: 'expenses', label: 'Expenses' }, { key: 'banking', label: 'Banking' }, { key: 'reports', label: 'Reports' }, { key: 'subscriptions', label: 'Subscriptions' }],
          reports:       [{ key: 'expenses', label: 'Expenses' }, { key: 'banking', label: 'Banking' }, { key: 'reports', label: 'Reports' }, { key: 'subscriptions', label: 'Subscriptions' }],
          subscriptions: [{ key: 'expenses', label: 'Expenses' }, { key: 'banking', label: 'Banking' }, { key: 'reports', label: 'Reports' }, { key: 'subscriptions', label: 'Subscriptions' }],
          'tools-hub':      [{ key: 'tools-hub', label: 'All Tools' }, { key: 'business-tools', label: 'Business Tools' }, { key: 'personal-tools', label: 'Personal Tools' }, { key: 'marketing', label: 'Marketing Tools' }, { key: 'social-media', label: 'Social Media Tools' }],
          'business-tools': [{ key: 'tools-hub', label: 'All Tools' }, { key: 'business-tools', label: 'Business Tools' }, { key: 'personal-tools', label: 'Personal Tools' }, { key: 'marketing', label: 'Marketing Tools' }, { key: 'social-media', label: 'Social Media Tools' }],
          'personal-tools': [{ key: 'tools-hub', label: 'All Tools' }, { key: 'business-tools', label: 'Business Tools' }, { key: 'personal-tools', label: 'Personal Tools' }, { key: 'marketing', label: 'Marketing Tools' }, { key: 'social-media', label: 'Social Media Tools' }],
          marketing:        [{ key: 'tools-hub', label: 'All Tools' }, { key: 'business-tools', label: 'Business Tools' }, { key: 'personal-tools', label: 'Personal Tools' }, { key: 'marketing', label: 'Marketing Tools' }, { key: 'social-media', label: 'Social Media Tools' }],
          'social-media':   [{ key: 'tools-hub', label: 'All Tools' }, { key: 'business-tools', label: 'Business Tools' }, { key: 'personal-tools', label: 'Personal Tools' }, { key: 'marketing', label: 'Marketing Tools' }, { key: 'social-media', label: 'Social Media Tools' }],
        };
        const pills = subNavs[view];
        if (!pills) return null;
        return (
          <div className="centris-subnav flex fixed left-0 right-0 z-[89]"
            style={{ top: 'calc(44px + env(safe-area-inset-top, 0px))', background: 'var(--subnav-bg)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderBottom: '1px solid var(--subnav-border)', gap: 6, padding: '7px 12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {pills.map(({ key, label }) => (
              <button key={key} onClick={() => { haptic(); setView(key); }} className="mobile-tap"
                style={{ padding: '5px 14px', borderRadius: 20, border: 'none', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: view === key ? (lightMode ? '#2563eb' : '#7c3aed') : (lightMode ? 'rgba(37,99,235,0.08)' : 'rgba(124,58,237,0.08)'),
                  color: view === key ? '#fff' : 'var(--text-muted)' }}>
                {label}
              </button>
            ))}
          </div>
        );
      })()}

      {/* ===== MAIN CONTENT ===== */}
      <main
        ref={pullMainRef}
        onTouchStart={handlePullStart}
        onTouchMove={handlePullMove}
        onTouchEnd={handlePullEnd}
        className={`flex-1 transition-all duration-300 centris-content ${isMobileDevice ? 'pb-[calc(4rem+env(safe-area-inset-bottom))]' : 'pb-8'}`}
        style={{
          paddingTop: isMobileDevice ? `calc(${['overview','liquidity','expenses','banking','reports','subscriptions','tools-hub','business-tools','personal-tools','marketing','social-media'].includes(view) ? '90px' : '44px'} + env(safe-area-inset-top, 0px))` : 0,
          marginLeft: isMobileDevice ? 0 : (sidebarCollapsed ? 68 : 240),
        }}
      >
        {(pullRefreshing || pullProgress > 0) && (
          <div className="md:hidden flex items-center justify-center py-3" style={{ opacity: pullRefreshing ? 1 : pullProgress }}>
            <RefreshCw className={`w-5 h-5 text-violet-400 ${pullRefreshing ? 'animate-spin' : ''}`} style={{ transform: `rotate(${pullProgress * 360}deg)` }} />
          </div>
        )}
        {!isSecurityCompliant && (
          <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur-sm border-b border-amber-500/20 px-4 py-2 text-center">
            <p className="text-xs text-amber-300/80">Security setup required: verify email and enable SMS 2FA to access financial systems.</p>
          </div>
        )}
        <div className="max-w-[112rem] mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-8">
          <div key={displayedView} className={pageAnimClass}>

          {/* ---- OVERVIEW ---- */}
          {displayedView === 'overview' && (
            <div className="relative centris-page" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
              <div className="relative flex flex-col gap-5" style={{ zIndex: 1 }}>

                {/* ── Page header — desktop only ── */}
                {!isMobileDevice && (
                  <div className="pb-1">
                    <h1 className="text-2xl font-semibold text-white">Dashboard</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Your business at a glance</p>
                  </div>
                )}


                {/* ── Quantra proactive insight (mobile only) ── */}
                {isMobileDevice && (quantraInsights.length > 0 || bankInsights.length > 0) && (() => {
                  const insight = quantraInsights[0] || bankInsights[0];
                  return (
                    <div className="mobile-tap-card" onClick={() => setView('quantra')} role="button"
                      style={{ cursor: 'pointer', borderRadius: 16, overflow: 'hidden', border: lightMode ? '1px solid rgba(37,99,235,0.2)' : '1px solid rgba(124,58,237,0.22)',
                        background: lightMode ? 'rgba(37,99,235,0.05)' : 'rgba(124,58,237,0.08)',
                        boxShadow: lightMode ? '0 2px 16px rgba(37,99,235,0.08)' : '0 2px 16px rgba(124,58,237,0.1)' }}>
                      <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '12px 14px 13px' }}>
                        {/* Orb */}
                        <div style={{ width: 34, height: 34, borderRadius: 10, background: lightMode ? 'linear-gradient(135deg,#2563eb,#60a5fa)' : 'linear-gradient(135deg,#7c3aed,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: lightMode ? '0 4px 12px rgba(37,99,235,0.25)' : '0 4px 12px rgba(124,58,237,0.35)' }}>
                          <Brain style={{ width: 16, height: 16, color: '#fff' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: lightMode ? '#2563eb' : '#a78bfa' }}>Quantra</p>
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#34d399', boxShadow: '0 0 6px #34d399', flexShrink: 0 }} />
                          </div>
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, fontWeight: 500 }}>{insight.message || insight.title}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', height: 34, flexShrink: 0 }}>
                          <ChevronRight style={{ width: 16, height: 16, color: lightMode ? 'rgba(37,99,235,0.5)' : 'rgba(124,58,237,0.5)' }} />
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ── No data banner ── */}
                {expenses.length === 0 && revenue.length === 0 && invoices.length === 0 && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-indigo-500/5 border border-indigo-500/10">
                    <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
                    <p className="text-xs text-slate-400 flex-1">No data yet — scan a receipt or import a bank statement to get started.</p>
                    <button onClick={() => setView('expenses')} className="text-xs text-indigo-400 font-semibold shrink-0">Start &rarr;</button>
                  </div>
                )}

                {/* ── Top stat strip ── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-tour="dashboard-stats">
                  {(() => { const cs = getCurrencySymbol(user?.currency); return [
                    { label: 'Revenue', value: cs + totalRevenue.toLocaleString('en-US', {minimumFractionDigits:0,maximumFractionDigits:0}), sub: 'this year', color: '#34d399' },
                    { label: 'Expenses', value: cs + totalExpenses.toLocaleString('en-US', {minimumFractionDigits:0,maximumFractionDigits:0}), sub: 'this year', color: '#f87171' },
                    { label: 'Net Profit', value: cs + (totalRevenue - totalExpenses).toLocaleString('en-US', {minimumFractionDigits:0,maximumFractionDigits:0}), sub: 'this year', color: totalRevenue >= totalExpenses ? '#34d399' : '#f87171' },
                    { label: 'Open Invoices', value: invoices.filter(i=>i.status!=='paid').length, sub: cs + invoices.filter(i=>i.status!=='paid').reduce((s,i)=>s+(i.total||0),0).toLocaleString('en-US',{minimumFractionDigits:0}) + ' outstanding', color: lightMode ? '#2563eb' : '#818cf8' },
                  ]; })().map(({ label, value, sub, color }) => (
                    <div key={label} className="mobile-tap-card centris-card rounded-2xl p-5 lg:p-7">
                      <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 lg:mb-2">{label}</p>
                      <p className="text-xl md:text-2xl lg:text-3xl font-bold" style={{ color }}>{value}</p>
                      <p className="text-xs text-slate-500 mt-1">{sub}</p>
                    </div>
                  ))}
                </div>

                {/* ── Alerts & Insights ── */}
                {(quantraInsights.length > 0 || bankInsights.length > 0) && (
                  <div className="centris-card rounded-xl p-4 lg:p-6 space-y-3">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Alerts & Insights</p>
                    {quantraInsights.map((insight, idx) => (
                      <div key={`qi-${idx}`} className={`flex items-start gap-3 rounded-lg px-3 py-2.5 lg:px-4 lg:py-3 border ${insight.type === 'urgent' ? 'bg-red-500/10 border-red-500/20' : insight.type === 'warning' ? 'bg-amber-500/10 border-amber-500/20' : 'bg-blue-500/10 border-blue-500/20'}`}>
                        <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${insight.type === 'urgent' ? 'text-red-400' : insight.type === 'warning' ? 'text-amber-400' : 'text-blue-400'}`} />
                        <div><p className="text-sm lg:text-base font-semibold text-white">{insight.title}</p><p className="text-xs lg:text-sm text-slate-300 mt-0.5">{insight.message}</p></div>
                      </div>
                    ))}
                    {bankInsights.map((insight, idx) => (
                      <div key={`bi-${idx}`} className="flex items-start gap-2 centris-card text-sm text-slate-200 rounded-lg px-3 py-2">
                        <span className="text-indigo-400 mt-0.5">•</span>
                        <span>{insight.message}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Setup checklist (compact) ── */}
                <SetupChecklist
                  expenses={expenses}
                  invoices={invoices}
                  extensionPaired={extensionPaired}
                  onNavigate={(nextView) => { if (nextView) setView(nextView); }}
                />

                {/* ── Performance section ── */}
                <div>
                  <p className="text-xs lg:text-sm font-semibold tracking-[0.18em] uppercase text-slate-400 mb-3">Performance</p>
                  <RevenueChartsPanel expenses={expenses} invoices={invoices} revenue={revenue} lightMode={lightMode} />
                </div>

                {/* ── Spending breakdown + goal ── */}
                <div>
                  <p className="text-xs lg:text-sm font-semibold tracking-[0.18em] uppercase text-slate-400 mb-3">Breakdown</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="centris-card rounded-2xl p-5 lg:p-7">
                      <h2 className="text-sm lg:text-base font-semibold text-white mb-3 flex items-center gap-2"><PieChart className="w-4 h-4 lg:w-5 lg:h-5 text-indigo-400" /><span>Spending by Category</span></h2>
                      {donutTotal > 0 ? (
                        <div className="flex items-center gap-4">
                          <svg width="100" height="100" viewBox="0 0 190 190" className="shrink-0">
                            <circle cx="95" cy="95" r="62" fill="none" stroke="#1e293b" strokeWidth="24" />
                            {(() => {
                              const circumference = 2 * Math.PI * 62;
                              let running = 0;
                              return donutData.map(([category, amount], idx) => {
                                const pct = amount / donutTotal;
                                const dash = pct * circumference;
                                const offset = circumference * (1 - running);
                                running += pct;
                                return (
                                  <circle key={category} cx="95" cy="95" r="62" fill="none"
                                    stroke={donutColors[idx % donutColors.length]} strokeWidth="24"
                                    strokeDasharray={`${dash} ${circumference - dash}`}
                                    strokeDashoffset={offset} transform="rotate(-90 95 95)" strokeLinecap="butt" />
                                );
                              });
                            })()}
                            <text x="95" y="92" textAnchor="middle" fill={lightMode ? '#0f172a' : '#ffffff'} fontSize="22" fontWeight="700">${Math.round(donutTotal / 1000)}k</text>
                            <text x="95" y="112" textAnchor="middle" fill={lightMode ? '#64748b' : '#94a3b8'} fontSize="11">total</text>
                          </svg>
                          <div className="flex-1 space-y-1.5 min-w-0">
                            {donutData.slice(0, 5).map(([category, amount], idx) => (
                              <div key={category} className="flex items-center justify-between text-xs gap-2">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: donutColors[idx % donutColors.length] }} />
                                  <span className="text-slate-400 truncate">{category}</span>
                                </div>
                                <span className="text-white font-medium shrink-0">${amount.toFixed(0)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 py-4 text-center">No expense data yet</p>
                      )}
                    </div>

                    <div className="centris-card rounded-2xl p-5 lg:p-7">
                      <h2 className="text-sm lg:text-base font-semibold text-white mb-3 flex items-center gap-2"><Target className="w-4 h-4 lg:w-5 lg:h-5 text-indigo-400" /><span>Monthly Goal</span></h2>
                      {!goalAmount ? (
                        <div className="space-y-2.5">
                          <p className="text-xs text-slate-400">Set a monthly revenue target.</p>
                          <input type="number" value={goalInput} onChange={(e) => setGoalInput(e.target.value)}
                            placeholder="e.g. 10000"
                            className="w-full bg-slate-900/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
                          <button onClick={() => { const v = parseFloat(goalInput); if (v > 0) { setGoalAmount(v); try { localStorage.setItem('centris_monthly_goal', String(v)); } catch {} } }}
                            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold py-2 rounded-lg">
                            Set Goal
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-400">Goal</span>
                            <span className="text-white font-medium">${goalAmount.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs mb-2">
                            <span className="text-slate-400">Projected</span>
                            <span className={`font-medium ${goalProjected >= goalAmount ? 'text-emerald-400' : 'text-amber-400'}`}>${goalProjected.toFixed(0)}</span>
                          </div>
                          <div className="w-full bg-slate-700/40 rounded-full h-2 mb-2">
                            <div className={`h-2 rounded-full ${goalProjected >= goalAmount ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                              style={{ width: `${Math.min(100, (monthlyRevenue / goalAmount) * 100)}%` }} />
                          </div>
                          <p className={`text-xs ${goalProjected >= goalAmount ? 'text-emerald-400' : 'text-slate-400'}`}>
                            {goalProjected >= goalAmount ? '+' : ''}{(goalProjected - goalAmount).toFixed(0)} vs goal
                          </p>
                          <button onClick={() => { setGoalAmount(null); setGoalInput(''); }} className="text-[10px] text-slate-600 hover:text-slate-400 mt-2">Reset</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Spending Heatmap ── */}
                <div>
                  <p className="text-xs lg:text-sm font-semibold tracking-[0.18em] uppercase text-slate-400 mb-3">Activity</p>
                  {(() => {
                    const today = new Date();
                    const days = Array.from({ length: 91 }, (_, i) => {
                      const d = new Date(today);
                      d.setDate(today.getDate() - (90 - i));
                      return d.toISOString().split('T')[0];
                    });
                    const spendByDay = expenses.reduce((acc, e) => {
                      const day = (e.date || '').split('T')[0];
                      if (day) acc[day] = (acc[day] || 0) + (e.amount || 0);
                      return acc;
                    }, {});
                    const maxSpend = Math.max(...days.map((d) => spendByDay[d] || 0), 1);
                    const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

                    const firstDate = new Date(`${days[0]}T00:00:00`);
                    const leadingPadding = firstDate.getDay();
                    const gridDays = [...Array.from({ length: leadingPadding }, () => null), ...days];
                    while (gridDays.length % 7 !== 0) gridDays.push(null);

                    const weeks = [];
                    for (let i = 0; i < gridDays.length; i += 7) weeks.push(gridDays.slice(i, i + 7));
                    const weekCount = weeks.length;
                    const activeDay = heatmapHoverDay || heatmapSelectedDay || days[days.length - 1];
                    const activeAmount = activeDay ? (spendByDay[activeDay] || 0) : 0;
                    const activeDate = activeDay ? new Date(`${activeDay}T00:00:00`) : null;

                    return (
                      <div className="centris-card rounded-2xl p-4 lg:p-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h2 className="text-sm lg:text-base font-semibold text-white">Spending Heatmap</h2>
                            <p className="text-xs text-slate-500">Last 90 days</p>
                          </div>
                          <div className="flex items-center gap-1 text-[9px] text-slate-500">
                            <span>Low</span>
                            {(lightMode
                              ? ['rgba(15,23,42,0.06)', '#bfdbfe', '#93c5fd', '#3b82f6', '#1d4ed8']
                              : ['rgba(51,65,85,0.6)', 'rgba(30,27,75,0.8)', 'rgba(67,56,202,0.8)', 'rgb(99,102,241)', 'rgb(129,140,248)']).map((c, i) => (
                              <div key={i} className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c }} />
                            ))}
                            <span>High</span>
                          </div>
                        </div>
                        <div className="w-full overflow-x-auto">
                          <div className="min-w-[20rem]">
                            <div className="grid gap-1" style={{ gridTemplateColumns: `2rem repeat(${weekCount}, minmax(0, 1fr))` }}>
                              <div />
                              {weeks.map((week, wi) => {
                                const firstDay = week.find(Boolean);
                                const firstDayDate = firstDay ? new Date(`${firstDay}T00:00:00`) : null;
                                const showWeekLabel = Boolean(firstDayDate) && (wi % 4 === 0);
                                const weekLabel = firstDayDate
                                  ? firstDayDate.toLocaleString('en-US', { month: 'short' })
                                  : '';
                                return (
                                  <span key={`label-${wi}`} className="text-[9px] text-slate-600 text-center h-3 leading-3">
                                    {showWeekLabel ? weekLabel : ''}
                                  </span>
                                );
                              })}

                              {weekdayLabels.map((label, dayIndex) => (
                                <React.Fragment key={label}>
                                  <span className="text-[9px] text-slate-600 h-4 leading-4">{label.slice(0,1)}</span>
                                  {weeks.map((week, wi) => {
                                    const day = week[dayIndex];
                                    if (!day) {
                                      return <div key={`pad-${label}-${wi}`} className="h-4 rounded-sm bg-transparent" />;
                                    }
                                    const amt = spendByDay[day] || 0;
                                    const intensity = amt / maxSpend;
                                    const cellColor = lightMode
                                      ? (amt === 0 ? 'rgba(15,23,42,0.06)' :
                                        intensity < 0.25 ? '#bfdbfe' :
                                        intensity < 0.5  ? '#93c5fd' :
                                        intensity < 0.75 ? '#3b82f6' : '#1d4ed8')
                                      : (amt === 0 ? 'rgba(51,65,85,0.4)' :
                                        intensity < 0.25 ? 'rgba(30,27,75,0.8)' :
                                        intensity < 0.5  ? 'rgba(67,56,202,0.8)' :
                                        intensity < 0.75 ? 'rgb(99,102,241)' : 'rgb(129,140,248)');
                                    const isSelected = heatmapSelectedDay === day;
                                    const dt = new Date(`${day}T00:00:00`);
                                    return (
                                      <button
                                        key={day}
                                        onClick={() => setHeatmapSelectedDay((prev) => (prev === day ? null : day))}
                                        onMouseEnter={() => setHeatmapHoverDay(day)}
                                        onMouseLeave={() => setHeatmapHoverDay(null)}
                                        className={`h-4 rounded-sm transition-all ${isSelected ? 'ring-1 ring-indigo-300/90' : 'ring-0'}`}
                                        style={{ backgroundColor: cellColor }}
                                        title={`${dt.toLocaleDateString()}: $${amt.toFixed(2)}`}
                                        aria-label={`${dt.toLocaleDateString()} spending ${amt.toFixed(2)} dollars`}
                                      />
                                    );
                                  })}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        </div>
                        {activeAmount > 0 && (
                          <p className="text-xs text-slate-500 mt-2">
                            {activeDate ? activeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}: <span className="text-white font-medium">${activeAmount.toFixed(2)}</span>
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* ── Quick-nav ── */}
                <div>
                  <p className="text-xs lg:text-sm font-semibold tracking-[0.18em] uppercase text-slate-400 mb-3">Explore</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
                    {[
                      { view: 'liquidity', Icon: Activity, color: 'text-indigo-400', border: 'hover:border-indigo-500/30', label: 'Pulse', desc: 'Cash flow · Runway · Alerts' },
                      { view: 'quantra',  Icon: Brain,    color: 'text-violet-400', border: 'hover:border-violet-500/30', label: 'Quantra', desc: 'Your AI financial advisor' },
                      { view: 'reports',  Icon: PieChart, color: 'text-emerald-400', border: 'hover:border-emerald-500/30', label: 'Tax & Reports', desc: 'Export reports for your accountant' },
                    ].map(({ view: v, Icon, color, border, label, desc }) => (
                      <button key={v} onClick={() => { if (v === 'quantra' && !isMobileDevice) { setQuantraPopupOpen(true); } else { setView(v); } }} className={`centris-card rounded-2xl p-5 lg:p-7 text-left ${border} transition-all group`}>
                        <div className="flex items-center justify-between mb-3 lg:mb-4">
                          <Icon className={`w-5 h-5 lg:w-6 lg:h-6 ${color}`} />
                          <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-slate-300 transition-colors" />
                        </div>
                        <p className="text-sm lg:text-base font-semibold text-white">{label}</p>
                        <p className="text-xs lg:text-sm text-slate-400 mt-1">{desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          )}

          {displayedView === 'liquidity' && (
            <div className="relative" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
              <div className="relative" style={{ zIndex: 1 }}>
                <UnifiedDashboard user={user} expenses={expenses} invoices={invoices} mileageTrips={mileageTrips} subscriptions={subscriptions} bankAccounts={bankAccounts} revenue={revenue} taxVaultSettings={forecastTaxVaultSettings} perms={perms} onShowPricing={() => setShowPricingModal(true)} liveBankImportActive={liveBankImportActive} />
              </div>
            </div>
          )}
          {displayedView === 'quantra' && isMobileDevice && (
            <div className="relative" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
              <div className="relative" style={{ zIndex: 1 }}>
                <div className="space-y-4">
                  <QuantraAgent
                    user={user}
                    expenses={expenses}
                    invoices={invoices}
                    mileageTrips={mileageTrips}
                    subscriptions={subscriptions}
                    bankAccounts={bankAccounts}
                    revenue={revenue}
                    bills={bills}
                    budgets={budgets}
                    taxVaultSettings={forecastTaxVaultSettings}
                    monthlyGoal={goalAmount}
                    perms={perms}
                    onCanProceed={canProceed}
                    showToast={showToast}
                    paygPrices={PAYG_PRICES}
                    onInsights={setQuantraInsights}
                  />
                </div>
              </div>
            </div>
          )}
          {displayedView === 'settings' && (
            <div className="relative" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
              <div className="relative" style={{ zIndex: 1 }}>
                <SettingsPage
                  user={user}
                  currentPlan={currentPlan}
                  perms={perms}
                  profileForm={profileForm}
                  setProfileForm={setProfileForm}
                  profileSaving={profileSaving}
                  setProfileSaving={setProfileSaving}
                  profileSaved={profileSaved}
                  setProfileSaved={setProfileSaved}
                  setShowPricingModal={setShowPricingModal}
                  setShowAddCreditsModal={setShowAddCreditsModal}
                  twoFactorEnabled={twoFactorEnabled}
                  setShowTwoFactorModal={setShowTwoFactorModal}
                  handleLogout={handleLogout}
                  settingsTab={settingsTab}
                  setSettingsTab={setSettingsTab}
                  bankImportMode={bankImportMode}
                  setBankImportMode={setBankImportMode}
                  onResendVerification={handleSendVerificationEmail}
                  onRefreshVerification={refreshEmailVerificationStatus}
                  onDeleteAccount={handleDeleteAccount}
                  showToast={showToast}
                />
              </div>
            </div>
          )}
{displayedView === 'tools-hub' && (() => {
  const ALL_TOOLS = [
    { section: 'Finance', tools: [
      { key: 'tax-vault',       Icon: Lock,       color: 'emerald', label: 'Tax Vault',              desc: 'Set aside taxes from every income payment' },
      { key: 'tax-writeoffs',   Icon: Receipt,    color: 'indigo',  label: 'Receipt Scanner',       desc: 'Scan receipts and find tax deductions' },
      { key: 'invoices',        Icon: FileText,   color: 'purple',  label: 'Invoice Generator',     desc: 'Create and send professional invoices' },
      { key: 'mileage',         Icon: Car,        color: 'emerald', label: 'Mileage Tracker',       desc: 'Log trips, calculate IRS deductions' },
      { key: 'banking',         Icon: CreditCard, color: 'indigo',  label: 'Bank Accounts',         desc: 'Connect and monitor your accounts' },
      { key: 'expenses',        Icon: DollarSign, color: 'pink',    label: 'Expenses & Revenue',    desc: 'Track income and spending' },
      { key: 'subscriptions',   Icon: Bell,       color: 'indigo',  label: 'Subscriptions',         desc: 'Track and manage recurring costs' },
      { key: 'reports',         Icon: PieChart,   color: 'emerald', label: 'Tax & Reports',         desc: 'Tax summaries and P&L reports' },
    ]},
    { section: 'Personal', tools: [
      { key: 'budget',          Icon: PieChart,   color: 'violet',  label: 'Budgets',               desc: 'Monthly and paycheck-based budgets' },
      { key: 'bills',           Icon: Calendar,   color: 'orange',  label: 'Bill Reminders',        desc: 'Upcoming bills with due dates' },
      { key: 'scheduled',       Icon: Activity,   color: 'indigo',  label: 'Scheduled Transactions',desc: 'Recurring income and expenses' },
      { key: 'alerts',          Icon: Bell,       color: 'orange',  label: 'Smart Alerts',          desc: 'Unusual activity and budget alerts' },
    ]},
    { section: 'Business', tools: [
      { key: 'quantra',         Icon: Sparkles,   color: 'violet',  label: 'Quantra AI CFO',        desc: 'AI-powered financial advisor' },
      { key: 'pulse',           Icon: TrendingUp, color: 'emerald', label: 'Pulse Analytics',       desc: 'Business health and cash flow insights' },
      { key: 'idea-engine',     Icon: Lightbulb,  color: 'amber',   label: 'Business Ideas',        desc: 'AI-powered trending opportunities' },
      { key: 'product-builder', Icon: Zap,        color: 'violet',  label: 'Product Builder',       desc: 'Build websites and digital products with AI' },
      { key: 'side-hustles',    Icon: DollarSign, color: 'emerald', label: 'Side Hustles',          desc: 'Find income opportunities for your skills' },
      { key: 'website-builder', Icon: Globe,      color: 'emerald', label: 'Website Builder',       desc: 'Launch a website for your business' },
    ]},
    { section: 'Marketing', tools: [
      { key: 'marketing-hub',   Icon: TrendingUp, color: 'pink',    label: 'Marketing Hub',         desc: 'AI scripts, leads, content strategy' },
      { key: 'social-media-hub',Icon: Send,       color: 'indigo',  label: 'Social Media',          desc: 'Schedule and automate your posts' },
    ]},
  ];
  const colorMap2 = {
    indigo:  { text: 'text-indigo-400',  bg: 'bg-indigo-500/10',  hover: 'hover:border-indigo-400/40' },
    purple:  { text: 'text-purple-400',  bg: 'bg-purple-500/10',  hover: 'hover:border-purple-400/40' },
    emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', hover: 'hover:border-emerald-400/40' },
    violet:  { text: 'text-violet-400',  bg: 'bg-violet-500/10',  hover: 'hover:border-violet-400/40' },
    amber:   { text: 'text-amber-400',   bg: 'bg-amber-500/10',   hover: 'hover:border-amber-400/40' },
    orange:  { text: 'text-orange-400',  bg: 'bg-orange-500/10',  hover: 'hover:border-orange-400/40' },
    pink:    { text: 'text-pink-400',    bg: 'bg-pink-500/10',    hover: 'hover:border-pink-400/40' },
  };
  const BUSINESS_DIRECT_TOOLS = ['tax-writeoffs', 'invoices', 'mileage', 'idea-engine', 'product-builder', 'side-hustles', 'website-builder'];
  const handleToolClick = (key) => {
    if (['budget','bills','scheduled','alerts'].includes(key)) { setView('personal-tools'); return; }
    if (key === 'tax-vault')        { setView('reports'); return; }
    if (key === 'banking')          { setView('banking'); return; }
    if (key === 'expenses')         { setView('expenses'); return; }
    if (key === 'subscriptions')    { setView('subscriptions'); return; }
    if (key === 'reports')          { setView('reports'); return; }
    if (key === 'quantra')          { if (!isMobileDevice) { setQuantraPopupOpen(true); } else { setView('quantra'); } return; }
    if (key === 'pulse')            { setView('liquidity'); return; }
    if (key === 'marketing-hub')    { setView('marketing'); return; }
    if (key === 'social-media-hub') { setView('social-media'); return; }
    if (BUSINESS_DIRECT_TOOLS.includes(key)) {
      setBusinessInitialTool(key);
      setView('business-tools');
      return;
    }
    setBusinessInitialTool(null);
    setView('business-tools');
  };
  const q = toolSearch.toLowerCase().trim();
  const filteredSections = q
    ? ALL_TOOLS.map(s => ({ ...s, tools: s.tools.filter(t => t.label.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q)) })).filter(s => s.tools.length > 0)
    : ALL_TOOLS;
  return (
    <div className="space-y-6 lg:space-y-8" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
      <div>
        <h1 className="text-3xl lg:text-4xl font-bold text-white" style={{ letterSpacing: '-0.03em' }}>All Tools</h1>
        <p className="text-sm lg:text-base text-slate-500 mt-1">Every tool in one place</p>
      </div>
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
        <input
          type="text"
          placeholder="Search tools…"
          value={toolSearch}
          onChange={e => setToolSearch(e.target.value)}
          className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl pl-9 pr-4 py-2.5 lg:py-3 text-sm lg:text-base text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/30 transition-all"
        />
        {toolSearch && (
          <button onClick={() => setToolSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {filteredSections.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-8">No tools match "{toolSearch}"</p>
      )}
      {filteredSections.map(({ section, tools }) => (
        <div key={section}>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3 lg:mb-4">{section}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
            {tools.map(({ key, Icon, color, label, desc }) => {
              const c = colorMap2[color] || colorMap2.indigo;
              return (
                <button key={key} onClick={() => handleToolClick(key)} className={`mobile-tap centris-card rounded-xl p-4 lg:p-6 text-left ${c.hover} transition-all flex flex-col gap-2 lg:gap-3`}>
                  <div className={`w-9 h-9 lg:w-12 lg:h-12 rounded-xl ${c.bg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 lg:w-5 lg:h-5 ${c.text}`} />
                  </div>
                  <div>
                    <p className="text-sm lg:text-base font-bold text-white">{label}</p>
                    <p className="text-xs text-slate-400 mt-0.5 leading-snug">{desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
})()}
{displayedView === 'business-tools' && <BusinessToolsHub key={businessInitialTool || 'default'} user={user} expenses={expenses} setExpenses={setExpenses} saveExpense={saveExpense} setShowUploadModal={setShowUploadModal} invoices={invoices} setRevenue={setRevenue} withBackgroundTask={withBackgroundTask} setTaxVaultReminder={setTaxVaultReminder} showToast={showToast} perms={perms} onCanProceed={canProceed} paygPrices={PAYG_PRICES} initialTool={businessInitialTool} />}
{displayedView === 'marketing' && <React.Suspense fallback={<div className="text-slate-400 p-8 text-center">Loading...</div>}><MarketingHub user={user} invoices={invoices} expenses={expenses} initialTab="marketing" perms={perms} /></React.Suspense>}
{displayedView === 'social-media' && <React.Suspense fallback={<div className="text-slate-400 p-8 text-center">Loading...</div>}><MarketingHub user={user} invoices={invoices} expenses={expenses} initialTab="social" perms={perms} /></React.Suspense>}
{/* website-builder moved into BusinessToolsHub */}
{displayedView === 'banking' && (
  <BankingHub
    user={user}
    bankAccounts={bankAccounts}
    onConnectBank={() => handleOpenPlaidModal()}
    onImportTransactions={() => importBankTransactions()}
    plaidConnectEnabled={liveBankImportActive}
  />
)}
{displayedView === 'personal-tools' && (
  <div className="space-y-4">
    <PersonalToolsHub
      user={user}
      expenses={expenses}
      budgets={budgets}
      setBudgets={setBudgets}
      bills={bills}
      setBills={setBills}
      scheduledTx={scheduledTx}
      setScheduledTx={setScheduledTx}
      smartAlerts={smartAlerts}
      dismissSmartAlert={dismissSmartAlert}
      invoices={invoices}
      bankAccounts={bankAccounts}
      onOpenBanking={() => setView('banking')}
      liveBankImportActive={liveBankImportActive}
      showToast={showToast}
    />

    {showTwoFactorModal && false && <ModalPortal>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[999] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-slate-900/95 shadow-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-white">Two-Factor Authentication</h3>
              <p className="text-xs text-slate-500 mt-0.5">{twoFactorEnabled ? '2FA is currently enabled.' : 'Set up SMS verification.'}</p>
            </div>
            <button onClick={() => { setShowTwoFactorModal(false); setReauthRequired(false); setReauthPassword(''); setTwoFactorError(''); }} disabled={twoFactorLoading} className="p-1 text-slate-500 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {!twoFactorEnabled && (
              <>
                {!twoFactorVerificationId ? (
                  <>
                    {reauthRequired ? (
                      <>
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                          <p className="text-xs text-amber-200">For security, confirm your password to continue setting up 2FA.</p>
                        </div>
                        <input
                          type="password"
                          value={reauthPassword}
                          onChange={(e) => setReauthPassword(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleReauth()}
                          placeholder="Enter your password"
                          className="w-full px-4 py-3 bg-slate-800 border border-white/[0.08] rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                          autoFocus
                        />
                        <button
                          onClick={handleReauth}
                          disabled={reauthLoading || !reauthPassword}
                          className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {reauthLoading ? <><Spinner /><span>Verifying...</span></> : 'Confirm & Continue'}
                        </button>
                      </>
                    ) : (
                      <>
                    <input
                      type="tel"
                      value={twoFactorPhone}
                      onChange={(e) => setTwoFactorPhone(e.target.value)}
                      placeholder="+1 555 123 4567"
                      className="w-full px-4 py-3 bg-slate-800 border border-white/[0.08] rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                    />
                    {/* Always in DOM so useEffect can render reCAPTCHA on web; hidden on native */}
                    <div id="mfa-enroll-recaptcha" className={`flex justify-center${reauthRequired || isNative ? ' hidden' : ''}`} />
                    <button
                      onClick={startTwoFactorEnrollment}
                      disabled={twoFactorLoading || !twoFactorPhone.trim() || (!isNative && !recaptchaSolved)}
                      className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {twoFactorLoading ? <><Spinner /><span>Sending code...</span></> : 'Send verification code'}
                    </button>
                    <p className="text-[11px] text-slate-500">A verification code will be sent to your phone via SMS.</p>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="w-full px-4 py-3 bg-slate-800 border border-white/[0.08] rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 tracking-[0.2em] text-center"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          setTwoFactorVerificationId('');
                          setTwoFactorCode('');
                        }}
                        className="py-3 rounded-xl bg-slate-800 border border-white/[0.08] text-slate-300 text-sm font-semibold hover:bg-slate-700 transition-all"
                      >
                        Back
                      </button>
                      <button
                        onClick={confirmTwoFactorEnrollment}
                        disabled={twoFactorLoading || twoFactorCode.length < 6}
                        className="py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {twoFactorLoading ? <><Spinner /><span>Enabling...</span></> : 'Enable 2FA'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {twoFactorEnabled && (
              <button
                onClick={disableTwoFactor}
                disabled={twoFactorLoading}
                className="w-full py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 text-sm font-semibold hover:bg-red-500/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {twoFactorLoading ? <><Spinner /><span>Disabling...</span></> : 'Disable 2FA'}
              </button>
            )}
            {twoFactorError && <p className="text-xs text-red-300">{twoFactorError}</p>}
          </div>
        </div>
      </div>
    </ModalPortal>}
  </div>
)}

          {displayedView === 'subscriptions' && (
            <div className="relative" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
              <div className="relative space-y-5" style={{ zIndex: 1 }}>
                {!isMobileDevice && (
                  <div className="pb-1">
                    <h1 className="text-2xl font-semibold text-white">Subscriptions</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Track and manage your recurring costs</p>
                  </div>
                )}
                <SubscriptionTracker
                  user={user}
                  expenses={expenses}
                  showToast={showToast}
                  openBankStatementSignal={bankStatementOpenSignal}
                  onExtensionPairedChange={setExtensionPaired}
                  onUnknownPatterns={(patterns, question) => {
                    setCfoPopupData({ type: 'unknown-subscriptions', unknownPatterns: patterns, question });
                    setCfoPopupOpen(true);
                  }}
                  onOpenCfoPopup={(popupData) => {
                    setCfoPopupData(popupData);
                    setCfoPopupOpen(true);
                  }}
                />
              </div>
            </div>
          )}

                    {/* ---- EXPENSES ---- */}
          {displayedView === 'expenses' && (
            <div className="relative space-y-10 centris-page" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
              <div className="relative space-y-6" style={{ zIndex: 1 }}>
              <div className="flex items-center justify-between">
                <div className="flex gap-1 centris-card rounded-lg p-1">
                  {[{ key: 'expenses', label: 'Expenses' }, { key: 'revenue', label: 'Revenue' }].map(t => (
                    <button key={t.key} onClick={() => { setExpensesSubTab(t.key); setExpenseSearchTerm(''); }}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${expensesSubTab === t.key ? 'bg-white/10 text-white' : 'text-slate-500'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowUploadModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600/20 text-indigo-400 border border-indigo-500/20 text-xs font-medium">
                    <Camera className="w-3 h-3" /> Scan
                  </button>
                  <button onClick={() => importBankTransactions()} disabled={importingTransactions} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/20 text-emerald-400 border border-emerald-500/20 text-xs font-medium disabled:opacity-50">
                    {importingTransactions ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                    Import
                  </button>
                  {expensesSubTab === 'expenses' && expenses.length > 0 && (
                    <button onClick={() => setShowDeleteAllConfirm(true)} className="p-1.5 text-red-400/60 hover:text-red-400 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {expensesSubTab === 'revenue' && revenue.length > 0 && (
                    <button onClick={() => setShowDeleteAllRevenueConfirm(true)} className="p-1.5 text-red-400/60 hover:text-red-400 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Search bar — shared across both tabs */}
              <div className="flex items-center gap-2 centris-card rounded-lg px-3 py-2">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  type="text"
                  placeholder={expensesSubTab === 'expenses' ? 'Search expenses...' : 'Search revenue...'}
                  value={expenseSearchTerm}
                  onChange={(e) => setExpenseSearchTerm(e.target.value)}
                  className="flex-1 bg-transparent border-none text-sm text-white placeholder-slate-500 focus:outline-none"
                />
                {expenseSearchTerm && (
                  <button onClick={() => setExpenseSearchTerm('')} className="text-slate-500 hover:text-white">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {expensesSubTab === 'expenses' && expenses.length > 0 && (() => {
                const totalSpent = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
                const totalDeductible = expenses.filter(e => e.taxDeductible).reduce((s, e) => s + (Number(e.amount) || 0), 0);
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="centris-card rounded-xl p-3 flex flex-col gap-0.5">
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest">Total Spent</p>
                      <p className="text-lg font-semibold text-red-400">${totalSpent.toFixed(2)}</p>
                      <p className="text-[10px] text-slate-600">{expenses.length} expenses</p>
                    </div>
                    <div className="centris-card rounded-xl border border-emerald-500/20 p-3 flex flex-col gap-0.5">
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest">Deductible</p>
                      <p className="text-lg font-semibold text-emerald-400">${totalDeductible.toFixed(2)}</p>
                      <p className="text-[10px] text-slate-600">Save ~${(totalDeductible * 0.25).toFixed(0)}</p>
                    </div>
                  </div>
                );
              })()}

              {expensesSubTab === 'expenses' && (
                <>
                  {expenses.length === 0 ? (
                    <div className="text-center py-20 centris-card rounded-xl border border-dashed border-slate-700">
                      <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400 mb-1">No expenses yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {expenses.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).filter(e => !expenseSearchTerm || (e.merchant || '').toLowerCase().includes(expenseSearchTerm.toLowerCase()) || (e.category || '').toLowerCase().includes(expenseSearchTerm.toLowerCase())).map((expense, index) => (
                        <div key={index} className="centris-card flex items-center justify-between p-4 rounded-xl hover:border-indigo-500/20 transition-all">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <p className="font-medium text-white text-sm">{expense.merchant}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <span className="text-[11px] px-2 py-0.5 bg-indigo-500/10 text-indigo-300 rounded-full border border-indigo-500/20">{expense.category}</span>
                                  <span className="text-[11px] text-slate-500">{new Date(expense.date).toLocaleDateString()}</span>
                                  {expense.taxDeductible && <span className="text-[11px] px-2 py-0.5 bg-green-500/10 text-green-300 rounded-full border border-green-500/20">Deductible</span>}
                                  {expense.fromBankStatement && <span className="text-[11px] px-2 py-0.5 bg-blue-500/10 text-blue-300 rounded-full border border-blue-500/20">Bank</span>}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-white">${Number(expense.amount || 0).toFixed(2)}</p>
                                {expense.taxDeductible && <p className="text-[11px] text-green-400">Save ${(Number(expense.amount || 0) * 0.25).toFixed(2)}</p>}
                              </div>
                            </div>
                          </div>
                          <button onClick={() => { haptic(ImpactStyle.Light); handleDeleteExpense(expense.id); }} className="ml-3 p-2 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {expensesSubTab === 'revenue' && (() => {
                const revenueTotal = revenue.reduce((s, r) => s + (Number(r.amount) || 0), 0);
                const paidInvoices = invoices.filter(i => i.status === 'paid');
                const invoiceTotal = paidInvoices.reduce((s, i) => s + (Number(i.total) || 0), 0);
                const combinedTotal = revenueTotal + invoiceTotal;

                const crossDuplicates = revenue.filter(r =>
                  paidInvoices.some(inv =>
                    Math.abs((Number(inv.total) || 0) - (Number(r.amount) || 0)) < 0.01 &&
                    inv.date === r.date
                  )
                );

                return (
                  <>

                    {crossDuplicates.length > 0 && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
                        <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-300">{crossDuplicates.length} possible duplicate{crossDuplicates.length > 1 ? 's' : ''} between Revenue and Invoices</p>
                          <p className="text-xs text-slate-400 mt-1">These bank payments match a paid invoice by amount and date - you may be counting the same payment twice.</p>
                          <div className="mt-2 space-y-1">
                            {crossDuplicates.map((r, i) => {
                              const matchedInv = paidInvoices.find(inv => Math.abs((Number(inv.total) || 0) - (Number(r.amount) || 0)) < 0.01 && inv.date === r.date);
                              return (
                                <p key={i} className="text-[11px] text-slate-400">
                                  ${Number(r.amount || 0).toFixed(2)} on {r.date} &rarr; matches Invoice {matchedInv?.invoiceNumber}
                                </p>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}



                    {showAddRevenue && (
                      <div className="centris-card rounded-xl p-4 space-y-3">
                        <p className="text-xs font-semibold text-white">New Revenue Entry</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Source / Client *</label>
                            <input type="text" placeholder="Acme Corp" value={newRevenue.source}
                              onChange={e => setNewRevenue(p => ({ ...p, source: e.target.value }))}
                              className="w-full bg-slate-900/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Amount *</label>
                            <div className="relative">
                              <span className="absolute left-3 top-2.5 text-slate-400 text-sm">$</span>
                              <input type="number" step="0.01" min="0" placeholder="0.00" value={newRevenue.amount}
                                onChange={e => setNewRevenue(p => ({ ...p, amount: e.target.value }))}
                                className="w-full pl-7 bg-slate-900/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Date</label>
                            <input type="date" value={newRevenue.date}
                              onChange={e => setNewRevenue(p => ({ ...p, date: e.target.value }))}
                              className="w-full bg-slate-900/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Status</label>
                            <select value={newRevenue.status} onChange={e => setNewRevenue(p => ({ ...p, status: e.target.value }))}
                              className="w-full bg-slate-900/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white">
                              <option value="received">Received</option>
                              <option value="pending">Pending</option>
                              <option value="partial">Partial</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 block mb-1">Description (optional)</label>
                          <input type="text" placeholder="e.g. Website redesign payment" value={newRevenue.description}
                            onChange={e => setNewRevenue(p => ({ ...p, description: e.target.value }))}
                            className="w-full bg-slate-900/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setShowAddRevenue(false)}
                            className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600">Cancel</button>
                          <button
                            onClick={async () => {
                              if (!newRevenue.source || !newRevenue.amount) return;
                              haptic(ImpactStyle.Medium);
                              const data = { ...newRevenue, amount: parseFloat(newRevenue.amount), createdAt: new Date().toISOString() };
                              if (isDemoMode) {
                                setRevenue(prev => [{ ...data, id: 'demo-r-' + Date.now() }, ...prev]);
                                setNewRevenue({ source: '', amount: '', date: new Date().toISOString().split('T')[0], description: '', status: 'received' });
                                setShowAddRevenue(false);
                                return;
                              }
                              const dupCheck = await checkDuplicateRevenue(user.uid, data, invoices);
                              if (dupCheck.isDuplicate) {
                                showToast(`Possible duplicate: this matches an existing ${dupCheck.duplicateIn === 'invoice' ? `Invoice ${dupCheck.invoiceNumber}` : 'revenue entry'}.`, 'warning');
                              }
                              const saveRes = await saveRevenue(user.uid, data);
                              if (saveRes.success) {
                                const vaultSettings = await loadTaxVaultSettings(user.uid);
                                const reservePct = vaultSettings?.settings?.reservePercent || 25;
                                const vaultAmount = parseFloat((data.amount * (reservePct / 100)).toFixed(2));
                                if (vaultAmount > 0) {
                                  setTaxVaultReminder({
                                    amount: vaultAmount,
                                    invoiceTotal: data.amount,
                                    reservePct,
                                    invoiceNumber: data.description || 'Income',
                                    client: data.source || 'income entry',
                                    invoiceId: data.id || Date.now(),
                                  });
                                }
                              }
                              const result = await loadRevenue(user.uid);
                              if (result.success) setRevenue(result.revenue);
                              setNewRevenue({ source: '', amount: '', date: new Date().toISOString().split('T')[0], description: '', status: 'received' });
                              setShowAddRevenue(false);
                            }}
                            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-500"
                          >Save Payment</button>
                        </div>
                      </div>
                    )}

                    {revenue.length === 0 ? (
                      <div className="text-center py-16 centris-card rounded-xl border border-dashed border-slate-700">
                        <TrendingUp className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400 text-sm mb-1">No revenue entries yet</p>
                        <p className="text-xs text-slate-500">Upload a bank statement in Tax Write-Offs to auto-import payments, or add one manually above</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {revenue.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).filter(r => !expenseSearchTerm || (r.source || '').toLowerCase().includes(expenseSearchTerm.toLowerCase()) || (r.description || '').toLowerCase().includes(expenseSearchTerm.toLowerCase())).map((r) => {
                          const isCrossDup = paidInvoices.some(inv =>
                            Math.abs((Number(inv.total) || 0) - (Number(r.amount) || 0)) < 0.01 &&
                            inv.date === r.date
                          );
                          return (
                            <div key={r.id} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isCrossDup ? 'bg-amber-500/5 border-amber-500/20' : 'centris-card hover:border-emerald-500/20'}`}>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-sm font-medium text-white">{r.source}</p>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                    r.status === 'received' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                    r.status === 'pending' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                    'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                                    {r.status}
                                  </span>
                                  {r.fromBankStatement && <span className="text-[10px] px-2 py-0.5 bg-slate-700/60 text-slate-400 rounded-full border border-white/[0.06]">From Bank</span>}
                                  {isCrossDup && <span className="text-[10px] px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded-full border border-amber-500/20">Possible Dup</span>}
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-slate-500">{new Date(r.date).toLocaleDateString()}</span>
                                  {r.description && <span className="text-xs text-slate-500">{r.description}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <p className="text-sm font-bold text-emerald-400">+${Number(r.amount || 0).toFixed(2)}</p>
                                <button
                                  onClick={async () => {
                                    await deleteRevenue(user.uid, r.id);
                                    const res = await loadRevenue(user.uid);
                                    if (res.success) setRevenue(res.revenue);
                                  }}
                                  className="p-2 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {paidInvoices.length > 0 && (
                      <div className="centris-card rounded-xl p-4">
                        <p className="text-xs font-semibold text-white mb-3 flex items-center gap-2">
                          <FileText className="w-3.5 h-3.5 text-blue-400" /> Paid Invoices (from Invoice Generator)
                        </p>
                        <div className="space-y-2">
                          {paidInvoices
                            .slice()
                            .sort((a, b) => new Date(b.createdAt?.toDate?.() || b.createdAt || 0) - new Date(a.createdAt?.toDate?.() || a.createdAt || 0))
                            .map(inv => (
                              <div key={inv.id} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
                                <div>
                                  <p className="text-sm text-white">{inv.clientName} <span className="text-xs text-slate-500">- {inv.invoiceNumber}</span></p>
                                  <p className="text-xs text-slate-500">{inv.dueDate || new Date(inv.createdAt?.toDate?.() || inv.createdAt).toLocaleDateString()}</p>
                                </div>
                                <p className="text-sm font-semibold text-blue-400">+${Number(inv.total || 0).toFixed(2)}</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              </div>
            </div>
          )}

          {/* ---- TAX & REPORTS ---- */}
          {displayedView === 'reports' && (
            <div className="relative space-y-4 centris-page" style={{ animation: 'cardFadeIn 0.5s ease-out both' }}>
              <div className="relative space-y-4" style={{ zIndex: 1 }}>
                {/* Key tax stats — compact row */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="centris-card rounded-xl p-3"><p className="text-[10px] text-slate-500 mb-1">Tax Deductions</p><p className="text-lg font-bold text-green-400">${(taxDeductibleTotal + mileageTrips.reduce((s, t) => s + (t.deduction || 0), 0)).toFixed(2)}</p></div>
                  <div className="centris-card rounded-xl p-3"><p className="text-[10px] text-slate-500 mb-1">Business Miles</p><p className="text-lg font-bold text-white">{mileageTrips.reduce((s, t) => s + (t.totalMiles || 0), 0).toFixed(1)} mi</p></div>
                  <div className="centris-card rounded-xl p-3"><p className="text-[10px] text-slate-500 mb-1">Invoices</p><p className="text-lg font-bold text-white">${invoices.reduce((s, i) => s + i.total, 0).toFixed(2)}</p><p className="text-[10px] text-slate-600">{invoices.length} total</p></div>
                  <div className="centris-card rounded-xl p-3"><p className="text-[10px] text-slate-500 mb-1">Unpaid</p><p className="text-lg font-bold text-amber-400">${invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + i.total, 0).toFixed(2)}</p></div>
                </div>

              <div className="centris-card rounded-xl p-6">
                <h3 className="text-base font-semibold text-white mb-4 flex items-center space-x-2"><DollarSign className="w-5 h-5 text-green-400" /><span>Tax Deduction Breakdown</span></h3>
                {expenses.filter(e => e.taxDeductible).length === 0 && mileageTrips.length === 0 ? (
                  <div className="text-center py-10"><AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-3" /><p className="text-slate-400 text-sm">No tax deductions recorded yet</p></div>
                ) : (
                  <div className="space-y-4">
                    {expenses.filter(e => e.taxDeductible).length > 0 && (
                      <div><p className="text-xs font-medium text-slate-400 mb-2">By Category</p><div className="space-y-2">{(() => {
                        const d = {}; expenses.filter(e => e.taxDeductible).forEach(e => { d[e.category] = (d[e.category] || 0) + e.amount; });
                        return Object.entries(d).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                          <div key={cat} className="flex justify-between items-center centris-card p-3 rounded-lg"><span className="text-sm text-slate-300 min-w-0 truncate mr-2">{cat}</span><div className="text-right flex-shrink-0"><span className="text-sm font-semibold text-white">${amt.toFixed(2)}</span><span className="text-[11px] text-green-400 ml-2">(Save ${(amt * 0.25).toFixed(2)})</span></div></div>
                        ));
                      })()}</div></div>
                    )}
                    {mileageTrips.length > 0 && (
                      <div><p className="text-xs font-medium text-slate-400 mb-2">Mileage</p><div className="centris-card rounded-lg p-4 text-sm space-y-2">
                        <div className="flex justify-between"><span className="text-slate-400">Total Miles</span><span className="text-white">{mileageTrips.reduce((s, t) => s + (t.totalMiles || 0), 0).toFixed(1)} mi</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">IRS Rate</span><span className="text-white">$0.70/mi</span></div>
                        <div className="flex justify-between border-t border-white/[0.04] pt-2"><span className="text-white font-medium">Deduction</span><span className="text-green-400 font-semibold">${mileageTrips.reduce((s, t) => s + (t.deduction || 0), 0).toFixed(2)}</span></div>
                      </div></div>
                    )}
                    <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-5 text-center">
                      <p className="text-green-300 text-xs font-semibold mb-1">TOTAL DEDUCTIONS</p>
                      <p className="text-3xl font-bold text-white">${(taxDeductibleTotal + mileageTrips.reduce((s, t) => s + (t.deduction || 0), 0)).toFixed(2)}</p>
                      <p className="text-xs text-green-300 mt-2">Savings (25%): <span className="font-bold text-green-400">${((taxDeductibleTotal + mileageTrips.reduce((s, t) => s + (t.deduction || 0), 0)) * 0.25).toFixed(2)}</span></p>
                    </div>
                  </div>
                )}
                <button onClick={async () => { const { exportTaxReport } = await import('./taxReportExport'); const r = await exportTaxReport(expenses, mileageTrips, user?.name || 'Client'); if (r.success) showToast(`Exported! ${r.summary.expenseCount} expenses · $${r.summary.grandTotal.toFixed(2)} total · $${r.summary.estimatedSavings.toFixed(2)} est. savings`, 'success', 6000); else showToast(r.error || 'Failed to generate tax report', 'error'); }}
                  className="mt-5 w-full bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 text-sm"><FileText className="w-4 h-4" /><span>Export Tax Report (PDF)</span></button>
              </div>

              {/* ── Detailed Tax Report ── */}
              <div className="centris-card rounded-xl p-6">
                <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2"><Download className="w-5 h-5 text-indigo-400" /><span>Detailed Tax Report</span></h3>
                <p className="text-xs text-slate-500 mb-4">Every deductible expense and mileage trip, line by line — formatted for your accountant</p>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <p className="text-[10px] text-slate-500 mb-1">Deductible Items</p>
                    <p className="text-lg font-bold text-white">{expenses.filter(e => e.taxDeductible).length}</p>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <p className="text-[10px] text-slate-500 mb-1">Categories</p>
                    <p className="text-lg font-bold text-white">{new Set(expenses.filter(e => e.taxDeductible).map(e => e.category)).size}</p>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <p className="text-[10px] text-slate-500 mb-1">Mileage Trips</p>
                    <p className="text-lg font-bold text-white">{mileageTrips.length}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center bg-indigo-500/8 border border-indigo-500/20 rounded-lg px-4 py-2.5 mb-4">
                  <span className="text-xs text-slate-400">Total deductions covered</span>
                  <span className="text-sm font-bold text-indigo-300">${(taxDeductibleTotal + mileageTrips.reduce((s, t) => s + (t.deduction || 0), 0)).toFixed(2)}</span>
                </div>
                <button onClick={async () => { const { exportDetailedTaxReport } = await import('./taxReportExport'); await exportDetailedTaxReport(expenses, mileageTrips, user?.name || 'Client'); }}
                  className="w-full bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 text-indigo-300 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 text-sm"><Download className="w-4 h-4" /><span>Export Detailed Report (Excel)</span></button>
              </div>

              {/* ── Income Report ── */}
              <div className="centris-card rounded-xl p-6">
                <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" /><span>Income Report</span></h3>
                <p className="text-xs text-slate-500 mb-4">All invoices and logged revenue — paid, pending, and outstanding</p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white/[0.03] rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Total Invoiced</p>
                    <p className="text-lg font-bold text-white">${invoices.reduce((s, i) => s + i.total, 0).toFixed(2)}</p>
                    <p className="text-[10px] text-slate-600">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Collected</p>
                    <p className="text-lg font-bold text-emerald-400">${invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0).toFixed(2)}</p>
                    <p className="text-[10px] text-slate-600">{invoices.filter(i => i.status === 'paid').length} paid</p>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Outstanding</p>
                    <p className="text-lg font-bold text-amber-400">${invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + i.total, 0).toFixed(2)}</p>
                    <p className="text-[10px] text-slate-600">{invoices.filter(i => i.status !== 'paid').length} unpaid</p>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Revenue Entries</p>
                    <p className="text-lg font-bold text-white">{revenue.length}</p>
                    <p className="text-[10px] text-slate-600">${revenue.reduce((s, r) => s + Number(r.amount || 0), 0).toFixed(2)} total</p>
                  </div>
                </div>
                <button onClick={async () => { const { exportIncomeReport } = await import('./taxReportExport'); const r = await exportIncomeReport(revenue, invoices, user?.name || 'Client'); if (!r?.success) showToast(r?.error || 'Failed to generate income report', 'error'); }}
                  className="w-full bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 text-sm"><TrendingUp className="w-4 h-4" /><span>Export Income Report (PDF)</span></button>
              </div>

              {/* ── Full P&L ── */}
              {(() => {
                const totalIncome = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0) + revenue.reduce((s, r) => s + Number(r.amount || 0), 0);
                const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);
                const net = totalIncome - totalExpenses;
                return (
                  <div className="centris-card rounded-xl p-6">
                    <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2"><DollarSign className="w-5 h-5 text-violet-400" /><span>Profit &amp; Loss</span></h3>
                    <p className="text-xs text-slate-500 mb-4">Every income and expense entry in one spreadsheet — {expenses.length + revenue.length + invoices.filter(i => i.status === 'paid').length} total rows</p>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                        <p className="text-[10px] text-slate-500 mb-1">Total Income</p>
                        <p className="text-lg font-bold text-emerald-400">${totalIncome.toFixed(2)}</p>
                      </div>
                      <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                        <p className="text-[10px] text-slate-500 mb-1">Total Expenses</p>
                        <p className="text-lg font-bold text-red-400">${totalExpenses.toFixed(2)}</p>
                      </div>
                      <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                        <p className="text-[10px] text-slate-500 mb-1">Net</p>
                        <p className={`text-lg font-bold ${net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{net < 0 ? '-' : ''}${Math.abs(net).toFixed(2)}</p>
                      </div>
                    </div>
                    <button onClick={async () => {
                      const allExp = expenses.map(e => [e.date,'expense',e.merchant,`-${e.amount}`,e.category,e.taxDeductible?'deductible':'']);
                      const allRev = revenue.map(r => [r.date,'revenue',r.source,`+${r.amount}`,'Income',r.status]);
                      const rows = [['Date','Type','Name','Amount','Category','Notes'],...[...allExp,...allRev].sort((a,b)=>a[0]>b[0]?-1:1)];
                      const csv = rows.map(r => r.map(v => `"${String(v||'').replace(/"/g,'""')}"`).join(',')).join('\n');
                      const fileName = `pl-report-${new Date().toISOString().split('T')[0]}.csv`;
                      if (Capacitor.isNativePlatform()) {
                        const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
                        const { Share } = await import('@capacitor/share');
                        try { await Filesystem.requestPermissions(); } catch (_) {}
                        const safeName = fileName.replace(/[^\w.\-]/g, '_');
                        await Filesystem.writeFile({ path: safeName, data: csv, directory: Directory.Cache, encoding: Encoding.UTF8 });
                        const { uri } = await Filesystem.getUri({ path: safeName, directory: Directory.Cache });
                        await Share.share({ title: 'P&L Report', url: uri, dialogTitle: 'Save or share CSV' });
                      } else {
                        const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv); a.download = fileName; a.click();
                      }
                    }} className="w-full bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/30 text-violet-300 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4" /><span>Export Full P&amp;L (CSV)</span>
                    </button>
                  </div>
                );
              })()}

              {invoices.length > 0 && (
                <div className="centris-card rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4"><h3 className="text-base font-semibold text-white">Recent Invoices</h3><span className="text-xs text-slate-500">{invoices.length} total</span></div>
                  <div className="space-y-2">{invoices.slice(0, 5).map(inv => (
                    <div key={inv.id} className="centris-card rounded-lg p-3 flex justify-between items-center"><div><p className="text-sm font-medium text-white">{inv.invoiceNumber}</p><p className="text-xs text-slate-500">{inv.clientName}</p></div><div className="text-right"><p className="text-sm font-semibold text-white">${inv.total.toFixed(2)}</p><span className={`text-[10px] px-2 py-0.5 rounded-full ${inv.status === 'paid' ? 'bg-green-500/10 text-green-400' : inv.status === 'sent' ? 'bg-blue-500/10 text-blue-400' : 'bg-slate-500/10 text-slate-400'}`}>{inv.status.toUpperCase()}</span></div></div>
                  ))}</div>
                  <button onClick={() => setView('business-tools')} className="w-full mt-3 text-xs text-indigo-400 hover:text-indigo-300 font-medium">View All Invoices &rarr;</button>
                </div>
              )}
              <TaxVault user={user} bankAccounts={bankAccounts} liveBankImportActive={liveBankImportActive} />
              </div>
            </div>
          )}
          </div>{/* end centris-page-transition wrapper */}
        </div>
        {/* ===== PERSISTENT GOAL TRACKER ===== */}
        {expenses.length > 0 && (
          <div className="fixed top-24 right-6 z-40 max-sm:bottom-24 max-sm:right-4 max-sm:top-auto max-sm:left-auto">
            {showGoalTracker ? (
              <div className="bg-slate-900/95 backdrop-blur-xl border border-indigo-500/30 rounded-2xl p-5 w-[22rem] max-sm:w-80 shadow-2xl shadow-indigo-900/20 max-h-[60vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="bg-indigo-500/15 p-1.5 rounded-lg">
                      <Target className="w-4 h-4 text-indigo-400" />
                    </div>
                    <span className="text-base font-bold text-white">30-Day Goal Tracker</span>
                  </div>

                  <button onClick={() => setShowGoalTracker(false)} className="text-slate-500 hover:text-slate-300 text-xs">x</button>
                </div>
                {!goalAmount ? (
                  <div>
                    <p className="text-xs text-slate-400 mb-3">Set a revenue or savings target for the current month.</p>
                    <input
                      type="number"
                      placeholder="e.g. 10000"
                      value={goalInput}
                      onChange={(e) => setGoalInput(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 mb-3"
                    />
                    <button
                      onClick={() => { const v = parseFloat(goalInput); if (v > 0) { setGoalAmount(v); try { localStorage.setItem('centris_monthly_goal', String(v)); } catch {} } }}
                      className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold py-2.5 rounded-xl transition-all"
                    >Set Goal</button>
                  </div>
                ) : (() => {
                  const progress = Math.min(100, (totalExpenses / goalAmount) * 100);
                  const daysIn = new Date().getDate();
                  const projectedEnd = daysIn > 0 ? (totalExpenses / daysIn) * 30 : 0;
                  const onTrack = projectedEnd >= goalAmount;
                  return (
                    <div>
                      <div className="flex justify-between text-xs text-slate-300 mb-1.5">
                        <span>${totalExpenses.toFixed(0)} tracked</span>
                        <span>Goal: ${goalAmount.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-slate-700/50 rounded-full h-3 mb-2.5">
                        <div
                          className={`h-3 rounded-full transition-all ${onTrack ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className={`text-sm font-medium ${onTrack ? 'text-emerald-400' : 'text-slate-300'}`}>
                        {onTrack ? `On track - projected $${projectedEnd.toFixed(0)}` : `Projected $${projectedEnd.toFixed(0)} by month end`}
                      </p>
                      <button onClick={() => { setGoalAmount(null); setGoalInput(''); }} className="text-xs text-slate-500 hover:text-slate-300 mt-3">Reset goal</button>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <button
                onClick={() => setShowGoalTracker(true)}
                className="bg-slate-900/95 backdrop-blur-xl border border-indigo-500/30 hover:border-indigo-400 text-white rounded-xl px-2.5 py-1.5 sm:px-4 sm:py-3 text-xs sm:text-sm font-semibold shadow-xl flex items-center gap-1.5 sm:gap-2 transition-all hover:shadow-indigo-500/20"
              >
                <Target className="w-4 h-4 text-indigo-400" />
                <span>{goalAmount ? `Goal: $${goalAmount.toLocaleString()}` : 'Set Goal'}</span>
              </button>
            )}
          </div>
        )}
        {/* ===== LEGAL DISCLAIMER FOOTER ===== */}
        <div className="px-6 py-2 text-center mt-8">
          <p className="text-[9px] text-slate-700 leading-relaxed max-w-3xl mx-auto">
            Not financial, tax, or legal advice. Calculations are estimates only.
          </p>
        </div>
      </main>

      {/* ===== QUANTRA GREETING POPUP ===== */}
      {showQuantraGreeting && !(isMobileDevice && view === 'quantra') && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[340px] overflow-hidden"
          style={{
            animation: 'quantraSlideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both',
          }}
        >
          <div
            className="rounded-2xl p-px"
            style={{
              background: 'linear-gradient(135deg, rgba(139,92,246,0.4), rgba(168,85,247,0.2), rgba(236,72,153,0.3))',
            }}
          >
            <div className="rounded-2xl bg-slate-900/95 backdrop-blur-xl overflow-hidden">
              <div className="px-5 pt-4 pb-0 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-slate-900" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white" style={{ letterSpacing: '-0.01em' }}>Quantra</p>
                    <p className="text-[10px] text-emerald-400 font-medium">Online</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowQuantraGreeting(false);
                    setQuantraGreetingDismissed(true);
                  }}
                  className="text-slate-500 hover:text-white transition-colors p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-5 py-4">
                <p className="text-sm text-slate-300 leading-relaxed">{quantraGreetingMsg}</p>
              </div>

              <div className="px-5 pb-4 flex gap-2">
                <button
                  onClick={() => {
                    setShowQuantraGreeting(false);
                    setQuantraGreetingDismissed(true);
                    if (!isMobileDevice) { setQuantraPopupOpen(true); } else { setView('quantra'); }
                  }}
                  className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white py-2 rounded-xl text-xs font-semibold hover:shadow-lg hover:shadow-violet-500/20 transition-all"
                >
                  Chat with me
                </button>
                <button
                  onClick={() => {
                    setShowQuantraGreeting(false);
                    setQuantraGreetingDismissed(true);
                  }}
                  className="px-4 py-2 bg-white/[0.04] text-slate-400 rounded-xl text-xs font-medium hover:bg-white/[0.08] transition-all border border-white/[0.06]"
                >
                  Later
                </button>
              </div>

              <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.3), transparent)' }} />
            </div>
          </div>
        </div>
      )}

      {/* ===== SMART ALERT POPUP ===== */}
      {currentAlertPopup && !cfoPopupOpen && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px]"
          style={{ animation: 'quantraSlideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both' }}
        >
          <div
            className="rounded-2xl p-px"
            style={{
              background: currentAlertPopup.severity === 'critical'
                ? 'linear-gradient(135deg, rgba(239,68,68,0.4), rgba(239,68,68,0.1))'
                : 'linear-gradient(135deg, rgba(245,158,11,0.4), rgba(245,158,11,0.1))',
            }}
          >
            <div className="rounded-2xl bg-slate-900/95 backdrop-blur-xl overflow-hidden">
              <div className="px-5 pt-4 pb-0 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      currentAlertPopup.severity === 'critical'
                        ? 'bg-red-500/15 border border-red-500/25'
                        : 'bg-amber-500/15 border border-amber-500/25'
                    }`}
                  >
                    <AlertTriangle className={`w-5 h-5 ${
                      currentAlertPopup.severity === 'critical' ? 'text-red-400' : 'text-amber-400'
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white" style={{ letterSpacing: '-0.01em' }}>
                      {currentAlertPopup.severity === 'critical' ? 'Urgent Alert' : 'Heads Up'}
                    </p>
                    <p className="text-[10px] text-slate-500 font-medium">{currentAlertPopup.category}</p>
                  </div>
                </div>
                <button onClick={() => dismissCurrentAlertPopup(false)} className="text-slate-500 hover:text-white transition-colors p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-5 py-4">
                <p className="text-sm font-semibold text-white mb-1">{currentAlertPopup.title}</p>
                <p className="text-xs text-slate-400 leading-relaxed">{currentAlertPopup.message}</p>
              </div>

              <div className="px-5 pb-4 flex gap-2">
                <button
                  onClick={() => {
                    setView('personal-tools');
                    dismissCurrentAlertPopup(false);
                  }}
                  className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white py-2 rounded-xl text-xs font-semibold"
                >
                  View Details
                </button>
                <button
                  onClick={() => dismissCurrentAlertPopup(true)}
                  className="px-4 py-2 bg-white/[0.04] text-slate-400 rounded-xl text-xs font-medium border border-white/[0.06]"
                >
                  Dismiss
                </button>
              </div>

              {alertPopupQueue.length > 1 && (
                <div className="px-5 pb-3">
                  <p className="text-[10px] text-slate-600">
                    +{alertPopupQueue.length - 1} more alert{alertPopupQueue.length > 2 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div className="fixed bottom-6 left-6 z-[60] flex flex-col gap-2" style={{ maxWidth: 360 }}>
          {toasts.map(t => (
            <div
              key={t.id}
              className={`rounded-xl px-4 py-3 text-sm font-medium shadow-lg backdrop-blur-xl border ${
                t.type === 'success' ? 'bg-emerald-900/90 border-emerald-500/30 text-emerald-200' :
                t.type === 'warning' ? 'bg-amber-900/90 border-amber-500/30 text-amber-200' :
                t.type === 'error' ? 'bg-red-900/90 border-red-500/30 text-red-200' :
                'bg-slate-800/90 border-slate-600/30 text-slate-200'
              }`}
              style={{ animation: 'quantraSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both' }}
            >
              <div className="flex items-center gap-2">
                <span>{t.type === 'success' ? '[OK]' : t.type === 'warning' ? '[!]' : t.type === 'error' ? '[X]' : '[i]'}</span>
                <span>{t.message}</span>
                <button
                  onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                  className="ml-auto text-white/40 hover:text-white/80 pl-2"
                >x</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== WORKSPACE INVITE ACCEPTANCE MODAL ===== */}
      {pendingInviteToken && inviteDetails && <ModalPortal>
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-slate-900 border border-white/[0.08] rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-indigo-500/15 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Workspace Invite</h2>
                <p className="text-xs text-slate-400">You've been invited to collaborate</p>
              </div>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4 mb-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Role</span>
                <span className="text-white capitalize font-medium">{inviteDetails.role}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Invited by</span>
                <span className="text-white">{inviteDetails.email || 'Workspace owner'}</span>
              </div>
            </div>
            {!user ? (
              <p className="text-sm text-amber-300 mb-4 text-center">Please sign in or create an account to accept this invite.</p>
            ) : null}
            <div className="flex gap-3">
              <button
                onClick={() => { setPendingInviteToken(null); setInviteDetails(null); }}
                className="flex-1 py-2.5 rounded-xl border border-white/[0.08] text-slate-300 text-sm font-medium hover:bg-white/[0.04] transition-all"
              >
                Decline
              </button>
              {user && (
                <button
                  disabled={inviteAccepting}
                  onClick={async () => {
                    setInviteAccepting(true);
                    const res = await acceptWorkspaceInvite(pendingInviteToken, user.uid, user.email, user.name);
                    setInviteAccepting(false);
                    if (res.success) {
                      setPendingInviteToken(null);
                      setInviteDetails(null);
                      showToast('You joined the workspace!', 'success');
                      setView('collaboration');
                    } else {
                      showToast(res.error || 'Failed to accept invite', 'error');
                    }
                  }}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {inviteAccepting ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Accept Invite
                </button>
              )}
            </div>
          </div>
        </div>
      </ModalPortal>}

      {/* ===== BOTTOM TAB BAR — mobile only ===== */}
      {isMobileDevice && (() => {
        const homeViews     = ['overview', 'liquidity'];
        const financeViews  = ['expenses', 'banking', 'reports', 'subscriptions'];
        const toolViews     = ['tools-hub', 'business-tools', 'personal-tools', 'marketing', 'social-media'];
        const tabs = [
          { label: 'Home',     icon: LayoutDashboard, defaultView: 'overview',    active: homeViews.includes(view) },
          { label: 'Finances', icon: DollarSign,       defaultView: 'expenses',   active: financeViews.includes(view) },
          { label: 'logo',     icon: null,             defaultView: 'overview',   active: false },
          { label: 'Tools',    icon: Briefcase,        defaultView: 'tools-hub',  active: toolViews.includes(view) },
        ];
        return (
          <nav className="centris-tab-bar fixed bottom-0 left-0 right-0 z-[150] flex items-stretch"
            style={{ background: 'var(--tabbar-bg)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderTop: '1px solid var(--tabbar-border)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {tabs.map(({ label, icon: Icon, defaultView, active }) => {
              if (label === 'logo') {
                return (
                  <button key="logo" onClick={() => { haptic(); setView('overview'); }} className="mobile-tap"
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 56, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    <img src={centrisLogo} alt="CENTRIS" style={{ width: 28, height: 28, objectFit: 'contain', mixBlendMode: 'normal', opacity: homeViews.includes(view) ? 1 : 0.75 }} />
                  </button>
                );
              }
              const tourId = label === 'Finances' ? 'nav-finances' : label === 'Quantra' ? 'nav-quantra' : label === 'Tools' ? 'nav-tools' : undefined;
              return (
                <button key={label}
                  data-tour={tourId}
                  onClick={() => { haptic(); setView(defaultView); }} className="mobile-tap"
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, minHeight: 56, position: 'relative', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                  {active && <span style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 32, height: 2, borderRadius: 2, background: lightMode ? 'linear-gradient(90deg, #2563eb, #60a5fa)' : 'linear-gradient(90deg, #8b5cf6, #c4b5fd)', boxShadow: lightMode ? '0 0 10px rgba(59,130,246,0.45)' : '0 0 10px rgba(167,139,250,0.7)' }} />}
                  <Icon style={{ width: 22, height: 22, color: active ? 'var(--tab-icon-active)' : 'var(--tab-icon-inactive)', filter: active ? (lightMode ? 'none' : 'drop-shadow(0 0 5px rgba(167,139,250,0.5))') : 'none', transition: 'color 0.2s ease' }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: active ? 'var(--tab-icon-active)' : 'var(--tab-icon-inactive)', lineHeight: 1, letterSpacing: '0.01em', transition: 'color 0.2s ease' }}>{label}</span>
                </button>
              );
            })}
          </nav>
        );
      })()}

      {/* ===== FLOATING Quantra BUTTON — desktop only ===== */}
      {!quantraPopupOpen && !cfoPopupOpen && (
        <button onClick={() => { haptic(); setQuantraPopupOpen(true); }} className={`hidden lg:block fixed bottom-24 right-8 z-40 bg-gradient-to-r ${lightMode ? 'from-emerald-500 to-emerald-400' : 'from-violet-600 to-purple-600'} text-white p-4 rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all`} title="Ask Quantra">
          <Brain className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-slate-950 animate-pulse"></span>
        </button>
      )}

      {/* ===== QUANTRA POPUP — desktop only ===== */}
      {quantraPopupOpen && (
        <div className="hidden lg:flex fixed bottom-28 right-8 z-50 flex-col"
          style={{ width: 440, height: 620, borderRadius: 20, overflow: 'hidden',
            background: lightMode ? '#ffffff' : '#0c1221',
            border: lightMode ? '1px solid rgba(15,23,42,0.10)' : '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.12)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: lightMode ? '1px solid rgba(15,23,42,0.08)' : '1px solid rgba(255,255,255,0.06)', flexShrink: 0, background: lightMode ? '#f8fafc' : 'rgba(255,255,255,0.02)' }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg, #7c3aed, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Brain style={{ width: 14, height: 14, color: '#fff' }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: lightMode ? '#0d1526' : '#fff', lineHeight: 1 }}>Quantra</p>
              <p style={{ fontSize: 10, color: lightMode ? '#64748b' : '#94a3b8', lineHeight: 1, marginTop: 2 }}>AI Financial Assistant</p>
            </div>
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', padding: '2px 7px', borderRadius: 4, background: lightMode ? 'rgba(37,99,235,0.12)' : 'rgba(124,58,237,0.15)', color: lightMode ? '#2563eb' : '#a78bfa', textTransform: 'uppercase' }}>AI</span>
            <button onClick={() => setQuantraPopupOpen(false)} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: lightMode ? '#64748b' : '#94a3b8' }}>
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            <QuantraAgent
              user={user}
              expenses={expenses}
              invoices={invoices}
              mileageTrips={mileageTrips}
              subscriptions={subscriptions}
              bankAccounts={bankAccounts}
              revenue={revenue}
              bills={bills}
              budgets={budgets}
              taxVaultSettings={forecastTaxVaultSettings}
              monthlyGoal={goalAmount}
              perms={perms}
              onCanProceed={canProceed}
              showToast={showToast}
              paygPrices={PAYG_PRICES}
              onInsights={setQuantraInsights}
            />
          </div>
        </div>
      )}

      {/* ===== FLOATING CFO POPUP (unknown subscription questions) ===== */}
      {cfoPopupOpen && cfoPopupData && (
        <CFOPopup
          user={user}
          data={cfoPopupData}
          onClose={() => { setCfoPopupOpen(false); setCfoPopupData(null); }}
          onConfirm={async (confirmedItems) => {
            // For unknown-subscriptions mode (bank statement patterns)
            if (cfoPopupData?.type === 'unknown-subscriptions') {
              for (const pattern of confirmedItems) {
                await saveSubscription(user.uid, {
                  id: `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  name: pattern.name,
                  cost: pattern.cost,
                  billingCycle: pattern.billingCycle,
                  category: 'Other',
                  status: 'active',
                  autoDetected: true,
                  learned: true,
                  renewalDate: new Date(Date.now() + (pattern.avgDaysBetween || 30) * 86400000).toISOString().split('T')[0],
                  notes: 'User confirmed from unknown pattern detection'
                });
              }
            }

            // For gmail-checklist mode, saving already happens in popup.
            const subsResult = await loadSubscriptions(user.uid);
            if (subsResult.success) setSubscriptions(subsResult.subscriptions);
            window.dispatchEvent(new CustomEvent('quantra-resolved-updated'));
            setCfoPopupOpen(false);
            setCfoPopupData(null);
          }}
        />
      )}

      {/* ===== MODALS ===== */}
      {/* Tax Vault Reminder */}
      {taxVaultReminder && (
        <TaxVaultReminderModal
          data={taxVaultReminder}
          userId={user?.uid}
          onConfirm={() => setTaxVaultReminder(null)}
          onSkip={() => setTaxVaultReminder(null)}
        />
      )}
      {showPricingModal && !isNative && (
        <PricingModal user={user} currentPlan={currentPlan} onClose={() => setShowPricingModal(false)} />
      )}
      {showPricingModal && isNative && (
        <NativePricingModal
          currentPlan={currentPlan}
          userId={user?.uid}
          onClose={() => setShowPricingModal(false)}
          onPurchaseSuccess={async (planKey, receiptData, productId) => {
            if (!user?.uid) return;
            // Optimistic update — show new plan in UI immediately
            setUser((prev) => prev ? { ...prev, plan: planKey } : prev);
            if (receiptData) {
              const controller = new AbortController();
              const abortId = setTimeout(() => controller.abort(), 15000);
              try {
                const token = await auth.currentUser?.getIdToken();
                const verifyRes = await fetch('https://us-central1-receiptwise-prod.cloudfunctions.net/verifyIAPPurchase', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ receiptData, uid: user.uid, planKey, productId }),
                  signal: controller.signal,
                });
                if (!verifyRes.ok) throw new Error(`verify failed: ${verifyRes.status}`);
              } catch (e) {
                console.warn('[IAP] Server verify failed, falling back to client write', e);
                const { doc, setDoc } = await import('firebase/firestore');
                const { db: fdb } = await import('./firebase');
                await setDoc(doc(fdb, 'users', user.uid, 'profile', 'data'), { plan: planKey }, { merge: true });
              } finally {
                clearTimeout(abortId);
              }
            } else {
              // No receipt — write plan directly (restore flow)
              const { doc, setDoc } = await import('firebase/firestore');
              const { db: fdb } = await import('./firebase');
              await setDoc(doc(fdb, 'users', user.uid, 'profile', 'data'), { plan: planKey }, { merge: true });
            }
          }}
        />
      )}
      {showAddCreditsModal && (
        <AddCreditsModal user={user} onClose={() => setShowAddCreditsModal(false)} showToast={showToast} />
      )}
      {showUploadModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl max-w-md w-full p-8">
            <h3 className="text-xl font-bold text-white mb-4">Scan Receipt</h3>
            {perms.tier === 'payg' && (
              <div className="mb-3">
                <PaygCostBadge action="receiptScan" user={user} paygPrices={PAYG_PRICES} />
              </div>
            )}
            {isNative ? (
              <div className="space-y-3">
                <button onClick={() => handleNativeCamera('camera')} className="centris-card flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-indigo-500/30 rounded-xl hover:border-indigo-500 transition-all">
                  <Camera className="w-8 h-8 text-indigo-400 mb-2" /><p className="text-white font-medium text-sm">Take Photo</p>
                </button>
                <button onClick={() => handleNativeCamera('library')} className="centris-card flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-violet-500/30 rounded-xl hover:border-violet-500 transition-all">
                  <Upload className="w-8 h-8 text-violet-400 mb-2" /><p className="text-white font-medium text-sm">Choose from Library</p>
                </button>
              </div>
            ) : (
              <label className="centris-card flex flex-col items-center justify-center w-full h-56 border-2 border-dashed border-indigo-500/30 rounded-xl cursor-pointer hover:border-indigo-500 transition-all"><Upload className="w-10 h-10 text-indigo-400 mb-3" /><p className="text-white font-medium text-sm">Click to upload receipt</p><p className="text-xs text-slate-400 mt-1">PNG, JPG up to 10MB</p><input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} /></label>
            )}
            <button onClick={() => setShowUploadModal(false)} className="w-full mt-4 bg-slate-800 text-slate-300 py-3 rounded-xl font-medium hover:bg-slate-700 transition-colors border border-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      </ModalPortal>}
      {scanningReceipt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl max-w-md w-full p-8 text-center py-10">
            <div className="relative inline-block mb-4"><div className="animate-spin rounded-full h-14 w-14 border-b-4 border-t-4 border-indigo-500"></div><Sparkles className="w-5 h-5 text-indigo-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" /></div>
            <p className="text-white font-medium">Scanning with AI...</p><p className="text-xs text-slate-400 mt-1">Extracting merchant, amount, and category</p>
          </div>
        </div>
      )}
      {showReceiptReview && ocrResult && <ReceiptReview ocrData={ocrResult} imageUrl={receiptImageUrl} onConfirm={handleReceiptConfirm} onCancel={handleReceiptCancel} />}
      {showOnboardingTour && (
        <OnboardingTour
          onComplete={() => {
            localStorage.setItem('centris_onboarding_v1', 'done');
            if (user?.uid && user.uid !== 'demo-user') saveUserProfile(user.uid, { onboardingCompleted: true }).catch(() => {});
            setShowOnboardingTour(false);
            setView('overview');
          }}
          onSkip={() => {
            localStorage.setItem('centris_onboarding_v1', 'done');
            if (user?.uid && user.uid !== 'demo-user') saveUserProfile(user.uid, { onboardingCompleted: true }).catch(() => {});
            setShowOnboardingTour(false);
          }}
        />
      )}
      {showDeleteConfirm && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl border border-red-500/20 max-w-sm w-full p-6">
            <div className="flex items-center space-x-3 mb-3"><AlertTriangle className="w-5 h-5 text-red-400" /><h3 className="text-lg font-bold text-white">Confirm Delete</h3></div>
            <p className="text-sm text-slate-300 mb-5">Are you sure? This action cannot be undone.</p>
            <div className="flex space-x-3">
              <button onClick={() => { setShowDeleteConfirm(false); setExpenseToDelete(null); }} className="flex-1 bg-slate-800 text-slate-300 px-4 py-2.5 rounded-xl font-medium hover:bg-slate-700 border border-slate-700 text-sm">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 bg-red-600 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-red-700 text-sm">Delete</button>
            </div>
          </div>
        </div>
      </ModalPortal>}
      {showDeleteAllConfirm && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl border border-red-500/20 max-w-sm w-full p-6">
            <div className="flex items-center space-x-3 mb-3"><AlertTriangle className="w-5 h-5 text-red-400" /><h3 className="text-lg font-bold text-white">Delete All?</h3></div>
            <p className="text-sm text-slate-300 mb-1">Delete all {expenses.length} expenses?</p><p className="text-xs text-red-400 mb-5">[!] This permanently deletes all data.</p>
            <div className="flex space-x-3">
              <button onClick={() => setShowDeleteAllConfirm(false)} className="flex-1 bg-slate-800 text-slate-300 px-4 py-2.5 rounded-xl font-medium hover:bg-slate-700 border border-slate-700 text-sm">Cancel</button>
              <button onClick={handleDeleteAll} className="flex-1 bg-red-600 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-red-700 text-sm">Delete All</button>
            </div>
          </div>
        </div>
      </ModalPortal>}
      {showDeleteAllRevenueConfirm && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl border border-red-500/20 max-w-sm w-full p-6">
            <div className="flex items-center space-x-3 mb-3"><AlertTriangle className="w-5 h-5 text-red-400" /><h3 className="text-lg font-bold text-white">Delete All?</h3></div>
            <p className="text-sm text-slate-300 mb-1">Delete all {revenue.length} revenue entries?</p><p className="text-xs text-red-400 mb-5">[!] This permanently deletes all data.</p>
            <div className="flex space-x-3">
              <button onClick={() => setShowDeleteAllRevenueConfirm(false)} className="flex-1 bg-slate-800 text-slate-300 px-4 py-2.5 rounded-xl font-medium hover:bg-slate-700 border border-slate-700 text-sm">Cancel</button>
              <button onClick={handleDeleteAllRevenue} className="flex-1 bg-red-600 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-red-700 text-sm">Delete All</button>
            </div>
          </div>
        </div>
      </ModalPortal>}

      {showTopLevelBankImport && <ModalPortal>
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[999] overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setShowTopLevelBankImport(false); }}>
          <div className="min-h-screen p-4 flex items-start justify-center">
            <div className="w-full max-w-4xl mt-10 mb-10">
              <button
                onClick={() => setShowTopLevelBankImport(false)}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-all group"
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/[0.03] group-hover:bg-white/[0.06] border border-white/[0.06]">
                  <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                </div>
                Back
              </button>
              <BankAnalyzer
                user={user}
                onCancel={() => setShowTopLevelBankImport(false)}
                onExpensesExtracted={async (expenseTransactions, incomeTransactions) => {
                  let importedCount = 0;
                  for (const t of (expenseTransactions || [])) {
                    const result = await saveExpense(user.uid, {
                      merchant: t.merchant || t.description,
                      amount: Math.abs(Number(t.amount)),
                      date: t.date,
                      category: t.category,
                      taxDeductible: t.taxDeductible || false,
                      notes: 'Imported from bank statement',
                      source: 'bank_statement_import',
                    });
                    if (result.success) importedCount++;
                  }
                  for (const t of (incomeTransactions || [])) {
                    await saveRevenue(user.uid, {
                      source: t.merchant || t.description,
                      amount: Math.abs(Number(t.amount)),
                      date: t.date,
                      description: t.description,
                      status: 'received',
                      fromBankStatement: true,
                      sourceType: 'bank_statement_import',
                      createdAt: new Date().toISOString(),
                    });
                  }
                  showToast(`Imported ${importedCount} transaction${importedCount !== 1 ? 's' : ''}. Check Finances to see your data.`, 'success', 5000);
                }}
              />
            </div>
          </div>
        </div>
      </ModalPortal>}

      {showTransactionReview && (
        <BankTransactionReviewModal
          transactions={bankTransactionsToReview}
          onConfirm={handleConfirmTransactionImport}
          onCancel={() => {
            setShowTransactionReview(false);
            setBankTransactionsToReview([]);
          }}
          existingExpenses={expenses}
          learnedKeywords={bankLearnedKeywords}
        />
      )}

      {/* Bank Onboarding Modal */}
      {showBankOnboarding && !bankOnboardingDismissed && <ModalPortal>
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl border border-indigo-500/30 max-w-lg w-full p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl"></div>

            <div className="relative">
              <button
                onClick={() => {
                  setShowBankOnboarding(false);
                  setBankOnboardingDismissed(true);
                  localStorage.setItem('bankOnboardingDismissed', 'true');
                }}
                className="absolute -top-4 -right-4 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-500/20 rounded-2xl mb-4">
                  <Building2 className="w-8 h-8 text-indigo-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Connect Your Bank Account</h2>
                <p className="text-slate-300 text-sm">Unlock powerful automation features</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3 centris-card rounded-lg p-4">
                  <div className="bg-green-500/20 p-2 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Auto-Track Income</p>
                    <p className="text-slate-400 text-xs mt-1">Automatically calculate tax reserves from deposits</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 centris-card rounded-lg p-4">
                  <div className="bg-blue-500/20 p-2 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Detect Subscriptions</p>
                    <p className="text-slate-400 text-xs mt-1">Find recurring charges automatically</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 centris-card rounded-lg p-4">
                  <div className="bg-purple-500/20 p-2 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Import Expenses</p>
                    <p className="text-slate-400 text-xs mt-1">Sync transactions for easy categorization</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 centris-card rounded-lg p-4">
                  <div className="bg-emerald-500/20 p-2 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Real-Time Balances</p>
                    <p className="text-slate-400 text-xs mt-1">See your cash flow at a glance</p>
                  </div>
                </div>
              </div>

              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-indigo-400" />
                  <p className="text-sm font-semibold text-indigo-300">Bank-Level Security</p>
                </div>
                <p className="text-xs text-indigo-200">
                  We use Plaid, trusted by over 11,000 financial apps. Your credentials are encrypted and we never store your login information.
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowBankOnboarding(false);
                    handleOpenPlaidModal();
                  }}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Building2 className="w-4 h-4" />
                  <span>Connect Bank Account</span>
                </button>

                <button
                  onClick={() => {
                    setShowBankOnboarding(false);
                    setBankOnboardingDismissed(true);
                    localStorage.setItem('bankOnboardingDismissed', 'true');
                  }}
                  className="w-full px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>}

      {/* Bank import options modal */}
      {showBankConnectComingSoon && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl border border-violet-500/30 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Import bank transactions</h3>
              <button type="button" onClick={() => setShowBankConnectComingSoon(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Import your transactions by uploading a bank statement (CSV/PDF) or scanning receipts — same deduction and categorization features, ready now.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowBankConnectComingSoon(false);
                  setShowTopLevelBankImport(true);
                }}
                className="w-full py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-500 transition-all flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload bank statement
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowBankConnectComingSoon(false);
                  setView('expenses');
                  setShowUploadModal(true);
                }}
                className="w-full py-2.5 rounded-xl border border-white/[0.1] text-slate-200 text-sm font-medium hover:bg-white/[0.05] transition-all flex items-center justify-center gap-2"
              >
                <Camera className="w-4 h-4" />
                Scan a receipt
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>}

      {showSwitchToLiveBankModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl border border-cyan-500/30 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Switch to live bank linking?</h3>
              <button type="button" onClick={() => setShowSwitchToLiveBankModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-300 mb-5">
              You&rsquo;ll connect accounts via Plaid for automatic sync. Bank statement uploads and receipt scans keep working anytime — they&rsquo;re not replaced.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => setShowSwitchToLiveBankModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-white/[0.1] text-slate-300 text-sm font-medium hover:bg-white/[0.05]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSwitchToLiveBankModal(false);
                  setBankImportMode(BANK_IMPORT_LIVE);
                  if (perms.plaidAccounts <= 0) {
                    setShowPricingModal(true);
                    return;
                  }
                  if (perms.plaidAccounts !== Infinity && bankAccounts.length >= perms.plaidAccounts) {
                    setShowPricingModal(true);
                    return;
                  }
                  setShowPlaidModal(true);
                }}
                className="flex-1 py-2.5 rounded-xl bg-cyan-600 text-white text-sm font-semibold hover:bg-cyan-500"
              >
                Continue to connect
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>}

      {/* Plaid link modal — only when live bank connection is enabled */}
      {PLAID_BANK_CONNECT_ENABLED && showPlaidModal && <ModalPortal>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[999] p-4">
          <div className="centris-card rounded-2xl border border-indigo-500/30 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Connect Bank Account</h3>
              <button type="button" onClick={() => setShowPlaidModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-300 mb-6">
              Securely connect your bank to enable automatic transaction syncing, tax reserve calculations, and subscription detection.
            </p>

            {bankAccounts.length > 0 && (
              <div className="mb-6">
                <p className="text-xs text-slate-400 mb-2">Currently Connected:</p>
                {bankAccounts.map((account, idx) => (
                  <div key={idx} className="centris-card rounded-lg p-3 mb-2">
                    <p className="text-sm text-white font-medium">{account.institutionName}</p>
                    <p className="text-xs text-slate-400">{account.name} ****{account.mask}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
              <p className="text-xs text-blue-300 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Bank-level encryption. Powered by Plaid.</span>
              </p>
            </div>

            <PlaidLinkButton
              user={user}
              onSuccess={handleBankConnected}
              onError={handleBankError}
              buttonText={bankAccounts.length > 0 ? "Add Another Account" : "Connect Bank Account"}
            />
          </div>
        </div>
      </ModalPortal>}
    </div>
  );
};

export default CentrisFinanceApp;






















